import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Label } from './components/ui/label.jsx';
import { Alert, AlertDescription } from './components/ui/alert';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import { toast } from 'sonner';
import { Mic, Search, Heart, Users, Music, Disc3, Radio, Crown, Volume2, Play, SkipForward, Repeat, Shuffle, TrendingUp, Zap, AlertCircle, MicOff, ExternalLink, X, GripVertical, Plus, ListMusic, Headphones, ThumbsUp, Share2, Newspaper, MessageCircle, Mic2, ShoppingBag, GraduationCap, Star, Youtube, Lightbulb, Building2, Sparkles, ArrowLeft, Pencil, ChevronLeft, ChevronRight, Eye, EyeOff, Info, Bell } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import BetaGate from './components/BetaGate';
import GateCountdownSettings from './components/GateCountdownSettings';
import IdEngineDiagnostics from './components/IdEngineDiagnostics';
import EmailBlastTab from './components/EmailBlastTab';
import MiniCountdown from './components/MiniCountdown';
import ScanStage from './components/TrackID/ScanStage';
import TrackIntel from './components/TrackID/TrackIntel';
import RegisterEditPanel from './components/TrackID/RegisterEditPanel';
import IndexCounter from './components/TrackID/IndexCounter';
import VerifiedIdBadge from './components/TrackID/VerifiedIdBadge';
import WrongEditReport from './components/TrackID/WrongEditReport';
import ResultArtwork from './components/TrackID/ResultArtwork';
import ConfirmIdButton from './components/TrackID/ConfirmIdButton';
import CommunitySuggestions from './components/TrackID/CommunitySuggestions';
import GetItRow from './components/TrackID/GetItRow';
import EngineCredit from './components/TrackID/EngineCredit';
import IdCorrectionsPanel from './components/TrackID/IdCorrectionsPanel';
import CuratedIdQueue from './components/TrackID/CuratedIdQueue';
import IdHunterMedal from './components/IdHunterMedal';
import './components/TrackID/trackid.css';
import AuthAtmosphere, { AuthCelebration } from './components/AuthAtmosphere';
import UserBadge, { UserBadgeGroup } from './components/UserBadge';
import EarlyAccess from './components/EarlyAccess';
import PosterViewer from './components/PosterViewer';
import UnsubscribePage from './components/UnsubscribePage';
import NewMemberTicker from './components/NewMemberTicker';
import MutualFriendsModal from './components/MutualFriendsModal';
import MobileSearchResultsModal from './components/MobileSearchResultsModal';
import SuggestionBoxModal from './components/SuggestionBoxModal';
import PushToVoteModal from './components/PushToVoteModal';
import IdResultActions from './components/IdResultActions';
import SpotifyIntegration from './components/SpotifyIntegration';
import SpotifyFullViewModal from './components/SpotifyFullViewModal';
import SoundCloudLibraryModal from './components/SoundCloudLibraryModal';
import FavoritesPickerModal from './components/FavoritesPickerModal';
import PlaylistPickerModal from './components/PlaylistPickerModal';
import SpotifyAdvancedFilters from './components/SpotifyAdvancedFilters/SpotifyAdvancedFilters';
import NewCommunityChat from './components/NewCommunityChat';
import ProducersCorner from './components/ProducersCorner';
import DJBoothModal from './components/DJBoothModal';
import DJBoothFeatureModal from './components/DJBoothFeatureModal';
import { EmoteLabLite } from './components/EmoteLab';
import ProducerCornerFeatureModal from './components/ProducerCornerFeatureModal';
import FestivalEvents from './components/FestivalEvents';
import MyTracksModal from './components/MyTracksModal';
import WhisperModal from './components/WhisperModal';
import WhisperConversations from './components/WhisperConversations';
import FriendRequestsModal from './components/FriendRequestsModal';
import WhisperIcon from './components/WhisperIcon';
import EditProfileModal from './components/EditProfileModal';
import ProfileView from './components/ProfileView';
import FriendsModal from './components/FriendsModal';
import GlobeVisualization3D from './components/GlobeVisualization3D';
import ProfileCard from './components/ProfileCard';
import AreaActivityModal from './components/AreaActivityModal';
import { JamWebSocketProvider } from './contexts/JamWebSocketContext';
import PlaylistPlayer from './components/PlaylistPlayer';
import LocalTrackUpload from './components/LocalTrackUpload';
import { getLocalTracks, clearAllLocalTracks, createTrackURL } from './utils/localTrackStorage';
import { SpotifyPlayerProvider } from './contexts/SpotifyPlayerContext';
import CreateJamModal from './components/CreateJamModal';
import JamSessionList from './components/JamSessionList';
import JamSessionModal from './components/JamSessionModal';
import LastFmSimilarTracksRail from './components/LastFmSimilarTracksRail';
import JamSessionHub from './components/JamSessionHub';
import AddTrackModal from './components/AddTrackModal';
import DiscoverNewAlert from './components/DiscoverNewAlert';
import PromoFeedModal from './components/PromoSpot/PromoFeedModal';
import DiscordWidget from './components/DiscordWidget';
import ProviderConnectRow from './components/ConnectNudgePills';
import DiscordShareModal from './components/DiscordShareModal';
import WishlistModal from './components/WishlistModal';
import WishlistStar from './components/WishlistStar';
import ArtistNotificationBell from './components/ArtistNotificationBell';
import ArtistNotificationsModal from './components/ArtistNotificationsModal';
import ExpiringNotifications from './components/ExpiringNotifications';
import NotificationsPanel from './components/NotificationsPanel';
import SimilarTracksModal from './components/SimilarTracksModal';
import TheWall from './components/TheWall';
import ShareToWallModal from './components/ShareToWallModal';
import DigginInTheCrates from './components/DigginInTheCrates/DigginInTheCrates';
import { MyCrates, SetBuilder } from './components/CrateBox';
import TourBot from './components/TourBot/TourBot';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Cross-device provider connection sync — localStorage key map (module scope).
const PROVIDER_TOKEN_KEYS = {
  spotify: { access: 'spotify_access_token', refresh: 'spotify_refresh_token' },
  soundcloud: { access: 'soundcloud_access_token', refresh: 'soundcloud_refresh_token' },
  discord: { access: 'discord_access_token', refresh: null },
};

// Extract YouTube video ID from various URL formats
const extractYoutubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /[?&]v=([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};


// Custom Audio Player Component
const CustomAudioPlayer = ({ trackId, previewUrl, trackTitle, trackArtist }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Listen for other players starting - stop this one
  useEffect(() => {
    const handleOtherPlayerStart = (e) => {
      if (e.detail.trackId !== trackId && audioRef.current && isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    };
    
    window.addEventListener('audioPlayerStart', handleOtherPlayerStart);
    return () => {
      window.removeEventListener('audioPlayerStart', handleOtherPlayerStart);
    };
  }, [trackId, isPlaying]);

  // Reset player when track changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [trackId, previewUrl]);

  // Update current time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };

    const updateDuration = () => {
      setDuration(audio.duration || 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isDragging]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        // Dispatch event to stop other players before playing this one
        window.dispatchEvent(new CustomEvent('audioPlayerStart', { detail: { trackId } }));
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleProgressDrag = (e) => {
    if (!isDragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    setCurrentTime(newTime);
  };

  const handleProgressMouseDown = () => {
    setIsDragging(true);
  };

  const handleProgressMouseUp = (e) => {
    if (isDragging && audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const percentage = x / rect.width;
      const newTime = percentage * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
    setIsDragging(false);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="discovery-player-section">
      <div className="preview-label-above" style={{ marginTop: '5px', marginBottom: '4px' }}>Preview</div>
      <div className="custom-audio-player">
        <audio ref={audioRef} src={previewUrl} preload="metadata" />
        
        {/* Main Player Row */}
        <div className="player-main-row">
          {/* Play/Pause Button */}
          <button className="player-play-btn" onClick={togglePlay} title={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? '⏸' : '▶'}
          </button>
          
          {/* Progress Bar */}
          <div 
            className="player-progress-container"
            onClick={handleProgressClick}
            onMouseDown={handleProgressMouseDown}
            onMouseMove={handleProgressDrag}
            onMouseUp={handleProgressMouseUp}
            onMouseLeave={handleProgressMouseUp}
          >
            <div className="player-progress-track">
              <div 
                className="player-progress-fill" 
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              >
                <div className="player-progress-scrubber"></div>
              </div>
            </div>
          </div>
          
          {/* Time Display */}
          <div className="player-time">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
        
        {/* Volume Control Row */}
        <div className="player-volume-row">
          <button className="player-volume-btn" onClick={toggleMute} title={isMuted ? 'Unmute' : 'Mute'}>
            {isMuted ? '🔇' : volume > 0.5 ? '🔊' : '🔉'}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="player-volume-slider"
          />
        </div>
      </div>
    </div>
  );
};

// WORKING MINIMAL VERSION - ALL YOUR PROGRESS PRESERVED
const ElectroShazam = ({ 
  showCreatePlaylistModal, 
  setShowCreatePlaylistModal, 
  showPlaylistDetailModal,
  setShowPlaylistDetailModal,
  showAddTrackModal,
  setShowAddTrackModal,
  showManageSuggestionsModal,
  setShowManageSuggestionsModal,
  showSpotifyLibraryModal,
  setShowSpotifyLibraryModal,
  currentPlaylist,
  setCurrentPlaylist,
  playlistModalData, 
  setPlaylistModalData,
  activePlaylistForAdding,
  setActivePlaylistForAdding,
  playPlaylist,
  playTrack,
  showPlaylistsModal,
  setShowPlaylistsModal,
  addTrackSearchResults,
  setAddTrackSearchResults,
  addTrackSearching,
  setAddTrackSearching
}) => {
  // Beta Access state - check if user has beta access
  // Also check for ?preview-gate=true URL param to force show gate
  const [hasBetaAccess, setHasBetaAccess] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('preview-gate') === 'true') {
      return false; // Force show gate for preview
    }
    if (urlParams.get('gate-bypass') === '1') {
      return true; // Automation/debug bypass — no storage needed
    }
    // Bypass beta gate for the Original 40 landing page + the public poster viewer
    const path = window.location.pathname;
    if (path === '/early-access' || path === '/poster') {
      localStorage.setItem('beatseek_beta_access', 'true');
      return true;
    }
    return localStorage.getItem('beatseek_beta_access') === 'true';
  });
  
  // State to force show gate (for admin preview)
  const [forceShowGate, setForceShowGate] = useState(false);

  // Event invite link (?invite=TOKEN) — bypasses the gate & nudges to the event after login
  const [pendingInvite, setPendingInvite] = useState(() => {
    try { return JSON.parse(localStorage.getItem('beatseek_pending_invite') || 'null'); } catch { return null; }
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inviteToken = urlParams.get('invite');
    if (!inviteToken) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/beta/verify-invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: inviteToken })
        });
        const data = await res.json();
        if (data.valid) {
          localStorage.setItem('beatseek_beta_access', 'true');
          const invite = { event_id: data.event_id, title: data.title };
          localStorage.setItem('beatseek_pending_invite', JSON.stringify(invite));
          setPendingInvite(invite);
          setHasBetaAccess(true);
        }
      } catch (e) {
        console.error('Invite verification failed:', e);
      }
      window.history.replaceState({}, '', window.location.pathname);
    })();
  }, []);

  const dismissPendingInvite = () => {
    localStorage.removeItem('beatseek_pending_invite');
    setPendingInvite(null);
  };

  const openInvitedEvent = () => {
    const inv = pendingInvite;
    dismissPendingInvite();
    if (!inv) return;
    setShowDJBoothModal(true);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('djBoothOpenEventPlanner', { detail: { eventId: inv.event_id } }));
    }, 500);
  };
  
  // Beta notice banner state - shows randomly on dashboard visits
  const [showBetaBanner, setShowBetaBanner] = useState(false);
  
  const dismissBetaBanner = () => {
    setShowBetaBanner(false);
  };
  
  // Core state - authentication system
  const [user, setUser] = useState(null);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [authCelebration, setAuthCelebration] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [countdown, setCountdown] = useState(10); // Add countdown state
  const countdownIntervalRef = useRef(null); // Add countdown interval reference
  const [identifiedTrack, setIdentifiedTrack] = useState(null);
  // Dev/QA: ?idpreview=1 renders a sample ID result card without recording audio
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get('idpreview')) return;
    if (identifiedTrack) return;
    const t = setTimeout(() => {
      setIdentifiedTrack({
      id: 'idpreview', title: 'Delilah (pull me out of this)', artist: 'Fred again..',
      genre: 'House', subgenre: 'UK Garage', artwork: null, bpm: 140, key: 'F minor',
      bpmKeySource: 'getsongbpm', canonicalGenre: 'House', subgenres: ['UK Garage', 'Bass House'],
      genreVerified: true, versionType: 'remix', remixer: 'Sammy Virji', label: 'Atlantic', year: 2022,
      confidence: '92%', confidenceScore: 92, matchedIn: 3.2, source: 'ACRCloud+AudD',
      message: 'Electronic track identified successfully!', versions: [], alternates: [],
      });
      setActiveTab('listen');
    }, 2500);
    return () => clearTimeout(t);
  }, [identifiedTrack]);


  const [history, setHistory] = useState([]);
  const [idStats, setIdStats] = useState(null);
  const [favorites, setFavorites] = useState([]);
  // Check localStorage for user's default tab preference
  const [activeTab, setActiveTab] = useState(() => {
    const savedDefault = localStorage.getItem('beatseek_default_tab');
    return savedDefault || 'social'; // Default to Dashboard ('social')
  });
  const [authError, setAuthError] = useState(null);

  // Show beta banner randomly when user logs in or returns to dashboard
  useEffect(() => {
    if (user) {
      // 70% chance to show on each dashboard visit
      const shouldShow = Math.random() < 0.7;
      if (shouldShow) {
        setShowBetaBanner(true);
        // Auto-hide after 30 seconds
        const timer = setTimeout(() => {
          setShowBetaBanner(false);
        }, 30000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, activeTab]);

  // Handle social auth callback (Google, Discord, X)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get('auth_token');
    const authProvider = urlParams.get('auth_provider');
    
    // Handle OAuth callback with token
    if (authToken) {
      console.log(`✅ Social login callback from ${authProvider || 'unknown'}`);
      
      // Store token
      localStorage.setItem('beatseek_token', authToken);
      
      // Grant beta access for social login users
      localStorage.setItem('beatseek_beta_access', 'true');
      setHasBetaAccess(true);
      
      // Fetch user data
      fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      })
        .then(res => res.json())
        .then(userData => {
          const userWithUserId = { ...userData, user_id: userData.id };
          setUser(userWithUserId);
          localStorage.setItem('beatseek_user', JSON.stringify(userWithUserId));
          setActiveTab(localStorage.getItem('beatseek_default_tab') || 'social');
          console.log(`✅ Logged in via ${authProvider}:`, userWithUserId.name);
        })
        .catch(err => console.error('Error fetching user after social login:', err));
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Handle Emergent Google Auth callback (session_id in hash)
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        console.log('✅ Google auth callback with session_id:', sessionId);
        console.log('✅ Using BACKEND_URL:', BACKEND_URL);
        
        // Grant beta access for Google login users
        localStorage.setItem('beatseek_beta_access', 'true');
        setHasBetaAccess(true);
        
        // Exchange session_id for token
        fetch(`${BACKEND_URL}/api/auth/google/session`, {
          headers: { 'X-Session-ID': sessionId }
        })
          .then(res => {
            console.log('✅ Google session response status:', res.status);
            return res.json();
          })
          .then(data => {
            console.log('✅ Google session data:', data);
            if (data.access_token) {
              localStorage.setItem('beatseek_token', data.access_token);
              const userWithUserId = { ...data.user, user_id: data.user.id };
              setUser(userWithUserId);
              localStorage.setItem('beatseek_user', JSON.stringify(userWithUserId));
              setActiveTab(localStorage.getItem('beatseek_default_tab') || 'social');
              console.log('✅ Logged in via Google:', userWithUserId.name);
            } else if (data.detail) {
              console.error('❌ Google auth error:', data.detail);
            }
          })
          .catch(err => {
            console.error('❌ Error with Google auth:', err);
          });
        
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // Discovery Zone state
  const [discogsTracks, setDiscogsTracks] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('All Genres');  // Default to all genres for variety
  const [discoverySearchQuery, setDiscoverySearchQuery] = useState('');
  const [isLoadingDiscogs, setIsLoadingDiscogs] = useState(false);
  const [discogsError, setDiscogsError] = useState(null);

  // Recommended For You state  
  const [recommendedArtists, setRecommendedArtists] = useState([]);
  const [recommendedMessage, setRecommendedMessage] = useState('');
  const [recommendedLoading, setRecommendedLoading] = useState(false);

  // Recently Explored Artists state
  const [exploredArtists, setExploredArtists] = useState([]);

  // Trending on BEATSEEK state
  const [trendingBeatseekArtists, setTrendingBeatseekArtists] = useState([]);

  // Artist availability cache (which artists have TheAudioDB data)
  const [artistAvailability, setArtistAvailability] = useState({});

  // Artist Explorer state
  const [showArtistExplorer, setShowArtistExplorer] = useState(false);
  const [explorerArtists, setExplorerArtists] = useState([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerQuery, setExplorerQuery] = useState('');
  const [explorerGenre, setExplorerGenre] = useState('All Genres');
  const [explorerPage, setExplorerPage] = useState(1);
  const EXPLORER_PER_PAGE = 60;

  // Community Banner state
  const [bannerItems, setBannerItems] = useState([]);
  const [bannerLoading, setBannerLoading] = useState(false);

  // BEATSEEK News (admin announcements) state
  const [adminAnnouncements, setAdminAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '', priority: 'normal', expiry_date: '' });
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  // Songstats state
  const [songstatsData, setSongstatsData] = useState(null);
  const [editIndexPrefill, setEditIndexPrefill] = useState(null);
  const [songstatsLoading, setSongstatsLoading] = useState(false);
  const [trackDnaModal, setTrackDnaModal] = useState(null); // holds songstats_data for the popup
  
  // Filter checkboxes and link state
  const [genreFilterEnabled, setGenreFilterEnabled] = useState(true);
  const [searchFilterEnabled, setSearchFilterEnabled] = useState(false);
  const [filtersLinked, setFiltersLinked] = useState(false);
  
  // Advanced Filters state - Source selection and BPM
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedSources, setSelectedSources] = useState(['spotify', 'soundcloud', 'deezer', 'discogs']);
  const [bpmRange, setBpmRange] = useState({ min: 120, max: 130 });
  const [bpmFilterEnabled, setBpmFilterEnabled] = useState(false);
  const [bpmOnlyMode, setBpmOnlyMode] = useState(false); // BPM Only mode - searches all genres
  const [selectedBpmPreset, setSelectedBpmPreset] = useState(null); // Track which preset is selected
  const [advancedGenre, setAdvancedGenre] = useState('All Genres');
  const [filterMode, setFilterMode] = useState('basic'); // 'basic' or 'advanced'
  
  // Spotify Advanced Audio Filters state
  const [spotifyAudioFilters, setSpotifyAudioFilters] = useState({
    bpmEnabled: false,
    bpmRange: { min: 120, max: 130 },
    keyEnabled: false,
    selectedKeys: [],
    energyEnabled: false,
    energyRange: { min: 0.4, max: 0.8 },
    danceabilityEnabled: false,
    danceabilityRange: { min: 0.5, max: 0.9 }
  });
  const [isApplyingSpotifyFilters, setIsApplyingSpotifyFilters] = useState(false);
  
  // Track previous source for auto-refresh
  const prevSelectedSourceRef = useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTracks, setHasMoreTracks] = useState(true);
  const tracksPerPage = 25;
  
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);
  const [selectedYouTubeTrack, setSelectedYouTubeTrack] = useState(null);
  const [showSimilarTracksModal, setShowSimilarTracksModal] = useState(false);
  const [similarTracksSource, setSimilarTracksSource] = useState(null);
  const [cameFromSimilarTracks, setCameFromSimilarTracks] = useState(false);
  const [showTheWall, setShowTheWall] = useState(false);
  // Discovery Zone → Share to The Wall (Phase 2)
  const [shareToWallTrack, setShareToWallTrack] = useState(null);
  const [youtubeVideoData, setYoutubeVideoData] = useState(null);
  const [loadingYoutubeVideo, setLoadingYoutubeVideo] = useState(false);
  const [youtubeError, setYoutubeError] = useState(null);
  
  // Custom YouTube redirect alert
  const [showYouTubeRedirectAlert, setShowYouTubeRedirectAlert] = useState(false);
  const [youtubeRedirectUrl, setYoutubeRedirectUrl] = useState('');

  // Artist Deep Dive modal state
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [artistModalData, setArtistModalData] = useState(null);
  const [artistModalLoading, setArtistModalLoading] = useState(false);
  const [artistAlbums, setArtistAlbums] = useState([]);
  const [artistVideos, setArtistVideos] = useState([]);
  const [similarArtists, setSimilarArtists] = useState([]);
  const [artistMbData, setArtistMbData] = useState(null);
  const [artistMbReleases, setArtistMbReleases] = useState([]);
  const [artistInsights, setArtistInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showInsights, setShowInsights] = useState(false);

  // Jam Session state - "That's My Jam"
  const [showCreateJamModal, setShowCreateJamModal] = useState(false);
  const [showJamSessionList, setShowJamSessionList] = useState(false);
  const [currentJamSession, setCurrentJamSession] = useState(null);
  const [showJamSessionModal, setShowJamSessionModal] = useState(false);
  
  // Force render trigger for modal
  const [modalRenderKey, setModalRenderKey] = useState(0);


  // Debug: Log when showCreateJamModal changes
  useEffect(() => {
    console.log('🔄 showCreateJamModal state changed:', showCreateJamModal);
  }, [showCreateJamModal]);


  // Spotify Integration state
  const [showSpotifyModal, setShowSpotifyModal] = useState(false);
  const [spotifyTrackToSave, setSpotifyTrackToSave] = useState(null);
  const [showSpotifyFullView, setShowSpotifyFullView] = useState(false);
  const [showSoundCloudLibrary, setShowSoundCloudLibrary] = useState(false);
  const [showFavoritesPicker, setShowFavoritesPicker] = useState(false);
  const [showPlaylistPicker, setShowPlaylistPicker] = useState(false);
  const [showLocalTrackUpload, setShowLocalTrackUpload] = useState(false);
  const [showJamSessionSelector, setShowJamSessionSelector] = useState(false);
  const [selectedLocalTrackForJam, setSelectedLocalTrackForJam] = useState(null);
  const [availableJamSessions, setAvailableJamSessions] = useState([]);
  const [localTracks, setLocalTracks] = useState([]);

  // Discord Share state
  const [showDiscordShareModal, setShowDiscordShareModal] = useState(false);
  const [discordShareTrack, setDiscordShareTrack] = useState(null);
  const [discordShareType, setDiscordShareType] = useState('track'); // 'track', 'playlist', 'stats', 'gear', 'jam'

  // Load local tracks on mount
  useEffect(() => {
    const loadLocalTracks = async () => {
      try {
        const tracks = await getLocalTracks();
        setLocalTracks(tracks);
        console.log(`✅ Loaded ${tracks.length} local tracks from IndexedDB`);
      } catch (error) {
        console.error('Error loading local tracks:', error);
      }
    };
    
    loadLocalTracks();
  }, []);
  
  // Feature Card Stats - Fetch on mount
  useEffect(() => {
    const fetchFeatureCardStats = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/feature-cards/stats`);
        if (response.ok) {
          const stats = await response.json();
          const statsMap = {};
          stats.forEach(stat => {
            statsMap[stat.feature_id] = {
              likes: stat.likes_count,
              shares: stat.shares_count
            };
          });
          setFeatureCardStats(statsMap);
        }
      } catch (error) {
        console.error('Error fetching feature card stats:', error);
      }
    };
    
    fetchFeatureCardStats();
  }, []);
  
  // Producer Corner Feature Card Stats - Fetch on mount
  useEffect(() => {
    const fetchProducerFeatureStats = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/producer-features/stats`);
        if (response.ok) {
          const stats = await response.json();
          const statsMap = {};
          stats.forEach(stat => {
            statsMap[stat.feature_id] = {
              likes: stat.likes_count,
              shares: stat.shares_count
            };
          });
          setProducerFeatureStats(statsMap);
        }
      } catch (error) {
        console.error('Error fetching producer feature stats:', error);
      }
    };
    
    fetchProducerFeatureStats();
  }, []);
  
  // Fetch user's liked features when user logs in
  useEffect(() => {
    const fetchUserLikedFeatures = async () => {
      if (!user) {
        setUserLikedFeatures({});
        return;
      }
      
      const token = localStorage.getItem('beatseek_token');
      if (!token) return;
      
      const featureIds = ['news', 'forum', 'gigs', 'marketplace', 'tools', 'academy'];
      const likedMap = {};
      
      for (const featureId of featureIds) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/feature-cards/${featureId}/user-status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            likedMap[featureId] = data.has_liked;
          }
        } catch (error) {
          console.error(`Error fetching like status for ${featureId}:`, error);
        }
      }
      
      setUserLikedFeatures(likedMap);
    };
    
    fetchUserLikedFeatures();
  }, [user]);
  
  // Fetch user's liked producer features when user logs in
  useEffect(() => {
    const fetchUserLikedProducerFeatures = async () => {
      if (!user) {
        setUserLikedProducerFeatures({});
        return;
      }
      
      const token = localStorage.getItem('beatseek_token');
      if (!token) return;
      
      const featureIds = ['videos', 'forum', 'tips', 'demos', 'labels', 'news'];
      const likedMap = {};
      
      for (const featureId of featureIds) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/producer-features/${featureId}/user-status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            likedMap[featureId] = data.has_liked;
          }
        } catch (error) {
          console.error(`Error fetching producer like status for ${featureId}:`, error);
        }
      }
      
      setUserLikedProducerFeatures(likedMap);
    };
    
    fetchUserLikedProducerFeatures();
  }, [user]);
  
  // Load expiring items count when user logs in
  useEffect(() => {
    if (user) {
      loadExpiringCount();
    } else {
      setExpiringCount(0);
    }
  }, [user]);

  // NOTE: the dashboard action-icon legend no longer auto-opens on mobile — it was
  // covering the dashboard header and intercepting taps on the provider icons. Users
  // open it on demand via the "?" Quick Key button.

  // Handle feature card like toggle
  const handleFeatureCardLike = async (featureId, e) => {
    e.stopPropagation();
    
    if (!user) {
      alert('Please sign up or log in to like features!');
      return;
    }
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please log in to like features!');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/feature-cards/${featureId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update local state
        setUserLikedFeatures(prev => ({ ...prev, [featureId]: data.has_liked }));
        setFeatureCardStats(prev => ({
          ...prev,
          [featureId]: {
            ...prev[featureId],
            likes: data.likes_count
          }
        }));
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };
  
  // Handle feature card share
  const handleFeatureCardShare = async (featureId, e) => {
    e.stopPropagation();
    
    if (!user) {
      alert('Please sign up or log in to share features!');
      return;
    }
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please log in to share features!');
      return;
    }
    
    // Copy link to clipboard
    const shareUrl = `${window.location.origin}?feature=dj-booth-${featureId}`;
    navigator.clipboard.writeText(shareUrl);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/feature-cards/${featureId}/share`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update local state
        setFeatureCardStats(prev => ({
          ...prev,
          [featureId]: {
            ...prev[featureId],
            shares: data.shares_count
          }
        }));
        alert('Link copied! Share it with fellow Beatseekers 🎧');
      }
    } catch (error) {
      console.error('Error recording share:', error);
      // Still show success since link was copied
      alert('Link copied! Share it with fellow Beatseekers 🎧');
    }
  };
  
  // Open individual feature modal
  const openFeatureModal = (featureId) => {
    setActiveFeatureId(featureId);
    setShowFeatureModal(true);
  };
  
  // Open full DJ Booth modal (View All Features) - defaults to news tab
  const openFullDJBoothModal = () => {
    setShowDJBoothModal(true);
    // Reset to news tab
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('djBoothSetTab', { detail: 'news' }));
    }, 100);
  };
  
  // Producer Corner - Handle feature card like toggle
  const handleProducerFeatureLike = async (featureId, e) => {
    e.stopPropagation();
    
    if (!user) {
      alert('Please sign up or log in to like features!');
      return;
    }
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please log in to like features!');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/producer-features/${featureId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserLikedProducerFeatures(prev => ({ ...prev, [featureId]: data.has_liked }));
        setProducerFeatureStats(prev => ({
          ...prev,
          [featureId]: {
            ...prev[featureId],
            likes: data.likes_count
          }
        }));
      }
    } catch (error) {
      console.error('Error toggling producer feature like:', error);
    }
  };
  
  // Producer Corner - Handle feature card share
  const handleProducerFeatureShare = async (featureId, e) => {
    e.stopPropagation();
    
    if (!user) {
      alert('Please sign up or log in to share features!');
      return;
    }
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please log in to share features!');
      return;
    }
    
    const shareUrl = `${window.location.origin}?feature=producer-${featureId}`;
    navigator.clipboard.writeText(shareUrl);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/producer-features/${featureId}/share`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducerFeatureStats(prev => ({
          ...prev,
          [featureId]: {
            ...prev[featureId],
            shares: data.shares_count
          }
        }));
        alert('Link copied! Share it with fellow producers 🎹');
      }
    } catch (error) {
      console.error('Error recording producer feature share:', error);
      alert('Link copied! Share it with fellow producers 🎹');
    }
  };
  
  // Open individual producer feature modal
  const openProducerFeatureModal = (featureId) => {
    setActiveProducerFeatureId(featureId);
    setShowProducerFeatureModal(true);
  };
  
  // Open full Producer's Corner modal with specific category
  const openFullProducersCorner = (category = 'videos') => {
    setProducerCornerInitialCategory(category);
    setShowProducersCorner(true);
  };
  
  // Listen for cross-navigation events
  useEffect(() => {
    const handleOpenPromoSpot = (event) => {
      const { promo, promoId } = event.detail || {};
      console.log('🔵 handleOpenPromoSpot called with promoId:', promoId);
      // Close DJ Booth if open
      setShowDJBoothModal(false);
      // Switch to social tab
      setActiveTab('social');
      // Open Promo Spot
      setTimeout(() => {
        setShowPromoSpot(true);
        // If promoId provided, trigger opening that specific promo
        if (promoId) {
          setTimeout(() => {
            console.log('🔵 Dispatching openPromoDetail event with promoId:', promoId);
            const openPromoDetailEvent = new CustomEvent('openPromoDetail', {
              detail: { promoId }
            });
            window.dispatchEvent(openPromoDetailEvent);
          }, 500);
        }
      }, 100);
    };

    const handleOpenEventCalendar = (event) => {
      const { eventId, eventDate } = event.detail || {};
      console.log('🟢 handleOpenEventCalendar called with eventId:', eventId);
      // Close Promo Spot if open
      setShowPromoSpot(false);
      // Switch to DJ Booth
      setTimeout(() => {
        setShowDJBoothModal(true);
        // Trigger opening event calendar tab with specific event
        setTimeout(() => {
          console.log('🟢 Dispatching djBoothOpenEventCalendar event with eventId:', eventId);
          const openCalendarEvent = new CustomEvent('djBoothOpenEventCalendar', {
            detail: { eventId, eventDate }
          });
          window.dispatchEvent(openCalendarEvent);
        }, 500);
      }, 100);
    };

    const handleOpenEventFromPromo = (event) => {
      const { eventId } = event.detail || {};
      console.log('🟡 handleOpenEventFromPromo called with eventId:', eventId);
      // Close any modals
      setShowPromoSpot(false);
      // Switch to DJ Booth and open event calendar
      setTimeout(() => {
        setShowDJBoothModal(true);
        setTimeout(() => {
          console.log('🟡 Dispatching djBoothOpenEventCalendar event with eventId:', eventId);
          const openCalendarEvent = new CustomEvent('djBoothOpenEventCalendar', {
            detail: { eventId }
          });
          window.dispatchEvent(openCalendarEvent);
        }, 500);
      }, 100);
    };

    // Handle navigation from Festival Events to Gig & Event Hub calendar
    const handleNavigateToGigHub = (event) => {
      const { tab, eventDate } = event.detail || {};
      console.log('🎫 handleNavigateToGigHub called with tab:', tab, 'eventDate:', eventDate);
      // Close Festival Events modal is handled by onClose in FestivalEvents
      // Open DJ Booth Modal immediately
      setShowDJBoothModal(true);
      // Dispatch calendar event with minimal delay (just enough for modal to mount)
      setTimeout(() => {
        console.log('🎫 Dispatching djBoothOpenEventCalendar event with eventDate:', eventDate);
        const openCalendarEvent = new CustomEvent('djBoothOpenEventCalendar', {
          detail: { tab: 'calendar', eventDate }
        });
        window.dispatchEvent(openCalendarEvent);
      }, 150);
    };

    window.addEventListener('openPromoSpot', handleOpenPromoSpot);
    window.addEventListener('openEventCalendar', handleOpenEventCalendar);
    window.addEventListener('openEventFromPromo', handleOpenEventFromPromo);
    window.addEventListener('navigateToGigHub', handleNavigateToGigHub);

    // Open the Event Planner on a specific event (e.g. from a Wall announcement CTA)
    const handleOpenEventPlanner = (event) => {
      const { eventId } = event.detail || {};
      if (!eventId) return;
      setShowDJBoothModal(true);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('djBoothOpenEventPlanner', { detail: { eventId } }));
      }, 400);
    };
    window.addEventListener('openEventPlanner', handleOpenEventPlanner);

    return () => {
      window.removeEventListener('openPromoSpot', handleOpenPromoSpot);
      window.removeEventListener('openEventCalendar', handleOpenEventCalendar);
      window.removeEventListener('openEventFromPromo', handleOpenEventFromPromo);
      window.removeEventListener('navigateToGigHub', handleNavigateToGigHub);
      window.removeEventListener('openEventPlanner', handleOpenEventPlanner);
    };
  }, []);

  // Spotify OAuth state

  // Jam Session discovery context tracking
  const [isDiscoveringForJam, setIsDiscoveringForJam] = useState(false);
  const [targetSessionId, setTargetSessionId] = useState(null);

  // Crate Box discovery context tracking
  const [isDiscoveringForCrate, setIsDiscoveringForCrate] = useState(false);
  const [targetCrateId, setTargetCrateId] = useState(null);
  const [targetCrateName, setTargetCrateName] = useState(null);

  // Quick crate picker for discovery track cards
  const [quickCratePickerTrack, setQuickCratePickerTrack] = useState(null); // track.id with open picker
  const [quickCrateList, setQuickCrateList] = useState([]);
  const [fromReleaseRadar, setFromReleaseRadar] = useState(false); // navigated from Release Radar Find

  // Close crate picker when clicking outside
  useEffect(() => {
    if (!quickCratePickerTrack) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid^="crate-picker-"]') && !e.target.closest('[data-testid^="add-to-crate-"]')) {
        setQuickCratePickerTrack(null);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [quickCratePickerTrack]);

  const [spotifyConnected, setSpotifyConnected] = useState(() => {
    const token = localStorage.getItem('spotify_access_token');
    console.log('🎵 Spotify state init - token:', token ? 'exists' : 'none');
    return !!token;
  });
  const [spotifyAccessToken, setSpotifyAccessToken] = useState(() => {
    const token = localStorage.getItem('spotify_access_token');
    console.log('🎵 Spotify access token init:', token ? token.substring(0, 20) + '...' : 'none');
    return token;
  });
  
  // Spotify Playlist Manager state
  const [spotifyPlaylists, setSpotifyPlaylists] = useState([]);
  const [isLoadingPlaylists, setIsLoadingPlaylists] = useState(false);
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [playlistSearchTerm, setPlaylistSearchTerm] = useState('');
  
  // Enhanced Browse Mode state
  const [isBrowseMode, setIsBrowseMode] = useState(false);
  const [playlistSortBy, setPlaylistSortBy] = useState('recent');
  const [playlistFilterBy, setPlaylistFilterBy] = useState('all');

  // SoundCloud Integration state
  const [soundcloudConnected, setSoundcloudConnected] = useState(() => {
    const token = localStorage.getItem('soundcloud_access_token');
    console.log('☁️ SoundCloud state init - token:', token ? 'exists' : 'none');
    return !!token;
  });
  const [soundcloudAccessToken, setSoundcloudAccessToken] = useState(() => {
    const token = localStorage.getItem('soundcloud_access_token');
    console.log('☁️ SoundCloud access token init:', token ? token.substring(0, 20) + '...' : 'none');
    return token;
  });
  const [soundcloudUser, setSoundcloudUser] = useState(() => {
    const userData = localStorage.getItem('soundcloud_user');
    console.log('☁️ SoundCloud user init:', userData ? 'exists' : 'none');
    return userData ? JSON.parse(userData) : null;
  });
  const [soundcloudPlaylists, setSoundcloudPlaylists] = useState([]);
  const [soundcloudLikes, setSoundcloudLikes] = useState([]);
  const [isLoadingSoundcloud, setIsLoadingSoundcloud] = useState(false);

  // Enhanced playlist filtering and sorting
  const getFilteredAndSortedPlaylists = () => {
    let filtered = spotifyPlaylists.filter(playlist => {
      const matches = playlist.name.toLowerCase().includes((playlistSearchTerm || '').toLowerCase());
      return matches;
    });

    // Apply filter
    if (playlistFilterBy === 'my_playlists') {
      filtered = filtered.filter(playlist => playlist.owner === 'user');
    } else if (playlistFilterBy === 'followed') {
      filtered = filtered.filter(playlist => playlist.owner !== 'user');
    }

    // Apply sort
    switch (playlistSortBy) {
      case 'alphabetical':
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case 'track_count':
        return filtered.sort((a, b) => (b.tracks_total || 0) - (a.tracks_total || 0));
      case 'recent':
      default:
        return filtered; // Keep original order (most recent first)
    }
  };

  const filteredPlaylists = getFilteredAndSortedPlaylists();

  // Social Features - Phase 2: Friend Search & Connection System
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [mutualModalTarget, setMutualModalTarget] = useState(null);
  const [showMobileSearchModal, setShowMobileSearchModal] = useState(false);
  const [mobileSearchScrollTop, setMobileSearchScrollTop] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showFriendRequests, setShowFriendRequests] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [showGlobeModal, setShowGlobeModal] = useState(false);
  const [showHomeBaseModal, setShowHomeBaseModal] = useState(false); // New modal for Location Check button
  const [globeRotationAlertShown, setGlobeRotationAlertShown] = useState(false); // Track if rotation alert was shown
  
  // Enhanced user search filters
  const [searchUserType, setSearchUserType] = useState('all');
  const [searchGenre, setSearchGenre] = useState('all');
  const [highlightUserId, setHighlightUserId] = useState(null);
  
  // Globe interaction state
  const [globeRotation, setGlobeRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Check-In Global - Dual Location System
  const [globeTab, setGlobeTab] = useState('checkins'); // 'checkins' or 'area-activity' (removed 'headquarters')
  const [headquartersLocations, setHeadquartersLocations] = useState([]);
  const [activeCheckins, setActiveCheckins] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [myHeadquarters, setMyHeadquarters] = useState(null);
  const [myCheckin, setMyCheckin] = useState(null);
  
  // Globe Search state
  const [globeSearchQuery, setGlobeSearchQuery] = useState('');
  const [globeSearchResults, setGlobeSearchResults] = useState([]);
  
  // Pin Action Menu state
  const [showPinActionMenu, setShowPinActionMenu] = useState(false);
  const [selectedPinUser, setSelectedPinUser] = useState(null);
  const [selectedPinPosition, setSelectedPinPosition] = useState(null);
  
  // Recent Discoveries state
  const [showRecentDiscoveries, setShowRecentDiscoveries] = useState(false);
  const [discoveriesTab, setDiscoveriesTab] = useState('music'); // 'music' or 'community'
  const [trendingTab, setTrendingTab] = useState('music'); // 'music' or 'community'
  const [loadingDiscoveries, setLoadingDiscoveries] = useState(false);
  
  // Trending Tracks state
  const [showTrendingTracks, setShowTrendingTracks] = useState(false);
  
  // Community Chat state
  const [showCommunityChat, setShowCommunityChat] = useState(false);
  
  // The Promo Spot state
  const [showPromoSpot, setShowPromoSpot] = useState(false);
  
  // Discoveries choice modal
  const [showDiscoveriesChoice, setShowDiscoveriesChoice] = useState(false);
  
  // Producers Corner state
  const [showProducersCorner, setShowProducersCorner] = useState(false);
  const [producerCornerInitialCategory, setProducerCornerInitialCategory] = useState('videos');
  
  // Producer Corner Individual Feature Modal state
  const [showProducerFeatureModal, setShowProducerFeatureModal] = useState(false);
  const [activeProducerFeatureId, setActiveProducerFeatureId] = useState(null);
  
  // Producer Corner Feature Card Stats state
  const [producerFeatureStats, setProducerFeatureStats] = useState({});
  const [userLikedProducerFeatures, setUserLikedProducerFeatures] = useState({});
  
  // DJ Booth Modal state
  const [showDJBoothModal, setShowDJBoothModal] = useState(false);
  
  // Individual Feature Modal state
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [activeFeatureId, setActiveFeatureId] = useState(null);
  
  // Feature Card Stats state
  const [featureCardStats, setFeatureCardStats] = useState({});
  const [userLikedFeatures, setUserLikedFeatures] = useState({});
  
  // Festival and Events state
  const [showFestivalEvents, setShowFestivalEvents] = useState(false);
  const [festivalEventsInitialTab, setFestivalEventsInitialTab] = useState('upcoming');
  
  // Wishlist state
  const [showWishlistModal, setShowWishlistModal] = useState(false);
  
  // Artist Notifications state
  const [showArtistNotifications, setShowArtistNotifications] = useState(false);
  
  // Expiring Notifications state
  const [showExpiringNotifications, setShowExpiringNotifications] = useState(false);
  const [showSuggestionBox, setShowSuggestionBox] = useState(false);
  const [pushVoteTarget, setPushVoteTarget] = useState(null);
  const [pollTallies, setPollTallies] = useState({});
  const [expiringCount, setExpiringCount] = useState(0);

  // Platform Notifications state
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);

  // Dashboard action buttons legend (mobile icon key)
  const [showActionLegend, setShowActionLegend] = useState(false);
  
  // Refresh button spinning states
  const [isRefreshingMusic, setIsRefreshingMusic] = useState(false);
  const [isRefreshingCommunity, setIsRefreshingCommunity] = useState(false);
  
  // Helper to open Festival Events to My Events tab (also closes DJ Booth modal)
  const openFestivalEventsToMyEvents = useCallback(() => {
    // Close the DJ Booth feature modal first
    setShowFeatureModal(false);
    setActiveFeatureId(null);
    // Then open Festival Events to My Events tab
    setFestivalEventsInitialTab('my-events');
    setShowFestivalEvents(true);
  }, []);
  
  // Listen for window event to open Festival Events to My Events tab
  useEffect(() => {
    const handleOpenMyEvents = (event) => {
      console.log('📋 Received openFestivalEventsMyTab event');
      setFestivalEventsInitialTab('my-events');
      setShowFestivalEvents(true);
    };
    
    window.addEventListener('openFestivalEventsMyTab', handleOpenMyEvents);
    return () => window.removeEventListener('openFestivalEventsMyTab', handleOpenMyEvents);
  }, []);
  
  // Phase 5: My Tracks modal state from profile
  const [showMyTracksFromProfile, setShowMyTracksFromProfile] = useState(false);
  
  // My Playlists modal state - now received from props (showPlaylistsModal, setShowPlaylistsModal)
  
  // Crate Box - Diggin' in the Crates modal state
  const [showDigginInTheCrates, setShowDigginInTheCrates] = useState(false);
  
  // Crate Box - My Crates state
  const [showMyCrates, setShowMyCrates] = useState(false);
  const [selectedCrateForBuilder, setSelectedCrateForBuilder] = useState(null);
  
  // Private Feed modal state
  const [showPrivateFeedModal, setShowPrivateFeedModal] = useState(false);
  
  // Phase 4: Whisper state
  const [showWhisperConversations, setShowWhisperConversations] = useState(false);
  const [showWhisperModal, setShowWhisperModal] = useState(false);
  const [whisperTarget, setWhisperTarget] = useState(null); // {user_id, user_name, user_avatar, online}
  const [privateFeedReplies, setPrivateFeedReplies] = useState([]);
  
  // Navigation hook
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingPrivateFeed, setLoadingPrivateFeed] = useState(false);
  
  // Handle navigation state from profile page
  useEffect(() => {
    if (location.state?.openPromoSpot) {
      const promoId = location.state.promoId;
      console.log('🟣 Location state detected: openPromoSpot with promoId:', promoId);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
      // Open promo spot
      setActiveTab('social');
      setTimeout(() => {
        setShowPromoSpot(true);
        if (promoId) {
          setTimeout(() => {
            console.log('🟣 Dispatching openPromoDetail event with promoId:', promoId);
            window.dispatchEvent(new CustomEvent('openPromoDetail', {
              detail: { promoId }
            }));
          }, 500);
        }
      }, 100);
    } else if (location.state?.openEventCalendar) {
      const eventId = location.state.eventId;
      console.log('🟣 Location state detected: openEventCalendar with eventId:', eventId);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
      // Open DJ Booth and event calendar
      setTimeout(() => {
        setShowDJBoothModal(true);
        setTimeout(() => {
          console.log('🟣 Dispatching djBoothOpenEventCalendar event with eventId:', eventId);
          window.dispatchEvent(new CustomEvent('djBoothOpenEventCalendar', {
            detail: { eventId }
          }));
        }, 500);
      }, 100);
    }
  }, [location.state]);
  
  // Edit Profile modal state
  const [showEditProfile, setShowEditProfile] = useState(false);
  
  // Share Profile state
  const [profileCopied, setProfileCopied] = useState(false);
  
  // Friends Modal state
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  
  // Chat navigation state for highlighting messages
  const [chatNavigationState, setChatNavigationState] = useState(null);
  
  // Admin Moderation modal state
  const [showAdminModeration, setShowAdminModeration] = useState(false);
  const [pendingModerations, setPendingModerations] = useState([]);
  const [loadingModerations, setLoadingModerations] = useState(false);
  const [moderationTab, setModerationTab] = useState('messages'); // 'messages', 'emotes', or 'videos'
  // New Member Feed (Moderate tab + dashboard ticker)
  const [newMembersList, setNewMembersList] = useState([]);
  const [newMembersUnreadCount, setNewMembersUnreadCount] = useState(0);
  const [loadingNewMembers, setLoadingNewMembers] = useState(false);
  
  // Emotes state
  const [showMyEmotes, setShowMyEmotes] = useState(false);
  const [showEmoteLabLite, setShowEmoteLabLite] = useState(false);
  const [emoteLabPrefill, setEmoteLabPrefill] = useState({ prompt: '', style: 'neon' });
  const [showEmotePicker, setShowEmotePicker] = useState(false);
  const [myEmotes, setMyEmotes] = useState([]);
  const [emoteLimits, setEmoteLimits] = useState({ static: { used: 0, limit: 10, available: 10 }, animated: { used: 0, limit: 5, available: 5 } });
  const [globalEmotes, setGlobalEmotes] = useState([]);
  const [customEmotes, setCustomEmotes] = useState([]);
  const [allAvailableEmotes, setAllAvailableEmotes] = useState({ global: [], custom: [] });
  const [pendingEmotes, setPendingEmotes] = useState([]);
  const [loadingEmotes, setLoadingEmotes] = useState(false);
  const [uploadingEmote, setUploadingEmote] = useState(false);
  const [emoteNotifications, setEmoteNotifications] = useState([]);
  
  // Video Moderation state
  const [pendingVideos, setPendingVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  
  // Tool Suggestions state
  const [toolSuggestions, setToolSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // Badges tab (Moderate → grant/revoke badges) state
  const [badgeSearchQuery, setBadgeSearchQuery] = useState('');
  const [badgeSearchResults, setBadgeSearchResults] = useState([]);
  const [badgeSearchLoading, setBadgeSearchLoading] = useState(false);
  const [badgeUpdatingUserId, setBadgeUpdatingUserId] = useState(null);
  const [selectedBadgeToGrant, setSelectedBadgeToGrant] = useState('groovlens');
  const GRANTABLE_BADGES_FE = ['groovlens', 'original_40', 'creator_of_week', 'past_creator_of_week'];
  const BADGE_LABELS_FE = {
    groovlens: 'GroovLens',
    original_40: 'OG 50 — Founding Member',
    creator_of_week: 'Creator of the Week',
    past_creator_of_week: 'Past Creator of the Week',
  };

  // VIP Membership tab (Moderate) state
  const [vipList, setVipList] = useState([]);
  const [vipLoading, setVipLoading] = useState(false);
  const [vipEmailInput, setVipEmailInput] = useState('');
  const [vipUpdating, setVipUpdating] = useState(false);
  
  // Beta Access Management state
  const [betaCodes, setBetaCodes] = useState([]);
  const [betaRequests, setBetaRequests] = useState([]);
  // Curated fingerprint entries waiting on a moderator — drives the Moderate button badge
  const [pendingCuratedIds, setPendingCuratedIds] = useState(0);

  const [loadingBeta, setLoadingBeta] = useState(false);
  const [newCodeNote, setNewCodeNote] = useState('');
  const [newCodeMaxUses, setNewCodeMaxUses] = useState('');
  const [newCodeCustom, setNewCodeCustom] = useState('');
  const [newCodeExpiresDays, setNewCodeExpiresDays] = useState('');
  
  // BEATSEEK Playlists state (modal states moved to App component level)
  const [myPlaylists, setMyPlaylists] = useState([]);
  const [publicPlaylists, setPublicPlaylists] = useState([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [playlistTab, setPlaylistTab] = useState('my'); // 'my' or 'public'
  const [suggestedTracks, setSuggestedTracks] = useState([]);
  
  // Private Feed Functions
  const fetchPrivateFeed = async () => {
    if (!user) return;
    
    setLoadingPrivateFeed(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      console.log('Fetching private feed for user:', user.id);

      // Get all forum posts from all categories
      const categories = ['feedback', 'qa', 'stories'];
      const allPrivateReplies = [];
      
      for (const category of categories) {
        try {
          console.log(`Fetching posts from category: ${category}`);
          const postsResponse = await fetch(`${API}/forum/posts?category=${category}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!postsResponse.ok) {
            console.warn(`Failed to fetch posts from category ${category}`);
            continue;
          }

          const allPosts = await postsResponse.json();
          console.log(`Found ${allPosts.length} posts in ${category}`);
          
          // Filter posts by current user (posts they authored)
          const userPosts = allPosts.filter(post => post.author_id === user.id);
          console.log(`Found ${userPosts.length} user posts in ${category}`);
          
          // For each user post, fetch private replies
          for (const post of userPosts) {
            try {
              console.log(`Fetching private replies for post: ${post.id} - "${post.title}"`);
              const repliesResponse = await fetch(`${API}/forum/posts/${post.id}/replies/private`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (repliesResponse.ok) {
                const privateReplies = await repliesResponse.json();
                console.log(`Found ${privateReplies.length} private replies for post ${post.id}`);
                
                // Add post context to each reply
                const repliesWithContext = privateReplies.map(reply => ({
                  ...reply,
                  post_title: post.title,
                  post_id: post.id,
                  post_category: category
                }));
                allPrivateReplies.push(...repliesWithContext);
              } else {
                console.warn(`Failed to fetch private replies for post ${post.id}:`, repliesResponse.status);
              }
            } catch (error) {
              console.error(`Error fetching private replies for post ${post.id}:`, error);
            }
          }
        } catch (error) {
          console.error(`Error fetching posts from category ${category}:`, error);
        }
      }
      
      console.log(`Total private replies found: ${allPrivateReplies.length}`);
      
      // Sort by most recent first
      allPrivateReplies.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setPrivateFeedReplies(allPrivateReplies);
      
    } catch (error) {
      console.error('Error fetching private feed:', error);
    } finally {
      setLoadingPrivateFeed(false);
    }
  };

  // Open private feed modal and fetch data
  const openPrivateFeedModal = () => {
    setShowPrivateFeedModal(true);
    fetchPrivateFeed();
  };
  
  // Admin Moderation Functions
  const fetchPendingModerations = async () => {
    if (!user || user.role !== 'admin') return;
    
    setLoadingModerations(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      console.log('📋 Fetching pending moderations for admin:', user.name);

      // Phase 3: Smart Moderation System - Fetch all flagged content
      const response = await fetch(`${API}/admin/moderation/pending`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Found ${data.pending_count} pending moderations`);
        setPendingModerations(data.items || []);
      } else {
        console.warn('Failed to fetch pending moderations:', response.status);
        setPendingModerations([]);
      }
      
    } catch (error) {
      console.error('Error fetching pending moderations:', error);
      setPendingModerations([]);
    } finally {
      setLoadingModerations(false);
    }
  };

  // Approve flagged content
  const approveContent = async (contentType, contentId) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/admin/moderation/${contentType}/${contentId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log(`✅ Content approved: ${contentType}/${contentId}`);
        // Refresh the list
        fetchPendingModerations();
      } else {
        console.error('Failed to approve content:', response.status);
        alert('Failed to approve content');
      }
    } catch (error) {
      console.error('Error approving content:', error);
      alert('Error approving content');
    }
  };

  // Deny flagged content
  const denyContent = async (contentType, contentId) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/admin/moderation/${contentType}/${contentId}/deny`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log(`❌ Content denied: ${contentType}/${contentId}`);
        // Refresh the list
        fetchPendingModerations();
      } else {
        console.error('Failed to deny content:', response.status);
        alert('Failed to deny content');
      }
    } catch (error) {
      console.error('Error denying content:', error);
      alert('Error denying content');
    }
  };

  const refreshPendingCuratedIds = useCallback(async () => {
    if (!user || !['admin', 'moderator'].includes(user.role)) return setPendingCuratedIds(0);
    try {
      const r = await fetch(`${BACKEND_URL}/api/community-index/review?status=pending`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('beatseek_token')}` },
      });
      if (r.ok) setPendingCuratedIds((await r.json()).pending || 0);
    } catch (_) {}
  }, [user?.id, user?.role]);

  useEffect(() => {
    refreshPendingCuratedIds();
    const t = setInterval(refreshPendingCuratedIds, 60000);
    return () => clearInterval(t);
  }, [refreshPendingCuratedIds]);


  // Open admin moderation modal and fetch data
  const openAdminModerationModal = () => {
    setShowAdminModeration(true);
    setModerationTab('messages'); // Reset to messages tab
    fetchPendingModerations();
    if (user?.role === 'admin') {
      fetchPendingEmotes(); // Also fetch pending emotes for admin
      fetchPendingVideos(); // Also fetch pending videos for admin
      fetchToolSuggestions(); // Keep suggestion beaker accurate
    }
  };

  // Admin: load suggestions on dashboard mount so the Moderate dot reflects new pending ideas
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchToolSuggestions();
    }
  }, [user?.role]);

  // ==================== COMMUNITY BANNER FUNCTIONS ====================

  const fetchBannerFeed = async () => {
    if (!user) return;
    setBannerLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/community/banner-feed`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBannerItems(data.items || []);
      }
    } catch (err) {
      console.error('Banner feed error:', err);
    } finally {
      setBannerLoading(false);
    }
  };

  // ==================== ADMIN ANNOUNCEMENTS FUNCTIONS ====================

  // === New Member Feed (Moderate tab) ===
  const fetchNewMembersUnreadCount = async () => {
    if (user?.role !== 'admin') return;
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/new-members/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNewMembersUnreadCount(data.unread_count || 0);
      }
    } catch (err) { /* silent */ }
  };

  const fetchNewMembersList = async () => {
    setLoadingNewMembers(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/new-members/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNewMembersList(data.items || []);
      }
    } catch (err) {
      console.error('Fetch new members error:', err);
    } finally {
      setLoadingNewMembers(false);
    }
  };

  const markNewMembersViewed = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      await fetch(`${BACKEND_URL}/api/admin/new-members/mark-viewed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNewMembersUnreadCount(0);
    } catch (err) { /* silent */ }
  };

  const deleteNewMemberEntry = async (announcementId) => {
    if (!window.confirm('Delete this new-member entry from the ticker?')) return;
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/new-members/${announcementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setNewMembersList(prev => prev.filter(m => m.id !== announcementId));
      }
    } catch (err) {
      console.error('Delete new-member entry error:', err);
    }
  };

  // Poll the unread count for admin while they're using the app
  useEffect(() => {
    if (user?.role !== 'admin') return;
    fetchNewMembersUnreadCount();
    const id = setInterval(fetchNewMembersUnreadCount, 60000);
    return () => clearInterval(id);
  }, [user?.role]); // eslint-disable-line

  // Poll pending beta access requests for admin so the Moderate dot lights up in real time
  useEffect(() => {
    if (user?.role !== 'admin') return;
    fetchBetaData();
    const id = setInterval(fetchBetaData, 60000);
    return () => clearInterval(id);
  }, [user?.role]); // eslint-disable-line

  const fetchAdminAnnouncements = async () => {
    setAnnouncementsLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAdminAnnouncements(data.announcements || []);
      }
    } catch (err) {
      console.error('Fetch announcements error:', err);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  const createAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) return;
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/announcements`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newAnnouncement),
      });
      if (res.ok) {
        setNewAnnouncement({ title: '', message: '', priority: 'normal', expiry_date: '' });
        fetchAdminAnnouncements();
      }
    } catch (err) {
      console.error('Create announcement error:', err);
    }
  };

  const updateAnnouncement = async (id, updates) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/announcements/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        setEditingAnnouncement(null);
        fetchAdminAnnouncements();
      }
    } catch (err) {
      console.error('Update announcement error:', err);
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      const token = localStorage.getItem('beatseek_token');
      await fetch(`${BACKEND_URL}/api/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      fetchAdminAnnouncements();
    } catch (err) {
      console.error('Delete announcement error:', err);
    }
  };

  const seedBannerData = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/seed-banner-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        fetchAdminAnnouncements();
        fetchBannerFeed();
        alert('Mock banner data seeded successfully!');
      }
    } catch (err) {
      console.error('Seed banner error:', err);
    }
  };

  // ==================== SONGSTATS FUNCTIONS ====================

  const fetchSongstatsData = async (track) => {
    if (!track) return;
    setSongstatsLoading(true);
    setSongstatsData(null);
    try {
      const token = localStorage.getItem('beatseek_token');
      const params = new URLSearchParams();
      if (track.isrc) params.append('isrc', track.isrc);
      else if (track.spotify_id) params.append('spotify_id', track.spotify_id);
      else {
        // Try search as fallback to find ISRC
        const searchRes = await fetch(`${BACKEND_URL}/api/songstats/search?q=${encodeURIComponent(track.artist + ' ' + track.title)}&limit=1`);
        if (searchRes.ok) {
          const searchData = await searchRes.json();
          if (searchData.results?.length > 0) {
            const found = searchData.results[0];
            if (found.isrc) params.append('isrc', found.isrc);
            else if (found.spotify_track_id) params.append('spotify_id', found.spotify_track_id);
          }
        }
        if (!params.toString()) {
          setSongstatsData({ error: 'Track not found in Songstats database. Try a different track.' });
          setSongstatsLoading(false);
          return;
        }
      }
      const res = await fetch(`${BACKEND_URL}/api/songstats/track/full?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSongstatsData(data);
        } else {
          setSongstatsData({ error: data.message || 'Track not found in Songstats.' });
        }
      } else {
        setSongstatsData({ error: 'Could not fetch Songstats data.' });
      }
    } catch (err) {
      console.error('Songstats fetch error:', err);
      setSongstatsData({ error: 'Connection error. Please try again.' });
    } finally {
      setSongstatsLoading(false);
    }
  };

  // ==================== EMOTE FUNCTIONS ====================
  
  // Fetch user's emote limits
  const fetchEmoteLimits = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/emotes/limits`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const limits = await response.json();
        setEmoteLimits(limits);
      }
    } catch (error) {
      console.error('Error fetching emote limits:', error);
    }
  };
  
  // Fetch user's custom emotes (approved only)
  const fetchMyEmotes = async () => {
    if (!user) return;
    
    setLoadingEmotes(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/emotes/my-custom`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const emotes = await response.json();
        setMyEmotes(emotes);
      }
    } catch (error) {
      console.error('Error fetching my emotes:', error);
    } finally {
      setLoadingEmotes(false);
    }
  };
  
  // Fetch global emotes
  const fetchGlobalEmotes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/emotes/global`);
      
      if (response.ok) {
        const emotes = await response.json();
        setGlobalEmotes(emotes);
      }
    } catch (error) {
      console.error('Error fetching global emotes:', error);
    }
  };
  
  // Fetch custom emotes
  const fetchCustomEmotes = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/emotes/my-custom`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const emotes = await response.json();
        setCustomEmotes(emotes);
      }
    } catch (error) {
      console.error('Error fetching custom emotes:', error);
    }
  };
  
  // Fetch all available emotes (for emote picker)
  const fetchAllAvailableEmotes = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/emotes/all-available`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const emotes = await response.json();
        setAllAvailableEmotes(emotes);
      }
    } catch (error) {
      console.error('Error fetching available emotes:', error);
    }
  };
  
  // Upload custom emote
  const uploadCustomEmote = async (name, type, file) => {
    if (!user) return;
    
    setUploadingEmote(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const formData = new FormData();
      formData.append('name', name);
      formData.append('emote_type', type);
      formData.append('file', file);
      
      const response = await fetch(`${BACKEND_URL}/api/emotes/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`✅ Emote ':${name}:' uploaded successfully! Status: ${result.status}\nWaiting for admin approval.`);
        fetchEmoteLimits(); // Refresh limits
        fetchMyEmotes(); // Refresh emotes
        return true;
      } else {
        const error = await response.json();
        alert(`❌ Upload failed: ${error.detail || 'Unknown error'}`);
        return false;
      }
    } catch (error) {
      console.error('Error uploading emote:', error);
      alert(`❌ Upload failed: ${error.message}`);
      return false;
    } finally {
      setUploadingEmote(false);
    }
  };
  
  // Fetch pending emotes (admin only)
  const fetchPendingEmotes = async () => {
    if (!user || user.role !== 'admin') return;
    
    setLoadingEmotes(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/emotes/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const emotes = await response.json();
        setPendingEmotes(emotes);
      } else {
        setPendingEmotes([]);
      }
    } catch (error) {
      console.error('Error fetching pending emotes:', error);
      setPendingEmotes([]);
    } finally {
      setLoadingEmotes(false);
    }
  };
  
  // Approve emote (admin only)
  const approveEmote = async (emoteId) => {
    if (!user || user.role !== 'admin') return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/emotes/${emoteId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('✅ Emote approved successfully!');
        fetchPendingEmotes(); // Refresh pending list
        fetchGlobalEmotes(); // Refresh global emotes
      } else {
        const error = await response.json();
        alert(`❌ Approval failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving emote:', error);
      alert(`❌ Approval failed: ${error.message}`);
    }
  };
  
  // Deny emote (admin only)
  const denyEmote = async (emoteId) => {
    if (!user || user.role !== 'admin') return;
    
    if (!window.confirm('Are you sure you want to deny and delete this emote?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/emotes/${emoteId}/deny`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('✅ Emote denied and deleted successfully!');
        fetchPendingEmotes(); // Refresh pending list
      } else {
        const error = await response.json();
        alert(`❌ Denial failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error denying emote:', error);
      alert(`❌ Denial failed: ${error.message}`);
    }
  };
  
  // ==================== VIDEO MODERATION FUNCTIONS ====================
  
  // Fetch pending videos for admin review
  const fetchPendingVideos = async () => {
    if (!user || user.role !== 'admin') return;
    
    setLoadingVideos(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/videos/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const videos = await response.json();
        console.log(`✅ Found ${videos.length} pending videos for moderation`);
        setPendingVideos(videos);
      } else {
        console.warn('Failed to fetch pending videos:', response.status);
        setPendingVideos([]);
      }
    } catch (error) {
      console.error('Error fetching pending videos:', error);
      setPendingVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  };
  
  // Approve video (admin only)
  const approveVideo = async (videoId) => {
    if (!user || user.role !== 'admin') return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/videos/${videoId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('✅ Video approved successfully!');
        fetchPendingVideos(); // Refresh pending list
      } else {
        const error = await response.json();
        alert(`❌ Approval failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving video:', error);
      alert(`❌ Approval failed: ${error.message}`);
    }
  };
  
  // Reject video (admin only)
  const rejectVideo = async (videoId) => {
    if (!user || user.role !== 'admin') return;
    
    const reason = window.prompt('Please provide a reason for rejection (optional):');
    if (reason === null) return; // User cancelled
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/videos/${videoId}/reject?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('✅ Video rejected successfully!');
        fetchPendingVideos(); // Refresh pending list
      } else {
        const error = await response.json();
        alert(`❌ Rejection failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting video:', error);
      alert(`❌ Rejection failed: ${error.message}`);
    }
  };
  
  // ==================== BETA ACCESS MANAGEMENT FUNCTIONS ====================
  
  // Fetch beta codes and requests
  const fetchBetaData = async () => {
    if (!user || user.role !== 'admin') return;
    
    setLoadingBeta(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      
      // Fetch codes
      const codesResponse = await fetch(`${BACKEND_URL}/api/admin/beta/codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (codesResponse.ok) {
        const data = await codesResponse.json();
        setBetaCodes(data.codes || []);
      }
      
      // Fetch requests
      const requestsResponse = await fetch(`${BACKEND_URL}/api/admin/beta/requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (requestsResponse.ok) {
        const data = await requestsResponse.json();
        setBetaRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching beta data:', error);
    } finally {
      setLoadingBeta(false);
    }
  };

  const fetchToolSuggestions = async () => {
    if (!user || user.role !== 'admin') return;
    setLoadingSuggestions(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/suggestions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setToolSuggestions(data.suggestions || []);
      }
      const tr = await fetch(`${BACKEND_URL}/api/admin/polls/tallies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (tr.ok) {
        const td = await tr.json();
        setPollTallies(td.tallies || {});
      }
    } catch (e) { console.error('Error fetching suggestions:', e); }
    setLoadingSuggestions(false);
  };

  const deleteToolSuggestion = async (createdAt) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      await fetch(`${BACKEND_URL}/api/admin/suggestions/${encodeURIComponent(createdAt)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setToolSuggestions(prev => prev.filter(s => s.created_at !== createdAt));
    } catch (e) { console.error('Error deleting suggestion:', e); }
  };

  const updateSuggestionStatus = async (createdAt, status) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      await fetch(`${BACKEND_URL}/api/admin/suggestions/${encodeURIComponent(createdAt)}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      setToolSuggestions(prev => prev.map(s => s.created_at === createdAt ? { ...s, status } : s));
    } catch (e) { console.error('Error updating suggestion:', e); }
  };

  const closeAndAnnouncePoll = async (pollId) => {
    if (!window.confirm('Close this community vote and announce the result on The Wall? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/polls/${pollId}/close-announce`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || 'Failed to close & announce');
        return;
      }
      fetchToolSuggestions();
    } catch (e) { console.error('Error closing poll:', e); }
  };

  const setBuildStatus = async (pollId, status) => {
    if (status === 'shipped' && !window.confirm("Mark as SHIPPED? This will post a '🚀 It's Live!' announcement on The Wall (first time only).")) {
      fetchToolSuggestions();
      return;
    }
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/polls/${pollId}/build-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || 'Failed to update build status');
      }
      fetchToolSuggestions();
    } catch (e) { console.error('Error updating build status:', e); }
  };

  // ==== Badges tab (Moderate) — search users + grant/revoke badges ====
  const searchUsersForBadges = async (q) => {
    if (!user || user.role !== 'admin') return;
    const query = (q ?? badgeSearchQuery).trim();
    if (!query) {
      setBadgeSearchResults([]);
      return;
    }
    setBadgeSearchLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/users?query=${encodeURIComponent(query)}&limit=25`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const rows = Array.isArray(data) ? data : (data.users || []);
        setBadgeSearchResults(rows);
      } else {
        setBadgeSearchResults([]);
      }
    } catch (e) {
      console.error('Error searching users for badges:', e);
      setBadgeSearchResults([]);
    }
    setBadgeSearchLoading(false);
  };

  const grantBadgeToUser = async (userId, badge) => {
    if (!user || user.role !== 'admin') return;
    setBadgeUpdatingUserId(userId + ':' + badge + ':grant');
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/badges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ badge })
      });
      if (res.ok) {
        const data = await res.json();
        setBadgeSearchResults(prev => prev.map(u => u.id === userId ? { ...u, badges: data.user?.badges || u.badges } : u));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to grant badge');
      }
    } catch (e) {
      console.error('Error granting badge:', e);
      alert('Failed to grant badge');
    }
    setBadgeUpdatingUserId(null);
  };

  const revokeBadgeFromUser = async (userId, badge) => {
    if (!user || user.role !== 'admin') return;
    const label = BADGE_LABELS_FE[badge] || badge;
    if (!window.confirm(`Revoke '${label}' badge from this user?`)) return;
    setBadgeUpdatingUserId(userId + ':' + badge + ':revoke');
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/badges/${badge}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBadgeSearchResults(prev => prev.map(u => u.id === userId ? { ...u, badges: data.user?.badges || (u.badges || []).filter(b => b !== badge) } : u));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to revoke badge');
      }
    } catch (e) {
      console.error('Error revoking badge:', e);
      alert('Failed to revoke badge');
    }
    setBadgeUpdatingUserId(null);
  };

  // ==== VIP Membership tab (Moderate) — lifetime free access list ====
  const fetchVipList = async () => {
    if (!user || user.role !== 'admin') return;
    setVipLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/vip`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVipList(data.vip || []);
      }
    } catch (e) {
      console.error('Error fetching VIP list:', e);
    }
    setVipLoading(false);
  };

  const addVipEmail = async () => {
    if (!user || user.role !== 'admin') return;
    const email = vipEmailInput.trim();
    if (!email) return;
    setVipUpdating(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/vip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        const data = await res.json();
        setVipList(prev => [data.vip, ...prev]);
        setVipEmailInput('');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to add VIP');
      }
    } catch (e) {
      console.error('Error adding VIP:', e);
      alert('Failed to add VIP');
    }
    setVipUpdating(false);
  };

  const removeVipEmail = async (email) => {
    if (!user || user.role !== 'admin') return;
    if (!window.confirm(`Remove ${email} from VIP lifetime membership?`)) return;
    setVipUpdating(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/vip/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setVipList(prev => prev.filter(v => v.email !== email));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to remove VIP');
      }
    } catch (e) {
      console.error('Error removing VIP:', e);
      alert('Failed to remove VIP');
    }
    setVipUpdating(false);
  };

  
  // Generate new beta code
  const generateBetaCode = async () => {
    if (!user || user.role !== 'admin') return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/beta/codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: newCodeCustom || null,
          max_uses: newCodeMaxUses ? parseInt(newCodeMaxUses) : null,
          expires_days: newCodeExpiresDays ? parseFloat(newCodeExpiresDays) : null,
          note: newCodeNote || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ Code generated: ${data.code.code}`);
        setNewCodeNote('');
        setNewCodeMaxUses('');
        setNewCodeCustom('');
        setNewCodeExpiresDays('');
        fetchBetaData();
      } else {
        const error = await response.json();
        alert(`❌ Failed: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error generating code:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };
  
  // Delete/deactivate beta code
  const deleteBetaCode = async (codeId) => {
    if (!user || user.role !== 'admin') return;
    if (!window.confirm('Are you sure you want to deactivate this code?')) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/beta/codes/${codeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchBetaData();
      }
    } catch (error) {
      console.error('Error deleting code:', error);
    }
  };
  
  // Approve beta request
  const approveBetaRequest = async (requestId) => {
    if (!user || user.role !== 'admin') return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/beta/requests/${requestId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ Approved! Code sent: ${data.code}`);
        fetchBetaData();
      } else {
        const error = await response.json();
        alert(`❌ Failed: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };
  
  // Deny beta request
  const denyBetaRequest = async (requestId) => {
    if (!user || user.role !== 'admin') return;
    if (!window.confirm('Are you sure you want to deny this request?')) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/beta/requests/${requestId}/deny`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchBetaData();
      }
    } catch (error) {
      console.error('Error denying request:', error);
    }
  };
  
  // ==================== BEATSEEK PLAYLIST FUNCTIONS ====================
  
  // Fetch user's playlists
  const fetchMyPlaylists = async () => {
    if (!user) return;
    
    setLoadingPlaylists(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists/my-playlists`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const playlists = await response.json();
        setMyPlaylists(playlists);
      } else {
        console.error('Failed to fetch playlists:', response.status);
      }
    } catch (error) {
      console.error('Error fetching playlists:', error);
    } finally {
      setLoadingPlaylists(false);
    }
  };
  
  // Fetch public playlists
  const fetchPublicPlaylists = async () => {
    setLoadingPlaylists(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/playlists/public?limit=50`);
      
      if (response.ok) {
        const playlists = await response.json();
        setPublicPlaylists(playlists);
      } else {
        console.error('Failed to fetch public playlists:', response.status);
      }
    } catch (error) {
      console.error('Error fetching public playlists:', error);
    } finally {
      setLoadingPlaylists(false);
    }
  };
  
  // Create new playlist
  const createPlaylist = async (name, description, isPublic) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name,
          description: description || null,
          is_public: isPublic
        })
      });
      
      if (response.ok) {
        const newPlaylist = await response.json();
        setMyPlaylists(prev => [...prev, newPlaylist]);
        setShowCreatePlaylistModal(false);
        alert('✅ Playlist created successfully!');
        return newPlaylist;
      } else {
        let msg = `HTTP ${response.status}`;
        try {
          const error = await response.json();
          if (Array.isArray(error?.detail)) {
            // FastAPI 422 validation errors
            msg = error.detail.map(e => `${(e.loc || []).join('.')} — ${e.msg}`).join('\n');
          } else if (typeof error?.detail === 'string') {
            msg = error.detail;
          } else if (error?.message) {
            msg = error.message;
          }
        } catch (_) {
          try { msg = await response.text(); } catch (_) {}
        }
        console.error('Create playlist failed:', { status: response.status, body: msg });
        alert(`❌ Failed to create playlist: ${msg}`);
      }
    } catch (error) {
      console.error('Error creating playlist:', error);
      alert(`❌ Failed to create playlist: ${error.message}`);
    }
  };
  
  // Update playlist
  const updatePlaylist = async (playlistId, updates) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        const updatedPlaylist = await response.json();
        setMyPlaylists(prev => prev.map(p => p.id === playlistId ? updatedPlaylist : p));
        if (currentPlaylist?.id === playlistId) {
          setCurrentPlaylist(updatedPlaylist);
        }
        alert('✅ Playlist updated successfully!');
        return updatedPlaylist;
      } else {
        const error = await response.json();
        alert(`❌ Failed to update playlist: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating playlist:', error);
      alert(`❌ Failed to update playlist: ${error.message}`);
    }
  };
  
  // Delete playlist
  const deletePlaylist = async (playlistId) => {
    if (!user) return;
    
    if (!window.confirm('Are you sure you want to delete this playlist? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setMyPlaylists(prev => prev.filter(p => p.id !== playlistId));
        setShowPlaylistDetailModal(false);
        setCurrentPlaylist(null);
        setShowPlaylistsModal(true);
        alert('✅ Playlist deleted successfully!');
      } else {
        const error = await response.json();
        alert(`❌ Failed to delete playlist: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting playlist:', error);
      alert(`❌ Failed to delete playlist: ${error.message}`);
    }
  };
  
  // Add track to playlist
  const addTrackToPlaylist = async (playlistId, trackData) => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const token = localStorage.getItem('beatseek_token');
    const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}/tracks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(trackData)
    });
    
    if (response.ok) {
      const result = await response.json();
      // Refresh the current playlist if it's open
      if (currentPlaylist?.id === playlistId) {
        const updatedPlaylist = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}`).then(r => r.json());
        setCurrentPlaylist(updatedPlaylist);
      }
      // Refresh my playlists
      await fetchMyPlaylists();
      return result;
    } else {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to add track to playlist');
    }
  };
  
  // Remove track from playlist
  const removeTrackFromPlaylist = async (playlistId, trackId) => {
    if (!user) return;
    
    if (!window.confirm('Remove this track from the playlist?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}/tracks/${trackId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Refresh the current playlist
        if (currentPlaylist?.id === playlistId) {
          const updatedPlaylist = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}`).then(r => r.json());
          setCurrentPlaylist(updatedPlaylist);
        }
        await fetchMyPlaylists();
        alert('✅ Track removed from playlist!');
      } else {
        const error = await response.json();
        alert(`❌ Failed to remove track: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error removing track:', error);
      alert(`❌ Failed to remove track: ${error.message}`);
    }
  };
  
  // Suggest track to public playlist
  const suggestTrackToPlaylist = async (playlistId, trackData) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}/suggested-tracks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(trackData)
      });
      
      if (response.ok) {
        alert('✅ Track suggestion submitted! The playlist owner will review it.');
      } else {
        const error = await response.json();
        alert(`❌ Failed to suggest track: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error suggesting track:', error);
      alert(`❌ Failed to suggest track: ${error.message}`);
    }
  };
  
  // Fetch suggested tracks (for playlist owners)
  const fetchSuggestedTracks = async (playlistId) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}/suggested-tracks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const suggestions = await response.json();
        setSuggestedTracks(suggestions);
      }
    } catch (error) {
      console.error('Error fetching suggested tracks:', error);
    }
  };
  
  // Approve suggested track
  const approveSuggestedTrack = async (playlistId, suggestionId) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}/suggested-tracks/${suggestionId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('✅ Track approved and added to playlist!');
        await fetchSuggestedTracks(playlistId);
        // Refresh playlist
        if (currentPlaylist?.id === playlistId) {
          const updatedPlaylist = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}`).then(r => r.json());
          setCurrentPlaylist(updatedPlaylist);
        }
        await fetchMyPlaylists();
      }
    } catch (error) {
      console.error('Error approving suggestion:', error);
      alert(`❌ Failed to approve: ${error.message}`);
    }
  };
  
  // Reject suggested track
  const rejectSuggestedTrack = async (playlistId, suggestionId) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}/suggested-tracks/${suggestionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('✅ Suggestion rejected');
        await fetchSuggestedTracks(playlistId);
      }
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
      alert(`❌ Failed to reject: ${error.message}`);
    }
  };

  // Reorder tracks in playlist
  const reorderPlaylistTracks = async (playlistId, newTrackOrder) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}/tracks/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newTrackOrder)
      });
      
      if (response.ok) {
        // Refresh playlist
        const updatedPlaylist = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}`).then(r => r.json());
        setCurrentPlaylist(updatedPlaylist);
        await fetchMyPlaylists();
      }
    } catch (error) {
      console.error('Error reordering tracks:', error);
      alert(`❌ Failed to reorder: ${error.message}`);
    }
  };
  
  // Load playlists when user logs in
  useEffect(() => {
    if (user) {
      fetchMyPlaylists();
    }
  }, [user]);

  // Debug modal state changes
  useEffect(() => {
    console.log('🔍 Modal state changed:', showCreatePlaylistModal);
    if (showCreatePlaylistModal) {
      console.log('✅ Modal should be visible now!');
      console.log('📍 Check document.body for modal div');
    }
  }, [showCreatePlaylistModal]);
  
  // Utility function to render emotes in text
  const renderEmotesInText = (text) => {
    if (!text) return text;
    
    // Combine global and custom emotes
    const allEmotes = [
      ...globalEmotes,
      ...(allAvailableEmotes.custom || [])
    ];
    
    // Replace :emotename: with <img> tags
    let renderedText = text;
    allEmotes.forEach(emote => {
      const pattern = new RegExp(`:${emote.name}:`, 'g');
      const imgTag = emote.emoji 
        ? `<span style="font-size: 1.5em;">${emote.emoji}</span>` 
        : `<img src="${BACKEND_URL}${emote.file_path_28}" alt=":${emote.name}:" style="display: inline; width: 28px; height: 28px; vertical-align: middle;" />`;
      renderedText = renderedText.replace(pattern, imgTag);
    });
    
    return renderedText;
  };
  
  // Load emotes on component mount and user login
  useEffect(() => {
    if (user) {
      fetchGlobalEmotes();
      fetchCustomEmotes();
      fetchEmoteLimits();
      fetchMyEmotes();
      fetchAllAvailableEmotes();
    }
  }, [user]);

  // Global "Remix" hook — Featured Emote sidebar dispatches this event
  useEffect(() => {
    const handler = (e) => {
      const { prompt = '', style = 'neon' } = e.detail || {};
      setEmoteLabPrefill({ prompt, style });
      setShowEmoteLabLite(true);
    };
    window.addEventListener('beatseek:open-emote-lab', handler);
    return () => window.removeEventListener('beatseek:open-emote-lab', handler);
  }, []);

  // Global "Join Jam Session" hook — Wall shared-event cards dispatch this
  useEffect(() => {
    const handler = async (e) => {
      const sessionId = e.detail?.sessionId;
      if (!sessionId) return;
      const token = localStorage.getItem('beatseek_token');
      if (!token) { alert('Log in to join a jam session'); return; }
      try {
        const response = await axios.post(`${API}/jam/${sessionId}/join`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setShowTheWall(false);
        setActiveTab('jam-session');
        setCurrentJamSession(response.data);
        setShowJamSessionModal(true);
      } catch (err) {
        alert(err.response?.data?.detail || 'This jam session has ended or is no longer available');
      }
    };
    window.addEventListener('beatseek:join-jam-session', handler);
    return () => window.removeEventListener('beatseek:join-jam-session', handler);
  }, []);
  
  // Social Links state
  const [socialLinks, setSocialLinks] = useState([]);
  const [showAddSocialLink, setShowAddSocialLink] = useState(false);
  const [editingSocialLinkIndex, setEditingSocialLinkIndex] = useState(null);

  // Social Links Helper Functions
  const getSocialIcon = (platform) => {
    // Define platforms array here to ensure it's always accessible
    const platformsData = [
      // Music & Producer Platforms
      { id: 'beatport', name: 'Beatport', logoUrl: '/images/social-logos/beatport.png' },
      { id: 'lfgtv', name: 'LFG.TV', logoUrl: '/images/social-logos/lfgtv.png' },
      { id: 'mixcloud', name: 'Mixcloud', logoUrl: '/images/social-logos/mixcloud.svg' },
      { id: 'soundcloud', name: 'SoundCloud', logoUrl: '/images/social-logos/soundcloud.svg' },
      { id: 'spotify', name: 'Spotify', logoUrl: '/images/social-logos/spotify.png' },
      { id: 'traxsource', name: 'Traxsource', logoUrl: '/images/social-logos/traxsource.png' },
      { id: 'twitch', name: 'Twitch', logoUrl: '/images/social-logos/twitch.svg' },
      // Personal & Social Platforms  
      { id: 'discord', name: 'Discord', logoUrl: '/images/social-logos/discord.svg' },
      { id: 'facebook', name: 'Facebook', logoUrl: '/images/social-logos/facebook.svg' },
      { id: 'instagram', name: 'Instagram', logoUrl: '/images/social-logos/instagram.svg' },
      { id: 'tiktok', name: 'TikTok', logoUrl: '/images/social-logos/tiktok.svg' },
      { id: 'website', name: 'Website', logoUrl: null },
      { id: 'youtube', name: 'YouTube', logoUrl: '/images/social-logos/youtube.svg' },
      { id: 'other', name: 'Other', logoUrl: null }
    ];

    const platformData = platformsData.find(p => p.id === platform);
    
    // Handle website with white icon
    if (platform === 'website') {
      return (
        <svg 
          width="14" 
          height="14" 
          viewBox="0 0 24 24" 
          fill="white" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
      );
    }
    
    // Handle Discord with brand color icon
    if (platform === 'discord') {
      return (
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="#5865F2" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
      );
    }
    
    // Handle Other with link icon
    if (platform === 'other') {
      return (
        <svg 
          width="14" 
          height="14" 
          viewBox="0 0 24 24" 
          fill="white" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
        </svg>
      );
    }
    
    // Handle Twitch with deep purple brand color
    if (platform === 'twitch') {
      return (
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="#4B0082" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
        </svg>
      );
    }
    
    if (platformData && platformData.logoUrl && platformData.logoUrl.startsWith('/images/social-logos/')) {
      // Handle PNG files differently (many are PNG now, some are SVG)
      const isPng = platformData.logoUrl.endsWith('.png');
      
      return (
        <img 
          src={platformData.logoUrl} 
          alt={`${platformData.name} logo`}
          className="w-4 h-4"
          style={{ 
            filter: 'brightness(0) saturate(0) invert(1) contrast(2)', // All icons pure white now
            objectFit: 'contain',
            width: isPng ? 'auto' : '16px', 
            height: '16px',
            maxWidth: isPng ? '24px' : '16px'
          }}
        />
      );
    }
    
    // Fallback to emoji icons for platforms without custom logos or when platformData is undefined
    const icons = {
      beatport: '🎛️',
      discord: '💬',
      facebook: '👥',
      instagram: '📷',
      lfgtv: '📺',
      mixcloud: '🎛️',
      other: '🔗',
      soundcloud: '🎧',
      spotify: '🎵',
      tiktok: '🎬',
      traxsource: '🎚️',
      twitch: '🎮',
      website: '🌐',
      youtube: '📹',
      // Add any legacy platform IDs that might exist
      twitter: '🐦',
      linkedin: '💼'
    };
    return icons[platform] || '🔗';
  };

  // Tier Icon Helper Function
  const getTierIcon = (subscription) => {
    switch (subscription) {
      case 'premium':
        return <Crown className="w-4 h-4 text-yellow-400" title="Premium" />;
      case 'plus':
        return <Zap className="w-4 h-4 text-blue-400" title="Plus" />;
      case 'free':
      default:
        return <Music className="w-4 h-4 text-gray-400" title="Free" />;
    }
  };

  const addSocialLink = async (platform, username) => {
    const platformData = {
      // Music & Producer Platforms
      beatport: { name: 'Beatport', urlTemplate: 'https://beatport.com/artist/' },
      lfgtv: { name: 'LFG.TV', urlTemplate: 'https://lfg.tv/' },
      mixcloud: { name: 'Mixcloud', urlTemplate: 'https://mixcloud.com/' },
      soundcloud: { name: 'SoundCloud', urlTemplate: 'https://soundcloud.com/' },
      spotify: { name: 'Spotify', urlTemplate: 'https://open.spotify.com/artist/' },
      traxsource: { name: 'Traxsource', urlTemplate: 'https://traxsource.com/artist/' },
      twitch: { name: 'Twitch', urlTemplate: 'https://twitch.tv/' },
      // Personal & Social Platforms
      discord: { name: 'Discord', urlTemplate: 'https://discord.gg/' },
      facebook: { name: 'Facebook', urlTemplate: 'https://facebook.com/' },
      instagram: { name: 'Instagram', urlTemplate: 'https://instagram.com/' },
      tiktok: { name: 'TikTok', urlTemplate: 'https://tiktok.com/@' },
      website: { name: 'Website', urlTemplate: '' },
      youtube: { name: 'YouTube', urlTemplate: 'https://youtube.com/' },
      other: { name: 'Other', urlTemplate: '' }
    };

    const newLink = {
      platform,
      platformName: platformData[platform].name,
      username: username.replace('@', ''),
      url: (platform === 'website' || platform === 'other') ? username : platformData[platform].urlTemplate + username.replace('@', '')
    };

    // If editing, replace at index; otherwise append
    const updatedLinks = editingSocialLinkIndex !== null
      ? socialLinks.map((l, i) => (i === editingSocialLinkIndex ? newLink : l))
      : [...socialLinks, newLink];
    setSocialLinks(updatedLinks);
    setShowAddSocialLink(false);
    setEditingSocialLinkIndex(null);

    // Save to backend if user is logged in
    const userId = user?.id || user?.user_id || user?.ID;
    console.log('Full user object:', user);
    console.log('Trying userId:', userId);
    console.log('User object keys:', user ? Object.keys(user) : 'No user');
    if (userId) {
      try {
        // Convert array to dictionary format for backend
        const socialLinksDict = {};
        updatedLinks.forEach(link => {
          socialLinksDict[link.platform] = {
            platform: link.platform,
            platformName: link.platformName,
            username: link.username,
            url: link.url
          };
        });
        
        console.log('Saving social links for user:', userId, 'Links array:', updatedLinks, 'Dict format:', socialLinksDict);
        const response = await fetch(`${API}/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
          },
          body: JSON.stringify({
            social_links: socialLinksDict
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to save social links to backend:', response.status, errorText);
        } else {
          console.log('Social links saved successfully');
        }
      } catch (error) {
        console.error('Error saving social links:', error);
      }
    } else {
      console.warn('No user ID found, cannot save social links');
    }
  };

  const removeSocialLink = async (index) => {
    const updatedLinks = socialLinks.filter((_, i) => i !== index);
    setSocialLinks(updatedLinks);

    // Save to backend if user is logged in
    const userId = user?.id || user?.user_id || user?.ID;
    console.log('Remove - Full user object:', user);
    console.log('Remove - Trying userId:', userId);
    if (userId) {
      try {
        // Convert array to dictionary format for backend
        const socialLinksDict = {};
        updatedLinks.forEach(link => {
          socialLinksDict[link.platform] = {
            platform: link.platform,
            platformName: link.platformName,
            username: link.username,
            url: link.url
          };
        });
        
        console.log('Saving social links for user:', userId, 'Links array:', updatedLinks, 'Dict format:', socialLinksDict);
        const response = await fetch(`${API}/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
          },
          body: JSON.stringify({
            social_links: socialLinksDict
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to save social links to backend:', response.status, errorText);
        } else {
          console.log('Social links saved successfully');
        }
      } catch (error) {
        console.error('Error saving social links:', error);
      }
    } else {
      console.warn('No user ID found, cannot save social links');
    }
  };

  // Handle drag end for reordering social links
  const handleSocialLinkDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(socialLinks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSocialLinks(items);

    // Save reordered links to backend
    const userId = user?.id || user?.user_id || user?.ID;
    console.log('Drag - Full user object:', user);
    console.log('Drag - Trying userId:', userId);
    if (userId) {
      try {
        // Convert array to dictionary format for backend
        const socialLinksDict = {};
        items.forEach(link => {
          socialLinksDict[link.platform] = {
            platform: link.platform,
            platformName: link.platformName,
            username: link.username,
            url: link.url
          };
        });
        
        console.log('Saving reordered social links for user:', userId, 'Links array:', items, 'Dict format:', socialLinksDict);
        const response = await fetch(`${API}/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
          },
          body: JSON.stringify({
            social_links: socialLinksDict
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to save reordered social links:', response.status, errorText);
        } else {
          console.log('Reordered social links saved successfully');
        }
      } catch (error) {
        console.error('Error saving reordered social links:', error);
      }
    }
  };

  // Load social links when user changes
  useEffect(() => {
    console.log('User changed:', user);
    console.log('User social_links:', user?.social_links);
    
    if (user?.social_links) {
      try {
        if (Array.isArray(user.social_links)) {
          // Handle legacy array format
          console.log('Loading social links from user (array format):', user.social_links);
          // Filter out any invalid links
          const validLinks = user.social_links.filter(link => 
            link && typeof link === 'object' && link.platform && link.platformName
          );
          setSocialLinks(validLinks);
        } else if (typeof user.social_links === 'object') {
          // Convert dictionary to array format for frontend
          const socialLinksArray = Object.values(user.social_links).filter(link => 
            link && typeof link === 'object' && link.platform && link.platformName
          );
          console.log('Loading social links from user (dict format):', user.social_links, 'Converted to array:', socialLinksArray);
          setSocialLinks(socialLinksArray);
        } else {
          console.log('Invalid social_links format, setting empty array');
          setSocialLinks([]);
        }
      } catch (error) {
        console.error('Error processing social links:', error);
        setSocialLinks([]);
      }
    } else {
      console.log('No social links found in user object, setting empty array');
      setSocialLinks([]);
    }
  }, [user]);

  // Mock user profile for vibe calculations
  const currentUserProfile = {
    preferredGenres: ['Electronic', 'House', 'Progressive House'],
    averageBPM: 125,
    listeningTimes: ['afternoon', 'weekend'],
    location: 'New York, USA',
    friends: ['maya_chen', 'dj_techno']
  };

  // Calculate "Your Vibe" percentage based on multiple factors
  const calculateVibeScore = (discovery) => {
    let totalScore = 0;
    
    // 1. Genre Compatibility (40% weight)
    const genreScore = currentUserProfile.preferredGenres.includes(discovery.track.genre) ? 85 : 
                      discovery.track.genre.includes('Electronic') ? 70 : 30;
    totalScore += genreScore * 0.4;
    
    // 2. BPM Preference (35% weight)  
    const bpmDifference = Math.abs(discovery.track.bpm - currentUserProfile.averageBPM);
    const bpmScore = Math.max(0, 100 - (bpmDifference * 2)); // Closer BPM = higher score
    totalScore += bpmScore * 0.35;
    
    // 3. Time/Activity Matching (25% weight)
    const timeScore = discovery.identified_at.includes('hours ago') && 
                     currentUserProfile.listeningTimes.includes('afternoon') ? 80 : 60;
    totalScore += timeScore * 0.25;
    
    // 4. Social Connections (15% weight)
    const socialScore = currentUserProfile.friends.includes(discovery.user.name.toLowerCase().replace(' ', '_')) ? 90 : 45;
    totalScore += socialScore * 0.15;
    
    // 5. Geographic Proximity (10% weight)
    const locationScore = discovery.user.location.includes(currentUserProfile.location.split(',')[1]) ? 85 : 
                         discovery.user.location.includes('USA') ? 70 : 50;
    totalScore += locationScore * 0.10;
    
    // Normalize to 100% and round
    return Math.round(Math.min(100, totalScore));
  };

  const [recentDiscoveries, setRecentDiscoveries] = useState([]);

  // Trending Tracks - populated from real user identifications
  const [trendingTracks, setTrendingTracks] = useState([]);

  // Verified genres that work in both Spotify and Discogs
  // Genre lists for each filter level
  const basicGenres = [
    { display: 'All Genres', spotify: '', discogs: '' },
    { display: 'Dance', spotify: 'dance', discogs: 'dance' },
    { display: 'Deep House', spotify: 'deep house', discogs: 'deep house' },
    { display: 'Drum & Bass', spotify: 'drum and bass', discogs: 'drum n bass' },
    { display: 'Electronic', spotify: 'electronic', discogs: 'electronic' },
    { display: 'House', spotify: 'house', discogs: 'house' },
    { display: 'Minimal Techno', spotify: 'minimal techno', discogs: 'minimal techno' },
    { display: 'Progressive House', spotify: 'progressive house', discogs: 'progressive house' },
    { display: 'Tech House', spotify: 'tech house', discogs: 'tech house' },
    { display: 'Techno', spotify: 'techno', discogs: 'techno' },
    { display: 'Trance', spotify: 'trance', discogs: 'trance' },
  ];

  const advancedGenres = [
    { display: 'All Genres', spotify: '', discogs: '' },
    { display: 'Afro House', spotify: 'afro house', discogs: 'afro house' },
    { display: 'Bass House', spotify: 'bass house', discogs: 'bass house' },
    { display: 'Breaks', spotify: 'breaks', discogs: 'breaks' },
    { display: 'Dance', spotify: 'dance', discogs: 'dance' },
    { display: 'Deep House', spotify: 'deep house', discogs: 'deep house' },
    { display: 'Drum & Bass', spotify: 'drum and bass', discogs: 'drum n bass' },
    { display: 'Dubstep', spotify: 'dubstep', discogs: 'dubstep' },
    { display: 'Electro', spotify: 'electro', discogs: 'electro' },
    { display: 'Electronic', spotify: 'electronic', discogs: 'electronic' },
    { display: 'Electronica', spotify: 'electronica', discogs: 'electronica' },
    { display: 'Funky House', spotify: 'funky house', discogs: 'funky house' },
    { display: 'Hard Dance', spotify: 'hard dance', discogs: 'hard dance' },
    { display: 'Hard Techno', spotify: 'hard techno', discogs: 'hard techno' },
    { display: 'House', spotify: 'house', discogs: 'house' },
    { display: 'Indie Dance', spotify: 'indie dance', discogs: 'indie dance' },
    { display: 'Jackin House', spotify: 'jackin house', discogs: 'jackin house' },
    { display: 'Melodic House & Techno', spotify: 'melodic house', discogs: 'melodic house' },
    { display: 'Minimal / Deep Tech', spotify: 'minimal', discogs: 'minimal' },
    { display: 'Nu Disco / Disco', spotify: 'nu disco', discogs: 'nu disco' },
    { display: 'Organic House', spotify: 'organic house', discogs: 'organic house' },
    { display: 'Progressive House', spotify: 'progressive house', discogs: 'progressive house' },
    { display: 'Psy-Trance', spotify: 'psy-trance', discogs: 'psy-trance' },
    { display: 'Tech House', spotify: 'tech house', discogs: 'tech house' },
    { display: 'Techno', spotify: 'techno', discogs: 'techno' },
    { display: 'Trance', spotify: 'trance', discogs: 'trance' },
    { display: 'Trap / Future Bass', spotify: 'trap', discogs: 'trap' },
    { display: 'UK Garage / Bassline', spotify: 'uk garage', discogs: 'uk garage' },
  ].sort((a, b) => a.display === 'All Genres' ? -1 : b.display === 'All Genres' ? 1 : a.display.localeCompare(b.display));

  const explorerGenresList = [
    'All Genres',
    'Afro House', 'Ambient', 'Bass House', 'Breaks', 'Dance', 'Deep House',
    'Downtempo', 'Drum & Bass', 'Dubstep', 'Electro', 'Electronic', 'Electronica',
    'Funky House', 'Hard Dance / Hardcore', 'Hard Techno', 'House',
    'IDM', 'Indie Dance', 'Jackin House', 'Melodic House & Techno',
    'Minimal / Deep Tech', 'Nu Disco / Disco', 'Organic House',
    'Progressive House', 'Psy-Trance', 'Synthwave', 'Tech House',
    'Techno', 'Trance', 'Trap / Future Bass', 'UK Garage / Bassline',
  ].sort((a, b) => a === 'All Genres' ? -1 : b === 'All Genres' ? 1 : a.localeCompare(b));

  // Basic genres used as default
  const verifiedGenres = basicGenres;

  // Multi-API Discovery with Fallback System and Pagination
  const loadDiscoverContent = async (genreDisplay = 'House', search = '', loadMore = false, pageNumber = null, forceRefresh = false) => {
    try {
      setIsLoadingDiscogs(true);
      setDiscogsError(null);
      
      const pageToLoad = pageNumber || (loadMore ? currentPage + 1 : 1);
      
      // Determine which filters to use based on filterMode
      let effectiveGenre, effectiveSearch, effectiveSources;
      
      if (filterMode === 'advanced') {
        // Advanced mode: use advanced filters
        effectiveSources = selectedSources.length > 0 ? selectedSources : ['spotify', 'soundcloud', 'deezer', 'discogs'];
        effectiveSearch = discoverySearchQuery?.trim() || undefined;
        
        // Determine genre based on BPM mode/preset state (only for SoundCloud)
        if (selectedSources.includes('soundcloud') && bpmFilterEnabled) {
          if (bpmOnlyMode) {
            // BPM Only mode: search all genres (no genre filter)
            effectiveGenre = undefined;
          } else if (selectedBpmPreset && selectedBpmPreset !== 'off') {
            // Preset selected: use the preset name as the genre
            effectiveGenre = selectedBpmPreset;
          } else {
            // Normal mode or "Off" preset: use the genre dropdown
            // "All Genres" = all electronic subgenres (BEATSEEK is an electronic music platform)
            effectiveGenre = advancedGenre === 'All Genres' ? 'electronic' : advancedGenre;
          }
        } else {
          // Non-SoundCloud sources or BPM disabled: use genre dropdown
          // "All Genres" = all electronic subgenres (BEATSEEK is an electronic music platform)
          effectiveGenre = advancedGenre === 'All Genres' ? 'electronic' : advancedGenre;
        }
      } else {
        // Basic mode: use basic filters
        // "All Genres" = all electronic subgenres (BEATSEEK is an electronic music platform)
        const genreValue = genreDisplay === 'All Genres' ? 'electronic' : genreDisplay;
        effectiveGenre = genreFilterEnabled ? genreValue : undefined;
        effectiveSearch = searchFilterEnabled ? search : undefined;
        effectiveSources = ['spotify', 'soundcloud', 'deezer', 'discogs']; // All sources in basic mode
      }
      
      // Build request body with source filters
      const requestBody = {
        genre: effectiveGenre,
        search: effectiveSearch,
        limit: tracksPerPage,
        offset: (pageToLoad - 1) * tracksPerPage,
        sources: effectiveSources
      };
      
      // Add refresh_seed when Refresh button is clicked to get different results
      if (forceRefresh) {
        requestBody.refresh_seed = Math.floor(Math.random() * 10000);
      }
      
      // Add BPM filter if enabled and SoundCloud is selected (only in advanced mode)
      if (filterMode === 'advanced' && bpmFilterEnabled && selectedSources.includes('soundcloud')) {
        requestBody.bpm_from = bpmRange.min;
        requestBody.bpm_to = bpmRange.max;
      }
      
      // Add SoundCloud token if available and SoundCloud is selected
      if (effectiveSources.includes('soundcloud')) {
        let scToken = soundcloudAccessToken || localStorage.getItem('soundcloud_access_token');
        
        // If no token found, try to refresh
        if (!scToken || scToken === 'mock_soundcloud_token') {
          console.log('🔄 No SoundCloud token found, attempting refresh...');
          scToken = await refreshSoundCloudToken();
        }
        
        if (scToken && scToken !== 'mock_soundcloud_token') {
          requestBody.soundcloud_token = scToken;
          console.log('✅ SoundCloud token attached to request');
        } else {
          console.log('⚠️ No valid SoundCloud token available - results may be limited');
        }
      }
      
      // Use new Discovery Zone API endpoint
      let response = await fetch(`${BACKEND_URL}/api/discovery/tracks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      // Handle SoundCloud token expiration - try to refresh and retry
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || '';
        
        // Check if it's a SoundCloud auth error
        if ((response.status === 520 || response.status === 500) && 
            effectiveSources.includes('soundcloud') &&
            (errorMessage.toLowerCase().includes('soundcloud') || 
             errorMessage.toLowerCase().includes('token') ||
             errorMessage.toLowerCase().includes('expired'))) {
          
          console.log('🔄 SoundCloud token may be expired, attempting refresh...');
          const newToken = await refreshSoundCloudToken();
          
          if (newToken) {
            // Retry with new token
            requestBody.soundcloud_token = newToken;
            response = await fetch(`${BACKEND_URL}/api/discovery/tracks`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(requestBody)
            });
          } else {
            // Refresh failed - prompt user to reconnect
            throw new Error('SoundCloud session expired. Please sign out and reconnect your SoundCloud account.');
          }
        }
        
        if (!response.ok) {
          throw new Error(`Discovery API error: ${response.status}`);
        }
      }

      const result = await response.json();
      
      // Map tracks to the format expected by the UI
      const tracks = result.tracks.map(track => ({
        id: track.id,
        title: track.title,
        artist: track.artist,
        album: track.album,
        genre: track.genre,
        artwork: track.artwork || null,
        source: track.source,
        preview_url: track.preview_url,
        duration_ms: track.duration_ms,
        popularity: track.popularity,
        bpm: track.bpm,
        year: track.year,
        external_url: track.external_url,
        spotify_uri: track.spotify_uri,
        youtubeAvailable: track.youtubeAvailable,
        youtubeVideoId: track.youtubeVideoId,
        isrc: track.isrc,
        songstats_data: track.songstats_data
      }));

      // Always replace tracks (not append) for pagination
      setDiscogsTracks(tracks);
      if (!pageNumber && !loadMore) {
        setSelectedGenre(genreDisplay);
      }
      
      // Batch check artist availability for TheAudioDB info icons
      const uniqueArtists = [...new Set(tracks.map(t => t.artist).filter(Boolean))];
      const unchecked = uniqueArtists.filter(a => !(a in artistAvailability));
      if (unchecked.length > 0) {
        fetch(`${BACKEND_URL}/api/enrichment/artists/check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ artists: unchecked })
        })
          .then(r => r.json())
          .then(data => {
            if (data.available) {
              setArtistAvailability(prev => ({ ...prev, ...data.available }));
            }
          })
          .catch(() => {});
      }
      
      // Check if there are more tracks to load
      // For SoundCloud and Songstats, be more lenient since they may return fewer tracks per query
      const isLenientSource = filterMode === 'advanced' && (selectedSources.includes('soundcloud') || selectedSources.includes('songstats'));
      const minTracksForMore = isLenientSource ? 5 : tracksPerPage;
      setHasMoreTracks(tracks.length >= minTracksForMore);

    } catch (error) {
      console.error('Discovery failed:', error);
      setDiscogsError(error.message || 'Failed to load tracks. Please try again.');
    } finally {
      setIsLoadingDiscogs(false);
      setIsApplyingSpotifyFilters(false); // Reset Spotify filters loading state
    }
  };

  // Auto-refresh when source changes in advanced mode
  useEffect(() => {
    // Only trigger if in advanced mode and source actually changed
    if (filterMode === 'advanced' && selectedSources.length > 0) {
      const currentSourceKey = selectedSources.sort().join(',');
      if (prevSelectedSourceRef.current !== null && prevSelectedSourceRef.current !== currentSourceKey) {
        setCurrentPage(1);
        loadDiscoverContent(advancedGenre, '', false, 1);
      }
      prevSelectedSourceRef.current = currentSourceKey;
    }
  }, [selectedSources, filterMode, advancedGenre]);

  // Ref to track if pagination was triggered by user
  const paginationTriggerRef = useRef(false);
  
  // Handle pagination when currentPage changes (triggered by Discover More / Previous buttons)
  useEffect(() => {
    // Only run if this was triggered by user pagination action
    if (paginationTriggerRef.current && activeTab === 'discover') {
      paginationTriggerRef.current = false; // Reset the flag
      const genre = filterMode === 'advanced' ? advancedGenre : selectedGenre;
      console.log('Pagination useEffect triggered - Page:', currentPage, 'Genre:', genre, 'Mode:', filterMode);
      loadDiscoverContent(genre, discoverySearchQuery, false, currentPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  // Load Discovery content when Discovery Zone tab is opened
  useEffect(() => {
    if (activeTab === 'discover' && discogsTracks.length === 0) {
      loadDiscoverContent(selectedGenre, discoverySearchQuery);
    }
    // Load recommendations when Discovery tab opens
    if (activeTab === 'discover' && recommendedArtists.length === 0) {
      const token = localStorage.getItem('beatseek_token');
      if (token) {
        setRecommendedLoading(true);
        fetch(`${BACKEND_URL}/api/recommendations/for-you`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(r => r.json())
          .then(data => {
            if (data.success) {
              setRecommendedArtists(data.recommended_artists || []);
              setRecommendedMessage(data.message || '');
            }
          })
          .catch(err => console.warn('Recommendations fetch failed:', err))
          .finally(() => setRecommendedLoading(false));

        // Fetch recently explored artists
        fetch(`${BACKEND_URL}/api/artists/explored`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(r => r.json())
          .then(data => {
            if (data.success) setExploredArtists(data.artists || []);
          })
          .catch(() => {});
      }
    }
    // Fetch trending artists (no auth needed — community-wide)
    if (activeTab === 'discover' && trendingBeatseekArtists.length === 0) {
      fetch(`${BACKEND_URL}/api/community/trending-artists`)
        .then(r => r.json())
        .then(data => {
          if (data.success) setTrendingBeatseekArtists(data.trending_artists || []);
        })
        .catch(() => {});
    }
  }, [activeTab]);

  // Fetch banner feed when Social Hub tab opens
  useEffect(() => {
    if (activeTab === 'social' && user && bannerItems.length === 0) {
      fetchBannerFeed();
    }
  }, [activeTab, user]);

  // YouTube Search Function


  // Track Action Handlers
  const handleStartJamSession = (track) => {
    console.log('🎵 handleStartJamSession called', { track });
    console.log('   Track fields:');
    console.log('   - id:', track.id);
    console.log('   - title:', track.title);
    console.log('   - artist:', track.artist);
    console.log('   - preview_url:', track.preview_url);
    console.log('   - spotify_uri:', track.spotify_uri);
    console.log('   - duration_ms:', track.duration_ms);
    console.log('   - artwork:', track.artwork);
    
    // Scenario 1: User is discovering for jam
    if (isDiscoveringForJam && targetSessionId) {
      console.log('📍 Scenario 1: Adding to existing session');
      const token = localStorage.getItem('beatseek_token');
      
      axios.post(
        `${API}/jam/${targetSessionId}/queue`,
        {
          track_id: track.id,
          title: track.title,
          artist: track.artist,
          preview_url: track.preview_url,
          spotify_uri: track.spotify_uri,
          duration_ms: track.duration_ms || 30000,
          artwork: track.artwork
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      ).then(() => {
        setIsDiscoveringForJam(false);
        setTargetSessionId(null);
        alert(`✅ "${track.title}" added to your jam session!`);
      }).catch(err => {
        console.error('Failed to add track:', err);
        alert('Failed to add track to session');
      });
      return;
    }
    
    // Scenario 2: Create Jam Session Modal - Direct DOM rendering
    console.log('📍 Scenario 2: Opening Create Jam Session modal via DOM');
    sessionStorage.setItem('pendingJamTrack', JSON.stringify(track));
    
    showCreateJamModalDirect(track);
  };
  
  // Direct DOM modal renderer
  const showCreateJamModalDirect = (track) => {
    // Remove any existing modal
    const existing = document.getElementById('create-jam-modal-container');
    if (existing) existing.remove();
    
    const modalHTML = `
      <div class="jam-modal-overlay" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.85); z-index: 99999; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.3s ease;">
        <div class="jam-modal-content" style="background: rgba(20, 20, 35, 0.98); border: 2px solid rgba(0, 255, 255, 0.3); border-radius: 20px; padding: 0; max-width: 600px; width: 90%; max-height: 90vh; box-shadow: 0 0 40px rgba(0, 255, 255, 0.3);">
          
          <!-- Header -->
          <div style="padding: 1.5rem; border-bottom: 1px solid rgba(0, 255, 255, 0.2); display: flex; justify-content: space-between; align-items: center;">
            <h2 style="margin: 0; background: linear-gradient(135deg, #00ffff 0%, #ff00ff 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-size: 1.5rem;">🎵 Start a Jam Session</h2>
            <button id="closeCreateJam" style="background: none; border: none; color: rgba(255, 255, 255, 0.7); font-size: 1.5rem; cursor: pointer; width: 32px; height: 32px; border-radius: 50%; transition: all 0.3s ease;">✕</button>
          </div>
          
          <!-- Body -->
          <div style="padding: 1.5rem;">
            <div style="margin-bottom: 1.5rem;">
              <label style="display: block; color: rgba(0, 255, 255, 0.9); font-size: 0.9rem; font-weight: 600; margin-bottom: 0.5rem;">Session Name</label>
              <input id="jamSessionName" type="text" placeholder="Late Night House Vibes 🎧" maxlength="50" style="width: 100%; padding: 0.75rem; background: rgba(26, 26, 46, 0.8); border: 2px solid rgba(0, 255, 255, 0.3); border-radius: 8px; color: white; font-size: 1rem; outline: none;" />
            </div>
            
            <div style="margin-bottom: 1.5rem;">
              <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer; color: rgba(255, 255, 255, 0.9);">
                <input id="jamIsPublic" type="checkbox" checked style="width: 18px; height: 18px; cursor: pointer; accent-color: #00ffff;" />
                <span>Public Session (anyone can join)</span>
              </label>
              <p id="jamPublicHelp" style="font-size: 0.85rem; color: rgba(255, 255, 255, 0.5); margin-top: 0.5rem; margin-bottom: 0;">🌍 Your session will be visible to everyone</p>
            </div>
            
            <div style="color: rgba(255, 255, 255, 0.7); font-size: 0.9rem; padding: 0.75rem; background: rgba(0, 255, 255, 0.05); border-radius: 8px; margin-bottom: 1rem;">
              <strong>Starting with track:</strong><br/>
              ${track.title} - ${track.artist}
            </div>
            
            <div id="jamErrorMessage" style="display: none; background: rgba(255, 0, 0, 0.1); border: 1px solid rgba(255, 0, 0, 0.3); border-radius: 8px; padding: 1rem; color: #ff6666; text-align: center; margin-bottom: 1rem;"></div>
          </div>
          
          <!-- Footer -->
          <div style="padding: 1rem 1.5rem; border-top: 1px solid rgba(0, 255, 255, 0.2); display: flex; justify-content: flex-end; gap: 1rem;">
            <button id="cancelCreateJam" style="padding: 0.75rem 1.5rem; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer; border: 2px solid rgba(255, 255, 255, 0.3); background: rgba(255, 255, 255, 0.1); color: white; transition: all 0.3s ease;">Cancel</button>
            <button id="confirmCreateJam" style="padding: 0.75rem 1.5rem; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer; border: 2px solid rgba(0, 255, 255, 0.6); background: linear-gradient(135deg, rgba(0, 255, 255, 0.3) 0%, rgba(255, 0, 255, 0.3) 100%); color: white; transition: all 0.3s ease;">🎵 Start Jamming</button>
          </div>
          
        </div>
      </div>
    `;
    
    const div = document.createElement('div');
    div.innerHTML = modalHTML;
    div.id = 'create-jam-modal-container';
    document.body.appendChild(div);
    
    // Event handlers
    const closeModal = () => {
      document.getElementById('create-jam-modal-container')?.remove();
    };
    
    document.getElementById('closeCreateJam').onclick = closeModal;
    document.getElementById('cancelCreateJam').onclick = closeModal;
    
    // Public checkbox toggle help text
    document.getElementById('jamIsPublic').onchange = (e) => {
      const helpText = document.getElementById('jamPublicHelp');
      helpText.textContent = e.target.checked 
        ? '🌍 Your session will be visible to everyone' 
        : '🔒 Only people with the link can join';
    };
    
    // Create session handler
    document.getElementById('confirmCreateJam').onclick = async () => {
      const sessionName = document.getElementById('jamSessionName').value.trim();
      const isPublic = document.getElementById('jamIsPublic').checked;
      const errorDiv = document.getElementById('jamErrorMessage');
      
      if (!sessionName) {
        errorDiv.textContent = 'Please enter a session name';
        errorDiv.style.display = 'block';
        return;
      }
      
      const confirmBtn = document.getElementById('confirmCreateJam');
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Creating...';
      
      try {
        const token = localStorage.getItem('beatseek_token');
        const response = await axios.post(
          `${API}/jam/create`,
          { name: sessionName, is_public: isPublic },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        console.log('✅ Jam session created:', response.data);
        const sessionId = response.data.session_id;
        
        // Add track to queue
        await axios.post(
          `${API}/jam/${sessionId}/queue`,
          {
            track_id: track.id,
            title: track.title,
            artist: track.artist,
            preview_url: track.preview_url,
            spotify_uri: track.spotify_uri,
            duration_ms: track.duration_ms || 30000,
            artwork: track.artwork
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        console.log('✅ Track added to queue');
        
        // Fetch updated session data with the track in queue
        const updatedSessionResponse = await axios.get(
          `${API}/jam/${sessionId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        console.log('✅ Fetched updated session with track:', updatedSessionResponse.data);
        
        // Close modal
        closeModal();
        
        // Use setTimeout to ensure React state updates happen after DOM cleanup
        setTimeout(() => {
          console.log('🔄 Setting jam session state and opening modal...');
          setActiveTab('jam-session');
          setCurrentJamSession(updatedSessionResponse.data);
          setShowJamSessionModal(true);
          console.log('✅ State updated - modal should open');
        }, 100);
        
      } catch (err) {
        console.error('❌ Failed to create session:', err);
        errorDiv.textContent = err.response?.data?.detail || 'Failed to create session';
        errorDiv.style.display = 'block';
        confirmBtn.disabled = false;
        confirmBtn.textContent = '🎵 Start Jamming';
      }
    };
    
    // Focus on input
    setTimeout(() => {
      document.getElementById('jamSessionName')?.focus();
    }, 100);
    
    console.log('📍 Create Jam Modal rendered via DOM');
  };

  const handleToggleFavorite = async (track) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) {
        alert('Please login to add favorites');
        return;
      }

      // Check if already favorited (we'll need to track this)
      const response = await axios.post(
        `${API}/favorites`,
        {
          id: track.id,
          title: track.title,
          artist: track.artist,
          preview_url: track.preview_url,
          artwork: track.artwork,
          duration_ms: track.duration_ms,
          genre: track.genre
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      alert(`✅ "${track.title}" added to favorites!`);
      console.log('✅ Track added to favorites:', response.data);
    } catch (err) {
      console.error('Failed to add to favorites:', err);
      if (err.response?.status === 401) {
        alert('Please login to add favorites');
      } else {
        alert(err.response?.data?.detail || 'Failed to add to favorites');
      }
    }
  };

  const handleToggleWishlist = async (track) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) {
        alert('Please login to add to wishlist');
        return;
      }

      const response = await axios.post(
        `${API}/wishlist`,
        {
          id: track.id,
          title: track.title,
          artist: track.artist,
          preview_url: track.preview_url,
          artwork: track.artwork,
          duration_ms: track.duration_ms,
          genre: track.genre
        },
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          params: { notes: null }
        }
      );
      
      alert(`📋 "${track.title}" added to wishlist!`);
      console.log('✅ Track added to wishlist:', response.data);
    } catch (err) {
      console.error('Failed to add to wishlist:', err);
      if (err.response?.status === 401) {
        alert('Please login to add to wishlist');
      } else {
        alert(err.response?.data?.detail || 'Failed to add to wishlist');
      }
    }
  };

  // Alias for DigginInTheCrates component
  const addToWishlist = handleToggleWishlist;

  // Open Artist Deep Dive modal
  const openArtistDeepDive = async (artistName) => {
    if (!artistName || artistName === 'Unknown Artist') return;
    setShowArtistModal(true);
    setArtistModalLoading(true);
    setArtistModalData(null);
    setArtistAlbums([]);
    setArtistVideos([]);
    setSimilarArtists([]);
    setArtistMbData(null);
    setArtistMbReleases([]);
    setArtistInsights(null);
    setInsightsLoading(false);
    setShowInsights(false);

    try {
      const [artistRes, videosRes, albumsRes, similarRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/enrichment/artist/${encodeURIComponent(artistName)}`),
        fetch(`${BACKEND_URL}/api/enrichment/artist/${encodeURIComponent(artistName)}/videos`),
        fetch(`${BACKEND_URL}/api/enrichment/artist/${encodeURIComponent(artistName)}/albums`),
        fetch(`${BACKEND_URL}/api/recommendations/similar-artists/${encodeURIComponent(artistName)}`),
      ]);

      const artistJson = await artistRes.json();
      const videosJson = await videosRes.json();
      const albumsJson = await albumsRes.json();
      const similarJson = await similarRes.json();

      const hasAudioDbBio = artistJson.success && artistJson.data?.bio;
      const hasAudioDbAlbums = albumsJson.albums && albumsJson.albums.length > 0;

      if (artistJson.success && artistJson.data) {
        setArtistModalData(artistJson.data);
        // Log exploration for history
        const token = localStorage.getItem('beatseek_token');
        if (token) {
          fetch(`${BACKEND_URL}/api/artists/explored`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              artist_name: artistJson.data.name || artistName,
              genre: artistJson.data.genre,
              style: artistJson.data.style,
              artwork_thumb: artistJson.data.artwork?.thumb,
              country: artistJson.data.country,
            }),
          }).then(() => {
            // Refresh explored list
            fetch(`${BACKEND_URL}/api/artists/explored`, { headers: { 'Authorization': `Bearer ${token}` } })
              .then(r => r.json())
              .then(d => { if (d.success) setExploredArtists(d.artists || []); })
              .catch(() => {});
          }).catch(() => {});
        }
      } else {
        setArtistModalData({ name: artistName, notFound: true });
      }
      setArtistVideos(videosJson.videos || []);
      setArtistAlbums(albumsJson.albums || []);
      setSimilarArtists(similarJson.similar_artists || []);

      // MusicBrainz fallback: fetch enrichment if TheAudioDB bio is missing or albums are missing
      if (!hasAudioDbBio || !hasAudioDbAlbums) {
        try {
          const mbRes = await fetch(`${BACKEND_URL}/api/musicbrainz/enrich/artist/${encodeURIComponent(artistName)}`);
          const mbJson = await mbRes.json();
          if (mbJson.success && mbJson.data) {
            setArtistMbData(mbJson.data);
            // If no TheAudioDB data at all, update modal with MusicBrainz basics
            if (!artistJson.success || !artistJson.data) {
              setArtistModalData({
                name: mbJson.data.name || artistName,
                country: mbJson.data.country,
                genre: mbJson.data.top_tags?.[0] || '',
                style: mbJson.data.top_tags?.[1] || '',
                musicbrainzFallback: true,
              });
            }
            // If no TheAudioDB albums, use MusicBrainz releases
            if (!hasAudioDbAlbums && mbJson.data.recent_releases?.length > 0) {
              setArtistMbReleases(mbJson.data.recent_releases);
            }
          }
        } catch (mbErr) {
          console.log('MusicBrainz fallback fetch skipped:', mbErr.message);
        }
      }
    } catch (err) {
      console.error('Artist Deep Dive error:', err);
      setArtistModalData({ name: artistName, notFound: true });
      // Try MusicBrainz as last resort
      try {
        const mbRes = await fetch(`${BACKEND_URL}/api/musicbrainz/enrich/artist/${encodeURIComponent(artistName)}`);
        const mbJson = await mbRes.json();
        if (mbJson.success && mbJson.data) {
          setArtistMbData(mbJson.data);
          setArtistModalData({
            name: mbJson.data.name || artistName,
            country: mbJson.data.country,
            genre: mbJson.data.top_tags?.[0] || '',
            style: mbJson.data.top_tags?.[1] || '',
            musicbrainzFallback: true,
          });
          if (mbJson.data.recent_releases?.length > 0) {
            setArtistMbReleases(mbJson.data.recent_releases);
          }
        }
      } catch (_) {}
    } finally {
      setArtistModalLoading(false);
    }
  };

  const handleSaveTrack = async (track) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) {
        alert('Please login to save tracks');
        return;
      }

      // Save to identification history
      const response = await axios.post(
        `${API}/identification_history`,
        {
          track_info: {
            title: track.title,
            artist: track.artist,
            preview_url: track.preview_url,
            artwork: track.artwork,
            duration_ms: track.duration_ms,
            genre: track.genre,
            album: track.album || {}
          },
          confidence: 0.95, // From Discovery Zone
          source: 'discovery_zone'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      alert(`💾 "${track.title}" saved to your library!`);
      console.log('✅ Track saved:', response.data);
    } catch (err) {
      console.error('Failed to save track:', err);
      if (err.response?.status === 401) {
        alert('Please login to save tracks');
      } else {
        alert(err.response?.data?.detail || 'Failed to save track');
      }
    }
  };

  const handleYouTubeSearch = async (track) => {
    try {
      setLoadingYoutubeVideo(true);
      setYoutubeError(null);
      setYoutubeVideoData(null);

      console.log('Searching YouTube for:', track);

      const response = await fetch(`${BACKEND_URL}/api/discovery/youtube-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artist: track.artist,
          title: track.title,
          genre: track.genre
        })
      });

      if (!response.ok) {
        throw new Error(`YouTube search failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.found) {
        setYoutubeVideoData(result);
      } else {
        setYoutubeError('No videos found for this track. Try searching on YouTube directly.');
      }

    } catch (error) {
      console.error('YouTube search error:', error);
      setYoutubeError(error.message || 'Failed to search YouTube. Please try again.');
    } finally {
      setLoadingYoutubeVideo(false);
    }
  };

  // 3-Tier Access Control - Updated for community building
  const isPlusUser = () => user?.subscription === 'plus' || user?.subscription === 'premium';
  const isPremiumUser = () => user?.subscription === 'premium';
  
  const canAccessDiscover = () => true; // Discovery Zone available to all users
  const canAccessSocial = () => true; // Social features available to all users for community building
  const canAccessWishlist = () => isPremiumUser();
  const canAccessDJTools = () => isPremiumUser();

  // Recording state management
  const [recordingState, setRecordingState] = useState('idle'); // 'idle', 'starting', 'recording', 'processing', 'stopping'
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  // ID Engine v2 progressive-capture session guards
  const idSessionRef = useRef(0);
  const idResolvedRef = useRef(false);
  const idQuickInFlightRef = useRef(false);
  const RECORD_QUICK_SECONDS = 6;
  const RECORD_FULL_SECONDS = 12;
  // Stream reference to keep microphone access
  const streamRef = useRef(null);
  // Audio chunks reference for retry functionality
  const audioChunksRef = useRef([]);

  // RETRY FUNCTIONALITY - Start new recording without requesting mic permissions again
  const handleRetry = async () => {
    if (!streamRef.current || streamRef.current.getTracks().every(track => track.readyState === 'ended')) {
      // If no active stream, fallback to full mic setup
      return handleListen();
    }

    try {
      console.log('🔄 RETRY: Starting new recording with existing microphone access...');
      
      // Clear any previous results
      setIdentifiedTrack(null);
      setSongstatsData(null);
      setRateLimitError(null);
      setAuthError(null);
      
      // Start new recording with existing stream
      await startRecordingWithStream(streamRef.current);
      
    } catch (error) {
      console.error('❌ Retry failed:', error);
      setAuthError(`Retry failed: ${error.message}`);
      // Fallback to full mic setup
      handleListen();
    }
  };

  // SEPARATE RECORDING FUNCTION - Can be called for initial or retry
  const startRecordingWithStream = async (stream) => {
    // Reset states
    setRecordingState('recording');
    setIsListening(true);
    setCountdown(RECORD_FULL_SECONDS);
    audioChunksRef.current = [];
    idSessionRef.current += 1;
    const sessionId = idSessionRef.current;
    idResolvedRef.current = false;
    idQuickInFlightRef.current = false;

    // Start countdown timer
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Find supported MIME type for cross-browser compatibility
    let mimeType = '';
    const supportedTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/wav'
    ];
    
    for (const type of supportedTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        mimeType = type;
        console.log('✅ Using supported MIME type:', mimeType);
        break;
      }
    }
    
    if (!mimeType) {
      console.log('⚠️ No preferred MIME type supported, using default');
    }

    const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 128000 } : {});
    const recorderMime = mediaRecorder.mimeType || mimeType || 'audio/webm';
    const recorderExt = recorderMime.includes('mp4') ? 'mp4' : recorderMime.includes('ogg') ? 'ogg' : recorderMime.includes('wav') ? 'wav' : 'webm';
    
    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      console.log('🛑 MediaRecorder stopped, processing audio...');
      
      // Clear countdown timer
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }

      if (sessionId !== idSessionRef.current) return;
      if (idResolvedRef.current) {
        setIsListening(false);
        setRecordingState('idle');
        return;
      }
      
      setRecordingState('processing');
      
      if (audioChunksRef.current.length === 0) {
        setAuthError('No audio data captured');
        setRecordingState('idle');
        setIsListening(false);
        return;
      }
      
      const audioBlob = new Blob(audioChunksRef.current, { type: recorderMime });
      console.log('🎵 Full clip blob:', audioBlob.size, 'bytes', recorderMime);
      
      if (audioBlob.size > 0) {
        await sendAudioForIdentification(audioBlob, null, { stage: 'full', ext: recorderExt, sessionId });
      }
    };

    mediaRecorder.onerror = (event) => {
      console.error('❌ MediaRecorder error:', event.error);
      setAuthError(`Recording error: ${event.error.message}`);
      setRecordingState('idle');
      setIsListening(false);
      
      // Clear countdown timer on error
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };

    console.log('🎙️ Starting recording (progressive capture)...');
    mediaRecorder.start(1000);

    // Progressive capture: quick pass at 6s (keeps recording), full pass at 12s
    setTimeout(() => {
      if (sessionId !== idSessionRef.current || idResolvedRef.current) return;
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;
      if (audioChunksRef.current.length === 0) return;
      const quickBlob = new Blob(audioChunksRef.current, { type: recorderMime });
      console.log('⚡ Quick pass at 6s:', quickBlob.size, 'bytes');
      idQuickInFlightRef.current = true;
      sendAudioForIdentification(quickBlob, null, { stage: 'quick', ext: recorderExt, sessionId });
    }, RECORD_QUICK_SECONDS * 1000);

    setTimeout(() => {
      if (sessionId !== idSessionRef.current) return;
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        console.log(`⏰ ${RECORD_FULL_SECONDS}s completed, stopping...`);
        mediaRecorderRef.current.stop();
      }
    }, RECORD_FULL_SECONDS * 1000);
  };

  // Audio verification and debugging
  const [audioDebug, setAudioDebug] = useState({
    isRecording: false,
    audioBlob: null,
    audioUrl: null,
    audioFormat: null,
    audioSize: 0,
    sampleRate: null,
    duration: 0
  });

  const verifyAudioCapture = (audioBlob, mimeType) => {
    console.log('🔍 AUDIO VERIFICATION START');
    console.log('📊 Audio Blob Size:', audioBlob.size, 'bytes');
    console.log('📊 Audio MIME Type:', mimeType);
    console.log('📊 Audio Type:', audioBlob.type);
    
    // Create URL for playback testing
    const audioUrl = URL.createObjectURL(audioBlob);
    
    // Create audio element to get metadata
    const audio = new Audio(audioUrl);
    
    audio.addEventListener('loadedmetadata', () => {
      console.log('🔍 AUDIO METADATA:');
      console.log('⏱️ Duration:', audio.duration, 'seconds');
      console.log('📡 Network State:', audio.networkState);
      console.log('🎵 Ready State:', audio.readyState);
      
      setAudioDebug(prev => ({
        ...prev,
        audioBlob,
        audioUrl,
        audioFormat: mimeType,
        audioSize: audioBlob.size,
        duration: audio.duration
      }));
    });
    
    audio.addEventListener('error', (error) => {
      console.error('❌ Audio verification error:', error);
    });
    
    // Load the audio to trigger metadata loading
    audio.load();
    
    // Also save to temporary "file" for debugging (in browser memory)
    console.log('💾 Audio blob created and ready for verification');
    console.log('🔗 Temporary audio URL:', audioUrl);
    
    return audioUrl;
  };

  // Real AudD microphone functionality with robust error handling
  // SIMPLIFIED MODE: Set unlimited usage from start
  const [dailyUsage, setDailyUsage] = useState({ used: 0, limit: -1, remaining: -1 });
  const [rateLimitError, setRateLimitError] = useState(null);
  const [micPermissionState, setMicPermissionState] = useState('unknown'); // 'unknown', 'granted', 'denied', 'prompt'

  // Update usage limits when user changes
  useEffect(() => {
    if (user) {
      let tierLimit, tierRemaining;
      
      switch (user.subscription) {
        case 'admin':
          tierLimit = -1; // Unlimited
          tierRemaining = -1; // Unlimited
          break;
        case 'free':
          tierLimit = 3;
          tierRemaining = 3; // Start fresh
          break;
        case 'plus':
          tierLimit = 50;
          tierRemaining = 50; // Start fresh
          break;
        case 'premium':
          tierLimit = -1; // Unlimited
          tierRemaining = -1; // Unlimited
          break;
        default:
          tierLimit = 3; // Default to free
          tierRemaining = 3;
      }
      
      console.log('👤 User tier changed:', user.subscription, '- Setting limits:', tierLimit);
      
      setDailyUsage({
        used: 0, // Reset on login
        limit: tierLimit,
        remaining: tierRemaining
      });
    }
  }, [user]); // Trigger when user changes

  const startRecording = async () => {
    try {
      console.log('🎬 START RECORDING - State transition: idle -> starting');
      setRecordingState('starting');
      setIsListening(true);
      
      // Clear any previous track result so the UI stays clean during recording.
      // The mic button area handles all status feedback ("Initializing mic...",
      // "Listening...", "Processing audio..."). No need for a placeholder card.
      setIdentifiedTrack(null);

      // Continue with existing microphone logic...
      return true;
    } catch (error) {
      console.error('❌ Start recording failed:', error);
      setRecordingState('idle');
      setIsListening(false);
      setAuthError(`Recording start failed: ${error.message}`);
      return false;
    }
  };

  const stopRecording = () => {
    console.log('🛑 STOP RECORDING - State transition:', recordingState, '-> stopping');
    setRecordingState('stopping');
    
    if (mediaRecorderRef.current) {
      console.log('🛑 MediaRecorder current state:', mediaRecorderRef.current.state);
      
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
        console.log('🛑 MediaRecorder stopped via ref');
      } else {
        console.log('🛑 MediaRecorder not in recording state:', mediaRecorderRef.current.state);
      }
    } else {
      console.log('🛑 No MediaRecorder found in ref');
    }
    
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
      console.log('🛑 Recording timer cleared');
    }
    
    setRecordingState('processing');
  };

  const handleListen = async () => {
    try {
      console.log('🎤 BUTTON CLICKED - Starting microphone access...');
      console.log('🔍 Current states - listening:', isListening, 'recording:', recordingState);
      
      // Prevent multiple concurrent recordings
      if (recordingState !== 'idle' || isListening) {
        console.log('⚠️ Recording already in progress, ignoring click');
        return;
      }

      // Clear previous states and set listening state
      setAuthError(null);
      setRateLimitError(null);
      setIdentifiedTrack(null);
      
      console.log('✅ State cleared, starting recording process');

      // Start the recording process
      const startResult = await startRecording();
      if (!startResult) {
        console.log('❌ Failed to start recording');
        return;
      }

      // Check daily limits (with proper tier logic + admin bypass)
      console.log('🔍 Checking rate limits for user:', user?.email, 'tier:', user?.subscription);
      console.log('🔍 Current usage:', dailyUsage);
      
      // Admin bypass - unlimited access
      if (user?.subscription === 'admin') {
        console.log('✅ Admin user - bypassing all rate limits');
      }
      // Free tier: 3/day limit
      else if (user?.subscription === 'free' && dailyUsage.remaining <= 0) {
        setRateLimitError(`Daily limit reached (${dailyUsage.limit} IDs/day for Free tier). Upgrade to Plus for 50 IDs/day!`);
        setIsListening(false);
        setRecordingState('idle');
        return;
      }
      // Plus tier: 50/day limit  
      else if (user?.subscription === 'plus' && dailyUsage.remaining <= 0) {
        setRateLimitError(`Daily limit reached (${dailyUsage.limit} IDs/day for Plus tier). Upgrade to Premium for unlimited IDs!`);
        setIsListening(false);
        setRecordingState('idle');
        return;
      }
      // Premium tier: unlimited (no check needed)
      else if (user?.subscription === 'premium') {
        console.log('✅ Premium user - unlimited access');
      }

      // CRITICAL FIX: Feature detection for MediaDevices API
      console.log('🔍 Checking browser support...');
      
      // Check if browser supports necessary APIs
      if (!navigator) {
        throw new Error('Navigator API not available');
      }

      if (!navigator.mediaDevices) {
        throw new Error('MediaDevices API not supported in this browser. Please use Chrome, Firefox, or Safari.');
      }

      if (!navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia not supported in this browser. Please update your browser.');
      }

      // Check if we're in a secure context (HTTPS)
      if (!window.isSecureContext) {
        throw new Error('Microphone access requires HTTPS. Please ensure you\'re using a secure connection.');
      }

      // Check MediaRecorder support
      if (!window.MediaRecorder) {
        throw new Error('Audio recording not supported in this browser. Please use a modern browser.');
      }

      console.log('✅ Browser support confirmed');

      // Check available audio devices
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(device => device.kind === 'audioinput');
        console.log('🎤 Available audio input devices:', audioInputs.length);
        console.log('🎤 Audio devices:', audioInputs.map(d => ({ label: d.label, deviceId: d.deviceId })));
      } catch (deviceError) {
        console.warn('⚠️ Could not enumerate devices:', deviceError);
      }

      console.log('✅ Browser support confirmed');

      // Request microphone permission with detailed logging
      let stream;
      try {
        console.log('🎤 Requesting microphone permission...');
        console.log('🔗 URL:', window.location.href);
        console.log('🔒 Secure context:', window.isSecureContext);
        
        const musicConstraints = {
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: 1,
            sampleRate: 44100,
          }
        };
        try {
          stream = await navigator.mediaDevices.getUserMedia(musicConstraints);
          console.log('🎚️ Music-grade constraints applied (voice processing disabled)');
        } catch (constraintErr) {
          console.warn('⚠️ Music constraints rejected, falling back to defaults:', constraintErr?.name);
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
        try {
          const settings = stream.getAudioTracks()[0]?.getSettings?.();
          console.log('🎚️ Applied mic settings:', settings);
        } catch (_) {}
        
        console.log('✅ Microphone permission granted successfully!');
        console.log('🎵 Audio stream:', stream);
        console.log('🎵 Audio tracks:', stream.getAudioTracks());
        
        // CRITICAL FIX: Add track ended event listeners
        const audioTracks = stream.getAudioTracks();
        audioTracks.forEach((track, index) => {
          console.log(`🎵 Setting up track ${index} monitoring...`);
          
          track.addEventListener('ended', () => {
            console.error('🛑 CRITICAL: Audio track ended unexpectedly!');
            console.error('🛑 Track state:', track.readyState);
            console.error('🛑 Track enabled:', track.enabled);
            
            // Handle track termination gracefully
            setAuthError('Microphone access was terminated by the system. Please try again.');
            setRecordingState('idle');
            setIsListening(false);
            
            // Stop any ongoing recording
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              console.log('🛑 Stopping MediaRecorder due to track termination');
              mediaRecorderRef.current.stop();
            }
          });
        });
        
        setMicPermissionState('granted');
        
        // Store stream for retry functionality
        streamRef.current = stream;
      } catch (permissionError) {
        console.error('❌ Microphone permission failed:', permissionError);
        console.error('❌ Error name:', permissionError.name);
        console.error('❌ Error message:', permissionError.message);
        
        setMicPermissionState('denied');
        
        let errorMessage = 'Microphone access failed: ';
        if (permissionError.name === 'NotAllowedError') {
          errorMessage += 'Permission denied. Please check your browser settings and ensure microphone access is enabled for this site.';
        } else if (permissionError.name === 'NotFoundError') {
          errorMessage += 'No microphone detected. Please ensure your device has a working microphone, check device permissions, and try refreshing the page. On mobile, make sure browser app has microphone permission.';
        } else if (permissionError.name === 'NotReadableError') {
          errorMessage += 'Microphone is in use by another application. Please close other apps using the microphone.';
        } else if (permissionError.name === 'OverconstrainedError') {
          errorMessage += 'Microphone settings not supported. Please try with different audio settings.';
        } else if (permissionError.name === 'SecurityError') {
          errorMessage += 'Security error. Please ensure you\'re using HTTPS and refresh the page.';
        } else {
          errorMessage += permissionError.message;
        }
        
        setAuthError(errorMessage);
        setIsListening(false);
        return;
      }

      // Start initial recording
      await startRecordingWithStream(stream);

    } catch (error) {
      console.error('❌ handleListen completely failed:', error);
      setAuthError(`Microphone setup failed: ${error.message}`);
      setIsListening(false);
    }
  };

  // SHAZAM ALGORITHM IMPLEMENTATION - Steps 3-4: Audio Fingerprinting
  const createAudioFingerprint = async (audioBlob) => {
    try {
      console.log('🔬 ===== SHAZAM ALGORITHM: AUDIO FINGERPRINTING =====');
      console.log('🔬 Step 3: Converting to frequency domain using Fourier Transform...');
      
      // Create AudioContext for audio processing
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      console.log('🔬 Audio decoded - Sample Rate:', audioBuffer.sampleRate, 'Duration:', audioBuffer.duration);
      
      // Get audio data (Step 3: Digital to frequency domain)
      const channelData = audioBuffer.getChannelData(0); // Get first channel
      const sampleRate = audioBuffer.sampleRate;
      
      console.log('🔬 Step 4: Creating unique audio fingerprint using spectrogram...');
      
      // Create spectrogram-based fingerprint (simplified version of Shazam's algorithm)
      const fingerprint = await generateSpectrogramFingerprint(channelData, sampleRate);
      
      console.log('🔬 Audio fingerprint created:', fingerprint.length, 'features');
      console.log('🔬 Fingerprint sample:', fingerprint.slice(0, 10));
      
      return {
        fingerprint,
        sampleRate,
        duration: audioBuffer.duration,
        features: fingerprint.length
      };
      
    } catch (error) {
      console.error('❌ Audio fingerprinting failed:', error);
      throw new Error(`Fingerprinting failed: ${error.message}`);
    }
  };

  const generateSpectrogramFingerprint = async (audioData, sampleRate) => {
    console.log('🔬 Generating spectrogram fingerprint...');
    
    // Shazam-like fingerprinting algorithm
    const windowSize = 1024; // FFT window size
    const hopSize = 512; // Overlap
    const fingerprint = [];
    
    // Process audio in overlapping windows (spectrogram approach)
    for (let i = 0; i < audioData.length - windowSize; i += hopSize) {
      const window = audioData.slice(i, i + windowSize);
      
      // Apply windowing function (Hamming window)
      const windowedData = window.map((sample, index) => 
        sample * (0.54 - 0.46 * Math.cos(2 * Math.PI * index / (windowSize - 1)))
      );
      
      // Simple FFT approximation for key frequencies
      const frequencies = extractKeyFrequencies(windowedData, sampleRate);
      
      // Create constellation map (Shazam's approach)
      const constellation = createConstellationMap(frequencies);
      
      fingerprint.push(...constellation);
    }
    
    console.log('🔬 Spectrogram fingerprint generated with', fingerprint.length, 'constellation points');
    return fingerprint;
  };

  const extractKeyFrequencies = (windowData, sampleRate) => {
    // Extract dominant frequencies using simplified FFT
    const frequencies = [];
    const nyquist = sampleRate / 2;
    
    // Focus on musical frequency ranges (like Shazam)
    const frequencyBands = [
      { min: 40, max: 80 },     // Bass
      { min: 80, max: 160 },    // Low-mid
      { min: 160, max: 320 },   // Mid
      { min: 320, max: 640 },   // High-mid
      { min: 640, max: 1280 },  // High
      { min: 1280, max: 2560 }  // Very high
    ];
    
    frequencyBands.forEach(band => {
      const bandEnergy = calculateBandEnergy(windowData, band.min, band.max, nyquist);
      if (bandEnergy > 0.1) { // Threshold for significant energy
        frequencies.push({
          frequency: (band.min + band.max) / 2,
          energy: bandEnergy,
          band: `${band.min}-${band.max}Hz`
        });
      }
    });
    
    return frequencies;
  };

  const calculateBandEnergy = (data, minFreq, maxFreq, nyquist) => {
    // Simplified energy calculation for frequency band
    let energy = 0;
    const dataLength = data.length;
    
    for (let i = 0; i < dataLength; i++) {
      const freq = (i / dataLength) * nyquist;
      if (freq >= minFreq && freq <= maxFreq) {
        energy += Math.abs(data[i]);
      }
    }
    
    return energy / dataLength;
  };

  const createConstellationMap = (frequencies) => {
    // Create constellation points (Shazam's key innovation)
    const constellation = [];
    
    frequencies.forEach((freq, index) => {
      // Create hash-like fingerprint points
      const hash = Math.floor(freq.frequency * freq.energy * 1000) % 10000;
      constellation.push({
        hash,
        frequency: freq.frequency,
        energy: freq.energy,
        timeIndex: index
      });
    });
    
    return constellation;
  };

  const sendAudioForIdentification = async (audioBlob, fingerprintData = null, opts = {}) => {
    const stage = opts.stage || 'full';
    const isQuick = stage === 'quick';
    const sessionId = opts.sessionId ?? idSessionRef.current;
    const stale = () => sessionId !== idSessionRef.current;
    try {
      console.log(`🎵 ===== ID ENGINE v2: ${stage.toUpperCase()} PASS =====`);
      console.log('📊 Audio Blob Size:', audioBlob.size, 'bytes', audioBlob.type);
      
      if (fingerprintData) {
        console.log('🔬 Fingerprint Data Available:');
        console.log('🔬 Features:', fingerprintData.features);
        console.log('🔬 Sample Rate:', fingerprintData.sampleRate);
        console.log('🔬 Duration:', fingerprintData.duration);
        console.log('🔬 This should improve matching accuracy vs raw audio');
      } else {
        console.log('⚠️ No fingerprint data - using direct audio method');
      }
      
      // Verify audio format is acceptable for AudD
      const acceptableFormats = ['audio/webm', 'audio/mp4', 'audio/wav', 'audio/ogg'];
      if (!acceptableFormats.includes(audioBlob.type) && audioBlob.type !== '') {
        console.warn('⚠️ AUDIO FORMAT WARNING: Unusual format:', audioBlob.type);
      }
      
      // Check if audio data looks valid
      if (audioBlob.size < 100) {
        throw new Error('Audio data too small - likely no sound was recorded');
      }
      
      const formData = new FormData();
      formData.append('file', audioBlob, `recording.${opts.ext || 'webm'}`);
      formData.append('stage', stage);
      formData.append('attempt_group', `${sessionId}-${Date.now().toString(36)}`);
      formData.append('client_format', audioBlob.type || '');
      formData.append('client_ua', navigator.userAgent || '');
      formData.append('user_tier', 'unlimited');
      formData.append('user_id', user?.id || 'beatseek_tester');
      
      // Include fingerprint data if available (Shazam-like enhancement)
      if (fingerprintData) {
        formData.append('fingerprint', JSON.stringify({
          features: fingerprintData.features,
          sampleRate: fingerprintData.sampleRate, 
          duration: fingerprintData.duration,
          algorithm: 'beatseek_shazam_v1'
        }));
        console.log('🔬 Enhanced: Including fingerprint data with audio');
      }

      console.log('🔗 Making request to PARALLEL backend (ACRCloud + AudD)...');
      const response = await fetch(`${BACKEND_URL}/api/parallel/recognize`, {
        method: 'POST',
        body: formData,
        signal: AbortSignal.timeout(25000)
      });

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error(`API request failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ ID Engine response (${stage}):`, result);

      if (stale()) return;

      const quickConfidence = Number(result?.result?.confidence || 0);
      if (isQuick) {
        idQuickInFlightRef.current = false;
        if (idResolvedRef.current) return;
        if (!(result.success && quickConfidence >= 75)) {
          console.log('⚡ Quick pass not confident enough — waiting for full 12s clip');
          return;
        }
        idResolvedRef.current = true;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
        console.log('⚡ Quick pass locked in at', quickConfidence, '%');
      } else if (idResolvedRef.current) {
        return;
      }

      if (result.success && result.result) {
        if (!isQuick) idResolvedRef.current = true;
        // Successful identification
        const auddResult = result.result;
        
        console.log('🎨 Debug artwork data:', {
          'auddResult.track?.artwork': auddResult.track?.artwork,
          'auddResult.track?.album_art': auddResult.track?.album_art,
          'auddResult structure': auddResult,
          'full result': result
        });

        // SIMPLIFIED: Get artwork URL, fallback to null for CSS placeholder
        // Check TheAudioDB enrichment first for HD artwork
        const enrichment = auddResult.enrichment || null;
        const enrichArtistArt = enrichment?.artist?.artwork_thumb;
        const enrichTrackArt = enrichment?.track?.artwork;
        
        const artworkUrl = enrichTrackArt ||
                          enrichArtistArt ||
                          auddResult.track?.artwork || 
                          auddResult.track?.album_art || 
                          auddResult.spotify_enhanced?.album?.images?.[0]?.url || 
                          auddResult.spotify?.album?.images?.[0]?.url || 
                          null;
        
        console.log('🎨 Raw artwork URL from API:', artworkUrl);
        
        // Validate URL and filter out bad domains
        let validArtworkUrl = null;
        
        if (artworkUrl) {
          try {
            new URL(artworkUrl); // Test if it's a valid URL
            // Check if it's not a known bad domain
            if (!artworkUrl.includes('picsbadaboom') && !artworkUrl.includes('placeholder.com/300/300')) {
              validArtworkUrl = artworkUrl;
            } else {
              console.log('🎨 Blocked bad domain artwork URL:', artworkUrl);
            }
          } catch (e) {
            console.log('🎨 Invalid artwork URL detected:', artworkUrl);
          }
        }
        
        console.log('🎨 Final artwork URL (null = CSS placeholder):', validArtworkUrl);

        // DEBUG: Check what genre data we're getting
        console.log('🎵 Debug genre data:', {
          'auddResult.track?.genres': auddResult.track?.genres,
          'auddResult.spotify_enhanced?.genres': auddResult.spotify_enhanced?.genres,
          'result.track?.genres': result.track?.genres,
          'all auddResult keys': Object.keys(auddResult),
          'result.is_electronic': result.is_electronic
        });

        // IMPROVED: Extract real genres from API responses
        // Also check TheAudioDB enrichment for genre data
        const gate = auddResult.gate || null;
        const enrichGenre = enrichment?.track?.genre || enrichment?.artist?.genre;
        const enrichStyle = enrichment?.track?.style || enrichment?.artist?.style;
        const apiGenres = auddResult.track?.genres || auddResult.spotify_enhanced?.genres || [];
        const mainGenre = gate?.canonical_genre || (apiGenres.length > 0 ? apiGenres[0] : (enrichGenre || 'Electronic'));
        const subGenres = (gate?.subgenres || []).filter(s => s !== mainGenre).slice(0, 3).join(', ') || apiGenres.slice(1).join(', ') || enrichStyle || 'Electronic Music';

        console.log('🎵 Final genre selection:', { mainGenre, subGenres, apiGenres, enrichGenre });

        const beatseekTrack = {
          id: `audd_${Date.now()}`,
          title: auddResult.track?.title || 'Unknown Title',
          artist: auddResult.track?.artist || 'Unknown Artist',
          genre: mainGenre,
          subgenre: subGenres,
          artwork: validArtworkUrl,
          bpm: auddResult.bpm_key?.tempo
            ? Math.round(auddResult.bpm_key.tempo)
            : Math.round(auddResult.spotify_enhanced?.audio_features?.tempo) || null,
          key: auddResult.bpm_key?.key_name
            ? `${auddResult.bpm_key.key_name}${auddResult.bpm_key.scale === 'minor' ? ' minor' : auddResult.bpm_key.scale === 'major' ? ' major' : ''}`
            : null,
          bpmKeySource: auddResult.bpm_key?.source || null,
          attemptId: auddResult.attempt_id || null,
          canonicalGenre: gate?.canonical_genre || null,
          subgenres: gate?.subgenres || [],
          genreVerified: gate ? gate.verified : null,
          year: auddResult.track?.year || null,
          previewUrl: auddResult.track?.deezer?.preview || null,
          deezerLink: auddResult.track?.deezer?.link || null,
          versions: auddResult.versions || [],
          community: auddResult.track?.community || null,
          verified: auddResult.track?.verified || null,
          basedOn: auddResult.track?.based_on || null,
          confidence: `${Math.round(Number(auddResult.confidence) || 0)}%`,
          confidenceScore: Number(auddResult.confidence) || 0,
          versionType: auddResult.track?.version_type || null,
          correctedByCommunity: !!auddResult.track?.corrected_by_community,
          correctionCredit: auddResult.track?.correction_credit || null,
          originalMatch: auddResult.track?.original_match || null,
          acrId: auddResult.track?.acr_id || null,
          remixer: auddResult.track?.remixer || null,
          label: auddResult.track?.label || null,
          isrc: auddResult.track?.isrc || null,
          alternates: auddResult.alternates || [],
          matchedIn: auddResult.timings?.total_ms ? Math.round(auddResult.timings.total_ms / 100) / 10 : null,
          source: auddResult.provider || 'BEATSEEK Engine',
          message: result.is_electronic ? 
            '🎉 Electronic track identified successfully!' : 
            'Track identified',
          enrichment: enrichment ? {
            artistBio: enrichment.artist?.bio || null,
            artistFullBio: enrichment.artist?.full_bio || null,
            artistThumb: enrichment.artist?.artwork_thumb || null,
            artistFanart: enrichment.artist?.artwork_fanart || null,
            artistBanner: enrichment.artist?.artwork_banner || null,
            artistWide: enrichment.artist?.artwork_wide || null,
            artistCountry: enrichment.artist?.country || null,
            artistFormedYear: enrichment.artist?.formed_year || null,
            trackMood: enrichment.track?.mood || null,
            trackTheme: enrichment.track?.theme || null,
            trackDescription: enrichment.track?.description || null,
            trackAlbum: enrichment.track?.album || null,
            musicVideoUrl: enrichment.track?.music_video_url || null,
            musicVideos: enrichment.music_videos || [],
          } : null,
          // Last.fm enrichment payload — tags, canonical names, electronic-music gate signal
          lastfm: auddResult.lastfm || null,
          lastfmTags: (auddResult.lastfm?.tags || []).map(t => t.name).filter(Boolean).slice(0, 6),
        };

        setIdentifiedTrack(beatseekTrack);

        // Phase-2 enrichment (BPM/key, artist bio/art, other versions + previews) fills the card progressively
        if (beatseekTrack.attemptId) {
          fetch(`${BACKEND_URL}/api/track-id/${beatseekTrack.attemptId}/enrich`)
            .then(r => (r.ok ? r.json() : null))
            .then(en => {
              if (!en || stale()) return;
              setIdentifiedTrack(prev => {
                if (!prev || prev.attemptId !== beatseekTrack.attemptId) return prev;
                const bk = en.bpm_key;
                const e = en.enrichment;
                return {
                  ...prev,
                  bpm: prev.bpm || (bk?.tempo ? Math.round(bk.tempo) : null),
                  key: prev.key || (bk?.key_name ? `${bk.key_name}${bk.scale === 'minor' ? ' minor' : bk.scale === 'major' ? ' major' : ''}` : null),
                  bpmKeySource: prev.bpmKeySource || bk?.source || null,
                  previewUrl: prev.previewUrl || en.preview || null,
                  versions: (en.versions && en.versions.length) ? en.versions : prev.versions,
                  artwork: prev.artwork || e?.track?.artwork || e?.artist?.artwork_thumb || null,
                  enrichment: prev.enrichment || (e ? {
                    artistBio: e.artist?.bio || null, artistFullBio: e.artist?.full_bio || null,
                    artistThumb: e.artist?.artwork_thumb || null, artistFanart: e.artist?.artwork_fanart || null,
                    artistBanner: e.artist?.artwork_banner || null, artistWide: e.artist?.artwork_wide || null,
                    artistCountry: e.artist?.country || null, artistFormedYear: e.artist?.formed_year || null,
                    trackMood: e.track?.mood || null, trackTheme: e.track?.theme || null,
                    trackDescription: e.track?.description || null, trackAlbum: e.track?.album || null,
                    musicVideoUrl: e.track?.music_video_url || null, musicVideos: e.music_videos || [],
                  } : null),
                };
              });
            })
            .catch(() => {});
        }
        
        // MusicBrainz cross-reference: fetch better credits, tags, and remix relationships
        const trackTitle = auddResult.track?.title;
        const trackArtist = auddResult.track?.artist;
        const trackIsrc = auddResult.track?.isrc || auddResult.spotify_enhanced?.external_ids?.isrc;
        if (trackTitle && trackArtist) {
          fetch(`${BACKEND_URL}/api/musicbrainz/recording/search?title=${encodeURIComponent(trackTitle)}&artist=${encodeURIComponent(trackArtist)}${trackIsrc ? `&isrc=${encodeURIComponent(trackIsrc)}` : ''}`)
            .then(r => r.json())
            .then(mbData => {
              if (mbData.success && mbData.data) {
                const mbRecording = mbData.data;
                setIdentifiedTrack(prev => prev ? ({
                  ...prev,
                  mbData: {
                    mbid: mbRecording.mbid,
                    artist_credits: mbRecording.artist_credits || [],
                    artist_display: mbRecording.artist_display || '',
                    tags: mbRecording.tags || [],
                    releases: mbRecording.releases || [],
                  }
                }) : prev);
                // Fetch remix/relationship data if we got an MBID
                if (mbRecording.mbid) {
                  fetch(`${BACKEND_URL}/api/musicbrainz/recording/${mbRecording.mbid}/relationships`)
                    .then(r => r.json())
                    .then(relData => {
                      if (relData.success && relData.data) {
                        setIdentifiedTrack(prev => prev ? ({
                          ...prev,
                          mbRelationships: {
                            remixes: relData.data.remixes || [],
                            covers: relData.data.covers || [],
                            samples: relData.data.samples || [],
                            dj_mixes: relData.data.dj_mixes || [],
                            total: relData.data.total_relationships || 0,
                          }
                        }) : prev);
                      }
                    })
                    .catch(() => {});
                }
              }
            })
            .catch(() => {});
        }

        // Check artist availability for info icon
        if (beatseekTrack.artist && !(beatseekTrack.artist in artistAvailability)) {
          fetch(`${BACKEND_URL}/api/enrichment/artists/check`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ artists: [beatseekTrack.artist] })
          })
            .then(r => r.json())
            .then(data => {
              if (data.available) setArtistAvailability(prev => ({ ...prev, ...data.available }));
            })
            .catch(() => {});
        }
        
        // Update usage stats using backend tier information
        if (result.usage) {
          console.log('📊 Backend usage response:', result.usage);
          
          // Use backend-provided tier limits instead of frontend calculation
          const backendUsage = result.usage;
          
          setDailyUsage({
            used: backendUsage.daily_usage,
            limit: backendUsage.daily_limit,
            remaining: backendUsage.daily_remaining
          });
          
          console.log('📊 Updated usage from backend - Used:', backendUsage.daily_usage, 'Limit:', backendUsage.daily_limit, 'Remaining:', backendUsage.daily_remaining);
        }

        // Add to history
        setHistory(prev => [beatseekTrack, ...prev.slice(0, 19)]);
        setTimeout(loadIdStats, 1500);

      } else {
        const possible = result.status === 'possible' && result.result?.track;
        const outsideScope = result.status === 'not_electronic' && result.result?.track;
        const reason = result.reason;
        const tips = {
          too_quiet: 'We barely heard anything. Move closer to the speaker and try again.',
          no_music: 'No music detected — only noise. Point the mic at the sound source.',
          timeout: 'Our recognition partners were slow to respond. Try again in a moment.',
        };
        setIdentifiedTrack(outsideScope ? {
          id: 'not_electronic',
          title: result.result.track.title,
          artist: result.result.track.artist,
          genre: result.result.gate?.evidence?.find(e => e.vote < 0)?.label || 'Not electronic',
          subgenre: 'outside BEATSEEK scope',
          artwork: result.result.track.artwork || null,
          confidence: `${Math.round(result.result.confidence)}%`,
          confidenceScore: result.result.confidence,
          outsideScope: true,
          source: result.result.provider || 'BEATSEEK Engine',
          message: null
        } : possible ? {
          id: 'possible_match',
          title: result.result.track.title,
          artist: result.result.track.artist,
          genre: 'Possible match',
          subgenre: `${Math.round(result.result.confidence)}% confidence`,
          artwork: result.result.track.artwork || null,
          confidence: `${Math.round(result.result.confidence)}%`,
          confidenceScore: result.result.confidence,
          possible: true,
          attemptId: result.result.attempt_id || null,
          canonicalGenre: result.result.gate?.canonical_genre || null,
          subgenres: result.result.gate?.subgenres || [],
          versionType: result.result.track.version_type || null,
          remixer: result.result.track.remixer || null,
          label: result.result.track.label || null,
          year: result.result.track.year || null,
          previewUrl: result.result.track.deezer?.preview || null,
          versions: result.result.versions || [],
          source: result.result.provider || 'BEATSEEK Engine',
          message: 'Low-confidence match — could be an edit or bootleg of this track. Try again closer to the speaker to confirm.'
        } : {
          id: 'not_found',
          title: '🔍 No Match',
          artist: 'Track not identified',
          genre: 'No match',
          subgenre: reason === 'too_quiet' ? 'Too quiet' : reason === 'no_music' ? 'No music detected' : 'Not in the databases yet',
          artwork: null,
          confidence: 'No Match',
          source: 'BEATSEEK ID Engine',
          message: tips[reason] || 'Get closer to the speaker, avoid talking over it, and hold steady for the full 12 seconds. Unreleased edits and bootlegs may not be in any database yet.'
        });
      }

    } catch (error) {
      if (stale()) return;
      if (isQuick) {
        idQuickInFlightRef.current = false;
        console.warn('⚡ Quick pass failed silently, full pass will follow:', error?.message);
        return;
      }
      if (idResolvedRef.current) return;
      console.error('❌ Audio identification failed:', error);
      
      const timedOut = error.name === 'AbortError' || error.name === 'TimeoutError';
      let errorMessage = timedOut
        ? 'The ID engine took too long to respond. Please try again.'
        : error.message.includes('Network') || error.message.includes('Failed to fetch')
          ? 'Network error. Please check your internet connection.'
          : `Something went wrong on our side: ${error.message}`;
      
      setAuthError(errorMessage);
      
      setIdentifiedTrack({
        id: 'error',
        title: timedOut ? '⏱️ Took Too Long' : '⚠️ Service Issue',
        artist: 'BEATSEEK ID Engine',
        genre: 'Error',
        subgenre: timedOut ? 'Timeout' : 'Technical issue',
        artwork: null,
        confidence: 'Error',  
        source: 'System',
        message: errorMessage
      });
    } finally {
      if (isQuick && !idResolvedRef.current) return;
      if (stale()) return;
      setIsListening(false);
      setRecordingState('idle'); // CRITICAL FIX: Reset recording state to enable retry button
      
      // Clear countdown timer if still running
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  };

  // Login function - Real API authentication
  const handleLogin = async (email, password) => {
    try {
      setAuthError(null);
      console.log('🔍 Login attempt for:', email);
      
      // Demo accounts removed - using real API authentication only

      console.log('🔍 Trying real API login for:', email);
      // Try real API login
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: (email || '').trim(), password }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Login response data:', data);
        console.log('🔍 User from response:', data.user);
        
        // Map 'id' to 'user_id' for compatibility
        const userWithUserId = { ...data.user, user_id: data.user.id };
        
        setAuthCelebration('login');
        setTimeout(() => setAuthCelebration(null), 2600);
        setUser(userWithUserId);
        localStorage.setItem('beatseek_user', JSON.stringify(userWithUserId));
        localStorage.setItem('beatseek_token', data.access_token);
        setActiveTab(localStorage.getItem('beatseek_default_tab') || 'social');
        console.log('✅ User set in state:', userWithUserId);
        console.log('✅ Current user state after setUser:', user);
        console.log('✅ LocalStorage beatseek_user:', localStorage.getItem('beatseek_user'));
        return true;
      } else {
        console.log('❌ API login failed, status:', response.status);
        const errorData = await response.json();
        console.log('❌ Error data:', errorData);
        // Handle both string and object error formats
        let errorMessage = 'Login failed';
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
        } else if (errorData.detail && errorData.detail.msg) {
          errorMessage = errorData.detail.msg;
        }
        setAuthError(errorMessage);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('Login failed. Please try again.');
      return false;
    }
  };

  // Registration function - New user registration
  const handleRegister = async (name, email, password, userType = 'regular', companyInfo = {}) => {
    try {
      setAuthError(null);
      
      const registrationData = { 
        name, 
        email, 
        password,
        user_type: userType
      };
      
      // Add company info for Labels/A&R
      if (userType === 'label' || userType === 'ar') {
        registrationData.company_name = companyInfo.companyName || null;
        registrationData.company_website = companyInfo.companyWebsite || null;
        registrationData.linkedin_profile = companyInfo.linkedinProfile || null;
        registrationData.demo_submission_email = companyInfo.demoSubmissionEmail || null;
      }
      
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Map 'id' to 'user_id' for compatibility
        const userWithUserId = { ...data.user, user_id: data.user.id };
        
        setAuthCelebration('signup');
        setTimeout(() => setAuthCelebration(null), 2600);
        setUser(userWithUserId);
        localStorage.setItem('beatseek_user', JSON.stringify(userWithUserId));
        localStorage.setItem('beatseek_token', data.access_token);
        setActiveTab(localStorage.getItem('beatseek_default_tab') || 'social');
        setIsSignupMode(false); // Switch back to login view
        return true;
      } else {
        const errorData = await response.json();
        // Handle both string and object error formats
        let errorMessage = 'Registration failed';
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
        } else if (errorData.detail && errorData.detail.msg) {
          errorMessage = errorData.detail.msg;
        }
        setAuthError(errorMessage);
        return false;
      }
    } catch (error) {
      console.error('Registration error:', error);
      setAuthError('Registration failed. Please try again.');
      return false;
    }
  };

  const handleLogout = async () => {
    // Clear local tracks before logging out
    try {
      await clearAllLocalTracks();
      console.log('✅ Local tracks cleared on logout');
    } catch (error) {
      console.error('Error clearing local tracks:', error);
    }
    
    setUser(null);
    setLocalTracks([]);
    localStorage.removeItem('beatseek_user');
    localStorage.removeItem('beatseek_token');
    setActiveTab(localStorage.getItem('beatseek_default_tab') || 'social');
  };

  const handleProfileUpdate = (updatedUser) => {
    console.log('Profile updated:', updatedUser);
    
    // Ensure user_id is present for compatibility
    const userWithUserId = { ...updatedUser, user_id: updatedUser.id || updatedUser.user_id };
    
    setUser(userWithUserId);
    localStorage.setItem('beatseek_user', JSON.stringify(userWithUserId));
  };


  const handleShareProfile = async () => {
    try {
      // Generate shareable profile URL using user ID
      const profileUrl = `${window.location.origin}/profile/${user?.id || user?.user_id}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(profileUrl);
      
      // Show success feedback
      setProfileCopied(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setProfileCopied(false);
      }, 3000);
      
      console.log('Profile URL copied:', profileUrl);
    } catch (error) {
      console.error('Failed to copy profile URL:', error);
      // Fallback: show alert if clipboard API fails
      alert('Failed to copy profile link. Please try again.');
    }
  };



  // Friend Search & Connection System - Phase 2
  const searchUsers = async (query, options = {}) => {
    const { userType = searchUserType, genre = searchGenre, randomDiscover = false } = options;
    
    console.log('🔍 searchUsers called:', { query, userType, genre, randomDiscover });
    
    // Allow search with filters even without query text
    if (!query?.trim() && !randomDiscover && userType === 'all' && genre === 'all') {
      console.log('🔍 Search blocked - no query and all filters');
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (query?.trim()) params.append('query', query.trim());
      if (userType && userType !== 'all') params.append('user_type', userType);
      if (genre && genre !== 'all') params.append('genre', genre);
      if (randomDiscover) params.append('random_discover', 'true');
      if (user?.user_id) params.append('viewer_id', user.user_id);
      params.append('limit', '20');
      
      const url = `${API}/users?${params.toString()}`;
      console.log('🔍 Fetching:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
      });

      console.log('🔍 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Search results:', data?.length || 0, 'users');
        // The API returns users directly, not wrapped in a users property
        const list = Array.isArray(data) ? data : [];
        setSearchResults(list);
        if (window.innerWidth <= 768 && list.length > 0) {
          setShowMobileSearchModal(true);
        }
      } else {
        console.error('User search failed:', response.status);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('User search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Mobile search modal: persist state + navigate to a profile, keeping results restorable
  const handleMobileViewProfile = (targetUser, scrollTop = 0) => {
    try {
      sessionStorage.setItem('beatseek_search_state', JSON.stringify({
        results: searchResults,
        query: searchQuery,
        userType: searchUserType,
        genre: searchGenre,
        scrollTop
      }));
    } catch (e) { /* storage full — pill just won't appear */ }
    setShowMobileSearchModal(false);
    navigate(`/profile/${targetUser.id}`);
  };

  const closeMobileSearch = () => {
    sessionStorage.removeItem('beatseek_search_state');
    setShowMobileSearchModal(false);
  };

  // Restore mobile search results when returning from a profile via "Back to results"
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('restoreSearch') === '1') {
      try {
        const saved = JSON.parse(sessionStorage.getItem('beatseek_search_state') || 'null');
        if (saved?.results?.length) {
          setSearchResults(saved.results);
          setSearchQuery(saved.query || '');
          setSearchUserType(saved.userType || 'all');
          setSearchGenre(saved.genre || 'all');
          setMobileSearchScrollTop(saved.scrollTop || 0);
          setShowMobileSearchModal(true);
        }
      } catch (e) { /* corrupt state — ignore */ }
    }
  }, []);

  const followUser = async (targetUserId) => {
    try {
      const response = await fetch(`${API}/users/${user.user_id}/follow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
        body: JSON.stringify({ followed_user_id: targetUserId })
      });

      if (response.ok) {
        // Update search results to show following status
        setSearchResults(prev => prev.map(searchUser => 
          searchUser.id === targetUserId 
            ? { ...searchUser, is_following: true }
            : searchUser
        ));
        
        // Update following count and reload data
        setFollowingCount(prev => prev + 1);
        loadFriends(); // Refresh friends list
        loadFollowers(); // Refresh followers count
        
        // Show success notification
        alert('You are now following this user!');
        return true;
      } else {
        const errorData = await response.json();
        console.error('Follow failed:', response.status, errorData);
        alert(`Failed to follow user: ${errorData.detail || 'Please try again.'}`);
        return false;
      }
    } catch (error) {
      console.error('Follow error:', error);
      alert('Follow failed. Please try again.');
      return false;
    }
  };

  const unfollowUser = async (targetUserId) => {
    try {
      const response = await fetch(`${API}/users/${user.user_id}/follow/${targetUserId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        }
      });

      if (response.ok) {
        // Update search results to show unfollowed status
        setSearchResults(prev => prev.map(searchUser => 
          searchUser.id === targetUserId 
            ? { ...searchUser, is_following: false }
            : searchUser
        ));
        
        // Update following count
        setFollowingCount(prev => Math.max(0, prev - 1));
        loadFriends(); // Refresh friends list
        loadFollowers(); // Refresh followers count
        
        return true;
      } else {
        const errorData = await response.json();
        console.error('Unfollow failed:', response.status, errorData);
        alert(`Failed to unfollow: ${errorData.detail || 'Please try again.'}`);
        return false;
      }
    } catch (error) {
      console.error('Unfollow error:', error);
      alert('Unfollow failed. Please try again.');
      return false;
    }
  };

  const sendFriendRequest = async (targetUserId) => {
    try {
      // Use user.user_id for consistency with rest of codebase
      const currentUserId = user?.user_id || user?.id;
      if (!currentUserId) {
        console.error('No user ID available for friend request');
        alert('Please log in to send friend requests');
        return false;
      }
      
      console.log('📤 Sending friend request from', currentUserId, 'to', targetUserId);
      
      const response = await fetch(`${API}/users/${currentUserId}/friend-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
        body: JSON.stringify({ to_user_id: targetUserId })
      });

      if (response.ok) {
        // Update search results to show request sent
        setSearchResults(prev => prev.map(searchUser => 
          searchUser.id === targetUserId 
            ? { ...searchUser, friend_status: 'request_sent' }
            : searchUser
        ));
        
        // Refresh social data
        loadFriends();
        loadFollowers();
        
        // Show success notification popup
        alert('Friend request sent! You will be notified when the connection is approved.');
        console.log('Friend request sent successfully');
        return true;
      } else {
        const errorData = await response.json();
        console.error('Friend request failed:', response.status, errorData);
        alert(`Friend request failed: ${errorData.detail || 'Please try again.'}`);
        return false;
      }
    } catch (error) {
      console.error('Friend request error:', error);
      return false;
    }
  };

  const acceptFriendRequest = async (requestId, fromUserId) => {
    try {
      console.log('✅ Accepting friend request:', requestId);
      const response = await fetch(`${API}/friends/requests/${requestId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
      });

      if (response.ok) {
        console.log('✅ Friend request accepted successfully');
        // Remove from friend requests list
        setFriendRequests(prev => prev.filter(req => req.id !== requestId));
        // Refresh friends and followers lists
        loadFriends();
        loadFollowers();
        alert('Friend request accepted!');
        return true;
      } else {
        const errorData = await response.json();
        console.error('Accept friend request failed:', response.status, errorData);
        alert(`Failed to accept request: ${errorData.detail || 'Please try again.'}`);
        return false;
      }
    } catch (error) {
      console.error('Accept friend request error:', error);
      alert('Error accepting friend request. Please try again.');
      return false;
    }
  };

  const declineFriendRequest = async (requestId) => {
    try {
      console.log('❌ Rejecting friend request:', requestId);
      const response = await fetch(`${API}/friends/requests/${requestId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
      });

      if (response.ok) {
        console.log('❌ Friend request rejected successfully');
        // Remove from friend requests list
        setFriendRequests(prev => prev.filter(req => req.id !== requestId));
        return true;
      } else {
        const errorData = await response.json();
        console.error('Reject friend request failed:', response.status, errorData);
        return false;
      }
    } catch (error) {
      console.error('Decline friend request error:', error);
      return false;
    }
  };

  const loadFriends = async () => {
    if (!user?.user_id) {
      console.log('No user_id found for loadFriends');
      return;
    }

    try {
      console.log('Loading following for user:', user.user_id);
      const response = await fetch(`${API}/users/${user.user_id}/following`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Following data received:', data, 'Length:', data.length);
        setFriends(Array.isArray(data) ? data : []);
        setFollowingCount(Array.isArray(data) ? data.length : 0);
        console.log('✅ Following count set to:', Array.isArray(data) ? data.length : 0);
      } else {
        console.error('❌ Following API failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Load friends error:', error);
    }
  };

  const loadFollowers = async () => {
    if (!user?.user_id) {
      console.log('No user_id found for loadFollowers');
      return;
    }

    try {
      console.log('Loading followers for user:', user.user_id);
      const response = await fetch(`${API}/users/${user.user_id}/followers`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Followers data received:', data, 'Length:', data.length);
        setFollowersCount(Array.isArray(data) ? data.length : 0);
        console.log('✅ Followers count set to:', Array.isArray(data) ? data.length : 0);
      } else {
        console.error('❌ Followers API failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Load followers error:', error);
    }
  };

  const loadFriendRequests = async () => {
    if (!user?.user_id) return;

    try {
      console.log('📥 Loading friend requests...');
      const response = await fetch(`${API}/friends/requests`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📥 Friend requests loaded:', data);
        // Map the response to the expected format for the UI
        const mappedRequests = (data.requests || []).map(req => ({
          id: req.id,
          user_id: req.from_user_id,
          name: req.from_user_name,
          email: req.from_user_email,
          avatar: req.from_user_avatar,
          created_at: req.created_at
        }));
        setFriendRequests(mappedRequests);
      } else {
        console.error('Failed to load friend requests:', response.status);
      }
    } catch (error) {
      console.error('Load friend requests error:', error);
    }
  };

  const loadIdStats = async () => {
    if (!user?.user_id) return;
    try {
      const r = await fetch(`${API}/users/${user.user_id}/id-stats`);
      if (r.ok) setIdStats(await r.json());
    } catch (e) {
      console.error('Load ID stats error:', e);
    }
  };

  const loadNotifications = async () => {
    if (!user?.user_id) return;

    try {
      const response = await fetch(`${API}/users/${user.user_id}/notifications`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(Array.isArray(data) ? data : (data.notifications || []));
      }
    } catch (error) {
      console.error('Load notifications error:', error);
    }
  };

  // Load expiring items count
  const loadExpiringCount = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) return;

      const response = await fetch(`${API}/expiring/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setExpiringCount(data.total || 0);
      }
    } catch (error) {
      console.error('Load expiring count error:', error);
    }
  };

  // Load user on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('beatseek_user');
    console.log('🔍 Loading saved user from localStorage:', savedUser);
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        console.log('🔍 Parsed user object:', parsedUser);
        
        // Ensure user_id exists for compatibility (map id to user_id if needed)
        const userWithUserId = { ...parsedUser, user_id: parsedUser.user_id || parsedUser.id };
        
        setUser(userWithUserId);
        
        // Fetch fresh user data from server to ensure we have latest social links & profile
        const token = localStorage.getItem('beatseek_token');
        if (token && userWithUserId.user_id) {
          fetch(`${API}/users/${userWithUserId.user_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
          .then(response => response.ok ? response.json() : null)
          .then(freshUserData => {
            if (freshUserData) {
              console.log('🔄 Refreshed user data from server:', freshUserData);
              const updatedUser = { ...freshUserData, user_id: freshUserData.user_id || freshUserData.id };
              setUser(updatedUser);
              localStorage.setItem('beatseek_user', JSON.stringify(updatedUser));
            }
          })
          .catch(err => console.log('Could not refresh user data:', err));
        }
      } catch (error) {
        console.error('🔍 Error parsing saved user:', error);
        localStorage.removeItem('beatseek_user');
      }
    }
  }, []);

  // Presence heartbeat - send every 60 seconds to update online status
  useEffect(() => {
    if (!user?.user_id) return;
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;
    
    const sendHeartbeat = async () => {
      try {
        await fetch(`${API}/presence/heartbeat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ user_id: user.user_id })
        });
      } catch (error) {
        // Silently fail - heartbeat is not critical
        console.debug('Heartbeat failed:', error);
      }
    };
    
    // Send initial heartbeat
    sendHeartbeat();
    
    // Set up interval to send heartbeat every 60 seconds
    const heartbeatInterval = setInterval(sendHeartbeat, 60000);
    
    // Cleanup on unmount or user change
    return () => clearInterval(heartbeatInterval);
  }, [user?.user_id]);

  // Track real network online/offline state for the user's own presence dot
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handleVisibility = () => {
      // Refresh actual connectivity when tab visibility changes
      if (typeof navigator !== 'undefined') setIsOnline(navigator.onLine);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Check URL for tab parameter and set active tab
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      console.log('🔗 Tab parameter detected:', tabParam);
      setActiveTab(tabParam);
    }
    
    // Check if openGlobe parameter is present
    const openGlobeParam = searchParams.get('openGlobe');
    if (openGlobeParam === 'true') {
      console.log('🌍 Opening globe from URL parameter');
      setShowGlobeModal(true);
      // Clean up URL
      navigate('/?tab=social', { replace: true });
    }

    // Check if feature parameter is present (for DJ Booth deep linking)
    const featureParam = searchParams.get('feature');
    if (featureParam && featureParam.startsWith('dj-booth-')) {
      console.log('🎧 DJ Booth feature link detected:', featureParam);
      const tabName = featureParam.replace('dj-booth-', '');
      setActiveTab('dj-booth');
      setShowDJBoothModal(true);
      // Dispatch event to set the tab after modal opens
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('djBoothSetTab', { detail: tabName }));
      }, 200);
      // Clean up URL
      navigate('/?tab=dj-booth', { replace: true });
    }
    
    // Handle demo-request deep linking from Gig Hub calendar
    if (featureParam === 'demo-request') {
      const requestId = searchParams.get('id');
      console.log('📋 Demo Request deep link detected:', requestId);
      setActiveTab('producers-corner');
      // Navigate to Producer's Corner and open the demo request
      setTimeout(() => {
        // Dispatch event to open demo requests tab and specific request
        window.dispatchEvent(new CustomEvent('producerCornerOpenDemoRequest', { 
          detail: { requestId: requestId }
        }));
      }, 300);
      // Clean up URL
      navigate('/?tab=producers-corner', { replace: true });
    }
  }, [location.search]);

  // Handle navigation state from ProfileView for chat message highlighting
  useEffect(() => {
    if (location.state?.openChat) {
      console.log('📬 Opening chat from profile activity:', location.state);
      setShowCommunityChat(true);
      
      // Store the chat navigation state
      if (location.state.chatRoom || location.state.highlightMessageId) {
        setChatNavigationState({
          room: location.state.chatRoom,
          highlightMessageId: location.state.highlightMessageId
        });
      }
      
      // Clear the state after handling
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Manual refresh function for debugging
  const refreshSocialData = async () => {
    if (user?.user_id) {
      console.log('🔄 Refreshing social data for user:', user.user_id);
      console.log('👤 Full user object:', user);
      await loadFriends();
      await loadFollowers();
      await loadFriendRequests();
      await loadNotifications();
    } else {
      console.log('❌ No user or user_id found for refresh');
      console.log('👤 Current user object:', user);
    }
  };

  // Helper function to check if two users are mutual friends
  const areMutualFriends = (targetUserId) => {
    const myId = user?.id || user?.user_id;
    if (!myId || !targetUserId) return false;
    // Check if target user is in our friends list
    return friends.some(friend => friend.user_id === targetUserId || friend.id === targetUserId);
  };

  // Function to initiate whisper (backend enforces mutual-follower check)
  const initiateWhisper = (targetUser) => {
    const targetId = targetUser?.user_id || targetUser?.id;
    const myId = user?.id || user?.user_id;

    // Don't whisper to yourself
    if (!targetId || targetId === myId) {
      alert('You cannot whisper to yourself');
      return;
    }

    // Open whisper modal — backend will reject with a friendly message if they're not mutual followers
    setWhisperTarget(targetUser);
    setShowWhisperModal(true);
  };

  // Listen for whisper events from ProfileView and other components
  useEffect(() => {
    const handleOpenWhisper = (event) => {
      const targetUser = event.detail;
      if (targetUser) {
        // Normalize: keep BOTH shapes (user_name/name, user_avatar/avatar) so any consumer works
        const uid = targetUser.user_id || targetUser.id;
        const uname = targetUser.user_name || targetUser.name;
        const uavatar = targetUser.user_avatar || targetUser.avatar || targetUser.profile_picture;
        const formattedUser = {
          user_id: uid,
          id: uid,
          user_name: uname,
          name: uname,
          user_avatar: uavatar,
          avatar: uavatar,
          online: targetUser.online || false
        };
        initiateWhisper(formattedUser);
      }
    };

    window.addEventListener('openWhisper', handleOpenWhisper);
    return () => window.removeEventListener('openWhisper', handleOpenWhisper);
  }, [user, friends]); // Re-add listener when user or friends change

  // Open the community chat from anywhere (e.g. the Wall profile card)
  useEffect(() => {
    const handleOpenChat = (event) => {
      setShowTheWall(false);
      if (event.detail?.room || event.detail?.room_id) setChatNavigationState({ room: event.detail.room, room_id: event.detail.room_id });
      setShowCommunityChat(true);
    };
    window.addEventListener('openCommunityChat', handleOpenChat);
    return () => window.removeEventListener('openCommunityChat', handleOpenChat);
  }, []);

  // Shareable room invite links: /?chat=<room_id>
  useEffect(() => {
    const roomId = new URLSearchParams(location.search).get('chat');
    if (!roomId || !user) return;
    setChatNavigationState({ room_id: roomId });
    setShowCommunityChat(true);
    navigate(location.pathname, { replace: true });
  }, [location.search, user, navigate, location.pathname]);

  // ==================== CHECK-IN GLOBAL FUNCTIONS ====================
  
  // Fetch Headquarters Locations (permanent)
  const fetchHeadquarters = async () => {
    if (!user) return;
    
    setLoadingLocations(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/home-locations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const locations = await response.json();
        
        // Convert to globe coordinates with all enhanced fields
        const globeLocations = locations.map(loc => ({
          user_id: loc.user_id,
          name: loc.user_name,
          user_name: loc.user_name,
          profile_picture: loc.profile_picture,
          x: ((loc.longitude + 180) / 360) * 100,
          y: ((90 - loc.latitude) / 180) * 100,
          location: `${loc.city}, ${loc.country}`,
          city: loc.city,
          state: loc.state,
          country: loc.country,
          home_city: loc.city,
          home_state: loc.state,
          home_country: loc.country,
          latitude: loc.latitude,
          longitude: loc.longitude,
          // Enhanced fields for pins
          online_status: loc.online_status || false,
          follower_count: loc.follower_count || 0,
          user_type: loc.user_type || [],
          bio: loc.bio || '',
          created_at: loc.created_at,
          member_since: loc.created_at,
          is_friend: loc.is_friend || false,
          is_following: loc.is_following || false,
          is_follower: loc.is_follower || false
        }));
        
        setHeadquartersLocations(globeLocations);
        console.log(`✅ Loaded ${globeLocations.length} headquarters locations`, globeLocations);
      }
    } catch (error) {
      console.error('Error fetching headquarters:', error);
    } finally {
      setLoadingLocations(false);
    }
  };
  
  // Fetch Active Check-ins (24hr temporary)
  const fetchActiveCheckins = async () => {
    if (!user) return;
    
    setLoadingLocations(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/checkins/active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const checkins = await response.json();
        
        // Convert to globe coordinates with all enhanced fields
        const globeCheckins = checkins.map(checkin => ({
          user_id: checkin.user_id,
          name: checkin.user_name,
          user_name: checkin.user_name,
          profile_picture: checkin.profile_picture,
          x: ((checkin.longitude + 180) / 360) * 100,
          y: ((90 - checkin.latitude) / 180) * 100,
          location: `${checkin.city}, ${checkin.country}`,
          city: checkin.city,
          country: checkin.country,
          latitude: checkin.latitude,
          longitude: checkin.longitude,
          hours_remaining: checkin.hours_remaining,
          created_at: checkin.created_at,
          // Enhanced fields for pins
          online_status: checkin.online_status || false,
          follower_count: checkin.follower_count || 0,
          user_type: checkin.user_type || [],
          bio: checkin.bio || '',
          member_since: checkin.member_since,
          is_friend: checkin.is_friend || false,
          is_following: checkin.is_following || false,
          is_follower: checkin.is_follower || false
        }));
        
        // Check if current user has a check-in - if so, add it if not already in list
        const hasMyCheckin = globeCheckins.some(c => c.user_id === user.id);
        
        // Store current user's check-in for reference
        const myCheckinData = globeCheckins.find(c => c.user_id === user.id);
        if (myCheckinData) {
          setMyCheckin(myCheckinData);
        }
        
        setActiveCheckins(globeCheckins);
        console.log(`✅ Loaded ${globeCheckins.length} active check-ins (including your own)`, globeCheckins);
      }
    } catch (error) {
      console.error('Error fetching active check-ins:', error);
    } finally {
      setLoadingLocations(false);
    }
  };
  
  // Globe Search Function
  const handleGlobeSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setGlobeSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/globe/search?q=${encodeURIComponent(query.trim())}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token')}`
        }
      });

      if (response.ok) {
        const results = await response.json();
        setGlobeSearchResults(results);
        console.log('🔍 Globe search results:', results);
      } else {
        console.error('Failed to search globe users');
        setGlobeSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching globe:', error);
      setGlobeSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // New Check-in Function (integrated with backend)
  const handleCheckIn = async () => {
    if (!user?.id) {
      alert('Please log in to check in');
      return;
    }

    try {
      // Get user's location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            
            // Create check-in via backend
            const token = localStorage.getItem('beatseek_token');
            const response = await fetch(`${API}/checkins`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ latitude, longitude })
            });
            
            if (response.ok) {
              const checkin = await response.json();
              alert(`✅ Checked in at ${checkin.city}, ${checkin.country}! Visible for 24 hours.`);
              
              // Refresh check-ins
              fetchActiveCheckins();
            } else {
              const error = await response.json();
              alert(`❌ Check-in failed: ${error.detail || 'Unknown error'}`);
            }
          },
          (error) => {
            console.error('Geolocation error:', error);
            alert('Could not get your location. Please enable location services.');
          },
          { timeout: 10000, enableHighAccuracy: true }
        );
      } else {
        alert('Geolocation is not supported by your browser.');
      }
    } catch (error) {
      console.error('Check-in error:', error);
      alert('Failed to check in. Please try again.');
    }
  };
  
  // Set Headquarters Function
  const handleSetHeadquarters = async () => {
    if (!user?.id) {
      alert('Please log in to set Home Base');
      return;
    }

    try {
      // Get user's location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            
            // First, reverse geocode to get city name
            const token = localStorage.getItem('beatseek_token');
            const geocodeResponse = await fetch(`${API}/location/reverse-geocode`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ latitude, longitude })
            });
            
            if (!geocodeResponse.ok) {
              alert('❌ Failed to get location name');
              return;
            }
            
            const locationData = await geocodeResponse.json();
            
            // Now set headquarters
            const response = await fetch(`${API}/users/${user.id}/home-location`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                city: locationData.city,
                country: locationData.country,
                latitude,
                longitude
              })
            });
            
            if (response.ok) {
              alert(`✅ Home Base set to ${locationData.city}, ${locationData.country}!`);
              
              // Refresh headquarters
              fetchHeadquarters();
            } else {
              const error = await response.json();
              alert(`❌ Failed to set Home Base: ${error.detail || 'Unknown error'}`);
            }
          },
          (error) => {
            console.error('Geolocation error:', error);
            alert('Could not get your location. Please enable location services.');
          },
          { timeout: 10000, enableHighAccuracy: true }
        );
      } else {
        alert('Geolocation is not supported by your browser.');
      }
    } catch (error) {
      console.error('Set headquarters error:', error);
      alert('Failed to set Home Base. Please try again.');
    }
  };
  
  const handlePinClick = (user, screenPosition) => {
    console.log('Pin clicked:', user, 'at screen position:', screenPosition);
    setSelectedPinUser(user);
    setSelectedPinPosition(screenPosition);
    setShowPinActionMenu(true);
  };

  const handleViewProfile = (userId) => {
    setShowPinActionMenu(false);
    setSelectedPinUser(null);
    setSelectedPinPosition(null);
    setShowGlobeModal(false);
    setShowHomeBaseModal(false);
    // Navigate to user profile
    navigate(`/profile/${userId}`);
  };

  const handleMessage = (userId) => {
    setShowPinActionMenu(false);
    // Navigate to whisper/message
    console.log('Message user:', userId);
    alert('Whisper messaging coming soon!');
  };

  // Globe Interaction Functions
  const handleGlobeMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    
    // Add dragging class to pause auto-rotation
    const globeElement = e.currentTarget;
    globeElement.classList.add('dragging');
  };

  const handleGlobeMouseMove = (e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    const deltaX = e.clientX - dragStart.x;
    const sensitivity = 1;
    
    const newRotation = globeRotation + (deltaX * sensitivity);
    setGlobeRotation(newRotation);
    
    // Update the wireframe rotation
    const wireframeElement = e.currentTarget.querySelector('.globe-wireframe');
    if (wireframeElement) {
      wireframeElement.style.transform = `rotateY(${newRotation}deg) rotateX(-10deg)`;
    }
    
    // Update drag start for continuous dragging
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleGlobeMouseUp = (e) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Remove dragging class to resume auto-rotation
    const globeElement = e.currentTarget;
    globeElement.classList.remove('dragging');
    
    // Resume auto-rotation from current position
    const wireframeElement = e.currentTarget.querySelector('.globe-wireframe');
    if (wireframeElement) {
      wireframeElement.style.animationDelay = '0s';
    }
  };

  // Load social data when user changes
  useEffect(() => {
    if (user?.user_id) {
      console.log('User changed, loading social data for:', user.user_id);
      loadFriends();
      loadFollowers();
      loadFriendRequests();
      loadNotifications();
      loadIdStats();
    }
  }, [user]);

  // Broadcast active tab (TourBot mobile visibility: dashboard only)
  useEffect(() => {
    window.__beatseekActiveTab = activeTab;
    window.dispatchEvent(new CustomEvent('beatseek-active-tab', { detail: activeTab }));
  }, [activeTab]);

  // Live bell badge sync — poll notifications + refresh when tab regains focus
  useEffect(() => {
    if (!user?.user_id) return;
    const interval = setInterval(() => {
      loadNotifications();
    }, 60000);
    const onFocus = () => loadNotifications();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.user_id]);
  
  // Fetch locations when globe modal opens
  useEffect(() => {
    if (showGlobeModal && user) {
      fetchActiveCheckins();
      
      // Show rotation alert on mobile (only once per session)
      if (window.innerWidth <= 768 && !globeRotationAlertShown) {
        setTimeout(() => {
          alert('📱 Rotate your phone for a better experience!');
          setGlobeRotationAlertShown(true);
        }, 500);
      }
      
      // Set up polling for real-time updates (every 30 seconds)
      const interval = setInterval(() => {
        fetchActiveCheckins();
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [showGlobeModal, user]);

  // Add/remove body class when globe modal is open (for hiding footer on mobile)
  useEffect(() => {
    if (showGlobeModal || showHomeBaseModal) {
      document.body.classList.add('globe-modal-open');
    } else {
      document.body.classList.remove('globe-modal-open');
    }
    return () => document.body.classList.remove('globe-modal-open');
  }, [showGlobeModal, showHomeBaseModal]);

  // Fetch home base locations when Home Base modal opens
  useEffect(() => {
    if (showHomeBaseModal && user) {
      fetchHeadquarters();
      
      // Set up polling for real-time updates (every 30 seconds)
      const interval = setInterval(() => {
        fetchHeadquarters();
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [showHomeBaseModal, user]);

  // Fetch recent discoveries when modal opens
  useEffect(() => {
    const fetchRecentDiscoveries = async () => {
      if (showRecentDiscoveries && discoveriesTab === 'music') {
        setLoadingDiscoveries(true);
        try {
          const response = await fetch(`${API}/discoveries/recent?limit=30`);
          if (response.ok) {
            const data = await response.json();
            if (data.discoveries && data.discoveries.length > 0) {
              // Calculate vibe scores for each discovery
              const discoveriesWithVibe = data.discoveries.map(d => ({
                ...d,
                vibeScore: calculateVibeScore({ confidence: d.confidence, track: d.track })
              }));
              setRecentDiscoveries(discoveriesWithVibe);
            }
          }
        } catch (error) {
          console.error('Error fetching discoveries:', error);
        } finally {
          setLoadingDiscoveries(false);
        }
      }
    };

    fetchRecentDiscoveries();
  }, [showRecentDiscoveries, discoveriesTab]);

  // Spotify Connect Function for Social Tab
  const connectSpotify = async () => {
    try {
      console.log('🎵 Initiating Spotify connection...');
      
      // Get Spotify auth URL from backend
      const response = await fetch(`${API}/spotify/auth/login`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get auth URL: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('🎵 Auth URL received:', data.auth_url);
      
      if (data.auth_url) {
        const isMobile = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || /Mobi/i.test(navigator.userAgent || '');
        if (isMobile) {
          // Mobile: full-page redirect — popups & window.opener are unreliable on phones
          window.location.assign(data.auth_url);
          return;
        }
        // Open Spotify OAuth in popup window
        const popup = window.open(
          data.auth_url, 
          'spotify-auth', 
          'width=500,height=700,scrollbars=yes,resizable=yes'
        );
        // Popup blocked → fall back to same-tab redirect
        if (!popup) {
          window.location.assign(data.auth_url);
          return;
        }
        
        // Listen for callback message from popup
        const messageHandler = (event) => {
          // Verify origin for security
          if (event.origin !== window.location.origin) {
            return;
          }
          
          if (event.data.type === 'SPOTIFY_AUTH_SUCCESS') {
            console.log('✅ Spotify auth successful!');
            setSpotifyAccessToken(event.data.token);
            setSpotifyConnected(true);
            
            // Load mock playlists if using mock token
            if (event.data.token === 'mock_token_for_testing') {
              setSpotifyPlaylists([
                { id: '1', name: 'BEATSEEK Discoveries', tracks_total: 12, owner: 'user', lastModified: '2 days ago', topTracks: ['Levels', 'Strobe', 'One More Time'] },
                { id: '2', name: 'Electronic Favorites', tracks_total: 45, owner: 'user', lastModified: '1 week ago', topTracks: ['Ghosts n Stuff', 'Midnight City', 'Language'] },
                { id: '3', name: 'Deep House Mix', tracks_total: 28, owner: 'user', lastModified: '3 days ago', topTracks: ['Disclosure Mix', 'Duke Dumont', 'Flume Remix'] },
                { id: '4', name: 'Workout Vibes', tracks_total: 67, owner: 'user', lastModified: '5 days ago', topTracks: ['Bangarang', 'Animals', 'Titanium'] },
                { id: '5', name: 'Chill Electronic', tracks_total: 23, owner: 'user', lastModified: '1 day ago', topTracks: ['Odesza Mix', 'Emancipator', 'Tycho Beat'] },
                { id: '6', name: 'Festival Bangers', tracks_total: 89, owner: 'user', lastModified: '6 days ago', topTracks: ['Ultra Mix', 'EDC Special', 'Tomorrowland'] },
                { id: '7', name: 'Late Night Sessions', tracks_total: 34, owner: 'user', lastModified: '4 days ago', topTracks: ['After Hours', 'Nocturnal', 'Underground'] }
              ]);
            }
            
            window.removeEventListener('message', messageHandler);
            popup.close();
            alert('✅ Spotify connected successfully!');
          } else if (event.data.type === 'SPOTIFY_AUTH_ERROR') {
            console.error('❌ Spotify auth error:', event.data.error);
            window.removeEventListener('message', messageHandler);
            popup.close();
            alert(`❌ Spotify connection failed: ${event.data.error}`);
          }
        };
        
        window.addEventListener('message', messageHandler);
        
        // Handle popup blocked or closed
        setTimeout(() => {
          if (popup.closed) {
            window.removeEventListener('message', messageHandler);
            console.log('🎵 Spotify auth popup was closed');
          }
        }, 1000);
        
      } else {
        throw new Error('No auth URL received from server');
      }
    } catch (error) {
      console.error('❌ Spotify connection failed:', error);
      alert(`Failed to connect to Spotify: ${error.message}`);
    }
  };

  // Handle Spotify OAuth callback and check existing tokens
  useEffect(() => {
    const handleSpotifyCallback = () => {
      try {
        // Check for existing Spotify tokens first
        const accessToken = localStorage.getItem('spotify_access_token');
        if (accessToken) {
          setSpotifyAccessToken(accessToken);
          setSpotifyConnected(true);
          console.log('🎵 Existing Spotify token found');
          
          // Load playlists if connected
          if (accessToken === 'mock_token_for_testing') {
            // Set enhanced mock playlists with metadata
            setSpotifyPlaylists([
              { id: '1', name: 'BEATSEEK Discoveries', tracks_total: 12, owner: 'user', lastModified: '2 days ago', topTracks: ['Levels', 'Strobe', 'One More Time'] },
              { id: '2', name: 'Electronic Favorites', tracks_total: 45, owner: 'user', lastModified: '1 week ago', topTracks: ['Ghosts n Stuff', 'Midnight City', 'Language'] },
              { id: '3', name: 'Deep House Mix', tracks_total: 28, owner: 'user', lastModified: '3 days ago', topTracks: ['Disclosure Mix', 'Duke Dumont', 'Flume Remix'] },
              { id: '4', name: 'Workout Vibes', tracks_total: 67, owner: 'user', lastModified: '5 days ago', topTracks: ['Bangarang', 'Animals', 'Titanium'] },
              { id: '5', name: 'Chill Electronic', tracks_total: 23, owner: 'user', lastModified: '1 day ago', topTracks: ['Odesza Mix', 'Emancipator', 'Tycho Beat'] },
              { id: '6', name: 'Festival Bangers', tracks_total: 89, owner: 'user', lastModified: '6 days ago', topTracks: ['Ultra Mix', 'EDC Special', 'Tomorrowland'] },
              { id: '7', name: 'Late Night Sessions', tracks_total: 34, owner: 'user', lastModified: '4 days ago', topTracks: ['After Hours', 'Nocturnal', 'Underground'] }
            ]);
          }
        }
        
        // Check for OAuth callback parameters
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const error = url.searchParams.get('error');
        
        // Skip if no OAuth parameters
        if (!code && !error) {
          return;
        }
        
        console.log('🎵 OAuth callback detected - Code:', !!code, 'Error:', error);
        
        // Handle OAuth error
        if (error) {
          console.error('❌ Spotify OAuth error:', error);
          alert(`Spotify connection failed: ${error}`);
          // Clean up URL immediately
          const cleanUrl = `${window.location.origin}${window.location.pathname}`;
          window.history.replaceState(null, '', cleanUrl);
          return;
        }
        
        // Handle OAuth success - exchange code for token
        if (code) {
          console.log('🎵 Processing Spotify OAuth callback...');
          
          // Clean URL first to prevent reprocessing
          const cleanUrl = `${window.location.origin}${window.location.pathname}`;
          window.history.replaceState(null, '', cleanUrl);
          
          // Exchange code for token
          const exchangeToken = async () => {
            try {
              const tokenUrl = `${API}/spotify/auth/token?code=${encodeURIComponent(code)}`;
              console.log('🎵 Making token request to:', tokenUrl);
              
              const response = await fetch(tokenUrl, {
                method: 'POST',
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                },
                credentials: 'same-origin'
              });
              
              console.log('🎵 Token response status:', response.status);
              
              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
              }
              
              const data = await response.json();
              console.log('🎵 Token data received:', data);
              
              if (data && data.access_token) {
                console.log('🎵 Token received - storing and setting state...');
                console.log('🎵 Access token length:', data.access_token.length);
                localStorage.setItem('spotify_access_token', data.access_token);
                localStorage.setItem('spotify_refresh_token', data.refresh_token);
                setSpotifyAccessToken(data.access_token);
                setSpotifyConnected(true);
                console.log('✅ Spotify connection successful');
                
                // Load user playlists after successful connection
                setTimeout(async () => {
                  try {
                    console.log('🎵 Loading playlists with token:', data.access_token ? `${data.access_token.substring(0, 20)}...` : 'null');
                    const playlistUrl = `${API}/spotify/playlists?access_token=${encodeURIComponent(data.access_token)}`;
                    console.log('🎵 Playlist URL:', playlistUrl);
                    
                    const playlistResponse = await fetch(playlistUrl);
                    console.log('🎵 Playlist response status:', playlistResponse.status);
                    
                    if (playlistResponse.ok) {
                      const playlistData = await playlistResponse.json();
                      console.log('🎵 Playlist data received:', playlistData);
                      console.log('🎵 Number of playlists:', playlistData.playlists?.length || 0);
                      setSpotifyPlaylists(playlistData.playlists || []);
                    } else {
                      const errorText = await playlistResponse.text();
                      console.error('🎵 Playlist loading failed:', playlistResponse.status, errorText);
                    }
                  } catch (error) {
                    console.error('🎵 Playlist loading error:', error);
                  }
                }, 1000);
                
                // Show success message after a brief delay
                setTimeout(() => {
                  alert('✅ Spotify account connected successfully! You can now manage your playlists.');
                }, 500);
              } else {
                throw new Error('No access token received');
              }
            } catch (error) {
              console.error('❌ Token exchange failed:', error);
              alert(`Failed to connect to Spotify: ${error.message}`);
            }
          };
          
          // Execute token exchange after a brief delay to ensure DOM is ready
          setTimeout(exchangeToken, 100);
        }
      } catch (error) {
        console.error('❌ Error in Spotify callback handling:', error);
        alert('Error processing Spotify authentication. Please refresh and try again.');
      }
    };
    
    // Run callback handler after component mounts
    handleSpotifyCallback();
  }, []);

  // Handle SoundCloud OAuth callback and check existing tokens
  useEffect(() => {
    const handleSoundCloudCallback = async () => {
      try {
        // Check for existing SoundCloud tokens first
        const accessToken = localStorage.getItem('soundcloud_access_token');
        const refreshToken = localStorage.getItem('soundcloud_refresh_token');
        
        if (accessToken && accessToken !== 'mock_soundcloud_token') {
          // Try to validate the token by fetching user info
          try {
            const userResponse = await fetch(`${API}/soundcloud/user?access_token=${encodeURIComponent(accessToken)}`);
            if (userResponse.ok) {
              // Token is valid
              const userData = await userResponse.json();
              setSoundcloudAccessToken(accessToken);
              setSoundcloudConnected(true);
              setSoundcloudUser(userData);
              console.log('🎵 SoundCloud token validated successfully');
            } else if (userResponse.status === 401 && refreshToken) {
              // Token expired, try to refresh
              console.log('🔄 SoundCloud token expired, attempting refresh...');
              const refreshResponse = await fetch(`${API}/soundcloud/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
              });
              
              if (refreshResponse.ok) {
                const newTokens = await refreshResponse.json();
                if (newTokens.access_token) {
                  localStorage.setItem('soundcloud_access_token', newTokens.access_token);
                  if (newTokens.refresh_token) {
                    localStorage.setItem('soundcloud_refresh_token', newTokens.refresh_token);
                  }
                  setSoundcloudAccessToken(newTokens.access_token);
                  setSoundcloudConnected(true);
                  console.log('✅ SoundCloud token refreshed on startup');
                  
                  // Load user info with new token
                  const userResponse2 = await fetch(`${API}/soundcloud/user?access_token=${encodeURIComponent(newTokens.access_token)}`);
                  if (userResponse2.ok) {
                    const userData = await userResponse2.json();
                    setSoundcloudUser(userData);
                  }
                }
              } else {
                // Refresh failed - clear tokens
                console.log('❌ SoundCloud token refresh failed, clearing tokens');
                localStorage.removeItem('soundcloud_access_token');
                localStorage.removeItem('soundcloud_refresh_token');
                setSoundcloudConnected(false);
              }
            } else {
              // Token invalid and no refresh token
              console.log('❌ SoundCloud token invalid, no refresh token available');
              localStorage.removeItem('soundcloud_access_token');
              setSoundcloudConnected(false);
            }
          } catch (err) {
            console.error('Failed to validate SoundCloud token:', err);
          }
        } else if (accessToken === 'mock_soundcloud_token') {
          // Handle mock token
          setSoundcloudAccessToken(accessToken);
          setSoundcloudConnected(true);
          setSoundcloudUser({
            username: 'MockDJ',
            display_name: 'Mock DJ User',
            avatar_url: null,
            followers_count: 1234,
            track_count: 56
          });
        }
      } catch (error) {
        console.error('❌ Error in SoundCloud callback handling:', error);
      }
    };
    
    // Listen for SoundCloud auth messages from popup
    const soundcloudMessageHandler = (event) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'SOUNDCLOUD_AUTH_SUCCESS') {
        console.log('✅ SoundCloud auth successful!');
        setSoundcloudAccessToken(event.data.token);
        setSoundcloudConnected(true);
        
        // Load user info
        fetch(`${API}/soundcloud/user?access_token=${encodeURIComponent(event.data.token)}`)
          .then(res => res.json())
          .then(userData => setSoundcloudUser(userData))
          .catch(err => console.error('Failed to load SoundCloud user:', err));
        
        alert('✅ SoundCloud connected successfully!');
      } else if (event.data.type === 'SOUNDCLOUD_AUTH_ERROR') {
        console.error('❌ SoundCloud auth error:', event.data.error);
        alert(`❌ SoundCloud connection failed: ${event.data.error}`);
      }
    };
    
    window.addEventListener('message', soundcloudMessageHandler);
    handleSoundCloudCallback();
    
    return () => {
      window.removeEventListener('message', soundcloudMessageHandler);
    };
  }, []);

  // SoundCloud connect function
  const connectSoundCloud = async () => {
    try {
      console.log('🎵 Initiating SoundCloud OAuth...');
      const response = await fetch(`${API}/soundcloud/auth/url`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('🎵 Got SoundCloud auth URL');
      
      if (data.auth_url) {
        const isMobile = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || /Mobi/i.test(navigator.userAgent || '');
        if (isMobile) {
          // Mobile: full-page redirect — popups & window.opener are unreliable on phones
          window.location.assign(data.auth_url);
          return;
        }
        // Open popup for SoundCloud auth
        const popup = window.open(
          data.auth_url,
          'soundcloud-auth',
          'width=500,height=700,scrollbars=yes,resizable=yes'
        );
        
        // Handle popup blocked → fall back to same-tab redirect
        if (!popup) {
          window.location.assign(data.auth_url);
          return;
        }
      } else {
        throw new Error('No auth URL received from server');
      }
    } catch (error) {
      console.error('❌ SoundCloud connection failed:', error);
      alert(`Failed to connect to SoundCloud: ${error.message}`);
    }
  };

  // Clicking a provider icon scrolls to that provider's full panel (the real connection
  // experience) and briefly highlights it.
  const handleConnectNudge = (provider) => {
    const el = document.getElementById(`connect-panel-${provider}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('connect-panel-highlight');
    setTimeout(() => el.classList.remove('connect-panel-highlight'), 2200);
  };

  // Reactive connection state for the badge/nudge row — flips instantly when a token
  // is written (OAuth popup is same-origin → fires a 'storage' event here; returning to
  // the tab fires 'focus'), instead of only on the next reload.
  const readConnected = () => ({
    spotify: !!localStorage.getItem('spotify_access_token'),
    soundcloud: !!localStorage.getItem('soundcloud_access_token'),
    discord: !!localStorage.getItem('discord_access_token'),
  });
  const [connectedServices, setConnectedServices] = useState(readConnected);
  useEffect(() => {
    const sync = () => setConnectedServices((prev) => {
      const next = readConnected();
      if (prev.spotify === next.spotify && prev.soundcloud === next.soundcloud && prev.discord === next.discord) return prev;
      return next;
    });
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('beatseek:connections-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('beatseek:connections-changed', sync);
    };
  }, []);
  // Keep the badge row in sync with the existing Spotify/SoundCloud connect states
  useEffect(() => {
    setConnectedServices((prev) => {
      const next = readConnected();
      if (prev.spotify === next.spotify && prev.soundcloud === next.soundcloud && prev.discord === next.discord) return prev;
      return next;
    });
  }, [spotifyConnected, soundcloudConnected]);

  // 🔗 Cross-device provider connection sync. Persists each provider's OAuth tokens on the
  // user's account (server-side) so a connection made on one device carries over to the
  // user's other devices without re-authing. Pull-on-login (hydrate), push-on-connect.
  // Re-runs whenever the logged-in user changes so hydration fires right after login.
  const providerHydratedRef = useRef({ spotify: false, soundcloud: false, discord: false });
  useEffect(() => {
    // New session (login/user switch/logout) → forget prior hydrate state so we never
    // mistake another user's fresh server tokens for a disconnect and delete them.
    providerHydratedRef.current = { spotify: false, soundcloud: false, discord: false };
    if (!user) return;
    let cancelled = false;
    let running = false;
    const reconcileProviders = async () => {
      if (running) return; // guard against re-entry from the event it dispatches
      const jwt = localStorage.getItem('beatseek_token');
      if (!jwt) return;
      running = true;
      const authHeaders = { 'Authorization': `Bearer ${jwt}` };
      let serverTokens = {};
      try {
        const res = await fetch(`${API}/users/me/provider-tokens`, { headers: authHeaders });
        if (res.ok) serverTokens = (await res.json()).provider_tokens || {};
      } catch (e) { running = false; return; }
      if (cancelled) { running = false; return; }
      let changed = false;
      for (const provider of Object.keys(PROVIDER_TOKEN_KEYS)) {
        const { access, refresh } = PROVIDER_TOKEN_KEYS[provider];
        const localTok = localStorage.getItem(access);
        const srv = serverTokens[provider];
        if (localTok && !srv) {
          // Connected on this device → save to account
          try {
            await fetch(`${API}/users/me/provider-tokens`, {
              method: 'PUT',
              headers: { ...authHeaders, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                provider,
                access_token: localTok,
                refresh_token: refresh ? localStorage.getItem(refresh) : null,
              }),
            });
          } catch (e) { /* ignore */ }
          providerHydratedRef.current[provider] = true;
        } else if (!localTok && srv) {
          if (providerHydratedRef.current[provider]) {
            // Was pulled earlier this session then removed here → user disconnected
            try { await fetch(`${API}/users/me/provider-tokens/${provider}`, { method: 'DELETE', headers: authHeaders }); } catch (e) { /* ignore */ }
          } else {
            // Fresh device → hydrate from account
            localStorage.setItem(access, srv.access_token);
            if (refresh && srv.refresh_token) localStorage.setItem(refresh, srv.refresh_token);
            providerHydratedRef.current[provider] = true;
            changed = true;
            if (provider === 'spotify') { setSpotifyAccessToken(srv.access_token); setSpotifyConnected(true); }
            if (provider === 'soundcloud') { setSoundcloudAccessToken(srv.access_token); setSoundcloudConnected(true); }
          }
        } else if (localTok && srv) {
          providerHydratedRef.current[provider] = true;
        }
      }
      running = false;
      if (changed && !cancelled) {
        setConnectedServices(readConnected());
        window.dispatchEvent(new Event('beatseek:connections-changed'));
      }
    };
    reconcileProviders();
    window.addEventListener('focus', reconcileProviders);
    window.addEventListener('beatseek:connections-changed', reconcileProviders);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', reconcileProviders);
      window.removeEventListener('beatseek:connections-changed', reconcileProviders);
    };
  }, [user]);



  // Disconnect SoundCloud
  const disconnectSoundCloud = () => {
    if (window.confirm('Disconnect SoundCloud account?')) {
      localStorage.removeItem('soundcloud_access_token');
      localStorage.removeItem('soundcloud_refresh_token');
      setSoundcloudAccessToken(null);
      setSoundcloudConnected(false);
      setSoundcloudUser(null);
      setSoundcloudPlaylists([]);
      setSoundcloudLikes([]);
      alert('✅ SoundCloud disconnected.');
    }
  };

  // Refresh SoundCloud token automatically
  const refreshSoundCloudToken = async () => {
    const refreshToken = localStorage.getItem('soundcloud_refresh_token');
    if (!refreshToken) {
      console.log('🔄 No SoundCloud refresh token available');
      return null;
    }
    
    try {
      console.log('🔄 Attempting to refresh SoundCloud token...');
      const response = await fetch(`${API}/soundcloud/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.access_token) {
          // Update stored tokens
          localStorage.setItem('soundcloud_access_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('soundcloud_refresh_token', data.refresh_token);
          }
          setSoundcloudAccessToken(data.access_token);
          console.log('✅ SoundCloud token refreshed successfully');
          return data.access_token;
        }
      }
      console.error('❌ Failed to refresh SoundCloud token');
      return null;
    } catch (error) {
      console.error('❌ SoundCloud token refresh error:', error);
      return null;
    }
  };

  // Load SoundCloud playlists
  const loadSoundCloudPlaylists = async () => {
    if (!soundcloudAccessToken) return;
    
    setIsLoadingSoundcloud(true);
    try {
      const response = await fetch(`${API}/soundcloud/me/playlists?access_token=${encodeURIComponent(soundcloudAccessToken)}`);
      if (response.ok) {
        const data = await response.json();
        setSoundcloudPlaylists(data.playlists || []);
      }
    } catch (error) {
      console.error('Failed to load SoundCloud playlists:', error);
    } finally {
      setIsLoadingSoundcloud(false);
    }
  };

  // Load SoundCloud likes
  const loadSoundCloudLikes = async () => {
    if (!soundcloudAccessToken) return;
    
    setIsLoadingSoundcloud(true);
    try {
      const response = await fetch(`${API}/soundcloud/me/likes?access_token=${encodeURIComponent(soundcloudAccessToken)}`);
      if (response.ok) {
        const data = await response.json();
        setSoundcloudLikes(data.tracks || []);
      }
    } catch (error) {
      console.error('Failed to load SoundCloud likes:', error);
    } finally {
      setIsLoadingSoundcloud(false);
    }
  };

  // Check microphone compatibility on Listen tab load
  useEffect(() => {
    const checkMicrophoneCompatibility = async () => {
      if (activeTab === 'listen' && user) {
        console.log('🔍 Checking microphone compatibility...');
        
        // Basic feature checks
        const hasNavigator = !!navigator;
        const hasMediaDevices = !!(navigator && navigator.mediaDevices);
        const hasGetUserMedia = !!(navigator && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        const hasMediaRecorder = !!window.MediaRecorder;
        const isSecure = window.isSecureContext;
        
        console.log('🔍 Compatibility check:', {
          hasNavigator,
          hasMediaDevices,
          hasGetUserMedia,
          hasMediaRecorder,
          isSecure,
          url: window.location.href,
          protocol: window.location.protocol
        });
        
        if (!hasNavigator || !hasMediaDevices || !hasGetUserMedia || !hasMediaRecorder) {
          setAuthError(`Browser compatibility issue: Missing required APIs. Please use Chrome, Firefox, or Safari.`);
        } else if (!isSecure) {
          setAuthError(`Security requirement: HTTPS required for microphone access. Current: ${window.location.protocol}`);
        } else {
          console.log('✅ Browser compatibility confirmed');
          setAuthError(null);
        }
      }
    };
    
    checkMicrophoneCompatibility();
  }, [activeTab, user]);

  return (
    <>
      {/* Beta Gate - Show first if no access or force preview */}
      {(!hasBetaAccess || forceShowGate) && (
        <BetaGate onAccessGranted={() => {
          setHasBetaAccess(true);
          setForceShowGate(false);
          // Remove preview param from URL if present
          const url = new URL(window.location.href);
          url.searchParams.delete('preview-gate');
          window.history.replaceState({}, '', url);
        }} />
      )}
      
      {/* Main App - Only show after beta access granted and not in preview mode */}
      {hasBetaAccess && !forceShowGate && (
    <div className="electroshazam-container">
      {!user && <AuthAtmosphere />}
      {authCelebration && <AuthCelebration mode={authCelebration} />}
      {user && pendingInvite && (
        <div
          className="fixed top-3 left-1/2 -translate-x-1/2 z-[9000] max-w-lg w-[92%] bg-gradient-to-r from-purple-900/95 to-fuchsia-900/95 border border-purple-400/60 rounded-xl px-4 py-3 shadow-2xl flex items-center gap-3 flex-wrap"
          data-testid="invite-banner"
        >
          <span className="text-white text-sm font-medium flex-1 min-w-0">
            🎛️ You've been invited to claim a slot at <span className="font-bold">"{pendingInvite.title}"</span>!
          </span>
          <button
            onClick={openInvitedEvent}
            className="bg-white text-purple-900 hover:bg-purple-100 text-xs font-bold px-3 py-1.5 rounded-lg shrink-0"
            data-testid="invite-banner-view-btn"
          >
            View Event →
          </button>
          <button onClick={dismissPendingInvite} className="text-purple-300 hover:text-white shrink-0" data-testid="invite-banner-dismiss" title="Dismiss">✕</button>
        </div>
      )}
      <div className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <img
              src={user ? "/beatseek-logo.png" : "/beatseek-logo-trans.png"}
              alt="BEATSEEK"
              className="app-logo-img"
              data-testid="app-logo"
            />
            <p className="app-subtitle" style={{ marginTop: '-6px' }}>Where Electronic Music Lives</p>
            {user && (
              <>
                <p className="app-subtitle" style={{ marginTop: '10px' }}>Bringing Electronic Music &amp; Community Together.</p>
                <p className="app-tagline">One Platform. Infinite Vibes.</p>
              </>
            )}
            <div style={{ marginTop: '8px' }}>
              <MiniCountdown />
            </div>
          </div>
          
          <div className="user-section">
            {user ? (
              <div className="user-info">
                {/* My Community Button */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowFriendsModal(true)}
                  className="relative text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                >
                  <Users size={20} className="mr-1" />
                  <span className="text-sm font-medium">My Community</span>
                </Button>
                
                {/* Beta Mode Notice - Dropdown */}
                {showBetaBanner && (
                  <div className="beta-notice-dropdown">
                    <span className="beta-icon">🧪</span>
                    <p><strong>Beta Mode:</strong> Some temporary sample data is displayed for exploration. Real content will grow as we grow!</p>
                    <button className="beta-dismiss" onClick={dismissBetaBanner}>✕</button>
                  </div>
                )}
                
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  Logout
                </Button>
              </div>
            ) : (
              <AuthForm 
                onLogin={handleLogin} 
                onRegister={handleRegister}
                authError={authError} 
                isSignupMode={isSignupMode}
                setIsSignupMode={setIsSignupMode}
              />
            )}
          </div>
        </div>
      </div>

      {!user && (
        <div className="landing-page">
          <div className="hero-section">
            <div className="features-grid-horizontal">
              <div className="feature-card">
                <Mic className="feature-icon" />
                <h3>AI Music Recognition</h3>
                <p>Advanced audio fingerprinting technology</p>
              </div>
              <div className="feature-card">
                <Users className="feature-icon" />
                <h3>Social Discovery</h3>
                <p>Share tracks and connect with other music lovers</p>
              </div>
              <div className="feature-card">
                <Search className="feature-icon" />
                <h3>Music Exploration</h3>
                <p>Discover new electronic genres and artists</p>
              </div>
            </div>
            
            <h2 className="hero-title">Discover Electronic Music Like Never Before</h2>
            <p className="hero-subtitle">
              Identify any electronic track in seconds with our advanced AI-powered recognition system
            </p>
            
            <div className="demo-section">
              <div className="quick-demo">
                <p className="demo-note">
                  Want to try without signing up? Use guest access with limited features
                </p>
                <Button 
                  variant="outline" 
                  className="try-guest-btn"
                  onClick={() => {
                    // Set a guest user for quick demo
                    const guestUser = { 
                      id: 'guest',
                      user_id: 'guest', 
                      name: 'Guest User', 
                      email: 'guest@beatseek.com', 
                      subscription: 'free' 
                    };
                    setUser(guestUser);
                    localStorage.setItem('beatseek_user', JSON.stringify(guestUser));
                  }}
                >
                  Try as Guest
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {user && (
        <div className="main-container">
          <Tabs value={activeTab} onValueChange={(value) => {
            setActiveTab(value);
            // Clear Release Radar navigation flag when leaving discovery
            if (value !== 'discover' && fromReleaseRadar) {
              setFromReleaseRadar(false);
            }
            // Refresh social data when social tab is clicked
            if (value === 'social' && user?.user_id) {
              console.log('🔄 Social tab clicked, refreshing data...');
              setTimeout(() => {
                refreshSocialData();
              }, 100);
            }
          }} className="main-tabs">
            <TabsList className="tabs-nav">
              <TabsTrigger value="listen" className="tab-trigger" data-tour-nav="listen" onClick={() => setActiveTab('listen')}>
                <Mic className="tab-icon" />
                ID
              </TabsTrigger>
              
              <TabsTrigger 
                value="discover" 
                className="tab-trigger"
                data-tour-nav="discover"
                onClick={() => setActiveTab('discover')}
              >
                <Search className="tab-icon" />
                Discovery Zone
              </TabsTrigger>
              
              <TabsTrigger 
                value="jam-session" 
                className="tab-trigger"
                data-tour-nav="jam-session"
                onClick={() => setActiveTab('jam-session')}
              >
                <Music className="tab-icon" />
                The Lounge
              </TabsTrigger>
              
              <TabsTrigger 
                value="social" 
                className="tab-trigger dashboard-tab-highlight"
                data-tour-nav="social"
                onClick={() => setActiveTab('social')}
              >
                <Users className="tab-icon" />
                Dashboard
              </TabsTrigger>
              
              <TabsTrigger 
                value="crate-box" 
                className="tab-trigger"
                data-tour-nav="crate-box"
                onClick={() => setActiveTab('crate-box')}
              >
                <Disc3 className="tab-icon" />
                Crate Box
              </TabsTrigger>
              
              <TabsTrigger 
                value="dj-tools" 
                className="tab-trigger"
                data-tour-nav="dj-tools"
                onClick={() => setActiveTab('dj-tools')}
              >
                <Headphones className="tab-icon" />
                The DJ Booth
              </TabsTrigger>
              
              <TabsTrigger 
                value="producers-corner" 
                className="tab-trigger"
                data-tour-nav="producers-corner"
                onClick={() => setActiveTab('producers-corner')}
              >
                <GraduationCap className="tab-icon" />
                Producers Corner
              </TabsTrigger>
            </TabsList>

            {/* Listen Tab */}
            <TabsContent value="listen" className="tab-content">
              <div className="listen-section">
                {authError && (
                  <Alert className="auth-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{authError}</AlertDescription>
                  </Alert>
                )}

                {rateLimitError && (
                  <Alert className="rate-limit-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{rateLimitError}</AlertDescription>
                  </Alert>
                )}

                <div className="listen-interface">
                  <div className="visualizer-container">
                    <div className={`listen-button-container ${isListening ? 'listening' : ''}`}>
                      {/* LEFT side waves - OUTSIDE the button */}
                      <svg 
                        className="wave-arcs-left"
                        width="60" 
                        height="120" 
                        viewBox="-5 0 60 120" 
                        fill="none"
                        style={{ overflow: 'visible' }}
                      >
                        {/* Wave 1 - innermost (thin) */}
                        <path 
                          d="M45 25 C30 40, 30 80, 45 95" 
                          stroke="#ff3b30" 
                          strokeWidth="4" 
                          strokeLinecap="round"
                          fill="none"
                          className={isListening ? 'wave-arc wave-1' : 'wave-arc-idle'}
                        />
                        {/* Wave 2 - middle (medium) */}
                        <path 
                          d="M35 15 C12 35, 12 85, 35 105" 
                          stroke="#ff3b30" 
                          strokeWidth="6" 
                          strokeLinecap="round"
                          fill="none"
                          className={isListening ? 'wave-arc wave-2' : 'wave-arc-idle'}
                        />
                        {/* Wave 3 - outermost (thickest) */}
                        <path 
                          d="M25 5 C-5 30, -5 90, 25 115" 
                          stroke="#ff3b30" 
                          strokeWidth="8" 
                          strokeLinecap="round"
                          fill="none"
                          className={isListening ? 'wave-arc wave-3' : 'wave-arc-idle'}
                        />
                      </svg>
                      
                      {/* Main button with microphone */}
                      <Button
                        onClick={(e) => {
                          console.log('🎤 MICROPHONE BUTTON EVENT TRIGGERED!');
                          console.log('🔍 Button disabled state:', isListening);
                          console.log('🔍 Current user:', user?.email);
                          handleListen();
                        }}
                        onTouchStart={(e) => {
                          // Prevent double-tap zoom on mobile
                          e.preventDefault();
                        }}
                        onTouchEnd={(e) => {
                          // Handle mobile touch
                          e.preventDefault();
                          if (!isListening) {
                            console.log('🎤 MOBILE TOUCH EVENT TRIGGERED!');
                            handleListen();
                          }
                        }}
                        style={{
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                        className={`listen-button ${isListening ? 'listening' : ''}`}
                        data-testid="track-id-listen-button"
                        size="lg"
                        disabled={isListening}
                      >
                        <Mic className="mic-icon-center" />
                      </Button>
                      
                      {/* RIGHT side waves - OUTSIDE the button */}
                      <svg 
                        className="wave-arcs-right"
                        width="60" 
                        height="120" 
                        viewBox="-5 0 60 120" 
                        fill="none"
                        style={{ overflow: 'visible' }}
                      >
                        {/* Wave 1 - innermost (thin) */}
                        <path 
                          d="M5 25 C20 40, 20 80, 5 95" 
                          stroke="#ff3b30" 
                          strokeWidth="4" 
                          strokeLinecap="round"
                          fill="none"
                          className={isListening ? 'wave-arc wave-1' : 'wave-arc-idle'}
                        />
                        {/* Wave 2 - middle (medium) */}
                        <path 
                          d="M15 15 C38 35, 38 85, 15 105" 
                          stroke="#ff3b30" 
                          strokeWidth="6" 
                          strokeLinecap="round"
                          fill="none"
                          className={isListening ? 'wave-arc wave-2' : 'wave-arc-idle'}
                        />
                        {/* Wave 3 - outermost (thickest) */}
                        <path 
                          d="M25 5 C55 30, 55 90, 25 115" 
                          stroke="#ff3b30" 
                          strokeWidth="8" 
                          strokeLinecap="round"
                          fill="none"
                          className={isListening ? 'wave-arc wave-3' : 'wave-arc-idle'}
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="listen-status" style={{ textAlign: 'center', marginTop: '15px' }}>
                    {recordingState === 'idle' && !isListening && (
                      <>
                        <p className="status-text" style={{ margin: '0 auto', textAlign: 'center' }}>Tap to identify your electronic track</p>
                        <IndexCounter />
                      </>
                    )}
                    {recordingState === 'starting' && (
                      <p className="status-text preparing">🎤 Initializing mic...</p>
                    )}
                    {recordingState === 'recording' && (
                      <p className="status-text listening" data-testid="id-status-listening">🎵 Listening… {countdown}s</p>
                    )}
                    {recordingState === 'processing' && (
                      <p className="status-text processing" data-testid="id-status-processing">🔎 Matching against ACRCloud + AudD…</p>
                    )}
                    <ScanStage stream={streamRef.current} recordingState={recordingState} countdown={countdown} quickSeconds={RECORD_QUICK_SECONDS} fullSeconds={RECORD_FULL_SECONDS} />
                  </div>

                  {/* Audio Debug Info - Only show when we have captured audio */}
                  {audioDebug.audioBlob && (
                    <div className="audio-debug-info">
                      <h4>🔍 Audio Capture Verification:</h4>
                      <div className="debug-details">
                        <p>📊 Size: {(audioDebug.audioSize / 1024).toFixed(2)} KB</p>
                        <p>🎵 Format: {audioDebug.audioFormat}</p>
                        <p>⏱️ Duration: {audioDebug.duration.toFixed(2)}s</p>
                        {audioDebug.audioUrl && (
                          <div className="audio-playback">
                            <p>🔊 Test Playback:</p>
                            <audio controls src={audioDebug.audioUrl} style={{width: '100%', maxWidth: '300px'}} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* SIMPLIFIED MODE: No usage stats needed */}
                </div>

                {identifiedTrack && (
                  <Card className="track-result" style={{ marginTop: '20px' }}>
                    <CardContent className="result-content">
                      {/* match % — upper-right corner of the card */}
                      {(() => {
                        const score = typeof identifiedTrack.confidenceScore === 'number' ? identifiedTrack.confidenceScore : null;
                        if (score === null || ['not_found', 'error', 'rate_limit'].includes(identifiedTrack.id)) return null;
                        const v = identifiedTrack.verified;
                        const tone = v ? '255,0,153' : score >= 85 ? '0,255,136' : score >= 60 ? '255,176,32' : '255,107,107';
                        return (
                          <span className="id-match-corner" data-testid="id-confidence-badge" style={{ color: `rgb(${tone})` }}>
                            {v && <VerifiedIdBadge size={18} title={`Verified by ${v.confirm_count || 0} BeatSeekers`} />}
                            {Math.round(score)}% match
                          </span>
                        );
                      })()}

                      {identifiedTrack.verified && (
                        <div className="id-verified-strip" data-testid="id-verified-strip">
                          <VerifiedIdBadge size={26} />
                          <div>
                            <strong>VERIFIED ID</strong>
                            <span>
                              {identifiedTrack.verified.locked_by_staff
                                ? 'Locked in by BEATSEEK — named instantly, straight from our own index'
                                : `Confirmed by ${identifiedTrack.verified.confirm_count || 0} BeatSeeker${(identifiedTrack.verified.confirm_count || 0) === 1 ? '' : 's'} — named instantly, straight from our own index`}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="id-split">
                       <div className="id-split-left">
                      <div className="track-info">
                        <ResultArtwork src={identifiedTrack.artwork} seed={identifiedTrack.artist || identifiedTrack.title || 'B'} />

                        <div className="track-details">
                          <h3 className="track-title">{identifiedTrack.title}</h3>
                          <p 
                            className="track-artist" 
                            data-testid="track-artist-link"
                            onClick={() => openArtistDeepDive(identifiedTrack.artist)}
                            style={{
                              fontSize: '0.92rem',
                              textAlign: 'left',
                              margin: '1px 0 0',
                              cursor: 'pointer',
                              transition: 'color 0.2s ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#ff00ff'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = ''; }}
                            title={artistAvailability[identifiedTrack.artist] ? "View artist details" : "Search artist"}
                          >
                            {identifiedTrack.artist}
                            {artistAvailability[identifiedTrack.artist] && (
                              <span style={{ marginLeft: '6px', fontSize: '11px', color: '#ff00ff', opacity: 0.85, fontWeight: 700, border: '1.5px solid rgba(255,0,255,0.5)', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle', lineHeight: 1 }}>i</span>
                            )}
                          </p>
                          
                          {/* Centered BPM and Dynamic Confidence - closer to artist */}
                          <div className="id-meta-row">
                            {identifiedTrack.bpm && (
                              <span className="id-meta-pill" data-testid="id-result-bpm">🥁 {identifiedTrack.bpm} BPM</span>
                            )}
                            {identifiedTrack.key && (
                              <span className="id-meta-pill" data-testid="id-result-key">🎹 {identifiedTrack.key}</span>
                            )}
                          </div>
                          {identifiedTrack.bpmKeySource === 'getsongbpm' && (identifiedTrack.bpm || identifiedTrack.key) && (
                            <a
                              href="https://getsongbpm.com"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="id-bpm-attrib"
                              data-testid="getsongbpm-attribution"
                              title="BPM & Key data via GetSongBPM"
                            >
                              via GetSongBPM
                            </a>
                          )}
                          
                          {/* Genre line even closer to BPM */}
                          {!identifiedTrack.outsideScope && !identifiedTrack.canonicalGenre && (
                          <p className="track-genre" style={{ 
                            textAlign: 'center', 
                            marginBottom: '10px',
                            marginTop: '0px',
                            fontSize: '0.95rem',
                            color: '#ccc'
                          }}>
                            {identifiedTrack.genre} • {identifiedTrack.subgenre}
                          </p>
                          )}

                          {/* Electronic intelligence: version / remixer / label / genre chips / other versions */}
                          <TrackIntel track={identifiedTrack} />

                          {/* Remove any message that might contain the old check mark */}
                          {identifiedTrack.message && !identifiedTrack.message.includes('✓') && (
                            <p className="track-message" style={{ marginTop: '8px', textAlign: 'center' }}>{identifiedTrack.message}</p>
                          )}
                        </div>
                      </div>
                       </div>

                       <div className="id-split-right">
                          {/* Crate / Playlist / Favorite / Wishlist actions */}
                          <IdResultActions track={identifiedTrack} />

                          {/* Quiet row: community correction / confirmation */}
                          <div className="id-quiet-row">
                            <ConfirmIdButton track={identifiedTrack} userId={user?.id} />
                            <WrongEditReport track={identifiedTrack} isLoggedIn={!!user} />
                            <CommunitySuggestions
                              track={identifiedTrack}
                              userId={user?.id}
                              onFingerprint={(t) => setEditIndexPrefill({
                                at: Date.now(),
                                original_artist: t.artist || '',
                                original_title: t.title || '',
                              })}
                            />
                          </div>
                       </div>
                      </div>

                      {/* Last.fm Similar Tracks rail — full width under both columns, collapsed by default */}
                      {identifiedTrack.artist && identifiedTrack.title && (
                        <LastFmSimilarTracksRail
                          artist={identifiedTrack.artist}
                          title={identifiedTrack.title}
                          limit={12}
                          onSaveToSpotify={(t) => {
                            setSpotifyTrackToSave({ ...t, title: t.title || t.track, artwork: t.cover_url });
                            setShowSpotifyModal(true);
                          }}
                          onShareToDiscord={(t) => {
                            setDiscordShareTrack({ ...t, title: t.title || t.track, artwork: t.cover_url });
                            setShowDiscordShareModal(true);
                          }}
                        />
                      )}

                      {/* TheAudioDB Enrichment Section */}
                      {identifiedTrack.enrichment && (
                        <div data-testid="track-enrichment-section" style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: 'rgba(0, 212, 255, 0.05)',
                          border: '1px solid rgba(0, 212, 255, 0.15)',
                          borderRadius: '10px',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          {/* Fanart background (subtle) */}
                          {identifiedTrack.enrichment.artistFanart && (
                            <div style={{
                              position: 'absolute',
                              top: 0, left: 0, right: 0, bottom: 0,
                              backgroundImage: `url(${identifiedTrack.enrichment.artistFanart})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              opacity: 0.08,
                              pointerEvents: 'none'
                            }} />
                          )}

                          {/* Mood & Theme Tags */}
                          {(identifiedTrack.enrichment.trackMood || identifiedTrack.enrichment.trackTheme) && (
                            <div data-testid="enrichment-mood-tags" style={{
                              display: 'flex',
                              gap: '8px',
                              flexWrap: 'wrap',
                              justifyContent: 'center',
                              marginBottom: '10px',
                              position: 'relative'
                            }}>
                              {identifiedTrack.enrichment.trackMood && (
                                <span style={{
                                  padding: '3px 10px',
                                  background: 'rgba(255, 165, 0, 0.2)',
                                  border: '1px solid rgba(255, 165, 0, 0.4)',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  color: '#ffa500',
                                  fontWeight: '600'
                                }}>
                                  Mood: {identifiedTrack.enrichment.trackMood}
                                </span>
                              )}
                              {identifiedTrack.enrichment.trackTheme && (
                                <span style={{
                                  padding: '3px 10px',
                                  background: 'rgba(138, 43, 226, 0.2)',
                                  border: '1px solid rgba(138, 43, 226, 0.4)',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  color: '#b06aff',
                                  fontWeight: '600'
                                }}>
                                  Theme: {identifiedTrack.enrichment.trackTheme}
                                </span>
                              )}
                              {identifiedTrack.enrichment.trackAlbum && (
                                <span style={{
                                  padding: '3px 10px',
                                  background: 'rgba(0, 212, 255, 0.15)',
                                  border: '1px solid rgba(0, 212, 255, 0.3)',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  color: '#00d4ff',
                                  fontWeight: '600'
                                }}>
                                  Album: {identifiedTrack.enrichment.trackAlbum}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Artist Bio */}
                          {identifiedTrack.enrichment.artistBio && (
                            <div data-testid="enrichment-artist-bio" style={{
                              display: 'flex',
                              gap: '10px',
                              alignItems: 'flex-start',
                              marginBottom: '10px',
                              position: 'relative'
                            }}>
                              {identifiedTrack.enrichment.artistThumb && (
                                <img
                                  src={identifiedTrack.enrichment.artistThumb}
                                  alt="Artist"
                                  style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '2px solid rgba(0, 212, 255, 0.3)',
                                    flexShrink: 0
                                  }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div>
                                <p style={{
                                  fontSize: '12px',
                                  color: '#aaa',
                                  lineHeight: '1.5',
                                  margin: 0
                                }}>
                                  {identifiedTrack.enrichment.artistBio}
                                  {identifiedTrack.enrichment.artistCountry && (
                                    <span style={{ color: '#00d4ff', marginLeft: '4px' }}>
                                      ({identifiedTrack.enrichment.artistCountry})
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Music Video Link — plays in BEATSEEK YouTube player */}
                          {identifiedTrack.enrichment.musicVideoUrl && (
                            <div data-testid="enrichment-music-video" style={{
                              textAlign: 'center',
                              position: 'relative'
                            }}>
                              <button
                                data-testid="play-music-video-btn"
                                onClick={() => {
                                  const videoId = extractYoutubeVideoId(identifiedTrack.enrichment.musicVideoUrl);
                                  if (videoId) {
                                    setSelectedYouTubeTrack({ title: identifiedTrack.title, artist: identifiedTrack.artist });
                                    setYoutubeVideoData({ found: true, video_id: videoId, title: `${identifiedTrack.artist} - ${identifiedTrack.title}` });
                                    setYoutubeError(null);
                                    setLoadingYoutubeVideo(false);
                                    setShowYouTubeModal(true);
                                  } else {
                                    window.open(identifiedTrack.enrichment.musicVideoUrl, '_blank');
                                  }
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '6px 14px',
                                  background: 'rgba(255, 0, 0, 0.15)',
                                  border: '1px solid rgba(255, 0, 0, 0.4)',
                                  borderRadius: '6px',
                                  color: '#ff4444',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.15)';
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0C.488 3.45.029 5.804 0 12c.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0C23.512 20.55 23.971 18.196 24 12c-.029-6.185-.484-8.549-4.385-8.816zM9 16V8l8 4-8 4z"/></svg>
                                Watch Music Video
                              </button>
                            </div>
                          )}

                          {/* Powered by badge */}
                          <div style={{
                            textAlign: 'right',
                            marginTop: '6px',
                            position: 'relative'
                          }}>
                            <span style={{ fontSize: '9px', color: '#555', fontStyle: 'italic' }}>
                              Enriched by TheAudioDB{identifiedTrack.mbData ? ' + MusicBrainz' : ''}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* ═══ MUSICBRAINZ TRACK INTELLIGENCE ═══ */}
                      {identifiedTrack.mbData && (
                        <div data-testid="mb-track-intelligence" style={{
                          marginTop: '10px', padding: '12px',
                          background: 'rgba(255, 0, 255, 0.04)',
                          border: '1px solid rgba(255, 0, 255, 0.15)',
                          borderRadius: '10px'
                        }}>
                          {/* MusicBrainz Credits */}
                          {identifiedTrack.mbData.artist_credits?.length > 1 && (
                            <div data-testid="mb-credits" style={{ marginBottom: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Credits: </span>
                              <span style={{ fontSize: '12px', color: '#ff66ff' }}>
                                {identifiedTrack.mbData.artist_credits.map((ac, i) => (
                                  <span key={i}>
                                    <span style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(255,0,255,0.3)' }}
                                      onClick={() => openArtistDeepDive(ac.name)}>{ac.name}</span>
                                    {ac.join_phrase && <span style={{ color: '#888' }}>{ac.join_phrase}</span>}
                                  </span>
                                ))}
                              </span>
                            </div>
                          )}

                          {/* MusicBrainz Genre Tags */}
                          {identifiedTrack.mbData.tags?.length > 0 && (
                            <div data-testid="mb-track-tags" style={{ marginBottom: '8px' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
                                {identifiedTrack.mbData.tags.slice(0, 8).map((tag, idx) => (
                                  <span key={idx} style={{
                                    padding: '2px 8px',
                                    background: idx === 0 ? 'rgba(255,0,255,0.15)' : 'rgba(255,0,255,0.06)',
                                    border: `1px solid rgba(255,0,255,${idx === 0 ? 0.3 : 0.12})`,
                                    borderRadius: '10px', fontSize: '10px', color: '#ff66ff', fontWeight: idx === 0 ? 600 : 400
                                  }}>
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Remix / Cover / Sample Relationships */}
                          {identifiedTrack.mbRelationships?.total > 0 && (
                            <div data-testid="mb-relationships" style={{ marginTop: '6px' }}>
                              {identifiedTrack.mbRelationships.remixes?.length > 0 && (
                                <div style={{ marginBottom: '6px' }}>
                                  {identifiedTrack.mbRelationships.remixes.map((remix, idx) => (
                                    <p key={idx} style={{ fontSize: '11px', color: '#b06aff', margin: '2px 0' }}>
                                      {remix.direction === 'backward'
                                        ? `Remix of "${remix.target_title}" by ${remix.target_artist}`
                                        : `Remixed as "${remix.target_title}" by ${remix.target_artist}`}
                                    </p>
                                  ))}
                                </div>
                              )}
                              {identifiedTrack.mbRelationships.covers?.length > 0 && (
                                <div style={{ marginBottom: '6px' }}>
                                  {identifiedTrack.mbRelationships.covers.slice(0, 3).map((cover, idx) => (
                                    <p key={idx} style={{ fontSize: '11px', color: '#00d4ff', margin: '2px 0' }}>
                                      {cover.direction === 'backward'
                                        ? `Cover of "${cover.target_title}" by ${cover.target_artist}`
                                        : `Covered by ${cover.target_artist}`}
                                    </p>
                                  ))}
                                </div>
                              )}
                              {identifiedTrack.mbRelationships.samples?.length > 0 && (
                                <div>
                                  {identifiedTrack.mbRelationships.samples.slice(0, 3).map((sample, idx) => (
                                    <p key={idx} style={{ fontSize: '11px', color: '#ffa500', margin: '2px 0' }}>
                                      Samples "{sample.target_title}" by {sample.target_artist}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          <div style={{ textAlign: 'right', marginTop: '4px' }}>
                            <span style={{ fontSize: '9px', color: '#555', fontStyle: 'italic' }}>MusicBrainz</span>
                          </div>
                        </div>
                      )}

                      {/* ═══ SONGSTATS INSIGHTS | FIND SIMILAR ═══ */}
                      <div style={{ marginTop: '12px', textAlign: 'center' }}>
                        <div className="id-extras-row">
                          <button
                            className="id-songstats-btn"
                            data-testid="songstats-btn"
                            disabled={songstatsLoading}
                            onClick={() => fetchSongstatsData(identifiedTrack)}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ff7a00" strokeWidth="2" strokeLinecap="round">
                              <rect x="3" y="12" width="4" height="9" rx="1" fill="rgba(255,122,0,0.35)"/>
                              <rect x="10" y="7" width="4" height="14" rx="1" fill="rgba(255,122,0,0.55)"/>
                              <rect x="17" y="3" width="4" height="18" rx="1" fill="rgba(255,122,0,0.75)"/>
                            </svg>
                            {songstatsLoading ? 'Loading Insights…' : songstatsData?.error ? 'Retry Songstats' : songstatsData ? 'Songstats Insights' : 'Get Songstats Insights'}
                          </button>
                          <span className="id-extras-sep" aria-hidden="true">|</span>
                          <button
                            className="id-gbtn"
                            data-testid="id-find-similar-btn"
                            onClick={() => {
                              setSimilarTracksSource(identifiedTrack);
                              setShowSimilarTracksModal(true);
                            }}
                            title="BEATSEEK AI — tracks that mix well with this one"
                          >
                            <Sparkles size={14} style={{ color: '#22d3ee' }} /> Find Similar
                          </button>
                        </div>

                        {songstatsLoading && (
                          <div style={{
                            padding: '20px',
                            background: 'rgba(255, 122, 0, 0.05)',
                            border: '1px solid rgba(255, 122, 0, 0.2)',
                            borderRadius: '10px',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                              <div className="animate-spin" style={{
                                width: '20px', height: '20px', border: '2px solid rgba(255,122,0,0.2)',
                                borderTop: '2px solid #ff7a00', borderRadius: '50%'
                              }} />
                              <span style={{ color: '#ff7a00', fontSize: '13px', fontWeight: '600' }}>
                                Fetching Songstats data...
                              </span>
                            </div>
                          </div>
                        )}

                        {songstatsData && songstatsData.error && (
                          <p data-testid="songstats-error" style={{
                            margin: '8px 0 0', fontSize: '11px', color: 'rgba(255,122,0,0.75)',
                            textAlign: 'center', fontStyle: 'italic',
                          }}>
                            {songstatsData.error}
                          </p>
                        )}

                        {songstatsData && !songstatsData.error && songstatsData.track_info && (
                          <div data-testid="songstats-results" style={{
                            padding: '14px',
                            background: 'rgba(255, 122, 0, 0.05)',
                            border: '1px solid rgba(255, 122, 0, 0.2)',
                            borderRadius: '10px',
                            textAlign: 'left',
                          }}>
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                              <span style={{ color: '#ff7a00', fontSize: '13px', fontWeight: '700', letterSpacing: '0.05em' }}>
                                SONGSTATS INSIGHTS
                              </span>
                              {songstatsData.from_cache && (
                                <span style={{ fontSize: '9px', color: '#666', background: 'rgba(255,122,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                                  cached
                                </span>
                              )}
                            </div>

                            {/* Audio Analysis — Track DNA Radar Chart */}
                            {songstatsData.audio_analysis?.length > 0 && (() => {
                              const analysisMap = {};
                              songstatsData.audio_analysis.forEach(a => { analysisMap[a.key] = parseFloat(a.value) || 0; });
                              
                              const axes = [
                                { key: 'energy', label: 'Energy', color: '#ff7a00' },
                                { key: 'danceability', label: 'Dance', color: '#fbbf24' },
                                { key: 'valence', label: 'Mood', color: '#34d399' },
                                { key: 'liveness', label: 'Live', color: '#a78bfa' },
                                { key: 'acousticness', label: 'Acoustic', color: '#60a5fa' },
                              ].filter(a => analysisMap[a.key] !== undefined);
                              
                              if (axes.length < 3) return null;
                              
                              const cx = 90, cy = 90, maxR = 65;
                              const angleStep = (2 * Math.PI) / axes.length;
                              const startAngle = -Math.PI / 2;
                              
                              const getPoint = (i, val) => {
                                const angle = startAngle + i * angleStep;
                                return {
                                  x: cx + maxR * val * Math.cos(angle),
                                  y: cy + maxR * val * Math.sin(angle),
                                };
                              };
                              
                              const dataPoints = axes.map((a, i) => getPoint(i, Math.max(analysisMap[a.key], 0.05)));
                              const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z';
                              
                              const gridLevels = [0.25, 0.5, 0.75, 1.0];
                              
                              // Normalize tempo for the pill display
                              const tempoVal = analysisMap['tempo'] ? Math.round(analysisMap['tempo']) : null;
                              const keyVal = songstatsData.audio_analysis.find(a => a.key === 'key')?.value;
                              
                              return (
                                <div style={{ marginBottom: '12px' }}>
                                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', fontWeight: '600', textAlign: 'center' }}>
                                    TRACK DNA
                                  </div>
                                  
                                  {/* Radar Chart */}
                                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                    <svg width="180" height="180" viewBox="0 0 180 180" style={{ overflow: 'visible' }}>
                                      {/* Grid rings */}
                                      {gridLevels.map((level) => {
                                        const ringPoints = axes.map((_, i) => getPoint(i, level));
                                        const ringPath = ringPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z';
                                        return (
                                          <path key={level} d={ringPath} fill="none" stroke="rgba(255,122,0,0.1)" strokeWidth="0.5" />
                                        );
                                      })}
                                      
                                      {/* Axis lines */}
                                      {axes.map((_, i) => {
                                        const end = getPoint(i, 1);
                                        return (
                                          <line key={i} x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="rgba(255,122,0,0.15)" strokeWidth="0.5" />
                                        );
                                      })}
                                      
                                      {/* Data polygon — animated fill */}
                                      <path d={dataPath} fill="rgba(255,122,0,0.15)" stroke="#ff7a00" strokeWidth="1.5"
                                        style={{ animation: 'radarFadeIn 0.8s ease-out forwards' }} />
                                      
                                      {/* Data points */}
                                      {dataPoints.map((p, i) => (
                                        <circle key={i} cx={p.x} cy={p.y} r="3" fill={axes[i].color} stroke="#0a0f1e" strokeWidth="1"
                                          style={{ animation: `radarDotPop 0.5s ease-out ${0.1 * i}s both` }} />
                                      ))}
                                      
                                      {/* Axis labels */}
                                      {axes.map((a, i) => {
                                        const labelPos = getPoint(i, 1.22);
                                        return (
                                          <text key={a.key} x={labelPos.x} y={labelPos.y}
                                            fill={a.color} fontSize="9" fontWeight="600" textAnchor="middle" dominantBaseline="middle">
                                            {a.label}
                                          </text>
                                        );
                                      })}
                                    </svg>
                                    
                                    {/* Value pills — BPM, Key + percentages */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '90px' }}>
                                      {tempoVal && (
                                        <div style={{ padding: '4px 10px', background: 'rgba(255,122,0,0.1)', border: '1px solid rgba(255,122,0,0.25)', borderRadius: '6px', textAlign: 'center' }}>
                                          <div style={{ fontSize: '9px', color: '#888', textTransform: 'uppercase' }}>BPM</div>
                                          <div style={{ fontSize: '15px', color: '#ff7a00', fontWeight: '700' }}>{tempoVal}</div>
                                        </div>
                                      )}
                                      {keyVal && (
                                        <div style={{ padding: '4px 10px', background: 'rgba(255,122,0,0.1)', border: '1px solid rgba(255,122,0,0.25)', borderRadius: '6px', textAlign: 'center' }}>
                                          <div style={{ fontSize: '9px', color: '#888', textTransform: 'uppercase' }}>Key</div>
                                          <div style={{ fontSize: '15px', color: '#ff7a00', fontWeight: '700' }}>{keyVal}</div>
                                        </div>
                                      )}
                                      {axes.map((a) => (
                                        <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0' }}>
                                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: a.color, flexShrink: 0 }} />
                                          <span style={{ fontSize: '10px', color: '#aaa', flex: 1 }}>{a.label}</span>
                                          <span style={{ fontSize: '11px', color: a.color, fontWeight: '700' }}>
                                            {Math.round(analysisMap[a.key] * 100)}%
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Cross-Platform Links */}
                            {songstatsData.track_info.links?.length > 0 && (
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px', fontWeight: '600' }}>
                                  AVAILABLE ON
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {Object.values(songstatsData.track_info.links
                                    .filter(l => ['beatport', 'traxsource', 'tracklist', 'spotify', 'apple_music', 'soundcloud', 'youtube', 'tidal', 'amazon', 'deezer'].includes(l.source))
                                    .reduce((acc, l) => {
                                      // Songstats returns one row per regional storefront — keep a single usable link per platform
                                      if (!acc[l.source] || (!acc[l.source].url && l.url)) acc[l.source] = l;
                                      return acc;
                                    }, {}))
                                    .filter(l => l.url)
                                    .map((link) => {
                                      const sourceColors = {
                                        beatport: '#94d500', traxsource: '#1da0c3', tracklist: '#ff6600',
                                        spotify: '#1db954', apple_music: '#fc3c44', soundcloud: '#ff5500',
                                        youtube: '#ff0000', tidal: '#000', amazon: '#ff9900', deezer: '#a238ff',
                                      };
                                      const sourceLabels = {
                                        beatport: 'Beatport', traxsource: 'Traxsource', tracklist: '1001TL',
                                        spotify: 'Spotify', apple_music: 'Apple Music', soundcloud: 'SoundCloud',
                                        youtube: 'YouTube', tidal: 'Tidal', amazon: 'Amazon', deezer: 'Deezer',
                                      };
                                      const color = sourceColors[link.source] || '#00d4ff';
                                      return (
                                        <a
                                          key={link.source}
                                          href={link.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          data-testid={`songstats-link-${link.source}`}
                                          style={{
                                            padding: '3px 8px', fontSize: '11px', fontWeight: '600',
                                            background: `${color}15`, border: `1px solid ${color}40`,
                                            borderRadius: '4px', color: color,
                                            textDecoration: 'none', transition: 'all 0.2s ease',
                                          }}
                                          onMouseEnter={(e) => { e.currentTarget.style.background = `${color}30`; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.background = `${color}15`; }}
                                        >
                                          {sourceLabels[link.source] || link.source}
                                        </a>
                                      );
                                    })}
                                </div>
                              </div>
                            )}

                            {/* Chart Positions & Stats */}
                            {songstatsData.stats?.length > 0 && (
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px', fontWeight: '600' }}>
                                  CHART & PLAYLIST DATA
                                </div>
                                {songstatsData.stats.map((stat) => (
                                  <div key={stat.source} style={{
                                    padding: '6px 10px', background: 'rgba(0,0,0,0.2)',
                                    borderRadius: '6px', marginBottom: '4px',
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span style={{ color: '#ccc', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize' }}>
                                        {stat.source === 'tracklist' ? '1001Tracklists' : stat.source}
                                      </span>
                                      <div style={{ display: 'flex', gap: '10px' }}>
                                        {stat.data?.playlist_count > 0 && (
                                          <span style={{ fontSize: '11px', color: '#a5b4fc' }}>
                                            {stat.data.playlist_count} playlists
                                          </span>
                                        )}
                                        {stat.data?.chart_count > 0 && (
                                          <span style={{ fontSize: '11px', color: '#fbbf24' }}>
                                            {stat.data.chart_count} charts
                                          </span>
                                        )}
                                        {stat.data?.total_plays > 0 && (
                                          <span style={{ fontSize: '11px', color: '#34d399' }}>
                                            {stat.data.total_plays.toLocaleString()} plays
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* DJ Supports / Activities */}
                            {songstatsData.activities?.length > 0 && (
                              <div style={{ marginBottom: '8px' }}>
                                <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px', fontWeight: '600' }}>
                                  DJ SUPPORT & ACTIVITY
                                </div>
                                <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                  {songstatsData.activities.slice(0, 5).map((act, idx) => (
                                    <div key={idx} style={{
                                      padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
                                      display: 'flex', alignItems: 'center', gap: '6px',
                                    }}>
                                      <span style={{
                                        fontSize: '9px', padding: '1px 5px', borderRadius: '3px',
                                        background: 'rgba(255,122,0,0.15)', color: '#ff7a00', fontWeight: '600',
                                        textTransform: 'capitalize', flexShrink: 0,
                                      }}>
                                        {act.source === 'tracklist' ? '1001TL' : act.source}
                                      </span>
                                      <span style={{ fontSize: '11px', color: '#aaa', lineHeight: '1.3' }}>
                                        {act.activity_text?.substring(0, 100)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Powered by Songstats footer */}
                            <div style={{ textAlign: 'right', marginTop: '6px' }}>
                              <a
                                href={songstatsData.track_info.songstats_track_url || 'https://songstats.com'}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: '9px', color: '#ff7a00', textDecoration: 'none', fontStyle: 'italic' }}
                                data-testid="songstats-attribution-link"
                              >
                                Powered by Songstats
                              </a>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Where to get it — one tight row: stores + Save/Share */}
                      <GetItRow
                        track={identifiedTrack}
                        onSaveToSpotify={() => {
                          setSpotifyTrackToSave(identifiedTrack);
                          setShowSpotifyModal(true);
                        }}
                        onShareToDiscord={() => {
                          if (!localStorage.getItem('discord_access_token')) {
                            toast.error('Connect Discord first — My Dashboard → Discord Integration');
                            return;
                          }
                          setDiscordShareTrack(identifiedTrack);
                          setShowDiscordShareModal(true);
                        }}
                      />

                      {/* Retry Button - Inside ID box, smaller size */}
                      {!isListening && (
                        <div className="retry-section-inline" style={{ 
                          textAlign: 'center', 
                          marginTop: '15px',
                          paddingTop: '15px',
                          borderTop: '1px solid #00d4ff'
                        }}>
                          <Button 
                            onClick={handleRetry}
                            className="retry-button-small"
                            variant="outline"
                            size="default"
                            disabled={isListening || recordingState !== 'idle'}
                            style={{
                              borderColor: '#00d4ff',
                              color: '#00d4ff',
                              backgroundColor: 'rgba(0, 212, 255, 0.1)'
                            }}
                          >
                            🔄 Try Again
                          </Button>
                          <p className="retry-hint-small" style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginTop: '5px',
                            margin: '5px 0 0 0'
                          }}>
                            No refresh needed - mic stays ready!
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Ad for Free Users */}
                {user?.subscription === 'free' && identifiedTrack && identifiedTrack?.id !== 'not_found' && (
                  <div className="free-tier-ad">
                    <div className="ad-label">Advertisement</div>
                    <div className="ad-placeholder">
                      <div className="ad-content">
                        <p>🎵 Discover More Electronic Music</p>
                        <p>Upgrade to Plus for ad-free experience + 50 IDs/day</p>
                        <Button variant="outline" size="sm">Upgrade Now</Button>
                      </div>
                    </div>
                  </div>
                )}
                <RegisterEditPanel isLoggedIn={!!user} prefill={editIndexPrefill} />
              </div>
            </TabsContent>

            {/* Other tabs with upgrade prompts */}
            <TabsContent value="discover" className="tab-content">
              <div className="discover-section">
                {/* Crate Mode Banner */}
                {isDiscoveringForCrate && targetCrateId && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.15), rgba(147, 51, 234, 0.15))',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '12px',
                    padding: '1rem 1.5rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>📦</span>
                      <div>
                        <div style={{ color: '#00ffff', fontWeight: 600, fontSize: '0.95rem' }}>
                          Adding tracks to: {targetCrateName}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                          Click 📦 on any track to add it to your crate
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button
                        onClick={async () => {
                          // Return to crate
                          const token = localStorage.getItem('beatseek_token');
                          const API = process.env.REACT_APP_BACKEND_URL;
                          try {
                            const response = await fetch(`${API}/api/crates/${targetCrateId}`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const crateData = await response.json();
                            setSelectedCrateForBuilder(crateData);
                            setIsDiscoveringForCrate(false);
                            setTargetCrateId(null);
                            setTargetCrateName(null);
                            setActiveTab('crate-box');
                          } catch (error) {
                            console.error('Error returning to crate:', error);
                          }
                        }}
                        style={{
                          background: 'rgba(0, 255, 255, 0.2)',
                          border: '1px solid rgba(0, 255, 255, 0.4)',
                          borderRadius: '8px',
                          padding: '0.5rem 1rem',
                          color: '#00ffff',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        ← Return to Crate
                      </button>
                      <button
                        onClick={() => {
                          setIsDiscoveringForCrate(false);
                          setTargetCrateId(null);
                          setTargetCrateName(null);
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          padding: '0.5rem 1rem',
                          color: 'rgba(255,255,255,0.6)',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Release Radar Navigation Banner */}
                {fromReleaseRadar && !isDiscoveringForCrate && (
                  <div data-testid="release-radar-nav-banner" style={{
                    background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.15), rgba(147, 51, 234, 0.15))',
                    border: '1px solid rgba(255, 0, 255, 0.3)',
                    borderRadius: '12px',
                    padding: '1rem 1.5rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>📡</span>
                      <div>
                        <div style={{ color: '#ff44ff', fontWeight: 600, fontSize: '0.95rem' }}>
                          Finding tracks from Release Radar
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                          Click 📦 on any track to add it to your crates
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid="return-to-crates-btn"
                      onClick={() => {
                        setFromReleaseRadar(false);
                        setShowMyCrates(true);
                        setActiveTab('crate-box');
                      }}
                      style={{
                        background: 'rgba(255, 0, 255, 0.2)',
                        border: '1px solid rgba(255, 0, 255, 0.4)',
                        borderRadius: '8px',
                        padding: '0.5rem 1rem',
                        color: '#ff44ff',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ← Return to My Crates
                    </button>
                  </div>
                )}

                <div className="discovery-header-row" style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0, flexShrink: 0 }}>Discovery Zone</h2>
                  
                  {/* Trending + Recently Explored — compact inline */}
                  <div className="discovery-widgets-inline" style={{ display: 'flex', gap: '12px', flex: 1, minWidth: 0, overflowX: 'auto', paddingBottom: '4px' }}>
                    {/* Trending on BEATSEEK */}
                    {trendingBeatseekArtists.length > 0 && (
                      <div data-testid="trending-on-beatseek" data-tour="disc-trending" className="discovery-widget-trending" style={{
                        padding: '8px 10px',
                        borderRadius: '10px', flexShrink: 0,
                        position: 'relative',
                      }}>
                        <div style={{ fontSize: '9px', color: '#ffa500', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                          Trending
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {trendingBeatseekArtists.slice(0, 5).map((ta, idx) => (
                            <button
                              key={idx}
                              data-testid={`trending-artist-${idx}`}
                              className="discovery-avatar-btn discovery-avatar-trending"
                              onClick={() => openArtistDeepDive(ta.artist_name)}
                              title={`${ta.artist_name} — ${ta.unique_users} explorer${ta.unique_users !== 1 ? 's' : ''}`}
                              style={{
                                flexShrink: 0, width: '56px',
                                background: 'transparent', border: 'none',
                                padding: '2px', textAlign: 'center',
                                cursor: 'pointer', color: '#fff',
                                position: 'relative',
                              }}
                            >
                              {idx < 3 && (
                                <span style={{
                                  position: 'absolute', top: '-2px', left: '0px', zIndex: 1,
                                  fontSize: '8px', fontWeight: 700,
                                  color: idx === 0 ? '#ffd700' : idx === 1 ? '#c0c0c0' : '#cd7f32',
                                  background: 'rgba(0,0,0,0.6)', borderRadius: '3px', padding: '0px 3px',
                                }}>#{idx + 1}</span>
                              )}
                              {ta.artwork_thumb ? (
                                <img src={ta.artwork_thumb} alt={ta.artist_name} style={{
                                  width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover',
                                  margin: '0 auto 3px', display: 'block', border: '1.5px solid rgba(255,165,0,0.25)'
                                }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                              ) : null}
                              <div style={{
                                width: '36px', height: '36px', borderRadius: '50%',
                                background: 'rgba(255,165,0,0.12)', margin: '0 auto 3px',
                                display: ta.artwork_thumb ? 'none' : 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: '14px', color: '#ffa500', fontWeight: 700
                              }}>{(ta.artist_name || '?')[0]}</div>
                              <p style={{ fontSize: '8px', fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ta.artist_name}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recently Explored */}
                    {exploredArtists.length > 0 && (
                      <div data-testid="recently-explored-artists" data-tour="disc-recently-explored" className="discovery-widget-explored" style={{
                        padding: '8px 10px',
                        borderRadius: '10px', flexShrink: 0,
                        position: 'relative',
                      }}>
                        <div style={{ fontSize: '9px', color: '#b06aff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                          Recently Explored
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {exploredArtists.slice(0, 5).map((ea, idx) => (
                            <button
                              key={idx}
                              data-testid={`explored-artist-${idx}`}
                              className="discovery-avatar-btn discovery-avatar-explored"
                              onClick={() => openArtistDeepDive(ea.artist_name)}
                              title={ea.artist_name}
                              style={{
                                flexShrink: 0, width: '56px',
                                background: 'transparent', border: 'none',
                                padding: '2px', textAlign: 'center',
                                cursor: 'pointer', color: '#fff',
                              }}
                            >
                              {ea.artwork_thumb ? (
                                <img src={ea.artwork_thumb} alt={ea.artist_name} style={{
                                  width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover',
                                  margin: '0 auto 3px', display: 'block', border: '1.5px solid rgba(138,43,226,0.2)'
                                }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                              ) : null}
                              <div style={{
                                width: '36px', height: '36px', borderRadius: '50%',
                                background: 'rgba(138,43,226,0.15)', margin: '0 auto 3px',
                                display: ea.artwork_thumb ? 'none' : 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: '14px', color: '#b06aff', fontWeight: 700
                              }}>{(ea.artist_name || '?')[0]}</div>
                              <p style={{ fontSize: '8px', fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ea.artist_name}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recommended For You Section */}
                {(recommendedArtists.length > 0 || recommendedLoading) && (
                  <div data-testid="recommended-for-you" data-tour="disc-recommended" style={{
                    marginBottom: '20px',
                    padding: '16px',
                    background: 'rgba(0, 212, 255, 0.04)',
                    border: '1px solid rgba(0, 212, 255, 0.12)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <h3 style={{ margin: 0, fontSize: '14px', color: '#00d4ff', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>
                        Recommended For You
                      </h3>
                      {recommendedMessage && (
                        <span style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>{recommendedMessage}</span>
                      )}
                    </div>
                    {recommendedLoading ? (
                      <p style={{ color: '#888', fontSize: '13px', textAlign: 'center', padding: '12px' }}>Analyzing your taste...</p>
                    ) : (
                      <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '6px' }}>
                        {recommendedArtists.map((ra, idx) => (
                          <button
                            key={idx}
                            data-testid={`recommended-artist-${idx}`}
                            onClick={() => openArtistDeepDive(ra.name)}
                            style={{
                              flexShrink: 0, width: '110px',
                              background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)',
                              borderRadius: '10px', padding: '12px 8px', textAlign: 'center',
                              cursor: 'pointer', transition: 'all 0.15s ease', color: '#fff'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.15)'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.35)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.15)'; }}
                          >
                            {ra.artwork_thumb ? (
                              <img src={ra.artwork_thumb} alt={ra.name} style={{
                                width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover',
                                margin: '0 auto 8px', display: 'block', border: '2px solid rgba(0,212,255,0.2)'
                              }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                            ) : null}
                            <div style={{
                              width: '52px', height: '52px', borderRadius: '50%',
                              background: 'rgba(0,212,255,0.12)', margin: '0 auto 8px',
                              display: ra.artwork_thumb ? 'none' : 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              fontSize: '20px', color: '#00d4ff', fontWeight: 700
                            }}>{(ra.name || '?')[0]}</div>
                            <p style={{ fontSize: '11px', fontWeight: 600, margin: '0 0 3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ra.name}</p>
                            <p style={{ fontSize: '9px', color: '#00d4ff', margin: '0 0 2px' }}>{ra.genre || ''}</p>
                            {ra.match_reason && (
                              <p style={{ fontSize: '8px', color: '#888', margin: 0, lineHeight: 1.3 }}>{ra.match_reason}</p>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                
                {/* Basic Filters block (toggle + controls — wrapped for tour spotlight) */}
                <div data-tour="disc-basic-filters" style={{ marginTop: '-15px' }}>
                {/* Basic Filters Toggle - Clean style */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <label style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '48px',
                    height: '26px',
                    cursor: 'pointer',
                    transform: 'translateY(-30px)'
                  }}>
                    <input
                      type="checkbox"
                      checked={filterMode === 'basic'}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFilterMode('basic');
                          setShowAdvancedFilters(false);
                          setShowArtistExplorer(false); // Turn off explorer
                        } else {
                          setFilterMode('advanced');
                          setShowAdvancedFilters(true);
                          setShowArtistExplorer(false); // Turn off explorer
                        }
                      }}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: filterMode === 'basic' ? '#00ffff' : 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '26px',
                      transition: '0.3s'
                    }}>
                      <span style={{
                        position: 'absolute',
                        height: '20px',
                        width: '20px',
                        left: filterMode === 'basic' ? '25px' : '3px',
                        bottom: '3px',
                        background: '#fff',
                        borderRadius: '50%',
                        transition: '0.3s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}></span>
                    </span>
                  </label>
                  <div style={{ transform: 'translateY(-20px)' }}>
                    <span style={{ 
                      color: filterMode === 'basic' ? '#00ffff' : 'rgba(255, 255, 255, 0.7)',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      Basic Filters
                    </span>
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      fontSize: '11px', 
                      color: 'rgba(255, 255, 255, 0.5)' 
                    }}>
                      Quick genre and search filtering
                    </p>
                  </div>
                </div>

                {/* Filter Controls with Checkboxes and Link */}
                <div className={`discovery-filter-controls ${filterMode === 'advanced' || showArtistExplorer ? 'dimmed' : ''}`}>
                  <div className="filter-row-top">
                    <div className="filter-checkbox-item genre-checkbox">
                      <input
                        type="checkbox"
                        id="genre-filter-checkbox"
                        checked={genreFilterEnabled}
                        onChange={(e) => {
                          setGenreFilterEnabled(e.target.checked);
                          if (filtersLinked && !e.target.checked) {
                            setFiltersLinked(false);
                          }
                        }}
                        className="filter-checkbox"
                        disabled={filterMode === 'advanced' || showArtistExplorer}
                      />
                      <label htmlFor="genre-filter-checkbox" className="filter-checkbox-label">
                        Genre
                      </label>
                    </div>
                    
                    <button
                      onClick={() => {
                        const newLinkedState = !filtersLinked;
                        setFiltersLinked(newLinkedState);
                        if (newLinkedState) {
                          // Linking: turn both ON
                          setGenreFilterEnabled(true);
                          setSearchFilterEnabled(true);
                        } else {
                          // Unlinking: turn both OFF
                          setGenreFilterEnabled(false);
                          setSearchFilterEnabled(false);
                        }
                      }}
                      className={`filter-link-btn ${filtersLinked ? 'linked' : ''}`}
                      title={filtersLinked ? "Unlink filters" : "Link filters together"}
                      disabled={filterMode === 'advanced' || showArtistExplorer}
                    >
                      🔗
                    </button>
                    
                    <div className="filter-checkbox-item search-checkbox">
                      <input
                        type="checkbox"
                        id="search-filter-checkbox"
                        checked={searchFilterEnabled}
                        onChange={(e) => {
                          setSearchFilterEnabled(e.target.checked);
                          if (filtersLinked && !e.target.checked) {
                            setFiltersLinked(false);
                          }
                        }}
                        className="filter-checkbox"
                        disabled={filterMode === 'advanced' || showArtistExplorer}
                      />
                      <label htmlFor="search-filter-checkbox" className="filter-checkbox-label">
                        Search
                      </label>
                    </div>
                  </div>
                  
                  <div className="filter-row-bottom">
                    {/* Genre Dropdown */}
                    <div className="genre-input-wrapper" style={showArtistExplorer ? { opacity: 0.35 } : {}}>
                      <select
                        id="genre-select"
                        value={selectedGenre}
                        onChange={(e) => {
                          setSelectedGenre(e.target.value);
                          if (filterMode === 'basic') {
                            loadDiscoverContent(e.target.value, discoverySearchQuery);
                          }
                        }}
                        disabled={!genreFilterEnabled || filterMode === 'advanced' || showArtistExplorer}
                        className="genre-dropdown-inline"
                      >
                        {verifiedGenres.map((genre) => (
                          <option key={genre.display} value={genre.display}>
                            {genre.display}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Search Field */}
                    <input
                      type="text"
                      placeholder={filterMode === 'advanced' ? "Search artist or track name..." : "Search by track name or artist..."}
                      value={discoverySearchQuery}
                      onChange={(e) => setDiscoverySearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          loadDiscoverContent(selectedGenre, discoverySearchQuery);
                        }
                      }}
                      disabled={(!searchFilterEnabled && filterMode !== 'advanced') || showArtistExplorer}
                      className="discovery-search-input-inline"
                      style={filterMode === 'advanced' ? { opacity: 1 } : showArtistExplorer ? { opacity: 0.35 } : {}}
                    />
                    
                    {/* Search Button */}
                    <Button 
                      onClick={() => loadDiscoverContent(selectedGenre, discoverySearchQuery)}
                      className="discovery-search-button-inline"
                      disabled={isLoadingDiscogs || showArtistExplorer}
                    >
                      🔍
                    </Button>
                  </div>
                  
                  {filterMode === 'basic' && (
                    <p className="filter-note">
                      ✨ More electronic subgenres coming soon
                      <span 
                        onClick={() => !isLoadingDiscogs && loadDiscoverContent(selectedGenre, discoverySearchQuery)}
                        style={{
                          marginLeft: '12px',
                          color: '#00ffff',
                          cursor: isLoadingDiscogs ? 'not-allowed' : 'pointer',
                          opacity: isLoadingDiscogs ? 0.5 : 1,
                          fontSize: '0.85rem'
                        }}
                        title="Refresh results"
                      >
                        {isLoadingDiscogs ? '⏳ Refreshing...' : '🔄 Refresh'}
                      </span>
                    </p>
                  )}
                </div>
                </div>

                  {/* Advanced Filters Section */}
                  <div className="advanced-filters-section" data-tour="disc-advanced-filters" style={{ marginTop: '-20px' }}>
                    {/* Advanced Mode Toggle - Clean style */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <label style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '48px',
                        height: '26px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={filterMode === 'advanced'}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilterMode('advanced');
                              setShowAdvancedFilters(true);
                              setSelectedSources([]); // No sources pre-selected, user chooses
                              setCurrentPage(1); // Reset pagination
                              setShowArtistExplorer(false); // Turn off Artist Explorer
                            } else {
                              setFilterMode('basic');
                              setShowAdvancedFilters(false);
                              setCurrentPage(1); // Reset pagination
                            }
                          }}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{
                          position: 'absolute',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: filterMode === 'advanced' ? '#00ffff' : 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '26px',
                          transition: '0.3s'
                        }}>
                          <span style={{
                            position: 'absolute',
                            height: '20px',
                            width: '20px',
                            left: filterMode === 'advanced' ? '25px' : '3px',
                            bottom: '3px',
                            background: '#fff',
                            borderRadius: '50%',
                            transition: '0.3s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}></span>
                        </span>
                      </label>
                      <div>
                        <span style={{ 
                          color: filterMode === 'advanced' ? '#00ffff' : 'rgba(255, 255, 255, 0.7)',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}>
                          Advanced Filters (Music Discovery) {filterMode === 'advanced' ? '' : ''}
                        </span>
                        <p style={{ 
                          margin: '4px 0 0 0', 
                          fontSize: '11px', 
                          color: 'rgba(255, 255, 255, 0.5)' 
                        }}>
                          Select sources to access custom audio filters
                        </p>
                      </div>
                    </div>

                    {/* Collapsible Advanced Filters Content */}
                    <button 
                      className={`advanced-filters-toggle ${showAdvancedFilters ? 'expanded' : ''}`}
                      onClick={() => {
                        if (filterMode !== 'advanced') {
                          setFilterMode('advanced');
                        }
                        setShowAdvancedFilters(!showAdvancedFilters);
                      }}
                      style={{
                        opacity: filterMode === 'advanced' ? 1 : 0.5,
                        pointerEvents: filterMode === 'advanced' ? 'auto' : 'none'
                      }}
                    >
                      <span>🎚️ Filter Options</span>
                      <span className="toggle-arrow">{showAdvancedFilters ? '▲' : '▼'}</span>
                    </button>
                    
                    {showAdvancedFilters && (
                      <div className="advanced-filters-content" style={{ overflow: 'visible' }}>
                        {/* Search Input - First in Advanced Filters */}
                        <div className="filter-group">
                          <label className="filter-group-label">Search</label>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              type="text"
                              placeholder="Search artist or track name..."
                              value={discoverySearchQuery}
                              onChange={(e) => setDiscoverySearchQuery(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  loadDiscoverContent(selectedGenre, discoverySearchQuery);
                                }
                              }}
                              data-testid="advanced-search-input"
                              className="advanced-genre-dropdown"
                              style={{ flex: 1, color: '#fff' }}
                            />
                            <button
                              onClick={() => loadDiscoverContent(selectedGenre, discoverySearchQuery)}
                              data-testid="advanced-search-btn"
                              style={{
                                padding: '8px 16px',
                                background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(255, 0, 255, 0.2))',
                                border: '1px solid rgba(0, 255, 255, 0.3)',
                                borderRadius: '8px',
                                color: '#00ffff',
                                fontSize: '14px',
                                cursor: 'pointer',
                                flexShrink: 0,
                              }}
                            >
                              🔍
                            </button>
                          </div>
                        </div>

                        {/* Genre Selection */}
                        <div className="filter-group">
                          <label className="filter-group-label" style={{
                            opacity: (selectedBpmPreset && selectedBpmPreset !== 'off') || bpmOnlyMode ? 0.5 : 1
                          }}>
                            Genre
                            {((selectedBpmPreset && selectedBpmPreset !== 'off') || bpmOnlyMode) && (
                              <span style={{ 
                                fontSize: '0.7rem', 
                                marginLeft: '8px', 
                                color: 'rgba(255, 165, 0, 0.8)',
                                fontWeight: 'normal'
                              }}>
                                {bpmOnlyMode ? '(BPM Only active)' : '(Preset active)'}
                              </span>
                            )}
                          </label>
                          <select
                            value={advancedGenre}
                            onChange={(e) => {
                              setAdvancedGenre(e.target.value);
                              setCurrentPage(1);
                            }}
                            className="advanced-genre-dropdown"
                            disabled={filterMode !== 'advanced' || (selectedBpmPreset && selectedBpmPreset !== 'off') || bpmOnlyMode}
                            style={{
                              opacity: (selectedBpmPreset && selectedBpmPreset !== 'off') || bpmOnlyMode ? 0.5 : 1,
                              cursor: (selectedBpmPreset && selectedBpmPreset !== 'off') || bpmOnlyMode ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {advancedGenres.map((genre) => (
                              <option key={genre.display} value={genre.display}>
                                {genre.display}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Source Selection */}
                        <div className="filter-group">
                          <label className="filter-group-label">Sources</label>
                          <div className="source-checkboxes">
                            {[
                              { id: 'spotify', name: 'Spotify', icon: '🎵', color: '#1db954' },
                              { id: 'soundcloud', name: 'SoundCloud', icon: '☁️', color: '#ff5500' },
                              { id: 'deezer', name: 'Deezer', icon: '🎧', color: '#feaa2d' },
                              { id: 'discogs', name: 'Discogs', icon: '💿', color: '#ff0066' },
                              { id: 'songstats', name: 'Songstats', icon: '📊', color: '#ff7a00' }
                            ].map(source => (
                              <label 
                                key={source.id} 
                                className={`source-checkbox-item ${selectedSources.includes(source.id) ? 'selected' : ''}`}
                                style={{ '--source-color': source.color }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedSources.includes(source.id)}
                                  onChange={() => {
                                    setSelectedSources(prev => {
                                      if (prev.includes(source.id)) {
                                        return prev.filter(s => s !== source.id);
                                      }
                                      return [...prev, source.id];
                                    });
                                    setCurrentPage(1);
                                  }}
                                  className="source-checkbox-input"
                                  disabled={filterMode !== 'advanced'}
                                />
                                <span className="source-icon">{source.icon}</span>
                                <span className="source-name">{source.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* BPM Filter - Only shown when SoundCloud is selected */}
                        {selectedSources.includes('soundcloud') && (
                          <div className="filter-group bpm-filter-group">
                            <div className="bpm-filter-header">
                              <label className="filter-group-label">
                                🎚️ BPM Range
                                <span className="soundcloud-exclusive-badge">SoundCloud</span>
                              </label>
                              <label className="bpm-toggle">
                                <input
                                  type="checkbox"
                                  checked={bpmFilterEnabled}
                                  onChange={(e) => {
                                    setBpmFilterEnabled(e.target.checked);
                                    if (!e.target.checked) {
                                      // Reset BPM-related states when turning off
                                      setBpmOnlyMode(false);
                                      setSelectedBpmPreset(null);
                                    }
                                  }}
                                />
                                <span className="bpm-toggle-label">{bpmFilterEnabled ? 'On' : 'Off'}</span>
                              </label>
                            </div>
                            {bpmFilterEnabled && (
                              <>
                                {/* BPM Only Checkbox - Centered above BPM counts */}
                                <div style={{ 
                                  marginBottom: '10px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center'
                                }}>
                                  <label style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    color: bpmOnlyMode ? '#00ffff' : 'rgba(255,255,255,0.7)'
                                  }}>
                                    <input
                                      type="checkbox"
                                      checked={bpmOnlyMode}
                                      onChange={(e) => {
                                        setBpmOnlyMode(e.target.checked);
                                        if (e.target.checked) {
                                          setSelectedBpmPreset(null); // Deselect any preset
                                        }
                                      }}
                                      style={{ 
                                        accentColor: '#00ffff',
                                        width: '14px',
                                        height: '14px'
                                      }}
                                    />
                                    BPM Only <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>(all genres)</span>
                                  </label>
                                </div>

                                <div className="bpm-sliders">
                                  <div className="bpm-input-group">
                                    <label>Min</label>
                                    <input
                                      type="number"
                                      min="60"
                                      max="200"
                                      value={bpmRange.min}
                                      onChange={(e) => {
                                        setBpmRange({ ...bpmRange, min: parseInt(e.target.value) || 60 });
                                        // Manual input clears preset selection (but not BPM Only mode)
                                        if (!bpmOnlyMode) setSelectedBpmPreset(null);
                                      }}
                                      className="bpm-input"
                                    />
                                  </div>
                                  <div className="bpm-range-display">
                                    {bpmRange.min} - {bpmRange.max} BPM
                                  </div>
                                  <div className="bpm-input-group">
                                    <label>Max</label>
                                    <input
                                      type="number"
                                      min="60"
                                      max="200"
                                      value={bpmRange.max}
                                      onChange={(e) => {
                                        setBpmRange({ ...bpmRange, max: parseInt(e.target.value) || 200 });
                                        // Manual input clears preset selection (but not BPM Only mode)
                                        if (!bpmOnlyMode) setSelectedBpmPreset(null);
                                      }}
                                      className="bpm-input"
                                    />
                                  </div>
                                </div>
                                
                                {/* Quick Presets - Hidden when BPM Only mode is active */}
                                {!bpmOnlyMode && (
                                  <div className="bpm-presets-inline" style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    flexWrap: 'wrap',
                                    gap: '4px'
                                  }}>
                                    <span className="presets-label" style={{ 
                                      display: 'flex',
                                      alignItems: 'center',
                                      lineHeight: '1'
                                    }}>Quick Presets:</span>
                                    {/* Off Option */}
                                    <span
                                      className={`bpm-preset-link ${selectedBpmPreset === 'off' ? 'active' : ''}`}
                                      onClick={() => {
                                        setSelectedBpmPreset('off');
                                        // "Off" re-enables manual mode without changing BPM values
                                      }}
                                      style={{
                                        color: selectedBpmPreset === 'off' ? '#00ffff' : 'rgba(255,255,255,0.6)',
                                        fontStyle: 'italic'
                                      }}
                                    >
                                      Off
                                    </span>
                                    <span className="preset-divider">•</span>
                                    {[
                                      { name: 'Deep House', min: 118, max: 125 },
                                      { name: 'Tech House', min: 124, max: 130 },
                                      { name: 'House', min: 120, max: 128 },
                                      { name: 'Techno', min: 130, max: 140 },
                                      { name: 'Trance', min: 138, max: 145 },
                                      { name: 'Dubstep', min: 140, max: 150 },
                                      { name: 'D&B', min: 170, max: 180 },
                                      { name: 'Downtempo', min: 90, max: 110 },
                                      { name: 'Ambient', min: 60, max: 90 }
                                    ].map((preset, i, arr) => (
                                      <span key={preset.name} style={{ display: 'flex', alignItems: 'center' }}>
                                        <span
                                          className={`bpm-preset-link ${selectedBpmPreset === preset.name ? 'active' : ''}`}
                                          onClick={() => {
                                            setBpmRange({ min: preset.min, max: preset.max });
                                            setSelectedBpmPreset(preset.name);
                                            setBpmOnlyMode(false); // Ensure BPM Only is unchecked
                                          }}
                                        >
                                          {preset.name}
                                        </span>
                                        {i < arr.length - 1 && <span className="preset-divider">•</span>}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {/* Spotify Advanced Audio Filters - Shown when Spotify is selected */}
                        {selectedSources.includes('spotify') && (
                          <SpotifyAdvancedFilters
                            filters={spotifyAudioFilters}
                            onFilterChange={setSpotifyAudioFilters}
                            onApplyFilters={() => {
                              setIsApplyingSpotifyFilters(true);
                              setCurrentPage(1); // Reset to page 1 when applying new filters
                              loadDiscoverContent(advancedGenre, '', false, 1);
                            }}
                            isLoading={isApplyingSpotifyFilters}
                          />
                        )}
                        
                        {/* Clear + Apply Buttons */}
                        <div style={{ 
                          marginTop: '12px', 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <button 
                            data-testid="clear-filters-btn"
                            onClick={() => {
                              setAdvancedGenre('All Genres');
                              setSelectedSources([]);
                              setDiscoverySearchQuery('');
                              setBpmFilterEnabled(false);
                              setBpmOnlyMode(false);
                              setSelectedBpmPreset(null);
                              setCurrentPage(1);
                            }}
                            disabled={filterMode !== 'advanced'}
                            style={{
                              padding: '7px 16px',
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.2)',
                              borderRadius: '8px',
                              color: 'rgba(255,255,255,0.7)',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
                          >
                            Clear
                          </button>
                          <button 
                            className="refresh-filters-btn"
                            data-testid="apply-filters-btn"
                            onClick={() => {
                              setCurrentPage(1);
                              loadDiscoverContent(advancedGenre, discoverySearchQuery, false, 1, true);
                            }}
                            disabled={isLoadingDiscogs || filterMode !== 'advanced'}
                            title="Apply filters and load results"
                          >
                            {isLoadingDiscogs ? '⏳' : '🔄'} Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                {discogsError && (
                  <Alert className="discovery-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{discogsError}</AlertDescription>
                  </Alert>
                )}

                {/* Discovery Results with Loading Overlay */}
                <div className="discovery-results" style={{ minHeight: '400px', position: 'relative' }}>
                  {/* Loading Overlay - appears on top without removing content */}
                  {isLoadingDiscogs && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(15, 23, 42, 0.85)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      borderRadius: '8px'
                    }}>
                      <p style={{ color: '#00ffff', fontSize: '1rem' }}>🔍 Finding {filterMode === 'advanced' ? advancedGenre : selectedGenre} tracks...</p>
                    </div>
                  )}
                  
                  {/* Local Tracks Section */}
                  {localTracks.length > 0 && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h3 style={{ 
                        color: '#00ffff', 
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        📁 My Uploads ({localTracks.length}/10)
                        <span style={{
                          fontSize: '0.75rem',
                          color: 'rgba(255, 165, 0, 0.8)',
                          fontWeight: 'normal'
                        }}>
                          • Removed on logout
                        </span>
                        </h3>
                        <div className="tracks-grid">
                          {localTracks.map((track) => (
                            <Card key={track.id} className="discovery-card" style={{ borderColor: 'rgba(0, 255, 255, 0.3)', position: 'relative' }}>
                              <CardContent className="discovery-content">
                                <div className="discovery-card-layout">
                                  {/* Left: Album Artwork */}
                                  <div className="discovery-artwork-wrapper">
                                    {track.artwork ? (
                                      <img 
                                        src={track.artwork} 
                                        alt={track.title}
                                        className="discovery-artwork"
                                        onError={(e) => {
                                          // Fallback to local music icon for local tracks
                                          e.target.style.display = 'none';
                                          e.target.parentElement.classList.add('no-image');
                                          e.target.onerror = null;
                                        }}
                                      />
                                    ) : (
                                      <div className="discovery-artwork no-image">🎵</div>
                                    )}
                                  </div>

                                  {/* Bottom Right: All Action Buttons */}
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '0.5rem',
                                    right: '0.5rem',
                                    display: 'flex',
                                    gap: '0.5rem',
                                    alignItems: 'center'
                                  }}>
                                    {/* Add to Playlist Button - Only show if NOT in jam session context */}
                                    {activePlaylistForAdding && !isDiscoveringForJam && (
                                      <button
                                        onClick={async () => {
                                          try {
                                            await addTrackToPlaylist(activePlaylistForAdding.id, {
                                              track_id: track.track_id,
                                              title: track.title,
                                              artist: track.artist,
                                              source: 'local',
                                              artwork: track.artwork,
                                              duration_ms: track.duration_ms,
                                              local_id: track.id
                                            });
                                            
                                            // Refresh playlist
                                            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                                            const updatedPlaylist = await fetch(`${BACKEND_URL}/api/playlists/${activePlaylistForAdding.id}`).then(r => r.json());
                                            setCurrentPlaylist(updatedPlaylist);
                                            setShowPlaylistDetailModal(true);
                                          } catch (error) {
                                            console.error('Error adding local track:', error);
                                            alert(`Failed to add track: ${error.message}`);
                                          }
                                        }}
                                        title={`Add to ${activePlaylistForAdding.name}`}
                                        style={{
                                          background: 'rgba(0, 255, 255, 0.1)',
                                          border: '1px solid rgba(0, 255, 255, 0.3)',
                                          borderRadius: '6px',
                                          fontSize: '1rem',
                                          padding: '0.4rem 0.6rem',
                                          cursor: 'pointer',
                                          color: '#00ffff',
                                          transition: 'all 0.2s'
                                        }}
                                        onMouseOver={(e) => {
                                          e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
                                          e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.5)';
                                        }}
                                        onMouseOut={(e) => {
                                          e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
                                          e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.3)';
                                        }}
                                      >
                                        🎧
                                      </button>
                                    )}
                                    
                                    {/* Add to Jam Session Button - Show if IN jam session context OR always available */}
                                    {isDiscoveringForJam && targetSessionId ? (
                                      <button
                                        onClick={async () => {
                                          try {
                                            console.log('🎵 JAM BUTTON CLICKED - Starting add to jam session');
                                            const token = localStorage.getItem('beatseek_token');
                                            const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                                            
                                            const addResponse = await axios.post(
                                              `${API}/api/jam/${targetSessionId}/queue`,
                                              {
                                                track_id: track.track_id,
                                                title: track.title,
                                                artist: track.artist,
                                                preview_url: null,
                                                spotify_uri: null,
                                                duration_ms: track.duration_ms || 0,
                                                artwork: track.artwork,
                                                source: 'local',
                                                local_id: track.id
                                              },
                                              { headers: { 'Authorization': `Bearer ${token}` } }
                                            );
                                            
                                            console.log('✅ Track added to jam queue:', addResponse.data);
                                            
                                            // Wait a bit for backend to update
                                            await new Promise(resolve => setTimeout(resolve, 300));
                                            
                                            // Fetch updated session and reopen modal
                                            const updatedSession = await axios.get(
                                              `${API}/api/jam/${targetSessionId}`,
                                              { headers: { 'Authorization': `Bearer ${token}` } }
                                            );
                                            
                                            // Clear old state first
                                            setShowJamSessionModal(false);
                                            setCurrentJamSession(null);
                                            setIsDiscoveringForJam(false);
                                            setTargetSessionId(null);
                                            
                                            // Set new state after a tick
                                            setTimeout(() => {
                                              setCurrentJamSession(updatedSession.data);
                                              setActiveTab('jam-session');
                                              setShowJamSessionModal(true);
                                            }, 100);
                                            
                                            alert(`✅ "${track.title}" added to your jam session queue!`);
                                          } catch (error) {
                                            console.error('Error adding local track to jam:', error);
                                            alert(`Failed to add track to jam session: ${error.message}`);
                                          }
                                        }}
                                        title="Add to Jam Session"
                                        style={{
                                          background: 'rgba(255, 0, 255, 0.1)',
                                          border: '1px solid rgba(255, 0, 255, 0.3)',
                                          borderRadius: '6px',
                                          fontSize: '1rem',
                                          padding: '0.4rem 0.6rem',
                                          cursor: 'pointer',
                                          color: '#ff00ff',
                                          transition: 'all 0.2s'
                                        }}
                                        onMouseOver={(e) => {
                                          e.currentTarget.style.background = 'rgba(255, 0, 255, 0.2)';
                                          e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.5)';
                                        }}
                                        onMouseOut={(e) => {
                                          e.currentTarget.style.background = 'rgba(255, 0, 255, 0.1)';
                                          e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.3)';
                                        }}
                                      >
                                        🎵
                                      </button>
                                    ) : (
                                      <button
                                        onClick={async () => {
                                          // Fetch available jam sessions
                                          try {
                                            const token = localStorage.getItem('beatseek_token');
                                            const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                                            const response = await axios.get(
                                              `${API}/api/jam/active`,
                                              { headers: { 'Authorization': `Bearer ${token}` } }
                                            );
                                            setAvailableJamSessions(response.data || []);
                                            setSelectedLocalTrackForJam(track);
                                            setShowJamSessionSelector(true);
                                          } catch (error) {
                                            console.error('Error fetching jam sessions:', error);
                                            alert('Failed to load jam sessions. Please try again.');
                                          }
                                        }}
                                        title="Add to jam session"
                                        style={{
                                          background: 'rgba(255, 0, 255, 0.1)',
                                          border: '1px solid rgba(255, 0, 255, 0.3)',
                                          borderRadius: '6px',
                                          fontSize: '1rem',
                                          padding: '0.4rem 0.6rem',
                                          cursor: 'pointer',
                                          color: '#ff00ff',
                                          transition: 'all 0.2s'
                                        }}
                                        onMouseOver={(e) => {
                                          e.currentTarget.style.background = 'rgba(255, 0, 255, 0.2)';
                                          e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.5)';
                                        }}
                                        onMouseOut={(e) => {
                                          e.currentTarget.style.background = 'rgba(255, 0, 255, 0.1)';
                                          e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.3)';
                                        }}
                                      >
                                        🎵
                                      </button>
                                    )}
                                    
                                    {/* Delete Button */}
                                    <button
                                      onClick={async () => {
                                        if (window.confirm(`Delete "${track.title}" from local storage?`)) {
                                          try {
                                            const { deleteLocalTrack, getLocalTracks } = await import('./utils/localTrackStorage');
                                            await deleteLocalTrack(track.id);
                                            
                                            // Refresh local tracks list
                                            const updatedTracks = await getLocalTracks();
                                            setLocalTracks(updatedTracks);
                                            
                                            alert(`✅ "${track.title}" deleted successfully!`);
                                          } catch (error) {
                                            console.error('Error deleting local track:', error);
                                            alert(`Failed to delete track: ${error.message}`);
                                          }
                                        }
                                      }}
                                      title="Delete local track"
                                      style={{
                                        background: 'rgba(255, 68, 68, 0.1)',
                                        border: '1px solid rgba(255, 68, 68, 0.3)',
                                        borderRadius: '6px',
                                        fontSize: '1rem',
                                        padding: '0.4rem 0.6rem',
                                        cursor: 'pointer',
                                        color: '#ff4444',
                                        transition: 'all 0.2s'
                                      }}
                                      onMouseOver={(e) => {
                                        e.currentTarget.style.background = 'rgba(255, 68, 68, 0.2)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 68, 68, 0.5)';
                                      }}
                                      onMouseOut={(e) => {
                                        e.currentTarget.style.background = 'rgba(255, 68, 68, 0.1)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 68, 68, 0.3)';
                                      }}
                                    >
                                      🗑️
                                    </button>
                                  </div>

                                  {/* Middle: Track Info */}
                                  <div className="discovery-info">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <h4 className="discovery-title" style={{ margin: 0, flex: 1 }}>{track.title}</h4>
                                      <span 
                                        style={{
                                          padding: '0.2rem 0.5rem',
                                          background: 'rgba(0, 255, 0, 0.2)',
                                          border: '1px solid rgba(0, 255, 0, 0.4)',
                                          borderRadius: '4px',
                                          fontSize: '0.7rem',
                                          fontWeight: '600',
                                          color: '#00ff00',
                                          whiteSpace: 'nowrap'
                                        }}
                                        title="Full track - Local file"
                                      >
                                        Full Track
                                      </span>
                                    </div>
                                    <p className="discovery-artist" onClick={(e) => { e.stopPropagation(); openArtistDeepDive(track.artist); }} style={{ cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#ff00ff'; }} onMouseLeave={(e) => { e.currentTarget.style.color = ''; }} title={artistAvailability[track.artist] ? "View artist details" : "Search artist"}>{track.artist}{artistAvailability[track.artist] && (<span style={{ marginLeft: '5px', fontSize: '10px', color: '#ff00ff', opacity: 0.85, fontWeight: 700, border: '1.5px solid rgba(255,0,255,0.5)', borderRadius: '50%', width: '15px', height: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle', lineHeight: 1 }}>i</span>)}</p>
                                    
                                    {/* Meta Info */}
                                    <div className="discovery-meta">
                                      <span>{Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}</span>
                                      <span>• {(track.size / (1024 * 1024)).toFixed(1)} MB</span>
                                    </div>

                                    {/* Moved buttons to bottom right corner */}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ═══ Artist Explorer — Standalone Panel ═══ */}
                    <div data-tour="disc-artist-explorer" style={{
                      marginBottom: '1.5rem',
                      background: 'rgba(26, 26, 46, 0.6)',
                      borderRadius: '12px',
                      border: `1px solid ${showArtistExplorer ? 'rgba(255,0,255,0.4)' : 'rgba(255,0,255,0.15)'}`,
                      padding: '16px',
                      transition: 'border-color 0.3s ease',
                    }}>
                      {/* Header + Slide Toggle (matching Advanced Filters style) */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: showArtistExplorer ? '12px' : 0
                      }}>
                        <label style={{
                          position: 'relative',
                          display: 'inline-block',
                          width: '48px',
                          height: '26px',
                          cursor: 'pointer'
                        }}>
                          <input
                            type="checkbox"
                            data-testid="artist-explorer-toggle"
                            checked={showArtistExplorer}
                            onChange={async (e) => {
                              const next = e.target.checked;
                              setShowArtistExplorer(next);
                              if (next) {
                                // Turn off both basic and advanced filters
                                setFilterMode('none');
                                setShowAdvancedFilters(false);
                                // Load artists if needed
                                if (explorerArtists.length === 0) {
                                  setExplorerLoading(true);
                                  try {
                                    const [audioDbRes, mbRes] = await Promise.all([
                                      fetch(`${BACKEND_URL}/api/enrichment/artists/explore`),
                                      fetch(`${BACKEND_URL}/api/musicbrainz/discovered-artists?limit=500`),
                                    ]);
                                    const audioDbData = await audioDbRes.json();
                                    const mbData = await mbRes.json();
                                    let allArtists = audioDbData.success ? (audioDbData.artists || []) : [];
                                    // Merge MB discovered artists (add source tag)
                                    if (mbData.success && mbData.artists?.length > 0) {
                                      const existingNames = new Set(allArtists.map(a => (a.name || '').toLowerCase()));
                                      const mbArtists = mbData.artists
                                        .filter(a => !existingNames.has((a.name || '').toLowerCase()))
                                        .map(a => ({
                                          name: a.name,
                                          genre: a.tags?.[0] || '',
                                          style: a.tags?.[1] || '',
                                          country: a.country || '',
                                          source: 'musicbrainz',
                                          mbid: a.mbid,
                                          tags: a.tags || [],
                                        }));
                                      allArtists = [...allArtists, ...mbArtists];
                                    }
                                    setExplorerArtists(allArtists);
                                  } catch (e) { console.error('Explorer error:', e); }
                                  setExplorerLoading(false);
                                }
                              } else {
                                // Restore basic filters as default
                                setFilterMode('basic');
                              }
                            }}
                            style={{ opacity: 0, width: 0, height: 0 }}
                          />
                          <span style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0, left: 0, right: 0, bottom: 0,
                            background: showArtistExplorer ? '#ff00ff' : 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '26px',
                            transition: '0.3s'
                          }}>
                            <span style={{
                              position: 'absolute',
                              height: '20px',
                              width: '20px',
                              left: showArtistExplorer ? '25px' : '3px',
                              bottom: '3px',
                              background: '#fff',
                              borderRadius: '50%',
                              transition: '0.3s',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}></span>
                          </span>
                        </label>
                        <div>
                          <span style={{
                            color: showArtistExplorer ? '#ff00ff' : 'rgba(255, 255, 255, 0.7)',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}>
                            Artist Explorer
                          </span>
                          <p style={{
                            margin: '4px 0 0 0',
                            fontSize: '11px',
                            color: 'rgba(255, 255, 255, 0.5)'
                          }}>
                            Discover artist bios, artwork & journeys
                          </p>
                        </div>
                      </div>

                      {/* Controls: Genre + Search — always visible, grayed when OFF */}
                      <div style={{
                        display: 'flex', gap: '8px', alignItems: 'center',
                        opacity: showArtistExplorer ? 1 : 0.35,
                        pointerEvents: showArtistExplorer ? 'auto' : 'none',
                        transition: 'opacity 0.3s ease',
                        marginTop: showArtistExplorer ? 0 : '12px',
                      }}>
                        <select
                          value={explorerGenre}
                          onChange={(e) => { setExplorerGenre(e.target.value); setExplorerPage(1); }}
                          className="advanced-genre-dropdown"
                          style={{ width: '180px', flexShrink: 0 }}
                          disabled={!showArtistExplorer}
                        >
                          <option value="All Genres">All Genres</option>
                          {explorerGenresList.filter(g => g !== 'All Genres').map((genre) => (
                            <option key={genre} value={genre}>{genre}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Search an artist..."
                          value={explorerQuery}
                          onChange={async (e) => {
                            const val = e.target.value;
                            setExplorerQuery(val);
                            // Reset to full list when search cleared
                            if (!val.trim() && explorerArtists.length <= 1) {
                              setExplorerLoading(true);
                              setExplorerPage(1);
                              try {
                                const res = await fetch(`${BACKEND_URL}/api/enrichment/artists/explore`);
                                const data = await res.json();
                                if (data.success) setExplorerArtists(data.artists);
                              } catch (err) { console.error(err); }
                              setExplorerLoading(false);
                            }
                          }}
                          onKeyPress={async (e) => {
                            if (e.key === 'Enter' && explorerQuery.trim()) {
                              setExplorerLoading(true);
                              setExplorerPage(1);
                              try {
                                const res = await fetch(`${BACKEND_URL}/api/enrichment/artists/explore?query=${encodeURIComponent(explorerQuery)}`);
                                const data = await res.json();
                                if (data.success) setExplorerArtists(data.artists);
                              } catch (err) { console.error(err); }
                              setExplorerLoading(false);
                            }
                          }}
                          data-testid="artist-explorer-search"
                          className="advanced-genre-dropdown"
                          style={{ flex: 1, color: '#fff' }}
                          disabled={!showArtistExplorer}
                        />
                        <button
                          onClick={async () => {
                            setExplorerLoading(true);
                            setExplorerPage(1);
                            try {
                              const url = explorerQuery.trim()
                                ? `${BACKEND_URL}/api/enrichment/artists/explore?query=${encodeURIComponent(explorerQuery)}`
                                : `${BACKEND_URL}/api/enrichment/artists/explore`;
                              const res = await fetch(url);
                              const data = await res.json();
                              if (data.success) setExplorerArtists(data.artists);
                            } catch (err) { console.error(err); }
                            setExplorerLoading(false);
                          }}
                          disabled={!showArtistExplorer}
                          style={{
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, rgba(255,0,255,0.2), rgba(180,0,255,0.2))',
                            border: '1px solid rgba(255,0,255,0.3)',
                            borderRadius: '8px',
                            color: '#ff00ff',
                            fontSize: '14px',
                            cursor: showArtistExplorer ? 'pointer' : 'not-allowed',
                            flexShrink: 0,
                          }}
                        >
                          🔍
                        </button>
                      </div>

                      {/* Clear + Apply inside explorer panel */}
                      {showArtistExplorer && (
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          marginTop: '10px',
                        }}>
                          <p style={{ fontSize: '0.7rem', color: 'rgba(255,0,255,0.5)', margin: 0, fontStyle: 'italic' }}>
                            Music discovery is hidden while Artist Explorer is active
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              data-testid="explorer-clear-btn"
                              onClick={async () => {
                                setExplorerGenre('All Genres');
                                setExplorerQuery('');
                                setExplorerPage(1);
                                setExplorerLoading(true);
                                try {
                                  const res = await fetch(`${BACKEND_URL}/api/enrichment/artists/explore`);
                                  const data = await res.json();
                                  if (data.success) setExplorerArtists(data.artists);
                                } catch (err) { console.error(err); }
                                setExplorerLoading(false);
                              }}
                              style={{
                                padding: '6px 14px',
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '8px',
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.color = '#fff'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
                            >
                              Clear
                            </button>
                            <button
                              data-testid="explorer-apply-btn"
                              onClick={async () => {
                                setExplorerLoading(true);
                                setExplorerPage(1);
                                try {
                                  const url = explorerQuery.trim()
                                    ? `${BACKEND_URL}/api/enrichment/artists/explore?query=${encodeURIComponent(explorerQuery)}`
                                    : `${BACKEND_URL}/api/enrichment/artists/explore`;
                                  const res = await fetch(url);
                                  const data = await res.json();
                                  if (data.success) setExplorerArtists(data.artists);
                                } catch (err) { console.error(err); }
                                setExplorerLoading(false);
                              }}
                              style={{
                                padding: '6px 14px',
                                background: 'linear-gradient(135deg, rgba(255,0,255,0.2), rgba(180,0,255,0.25))',
                                border: '1.5px solid rgba(255,0,255,0.4)',
                                borderRadius: '8px',
                                color: '#ff00ff',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,0,255,0.3), rgba(180,0,255,0.35))'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,0,255,0.2), rgba(180,0,255,0.25))'; }}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Artist Explorer Results Grid */}
                    {showArtistExplorer && (() => {
                      const electronicGenres = ['electronic', 'house', 'techno', 'trance', 'drum and bass', 'dnb', 'dubstep', 'ambient', 'idm', 'edm', 'dance', 'electro', 'downtempo', 'breakbeat', 'garage', 'jungle', 'trip hop', 'trip-hop', 'synthwave', 'synthpop', 'industrial', 'minimal', 'progressive', 'psytrance', 'psy trance', 'psy-trance', 'hardstyle', 'hardcore', 'uk garage', 'grime', 'lounge', 'chillout', 'chillwave', 'bass', 'dub', 'disco', 'nu-disco', 'nu disco', 'deep house', 'tech house', 'acid', 'rave', 'big beat', 'glitch', 'afro house', 'funky house', 'jackin house', 'hard dance', 'neo rave', 'hard techno', 'indie dance', 'mainstage', 'melodic house', 'deep tech', 'organic house', 'trap', 'future bass', 'bassline', 'breaks', 'electronica', 'drum & bass', 'drum n bass', 'clubbing', 'euro dance', 'new wave', 'synth'];
                      
                      const filtered = explorerArtists
                        .filter(a => {
                          const g = ((a.genre || '') + ' ' + (a.style || '') + ' ' + (a.tags || []).join(' ')).toLowerCase();
                          // Include if genre matches electronic whitelist, or if genre is empty (seeded as electronic), or if source is musicbrainz (pre-filtered)
                          return a.source === 'musicbrainz' || !g.trim() || electronicGenres.some(eg => g.includes(eg));
                        })
                        .filter(a => {
                          if (!explorerGenre || explorerGenre === 'All Genres') return true;
                          const g = ((a.genre || '') + ' ' + (a.style || '') + ' ' + (a.tags || []).join(' ')).toLowerCase();
                          return g.includes(explorerGenre.toLowerCase());
                        })
                        .filter((a, i, arr) => arr.findIndex(x => x.name === a.name) === i);
                      
                      const visible = filtered.slice((explorerPage - 1) * EXPLORER_PER_PAGE, explorerPage * EXPLORER_PER_PAGE);
                      const totalPages = Math.ceil(filtered.length / EXPLORER_PER_PAGE);
                      const hasMore = explorerPage < totalPages;
                      
                      return (
                        <div style={{ marginBottom: '2rem' }}>
                          {explorerLoading ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.5)' }}>Loading artists...</div>
                          ) : visible.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.4)' }}>No electronic artists found. Try a different search or genre.</div>
                          ) : (
                            <>
                              <div className="explorer-artist-grid" style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(6, 1fr)',
                                gap: '4px',
                                marginTop: '16px',
                              }}>
                                {visible.map((artist, idx) => (
                                  <div
                                    key={artist.id || idx}
                                    data-testid={`explorer-artist-${idx}`}
                                    className="discovery-avatar-btn discovery-avatar-explored"
                                    onClick={() => openArtistDeepDive(artist.name)}
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      padding: '16px 8px',
                                      background: 'transparent',
                                      border: 'none',
                                      borderRadius: '12px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                    }}
                                  >
                                    {artist.artwork?.thumb ? (
                                      <img
                                        src={artist.artwork.thumb}
                                        alt={artist.name}
                                        style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', marginBottom: '8px', border: '2px solid rgba(255,0,255,0.3)' }}
                                      />
                                    ) : (
                                      <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(255,0,255,0.3), rgba(180,0,255,0.3))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: 700, color: '#ff00ff', marginBottom: '8px' }}>
                                        {(artist.name || '?')[0]}
                                      </div>
                                    )}
                                    <span style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>{artist.name}</span>
                                    {artist.genre && (
                                      <span style={{ color: '#ff00ff', fontSize: '10px', marginTop: '4px', opacity: 0.7 }}>{artist.genre}</span>
                                    )}
                                    {artist.country && (
                                      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', marginTop: '2px' }}>{artist.country}</span>
                                    )}
                                    <span style={{ marginTop: '6px', fontSize: '10px', color: '#ff00ff', opacity: 0.85, fontWeight: 700, border: '1.5px solid rgba(255,0,255,0.5)', borderRadius: '50%', width: '15px', height: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>i</span>
                                  </div>
                                ))}
                              </div>
                              {totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '1.5rem' }}>
                                  <button
                                    onClick={() => setExplorerPage(p => Math.max(1, p - 1))}
                                    disabled={explorerPage <= 1}
                                    style={{
                                      padding: '8px 18px',
                                      background: explorerPage <= 1 ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, rgba(255,0,255,0.15), rgba(180,0,255,0.2))',
                                      border: `1.5px solid ${explorerPage <= 1 ? 'rgba(255,255,255,0.1)' : 'rgba(255,0,255,0.4)'}`,
                                      borderRadius: '24px',
                                      color: explorerPage <= 1 ? 'rgba(255,255,255,0.3)' : '#ff00ff',
                                      fontSize: '13px',
                                      fontWeight: 700,
                                      cursor: explorerPage <= 1 ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    Previous
                                  </button>
                                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                                    Page {explorerPage} of {totalPages}
                                  </span>
                                  <button
                                    onClick={() => setExplorerPage(p => Math.min(totalPages, p + 1))}
                                    disabled={!hasMore}
                                    style={{
                                      padding: '8px 18px',
                                      background: !hasMore ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, rgba(255,0,255,0.15), rgba(180,0,255,0.2))',
                                      border: `1.5px solid ${!hasMore ? 'rgba(255,255,255,0.1)' : 'rgba(255,0,255,0.4)'}`,
                                      borderRadius: '24px',
                                      color: !hasMore ? 'rgba(255,255,255,0.3)' : '#ff00ff',
                                      fontSize: '13px',
                                      fontWeight: 700,
                                      cursor: !hasMore ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    Discover More
                                  </button>
                                </div>
                              )}

                              {/* Artist count + Suggest button */}
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '12px' }}>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
                                  {filtered.length} artists
                                  {explorerArtists.some(a => a.source === 'musicbrainz') && (
                                    <span style={{ marginLeft: '4px', color: 'rgba(0,212,255,0.4)' }}>
                                      (incl. MusicBrainz discoveries)
                                    </span>
                                  )}
                                </span>
                                <button
                                  data-testid="suggest-artist-btn"
                                  onClick={() => {
                                    const name = window.prompt('Suggest an electronic music artist:');
                                    if (name && name.trim().length >= 2) {
                                      const token = localStorage.getItem('beatseek_token');
                                      fetch(`${BACKEND_URL}/api/artists/suggest`, {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                                        },
                                        body: JSON.stringify({ artist_name: name.trim() }),
                                      })
                                        .then(r => r.json())
                                        .then(d => {
                                          if (d.success) {
                                            alert(d.message);
                                          } else {
                                            alert(d.message || 'Could not submit suggestion');
                                          }
                                        })
                                        .catch(() => alert('Error submitting suggestion'));
                                    }
                                  }}
                                  style={{
                                    padding: '4px 12px',
                                    background: 'rgba(0,212,255,0.08)',
                                    border: '1px solid rgba(0,212,255,0.2)',
                                    borderRadius: '16px',
                                    color: '#00d4ff',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.18)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.08)'; }}
                                >
                                  + Suggest an Artist
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* Deezer Tracks Section - Hidden when Artist Explorer is open */}
                    {!showArtistExplorer && discogsTracks.length > 0 ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                          <h3 data-tour="disc-results-title" style={{ color: 'white', margin: 0 }}>🔍 Discovery Zone Results</h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {cameFromSimilarTracks && (
                              <button
                                data-testid="back-to-similar-tracks"
                                onClick={() => {
                                  setCameFromSimilarTracks(false);
                                  setShowSimilarTracksModal(true);
                                }}
                                style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', color: '#c026d3', cursor: 'pointer', fontSize: '11px', fontWeight: 600, padding: '4px 0' }}
                              >
                                <ArrowLeft size={12} /> Back to Similar Tracks
                              </button>
                            )}
                            <button
                              data-testid="similar-tracks-trigger"
                              data-tour="disc-similar-tracks"
                              className="similar-tracks-btn"
                              onClick={() => {
                                if (discogsTracks.length > 0) {
                                  setSimilarTracksSource(discogsTracks[0]);
                                  setCameFromSimilarTracks(false);
                                  setShowSimilarTracksModal(true);
                                }
                              }}
                            >
                              <div className="similar-tracks-btn-inner">
                                <Sparkles size={12} /> Similar Tracks
                              </div>
                            </button>
                          </div>
                        </div>
                        <div className="tracks-grid" data-tour="disc-results">
                          {discogsTracks.map((track) => (
                          <Card key={track.id} className="discovery-card">
                            <CardContent className="discovery-content">
                              <div className="discovery-card-layout">
                                {/* Left: Album Artwork with Source Badge below */}
                                <div className="discovery-artwork-column">
                                  <div className="discovery-artwork-wrapper">
                                    {track.artwork ? (
                                      <img 
                                        src={track.artwork} 
                                        alt={track.title}
                                        className="discovery-artwork"
                                        onError={(e) => {
                                          // Use source-specific logo as fallback
                                          const source = track.source?.toLowerCase();
                                          if (source === 'discogs') {
                                            e.target.src = '/logos/discogs-logo-custom.svg';
                                            e.target.classList.add('fallback-logo');
                                          } else if (source === 'spotify') {
                                            e.target.src = '/logos/spotify-logo.png';
                                            e.target.classList.add('fallback-logo');
                                          } else if (source === 'deezer') {
                                            e.target.src = '/logos/deezer-logo.svg';
                                            e.target.classList.add('fallback-logo');
                                          } else if (source === 'soundcloud') {
                                            e.target.src = '/logos/soundcloud-logo.svg';
                                            e.target.classList.add('fallback-logo');
                                          } else {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.classList.add('no-image');
                                          }
                                          e.target.onerror = null;
                                        }}
                                      />
                                    ) : (
                                      <div className="discovery-artwork no-image"></div>
                                    )}
                                  </div>
                                  {/* Source Badge - Below artwork */}
                                  {track.source && ['spotify', 'soundcloud', 'deezer', 'discogs', 'songstats'].includes(track.source.toLowerCase()) && (
                                    <span className={`source-badge-below source-${track.source.toLowerCase()}`}>
                                      {track.source.toLowerCase() === 'spotify' && '🎵 '}
                                      {track.source.toLowerCase() === 'soundcloud' && '☁️ '}
                                      {track.source.toLowerCase() === 'deezer' && '🎧 '}
                                      {track.source.toLowerCase() === 'discogs' && '💿 '}
                                      {track.source.toLowerCase() === 'songstats' && '📊 '}
                                      {track.source}
                                    </span>
                                  )}
                                </div>

                                {/* Middle: Track Info */}
                                <div className="discovery-info">
                                  <div style={{ paddingRight: '30px' }}>
                                    <h4 className="discovery-title" style={{ margin: 0 }}>{track.title}</h4>
                                  </div>
                                  {/* Wishlist Star - pinned top right of card */}
                                  <div className="track-wishlist-pin" style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 2 }}>
                                    <WishlistStar 
                                      itemType="track"
                                      itemData={{
                                        id: track.id,
                                        title: track.title,
                                        artist: track.artist,
                                        genre: track.genre,
                                        bpm: track.bpm,
                                        artwork: track.artwork
                                      }}
                                      size={16}
                                    />
                                  </div>
                                  <p className="discovery-artist" onClick={(e) => { e.stopPropagation(); openArtistDeepDive(track.artist); }} style={{ cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#ff00ff'; }} onMouseLeave={(e) => { e.currentTarget.style.color = ''; }} title={artistAvailability[track.artist] ? "View artist details" : "Search artist"}>{track.artist}{artistAvailability[track.artist] && (<span style={{ marginLeft: '5px', fontSize: '10px', color: '#ff00ff', opacity: 0.85, fontWeight: 700, border: '1.5px solid rgba(255,0,255,0.5)', borderRadius: '50%', width: '15px', height: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle', lineHeight: 1 }}>i</span>)}</p>
                                  
                                  {/* Meta Info Row */}
                                  <div className="discovery-meta">
                                    <span className="discovery-genre">{track.genre}</span>
                                    {track.year && <span className="discovery-year">• {track.year}</span>}
                                    {track.bpm && <span className="discovery-bpm">• {track.bpm} BPM</span>}
                                    {track.popularity && (
                                      <span className="discovery-popularity">• 🔥 {track.popularity}%</span>
                                    )}
                                  </div>

                                  {/* Songstats Track DNA — compact button for songstats source */}
                                  {track.source === 'songstats' && (
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
                                      {track.songstats_data?.audio_analysis?.length > 0 ? (
                                        <button
                                          data-testid={`track-dna-btn-${track.id}`}
                                          onClick={(e) => { e.stopPropagation(); setTrackDnaModal({ ...track.songstats_data, trackTitle: track.title, trackArtist: track.artist }); }}
                                          style={{
                                            padding: '3px 10px', fontSize: '11px', fontWeight: '700',
                                            background: 'rgba(255,122,0,0.1)', border: '1px solid rgba(255,122,0,0.35)',
                                            borderRadius: '5px', color: '#ff7a00', cursor: 'pointer',
                                            transition: 'all 0.2s ease', letterSpacing: '0.03em',
                                          }}
                                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,122,0,0.25)'; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,122,0,0.1)'; }}
                                        >
                                          Track DNA
                                        </button>
                                      ) : (
                                        <span style={{ fontSize: '10px', color: '#666', fontStyle: 'italic' }}>No DNA data</span>
                                      )}
                                      {/* Quick platform links inline */}
                                      {track.songstats_data?.track_info?.links?.filter(l => ['beatport','traxsource','tracklist'].includes(l.source)).slice(0, 3).map((link) => {
                                        const colors = { beatport: '#94d500', traxsource: '#1da0c3', tracklist: '#ff6600' };
                                        const labels = { beatport: 'Beatport', traxsource: 'Traxsource', tracklist: '1001TL' };
                                        return (
                                          <a key={link.source} href={link.url} target="_blank" rel="noopener noreferrer"
                                            style={{
                                              padding: '2px 7px', fontSize: '10px', fontWeight: '600',
                                              background: `${colors[link.source]}15`, border: `1px solid ${colors[link.source]}30`,
                                              borderRadius: '4px', color: colors[link.source], textDecoration: 'none',
                                            }}
                                          >{labels[link.source]}</a>
                                        );
                                      })}
                                      <a href={track.songstats_data?.track_info?.songstats_track_url || 'https://songstats.com'} target="_blank" rel="noopener noreferrer"
                                        style={{ fontSize: '8px', color: '#ff7a00', textDecoration: 'none', fontStyle: 'italic', marginLeft: 'auto' }}>
                                        Songstats
                                      </a>
                                    </div>
                                  )}

                                  {/* Purchase Link Buttons Row - REORDERED */}
                                  <div className="discovery-purchase-row">
                                    <button
                                      className="discovery-purchase-btn"
                                      onClick={() => window.open(`https://www.beatport.com/search?q=${encodeURIComponent(track.artist + ' ' + track.title)}`, '_blank')}
                                      title="Buy on Beatport"
                                    >
                                      <img src="/logos/beatport-icon-clean.png" alt="Beatport" className="purchase-logo" />
                                    </button>
                                    <button
                                      className="discovery-purchase-btn"
                                      onClick={() => window.open(`https://www.traxsource.com/search?term=${encodeURIComponent(track.artist + ' ' + track.title)}`, '_blank')}
                                      title="Buy on Traxsource"
                                    >
                                      <img src="/logos/traxsource-logo.png" alt="Traxsource" className="purchase-logo" />
                                    </button>
                                    <button
                                      className="discovery-purchase-btn"
                                      onClick={() => window.open(`https://www.discogs.com/search/?q=${encodeURIComponent(track.artist + ' ' + track.title)}&type=all`, '_blank')}
                                      title="Buy on Discogs"
                                    >
                                      <img src="/logos/discogs-logo-custom.svg" alt="Discogs" className="purchase-logo" />
                                    </button>
                                    <button
                                      className="discovery-purchase-btn"
                                      onClick={() => window.open(`https://open.spotify.com/search/${encodeURIComponent(track.artist + ' ' + track.title)}`, '_blank')}
                                      title="Listen on Spotify"
                                    >
                                      <img src="/logos/spotify-logo.png" alt="Spotify" className="purchase-logo" />
                                    </button>
                                    <button
                                      className="discovery-purchase-btn"
                                      onClick={() => window.open(`https://www.deezer.com/search/${encodeURIComponent(track.artist + ' ' + track.title)}`, '_blank')}
                                      title="Listen on Deezer"
                                    >
                                      <img src="/logos/deezer-logo.svg" alt="Deezer" className="purchase-logo" />
                                    </button>
                                    <button
                                      className="discovery-purchase-btn"
                                      onClick={() => window.open(`https://soundcloud.com/search?q=${encodeURIComponent(track.artist + ' ' + track.title)}`, '_blank')}
                                      title="Listen on SoundCloud"
                                    >
                                      <img src="/logos/soundcloud-logo.svg" alt="SoundCloud" className="purchase-logo" />
                                    </button>
                                    
                                    {/* YouTube button - always show with fallback */}
                                    <button
                                      className="discovery-purchase-btn youtube-btn"
                                      onClick={async () => {
                                        setSelectedYouTubeTrack(track);
                                        setShowYouTubeModal(true);
                                        setYoutubeError(null);
                                        setYoutubeVideoData(null);
                                        
                                        if (track.youtubeAvailable && track.youtubeVideoId) {
                                          setYoutubeVideoData({
                                            found: true,
                                            video_id: track.youtubeVideoId,
                                            title: `${track.artist} - ${track.title}`
                                          });
                                          setLoadingYoutubeVideo(false);
                                        } else {
                                          setLoadingYoutubeVideo(true);
                                          try {
                                            const response = await fetch(`${BACKEND_URL}/api/youtube/search?artist=${encodeURIComponent(track.artist)}&title=${encodeURIComponent(track.title)}`);
                                            if (response.ok) {
                                              const data = await response.json();
                                              if (data.found && data.video_id) {
                                                setYoutubeVideoData(data);
                                              } else {
                                                setYoutubeError(data.error || 'Video not found');
                                              }
                                            } else {
                                              setYoutubeError('Failed to search YouTube');
                                            }
                                          } catch (error) {
                                            console.error('YouTube search error:', error);
                                            setYoutubeError('Failed to connect to YouTube service');
                                          } finally {
                                            setLoadingYoutubeVideo(false);
                                          }
                                        }
                                      }}
                                      title="Watch on YouTube"
                                    >
                                      <img src="/logos/youtube-icon.svg" alt="YouTube" className="purchase-logo" />
                                    </button>
                                  </div>

                                  {/* Action Icons Row - always below purchase links */}
                                  <div className="discovery-purchase-row" style={{ marginTop: '4px' }}>
                                    <button
                                      className="track-action-icon"
                                      onClick={() => handleStartJamSession(track)}
                                      title="Start Jam Session"
                                    >
                                      🎵
                                    </button>
                                      <button
                                        className="track-action-icon"
                                        onClick={() => handleToggleFavorite(track)}
                                        title="Add to Favorites"
                                      >
                                        ❤️
                                      </button>
                                      <button
                                        className="track-action-icon"
                                        onClick={() => handleToggleWishlist(track)}
                                        title="Add to Wishlist"
                                      >
                                        📋
                                      </button>
                                      <button
                                        className="track-action-icon"
                                        onClick={() => handleSaveTrack(track)}
                                        title="Save Track"
                                      >
                                        💾
                                      </button>
                                      {/* Discord Share icon */}
                                      <button
                                        className="track-action-icon"
                                        onClick={() => {
                                          const discordToken = localStorage.getItem('discord_access_token');
                                          if (!discordToken) {
                                            alert('Please connect your Discord account to use this feature!\n\nGo to My Dashboard → Discord Integration → Connect with Discord');
                                            return;
                                          }
                                          setDiscordShareTrack({
                                            title: track.title,
                                            artist: track.artist,
                                            artwork: track.artwork,
                                            genre: track.genre,
                                            bpm: track.bpm,
                                            year: track.year
                                          });
                                          setShowDiscordShareModal(true);
                                        }}
                                        title="Share to Discord"
                                        style={{ 
                                          background: 'rgba(88, 101, 242, 0.3)',
                                          borderRadius: '50%',
                                          marginLeft: '2px'
                                        }}
                                      >
                                        <svg width="18" height="18" viewBox="0 0 71 55" fill="#5865F2">
                                          <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                                        </svg>
                                      </button>
                                      {/* Share to The Wall icon (Phase 2) */}
                                      <button
                                        className="track-action-icon"
                                        data-testid={`discovery-share-wall-${track.id}`}
                                        onClick={() => setShareToWallTrack(track)}
                                        title="Share to The Wall"
                                        style={{
                                          background: 'linear-gradient(135deg, rgba(6,182,212,0.18), rgba(192,38,211,0.18))',
                                          borderRadius: '50%',
                                          marginLeft: '2px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: '#22d3ee',
                                          border: '1px solid rgba(34,211,238,0.4)',
                                        }}
                                      >
                                        <Share2 size={14} />
                                      </button>
                                      {/* Add to Playlist icon */}
                                      {activePlaylistForAdding && (
                                        <button
                                          className="track-action-icon"
                                          onClick={async () => {
                                            if (playlistModalData?.addTrackToPlaylist) {
                                              await playlistModalData.addTrackToPlaylist(activePlaylistForAdding.id, {
                                                track_id: track.id,
                                                title: track.title,
                                                artist: track.artist,
                                                source: 'discovery',
                                                artwork: track.artwork,
                                                preview_url: track.preview_url,
                                                duration_ms: track.duration_ms
                                              });
                                              const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                                              const updatedPlaylist = await fetch(`${BACKEND_URL}/api/playlists/${activePlaylistForAdding.id}`).then(r => r.json());
                                              setCurrentPlaylist(updatedPlaylist);
                                              if (playlistModalData?.setActiveTab) {
                                                playlistModalData.setActiveTab('playlists');
                                              }
                                              setShowPlaylistDetailModal(true);
                                            }
                                          }}
                                          title={`Add to ${activePlaylistForAdding.name}`}
                                          style={{
                                            background: 'transparent',
                                            border: 'none',
                                            fontSize: '1.2rem',
                                            padding: '0.25rem'
                                          }}
                                        >
                                          🎧
                                        </button>
                                      )}

                                      {/* Add to Crate Button */}
                                      {isDiscoveringForCrate && targetCrateId ? (
                                        <button
                                          className="track-action-icon"
                                          onClick={async () => {
                                            try {
                                              const token = localStorage.getItem('beatseek_token');
                                              const API = process.env.REACT_APP_BACKEND_URL;
                                              const trackData = {
                                                track_id: track.id || `discovery_${Date.now()}`,
                                                source: track.source || 'discovery',
                                                title: track.title,
                                                artist: track.artist,
                                                album: track.album || '',
                                                duration_ms: track.duration_ms || 0,
                                                bpm: track.bpm || null,
                                                key: track.key || null,
                                                energy: track.energy || null,
                                                artwork_url: track.artwork || track.artwork_url || '',
                                                preview_url: track.preview_url || null,
                                                spotify_uri: track.spotify_uri || track.uri || null,
                                                youtube_id: track.youtube_id || null,
                                                external_url: track.external_url || track.url || null,
                                                purchase_url: track.purchase_url || null
                                              };
                                              if (track.source === 'discogs' && !track.preview_url && !track.youtube_id) {
                                                const proceed = window.confirm(
                                                  `⚠️ No audio preview available for this Discogs track.\n\n"${track.title}" by ${track.artist}\n\nAdd anyway? You can search for audio later.`
                                                );
                                                if (!proceed) return;
                                              }
                                              const response = await fetch(
                                                `${API}/api/crates/${targetCrateId}/tracks`,
                                                {
                                                  method: 'POST',
                                                  headers: {
                                                    'Authorization': `Bearer ${token}`,
                                                    'Content-Type': 'application/json'
                                                  },
                                                  body: JSON.stringify(trackData)
                                                }
                                              );
                                              if (!response.ok) throw new Error('Failed to add track');
                                              alert(`✅ "${track.title}" added to "${targetCrateName}"!`);
                                            } catch (error) {
                                              console.error('Error adding track to crate:', error);
                                              alert(`Failed to add track: ${error.message}`);
                                            }
                                          }}
                                          title={`Add to ${targetCrateName || 'Crate'}`}
                                          style={{
                                            background: 'rgba(0, 255, 255, 0.15)',
                                            border: '1px solid rgba(0, 255, 255, 0.4)',
                                            borderRadius: '6px',
                                            padding: '0.4rem 0.6rem',
                                            cursor: 'pointer',
                                            color: '#00ffff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.3rem'
                                          }}
                                        >
                                          📦
                                        </button>
                                      ) : user ? (
                                        /* General Add to Crate — shows crate picker */
                                        <div style={{ position: 'relative' }}>
                                          <button
                                            className="track-action-icon"
                                            data-testid={`add-to-crate-${track.id}`}
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (quickCratePickerTrack === track.id) {
                                                setQuickCratePickerTrack(null);
                                              } else {
                                                setQuickCratePickerTrack(track.id);
                                                if (quickCrateList.length === 0) {
                                                  try {
                                                    const token = localStorage.getItem('beatseek_token');
                                                    const API = process.env.REACT_APP_BACKEND_URL;
                                                    const res = await fetch(`${API}/api/crates`, {
                                                      headers: { 'Authorization': `Bearer ${token}` }
                                                    });
                                                    if (res.ok) {
                                                      const data = await res.json();
                                                      setQuickCrateList(Array.isArray(data) ? data : data.crates || []);
                                                    }
                                                  } catch (err) { console.error('Crate fetch:', err); }
                                                }
                                              }
                                            }}
                                            title="Add to Crate"
                                            style={{
                                              background: 'rgba(0, 255, 255, 0.15)',
                                              border: '1px solid rgba(0, 255, 255, 0.4)',
                                              borderRadius: '6px',
                                              padding: '0.4rem 0.6rem',
                                              cursor: 'pointer',
                                              color: '#00ffff',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '0.3rem',
                                              fontSize: '12px',
                                              transition: 'all 0.15s',
                                            }}
                                          >
                                            📦
                                          </button>
                                          {quickCratePickerTrack === track.id && (
                                            <div
                                              data-testid={`crate-picker-${track.id}`}
                                              style={{
                                                position: 'absolute', bottom: '110%', right: 0,
                                                background: 'rgba(15,15,20,0.98)',
                                                border: '1px solid rgba(0, 255, 255, 0.3)',
                                                borderRadius: '10px', padding: '6px',
                                                minWidth: '180px', maxHeight: '200px', overflowY: 'auto',
                                                zIndex: 10000, boxShadow: '0 8px 30px rgba(0,0,0,0.7)',
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <div style={{ fontSize: '9px', color: '#00ffff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', padding: '4px 8px', marginBottom: '2px' }}>
                                                Add to Crate
                                              </div>
                                              {quickCrateList.length === 0 ? (
                                                <div style={{ padding: '8px', color: '#666', fontSize: '11px' }}>No crates found</div>
                                              ) : quickCrateList.map((crate) => (
                                                <button
                                                  key={crate.id || crate._id}
                                                  data-testid={`crate-option-${crate.id || crate._id}`}
                                                  onClick={async () => {
                                                    try {
                                                      const token = localStorage.getItem('beatseek_token');
                                                      const API = process.env.REACT_APP_BACKEND_URL;
                                                      const crateId = crate.id || crate._id;
                                                      const trackData = {
                                                        track_id: track.id || `discovery_${Date.now()}`,
                                                        source: track.source || 'discovery',
                                                        title: track.title, artist: track.artist,
                                                        album: track.album || '',
                                                        duration_ms: track.duration_ms || 0,
                                                        artwork_url: track.artwork || track.artwork_url || '',
                                                        preview_url: track.preview_url || null,
                                                        spotify_uri: track.spotify_uri || track.uri || null,
                                                        external_url: track.external_url || track.url || null,
                                                      };
                                                      const res = await fetch(`${API}/api/crates/${crateId}/tracks`, {
                                                        method: 'POST',
                                                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                                        body: JSON.stringify(trackData)
                                                      });
                                                      if (!res.ok) throw new Error('Failed');
                                                      alert(`✅ "${track.title}" added to "${crate.name}"!`);
                                                      setQuickCratePickerTrack(null);
                                                    } catch (err) {
                                                      alert(`Failed: ${err.message}`);
                                                    }
                                                  }}
                                                  style={{
                                                    width: '100%', textAlign: 'left',
                                                    padding: '6px 8px', background: 'transparent',
                                                    border: 'none', borderRadius: '6px',
                                                    color: '#ddd', fontSize: '12px', cursor: 'pointer',
                                                    transition: 'background 0.15s', display: 'block',
                                                  }}
                                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)'; }}
                                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                >
                                                  <span style={{ fontWeight: 600 }}>{crate.name}</span>
                                                  <span style={{ fontSize: '10px', color: '#666', marginLeft: '6px' }}>
                                                    {crate.tracks?.length || 0} tracks
                                                  </span>
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ) : null}
                                  </div>
                                </div>

                                {/* Right: Custom Audio Player */}
                                {track.preview_url && (
                                  <div className="discovery-player-wrap" style={{ marginTop: '3.5rem', flexShrink: 0 }}>
                                    <CustomAudioPlayer 
                                      trackId={track.id}
                                      previewUrl={track.preview_url}
                                      trackTitle={track.title}
                                      trackArtist={track.artist}
                                    />
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                      
                      {/* Pagination: Previous/Next with Page Numbers */}
                      <div className="discovery-pagination">
                        <div className="pagination-info">
                          Showing tracks {((currentPage - 1) * tracksPerPage) + 1} - {Math.min(currentPage * tracksPerPage, ((currentPage - 1) * tracksPerPage) + discogsTracks.length)}
                          {filterMode === 'advanced' && selectedSources.length > 0 && (
                            <span style={{ marginLeft: '8px', color: 'rgba(255,255,255,0.5)' }}>
                              from {selectedSources.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
                            </span>
                          )}
                        </div>
                        
                        <div className="pagination-controls">
                          {currentPage > 1 && (
                            <Button 
                              onClick={() => {
                                // Set flag to indicate pagination was triggered by user
                                paginationTriggerRef.current = true;
                                setCurrentPage(currentPage - 1);
                              }}
                              className="pagination-btn prev-btn"
                              disabled={isLoadingDiscogs}
                            >
                              ← Previous
                            </Button>
                          )}
                          
                          <div className="page-number-display">
                            Page {currentPage}
                          </div>
                          
                          {hasMoreTracks && (
                            <Button 
                              onClick={() => {
                                // Set flag to indicate pagination was triggered by user
                                paginationTriggerRef.current = true;
                                console.log('Discover More clicked - Current page:', currentPage, 'Moving to:', currentPage + 1);
                                setCurrentPage(currentPage + 1);
                              }}
                              disabled={isLoadingDiscogs}
                              className="pagination-btn next-btn"
                            >
                              {isLoadingDiscogs ? '⏳ Loading...' : 'Discover More →'}
                            </Button>
                          )}
                        </div>
                        
                        {!hasMoreTracks && currentPage > 1 && (
                          <p className="end-of-results">🎉 You&apos;ve reached the end! Try a different genre or search.</p>
                        )}
                        
                        {/* Refresh hint for more results - clickable */}
                        <p style={{
                          textAlign: 'center',
                          marginTop: '1rem',
                          fontSize: '0.85rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontStyle: 'italic'
                        }}>
                          💡 Tap{' '}
                          <span 
                            onClick={() => {
                              const genre = filterMode === 'advanced' ? advancedGenre : selectedGenre;
                              setCurrentPage(1);
                              loadDiscoverContent(genre, discoverySearchQuery, false, 1, true);
                            }}
                            style={{ 
                              color: '#00ffff', 
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              fontWeight: '600'
                            }}
                          >
                            Refresh
                          </span>
                          {' '}for different results
                        </p>
                      </div>
                      </>
                    ) : !showArtistExplorer ? (
                      <div className="no-results" style={{
                        textAlign: 'center',
                        padding: '3rem 2rem',
                        background: 'rgba(26, 26, 46, 0.6)',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 255, 255, 0.2)'
                      }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                          {filterMode === 'advanced' && selectedSources.includes('soundcloud') && !soundcloudConnected ? '☁️' : '🔍'}
                        </div>
                        <p style={{ 
                          fontSize: '1.1rem', 
                          color: 'rgba(255, 255, 255, 0.9)',
                          marginBottom: '0.5rem'
                        }}>
                          {filterMode === 'advanced' && selectedSources.includes('soundcloud') && !soundcloudConnected 
                            ? 'SoundCloud Connection Required'
                            : 'No tracks found'
                          }
                        </p>
                        <p style={{ 
                          fontSize: '0.9rem', 
                          color: 'rgba(255, 255, 255, 0.6)',
                          marginBottom: '1.5rem'
                        }}>
                          {filterMode === 'advanced' && selectedSources.includes('soundcloud') && !soundcloudConnected 
                            ? 'Please connect your SoundCloud account to search tracks'
                            : filterMode === 'advanced' 
                              ? `Try adjusting your ${selectedSources[0] || 'source'} filters or tap Refresh for different results`
                              : discoverySearchQuery 
                                ? `No results for "${discoverySearchQuery}"`
                                : `No results for ${selectedGenre}`
                          }
                        </p>
                        {filterMode === 'advanced' && selectedSources.includes('soundcloud') && !soundcloudConnected ? (
                          <Button 
                            onClick={connectSoundCloud}
                            style={{
                              background: 'linear-gradient(135deg, rgba(255, 85, 0, 0.3), rgba(255, 136, 0, 0.3))',
                              border: '1px solid rgba(255, 85, 0, 0.5)',
                              color: '#ff5500'
                            }}
                          >
                            ☁️ Connect SoundCloud
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => {
                              const genre = filterMode === 'advanced' ? advancedGenre : selectedGenre;
                              loadDiscoverContent(genre, discoverySearchQuery, false, 1, true);
                            }}
                            style={{
                              background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.3), rgba(255, 0, 255, 0.3))',
                              border: '1px solid rgba(0, 255, 255, 0.5)',
                              color: '#00ffff'
                            }}
                          >
                            🔄 Refresh Results
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </div>
              </div>
            </TabsContent>

            {/* AI Similar Tracks Modal */}
            <SimilarTracksModal
              isOpen={showSimilarTracksModal}
              onClose={() => setShowSimilarTracksModal(false)}
              sourceTrack={similarTracksSource}
              onSearchInDiscovery={(query) => {
                setDiscoverySearchQuery(query);
                setSearchFilterEnabled(true);
                setShowSimilarTracksModal(false);
                setCameFromSimilarTracks(true);
                loadDiscoverContent(selectedGenre, query);
              }}
              onSaveToSpotify={(t) => {
                setSpotifyTrackToSave({ ...t, title: t.title, artwork: t.artwork });
                setShowSpotifyModal(true);
              }}
              onShareToDiscord={(t) => {
                setDiscordShareTrack({ ...t, title: t.title, artwork: t.artwork });
                setShowDiscordShareModal(true);
              }}
            />

            {/* THE WALL */}
            <TheWall
              isOpen={showTheWall}
              onClose={() => setShowTheWall(false)}
              currentUser={user}
            />

            {/* Discovery Zone → Share to The Wall (Phase 2) */}
            <ShareToWallModal
              isOpen={!!shareToWallTrack}
              onClose={() => setShareToWallTrack(null)}
              source="discovery"
              track={shareToWallTrack}
              currentUser={user}
            />

            {/* YouTube Modal */}
            {showYouTubeModal && selectedYouTubeTrack && ReactDOM.createPortal(
              <div 
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.85)',
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10003
                }}
                onClick={() => {
                  setShowYouTubeModal(false);
                  setYoutubeVideoData(null);
                  setYoutubeError(null);
                }}
              >
                <div 
                  style={{
                    width: '90%',
                    maxWidth: '900px',
                    background: 'rgba(26, 26, 46, 0.95)',
                    borderRadius: '16px',
                    padding: '1rem',
                    overflow: 'hidden',
                    position: 'relative',
                    border: '5px solid transparent',
                    backgroundImage: `
                      linear-gradient(rgba(26, 26, 46, 0.95), rgba(26, 26, 46, 0.95)),
                      linear-gradient(135deg, #00ffff 0%, #ff00ff 50%, #00ffff 100%)
                    `,
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '1px solid rgba(0, 255, 255, 0.2)'
                  }}>
                    <h3 style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      margin: 0,
                      background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      YouTube on BEATSEEK
                    </h3>
                    <button
                      onClick={() => {
                        setShowYouTubeModal(false);
                        setYoutubeVideoData(null);
                        setYoutubeError(null);
                      }}
                      style={{
                        background: 'rgba(255, 0, 128, 0.2)',
                        border: '1px solid rgba(255, 0, 128, 0.4)',
                        color: '#ff0080',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.25rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 0, 128, 0.4)';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 0, 128, 0.2)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Track Info */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <p style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#fff',
                      margin: '0 0 0.25rem 0'
                    }}>
                      {selectedYouTubeTrack.title}
                    </p>
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'rgba(255, 255, 255, 0.6)',
                      margin: 0
                    }}>
                      {selectedYouTubeTrack.artist}
                    </p>
                  </div>

                  {/* Video Content */}
                  {loadingYoutubeVideo && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4rem 2rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '12px'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                      <h4 style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: '600', 
                        color: '#fff',
                        margin: '0 0 0.5rem 0'
                      }}>
                        Searching YouTube...
                      </h4>
                      <p style={{ 
                        fontSize: '0.95rem', 
                        color: 'rgba(255, 255, 255, 0.6)',
                        margin: 0
                      }}>
                        Finding the perfect video for this track
                      </p>
                    </div>
                  )}
                  
                  {!loadingYoutubeVideo && youtubeError && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4rem 2rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '12px'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
                      <h4 style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: '600', 
                        color: '#fff',
                        margin: '0 0 0.5rem 0'
                      }}>
                        Video Not Found
                      </h4>
                      <p style={{ 
                        fontSize: '0.95rem', 
                        color: 'rgba(255, 255, 255, 0.6)',
                        margin: '0 0 1.5rem 0'
                      }}>
                        {youtubeError}
                      </p>
                      <Button 
                        onClick={() => window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(selectedYouTubeTrack.artist + ' ' + selectedYouTubeTrack.title + ' official audio')}`, '_blank')}
                        style={{
                          background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          color: '#fff',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        🔍 Search on YouTube
                      </Button>
                    </div>
                  )}
                  
                  {!loadingYoutubeVideo && !youtubeError && youtubeVideoData && youtubeVideoData.video_id && (
                    <div style={{ 
                      position: 'relative', 
                      width: '100%',
                      paddingTop: '56.25%',
                      background: '#000',
                      borderRadius: '12px',
                      overflow: 'hidden'
                    }}>
                      <iframe
                        src={`https://www.youtube.com/embed/${youtubeVideoData.video_id}?autoplay=1&controls=1&rel=0`}
                        title={youtubeVideoData.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 'none'
                        }}
                      />
                    </div>
                  )}

                  {/* Footer */}
                  <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                      margin: 0
                    }}>
                      🎵 Powered by YouTube • Keeping you on BEATSEEK
                    </p>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* Custom YouTube Redirect Alert Modal */}
            {showYouTubeRedirectAlert && (
              <div className="youtube-modal-overlay" onClick={() => setShowYouTubeRedirectAlert(false)}>
                <div className="youtube-redirect-alert" onClick={(e) => e.stopPropagation()}>
                  <div className="redirect-alert-icon">🎵</div>
                  <h3 className="redirect-alert-title">Heads Up!</h3>
                  <p className="redirect-alert-message">
                    This video isn&apos;t available for embedded playback on BEATSEEK.
                  </p>
                  <p className="redirect-alert-message">
                    You&apos;ll be redirected to YouTube to watch it there.
                  </p>
                  <p className="redirect-alert-community">
                    ✨ Don&apos;t forget to come back for more music &amp; our amazing community!
                  </p>
                  <div className="redirect-alert-buttons">
                    <Button
                      onClick={() => {
                        window.open(youtubeRedirectUrl, '_blank');
                        setShowYouTubeRedirectAlert(false);
                      }}
                      className="redirect-confirm-btn"
                    >
                      Continue to YouTube →
                    </Button>
                    <Button
                      onClick={() => setShowYouTubeRedirectAlert(false)}
                      className="redirect-cancel-btn"
                    >
                      Stay on BEATSEEK
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Track DNA Popup Modal */}
            {trackDnaModal && ReactDOM.createPortal(
              <div
                data-testid="track-dna-modal"
                style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0, 0, 0, 0.85)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 10004,
                }}
                onClick={() => setTrackDnaModal(null)}
              >
                <div
                  style={{
                    width: '90%', maxWidth: '420px',
                    background: 'rgba(16, 16, 32, 0.97)',
                    borderRadius: '14px', overflow: 'hidden',
                    border: '1px solid rgba(255, 122, 0, 0.3)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 30px rgba(255,122,0,0.1)',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div style={{
                    padding: '16px 20px 12px',
                    borderBottom: '1px solid rgba(255,122,0,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#ff7a00', fontWeight: '700', letterSpacing: '0.08em', marginBottom: '2px' }}>
                        TRACK DNA
                      </div>
                      <div style={{ fontSize: '14px', color: '#fff', fontWeight: '600' }}>
                        {trackDnaModal.trackTitle}
                      </div>
                      <div style={{ fontSize: '12px', color: '#aaa' }}>
                        {trackDnaModal.trackArtist}
                      </div>
                    </div>
                    <button
                      onClick={() => setTrackDnaModal(null)}
                      style={{ background: 'none', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer', padding: '4px' }}
                      data-testid="track-dna-close"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Body */}
                  <div style={{ padding: '16px 20px', maxHeight: '65vh', overflowY: 'auto' }}>
                    {(() => {
                      const analysisMap = {};
                      (trackDnaModal.audio_analysis || []).forEach(a => { analysisMap[a.key] = parseFloat(a.value) || 0; });
                      const axes = [
                        { key: 'energy', label: 'Energy', color: '#ff7a00' },
                        { key: 'danceability', label: 'Dance', color: '#fbbf24' },
                        { key: 'valence', label: 'Mood', color: '#34d399' },
                        { key: 'liveness', label: 'Live', color: '#a78bfa' },
                        { key: 'acousticness', label: 'Acoustic', color: '#60a5fa' },
                      ].filter(a => analysisMap[a.key] !== undefined);

                      const cx = 100, cy = 100, maxR = 72;
                      const angleStep = axes.length > 0 ? (2 * Math.PI) / axes.length : 0;
                      const startAngle = -Math.PI / 2;
                      const getPoint = (i, val) => ({
                        x: cx + maxR * val * Math.cos(startAngle + i * angleStep),
                        y: cy + maxR * val * Math.sin(startAngle + i * angleStep),
                      });

                      const tempoVal = analysisMap['tempo'] ? Math.round(analysisMap['tempo']) : null;
                      const keyVal = (trackDnaModal.audio_analysis || []).find(a => a.key === 'key')?.value;
                      const links = trackDnaModal.track_info?.links || [];
                      const activities = trackDnaModal.activities || [];
                      const sourceColors = {
                        beatport: '#94d500', traxsource: '#1da0c3', tracklist: '#ff6600',
                        spotify: '#1db954', apple_music: '#fc3c44', soundcloud: '#ff5500',
                        youtube: '#ff0000', tidal: '#000', amazon: '#ff9900', deezer: '#a238ff',
                      };
                      const sourceLabels = {
                        beatport: 'Beatport', traxsource: 'Traxsource', tracklist: '1001TL',
                        spotify: 'Spotify', apple_music: 'Apple Music', soundcloud: 'SoundCloud',
                        youtube: 'YouTube', tidal: 'Tidal', amazon: 'Amazon', deezer: 'Deezer',
                      };

                      return (
                        <>
                          {/* Radar Chart */}
                          {axes.length >= 3 && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                              <svg width="200" height="200" viewBox="0 0 200 200" style={{ overflow: 'visible' }}>
                                {[0.25, 0.5, 0.75, 1.0].map((level) => {
                                  const pts = axes.map((_, i) => getPoint(i, level));
                                  return <path key={level} d={pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z'} fill="none" stroke="rgba(255,122,0,0.1)" strokeWidth="0.5" />;
                                })}
                                {axes.map((_, i) => {
                                  const end = getPoint(i, 1);
                                  return <line key={i} x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="rgba(255,122,0,0.15)" strokeWidth="0.5" />;
                                })}
                                <path
                                  d={axes.map((a, i) => {
                                    const p = getPoint(i, Math.max(analysisMap[a.key], 0.05));
                                    return `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`;
                                  }).join(' ') + ' Z'}
                                  fill="rgba(255,122,0,0.15)" stroke="#ff7a00" strokeWidth="1.5"
                                  style={{ animation: 'radarFadeIn 0.8s ease-out forwards' }}
                                />
                                {axes.map((a, i) => {
                                  const p = getPoint(i, Math.max(analysisMap[a.key], 0.05));
                                  return <circle key={i} cx={p.x} cy={p.y} r="3.5" fill={a.color} stroke="#0a0f1e" strokeWidth="1" style={{ animation: `radarDotPop 0.5s ease-out ${0.1 * i}s both` }} />;
                                })}
                                {axes.map((a, i) => {
                                  const lp = getPoint(i, 1.25);
                                  return <text key={a.key} x={lp.x} y={lp.y} fill={a.color} fontSize="10" fontWeight="600" textAnchor="middle" dominantBaseline="middle">{a.label}</text>;
                                })}
                              </svg>
                            </div>
                          )}

                          {/* BPM + Key + Percentages */}
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '14px' }}>
                            {tempoVal && (
                              <div style={{ padding: '5px 12px', background: 'rgba(255,122,0,0.1)', border: '1px solid rgba(255,122,0,0.25)', borderRadius: '6px', textAlign: 'center' }}>
                                <div style={{ fontSize: '9px', color: '#888' }}>BPM</div>
                                <div style={{ fontSize: '16px', color: '#ff7a00', fontWeight: '700' }}>{tempoVal}</div>
                              </div>
                            )}
                            {keyVal && (
                              <div style={{ padding: '5px 12px', background: 'rgba(255,122,0,0.1)', border: '1px solid rgba(255,122,0,0.25)', borderRadius: '6px', textAlign: 'center' }}>
                                <div style={{ fontSize: '9px', color: '#888' }}>Key</div>
                                <div style={{ fontSize: '16px', color: '#ff7a00', fontWeight: '700' }}>{keyVal}</div>
                              </div>
                            )}
                            {axes.map((a) => (
                              <div key={a.key} style={{ padding: '5px 12px', background: 'rgba(255,122,0,0.06)', border: '1px solid rgba(255,122,0,0.15)', borderRadius: '6px', textAlign: 'center' }}>
                                <div style={{ fontSize: '9px', color: '#888' }}>{a.label}</div>
                                <div style={{ fontSize: '16px', color: a.color, fontWeight: '700' }}>{Math.round(analysisMap[a.key] * 100)}%</div>
                              </div>
                            ))}
                          </div>

                          {/* Cross-Platform Links */}
                          {links.length > 0 && (
                            <div style={{ marginBottom: '14px' }}>
                              <div style={{ fontSize: '10px', color: '#888', marginBottom: '6px', fontWeight: '600' }}>AVAILABLE ON</div>
                              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                {Object.values(links
                                  .filter(l => Object.keys(sourceColors).includes(l.source))
                                  .reduce((acc, l) => {
                                    if (!acc[l.source] || (!acc[l.source].url && l.url)) acc[l.source] = l;
                                    return acc;
                                  }, {}))
                                  .filter(l => l.url)
                                  .map((link) => (
                                  <a key={link.source} href={link.url} target="_blank" rel="noopener noreferrer"
                                    style={{
                                      padding: '3px 8px', fontSize: '11px', fontWeight: '600',
                                      background: `${sourceColors[link.source]}15`, border: `1px solid ${sourceColors[link.source]}35`,
                                      borderRadius: '4px', color: sourceColors[link.source], textDecoration: 'none',
                                      transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = `${sourceColors[link.source]}30`; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = `${sourceColors[link.source]}15`; }}
                                  >{sourceLabels[link.source] || link.source}</a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* DJ Supports */}
                          {activities.length > 0 && (
                            <div style={{ marginBottom: '14px' }}>
                              <div style={{ fontSize: '10px', color: '#888', marginBottom: '6px', fontWeight: '600' }}>DJ SUPPORT & ACTIVITY</div>
                              {activities.slice(0, 5).map((act, idx) => (
                                <div key={idx} style={{ padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: '6px', alignItems: 'center' }}>
                                  <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(255,122,0,0.15)', color: '#ff7a00', fontWeight: '600', flexShrink: 0 }}>
                                    {act.source === 'tracklist' ? '1001TL' : act.source}
                                  </span>
                                  <span style={{ fontSize: '11px', color: '#aaa' }}>{act.activity_text?.substring(0, 100)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Footer */}
                  <div style={{
                    padding: '12px 20px',
                    borderTop: '1px solid rgba(255,122,0,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <a href={trackDnaModal.track_info?.songstats_track_url || 'https://songstats.com'} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '10px', color: '#ff7a00', textDecoration: 'none', fontStyle: 'italic' }}>
                      Powered by Songstats
                    </a>
                    <button
                      data-testid="track-dna-back-btn"
                      onClick={() => setTrackDnaModal(null)}
                      style={{
                        padding: '6px 16px', fontSize: '12px', fontWeight: '600',
                        background: 'rgba(255,122,0,0.15)', border: '1px solid rgba(255,122,0,0.4)',
                        borderRadius: '6px', color: '#ff7a00', cursor: 'pointer',
                      }}
                    >
                      Back to Tracks
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* Artist Deep Dive Modal */}
            {showArtistModal && ReactDOM.createPortal(
              <div
                data-testid="artist-deep-dive-modal"
                style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0, 0, 0, 0.88)',
                  backdropFilter: 'blur(12px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 10002
                }}
                onClick={() => setShowArtistModal(false)}
              >
                <div
                  style={{
                    width: '92%', maxWidth: '720px', maxHeight: '85vh',
                    background: 'rgba(16, 16, 32, 0.97)',
                    borderRadius: '16px', overflow: 'hidden',
                    position: 'relative',
                    border: '2px solid rgba(0, 212, 255, 0.25)',
                    display: 'flex', flexDirection: 'column'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Banner / Header */}
                  <div style={{
                    position: 'relative', minHeight: '160px',
                    background: artistModalData?.artwork?.banner
                      ? `url(${artistModalData.artwork.banner}) center/cover`
                      : artistModalData?.artwork?.fanart
                      ? `url(${artistModalData.artwork.fanart}) center/cover`
                      : 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 100%)',
                    display: 'flex', alignItems: 'flex-end', padding: '16px'
                  }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent 30%, rgba(16,16,32,0.95) 100%)' }} />
                    <div style={{ position: 'relative', display: 'flex', gap: '14px', alignItems: 'flex-end', width: '100%' }}>
                      {artistModalData?.artwork?.thumb && (
                        <img src={artistModalData.artwork.thumb} alt="" style={{
                          width: '72px', height: '72px', borderRadius: '50%',
                          objectFit: 'cover', border: '3px solid rgba(0,212,255,0.4)', flexShrink: 0
                        }} onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                          {artistModalData?.name || 'Loading...'}
                        </h2>
                        {artistModalData?.genre && (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                            <span style={{ padding: '2px 8px', background: 'rgba(0,212,255,0.2)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: '10px', fontSize: '11px', color: '#00d4ff', fontWeight: 600 }}>
                              {artistModalData.genre}
                            </span>
                            {artistModalData?.style && artistModalData.style !== artistModalData.genre && (
                              <span style={{ padding: '2px 8px', background: 'rgba(138,43,226,0.2)', border: '1px solid rgba(138,43,226,0.3)', borderRadius: '10px', fontSize: '11px', color: '#b06aff', fontWeight: 600 }}>
                                {artistModalData.style}
                              </span>
                            )}
                            {artistModalData?.mood && (
                              <span style={{ padding: '2px 8px', background: 'rgba(255,165,0,0.2)', border: '1px solid rgba(255,165,0,0.3)', borderRadius: '10px', fontSize: '11px', color: '#ffa500', fontWeight: 600 }}>
                                {artistModalData.mood}
                              </span>
                            )}
                          </div>
                        )}
                        {!artistModalData?.genre && artistMbData?.top_tags?.length > 0 && (
                          <div data-testid="mb-header-tags" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                            {artistMbData.top_tags.slice(0, 3).map((tag, idx) => (
                              <span key={idx} style={{
                                padding: '2px 8px',
                                background: idx === 0 ? 'rgba(0,212,255,0.2)' : 'rgba(138,43,226,0.2)',
                                border: `1px solid ${idx === 0 ? 'rgba(0,212,255,0.3)' : 'rgba(138,43,226,0.3)'}`,
                                borderRadius: '10px', fontSize: '11px',
                                color: idx === 0 ? '#00d4ff' : '#b06aff', fontWeight: 600
                              }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        {artistModalData?.country && (
                          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                            {artistModalData.country}{artistModalData.formed_year ? ` · Since ${artistModalData.formed_year}` : ''}
                            {!artistModalData.formed_year && artistMbData?.begin_date ? ` · Since ${artistMbData.begin_date}` : ''}
                          </p>
                        )}
                        {!artistModalData?.country && artistMbData?.country && (
                          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                            {artistMbData.country}{artistMbData.begin_date ? ` · Since ${artistMbData.begin_date}` : ''}
                            {artistMbData.ended ? ` · Ended ${artistMbData.end_date || ''}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Close button */}
                    <button onClick={() => setShowArtistModal(false)} style={{
                      position: 'absolute', top: '12px', right: '12px',
                      background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff', width: '32px', height: '32px', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1rem', cursor: 'pointer', zIndex: 2
                    }}>✕</button>
                  </div>

                  {/* Scrollable Content */}
                  <div style={{ overflowY: 'auto', padding: '16px', flex: 1 }}>
                    {artistModalLoading ? (
                      <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '8px' }}>⏳</div>
                        <p style={{ color: '#888' }}>Loading artist data...</p>
                      </div>
                    ) : artistModalData?.notFound && !artistMbData ? (
                      <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <p style={{ color: '#888', fontSize: '14px' }}>No detailed info available for this artist yet.</p>
                      </div>
                    ) : (
                      <>
                        {/* Bio */}
                        {artistModalData?.bio && (
                          <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ fontSize: '13px', color: '#00d4ff', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>About</h4>
                            <p style={{ fontSize: '13px', color: '#bbb', lineHeight: 1.7, margin: 0 }}>
                              {artistModalData.bio}
                            </p>
                          </div>
                        )}

                        {/* MusicBrainz Fallback Info (when no TheAudioDB bio) */}
                        {!artistModalData?.bio && artistMbData && (
                          <div data-testid="mb-fallback-info" style={{ marginBottom: '20px' }}>
                            <h4 style={{ fontSize: '13px', color: '#00d4ff', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Artist Info</h4>
                            {artistMbData.disambiguation && (
                              <p style={{ fontSize: '13px', color: '#bbb', lineHeight: 1.7, margin: '0 0 8px' }}>
                                {artistMbData.disambiguation}
                              </p>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                              {artistMbData.type && (
                                <span style={{ padding: '3px 10px', background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.25)', borderRadius: '12px', fontSize: '11px', color: '#00d4ff', fontWeight: 600 }}>
                                  {artistMbData.type}
                                </span>
                              )}
                              {artistMbData.country && (
                                <span style={{ padding: '3px 10px', background: 'rgba(138,43,226,0.12)', border: '1px solid rgba(138,43,226,0.25)', borderRadius: '12px', fontSize: '11px', color: '#b06aff', fontWeight: 600 }}>
                                  {artistMbData.country}
                                </span>
                              )}
                              {artistMbData.begin_date && (
                                <span style={{ padding: '3px 10px', background: 'rgba(255,165,0,0.12)', border: '1px solid rgba(255,165,0,0.25)', borderRadius: '12px', fontSize: '11px', color: '#ffa500', fontWeight: 600 }}>
                                  {artistMbData.ended ? `${artistMbData.begin_date} - ${artistMbData.end_date || '?'}` : `Active since ${artistMbData.begin_date}`}
                                </span>
                              )}
                              {artistMbData.rating && (
                                <span style={{ padding: '3px 10px', background: 'rgba(255,215,0,0.12)', border: '1px solid rgba(255,215,0,0.25)', borderRadius: '12px', fontSize: '11px', color: '#ffd700', fontWeight: 600 }}>
                                  Rating: {artistMbData.rating}/5
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* MusicBrainz Genre Tags */}
                        {artistMbData?.tags?.length > 0 && (
                          <div data-testid="mb-genre-tags" style={{ marginBottom: '20px' }}>
                            <h4 style={{ fontSize: '13px', color: '#ff00ff', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Genre Tags</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {artistMbData.tags.slice(0, 12).map((tag, idx) => (
                                <span key={idx} style={{
                                  padding: '3px 10px',
                                  background: idx < 3 ? 'rgba(255,0,255,0.15)' : 'rgba(255,0,255,0.06)',
                                  border: `1px solid rgba(255,0,255,${idx < 3 ? 0.35 : 0.15})`,
                                  borderRadius: '12px', fontSize: '11px', color: '#ff66ff', fontWeight: idx < 3 ? 600 : 500
                                }}>
                                  {tag.name}
                                  {tag.count > 0 && <span style={{ marginLeft: '4px', opacity: 0.5, fontSize: '9px' }}>({tag.count})</span>}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* MusicBrainz Related Artists */}
                        {artistMbData?.related_artists?.length > 0 && (
                          <div data-testid="mb-related-artists" style={{ marginBottom: '20px' }}>
                            <h4 style={{ fontSize: '13px', color: '#00ff88', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Related Artists</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {artistMbData.related_artists.map((ra, idx) => (
                                <button key={idx} onClick={() => openArtistDeepDive(ra.name)} style={{
                                  padding: '4px 12px', background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)',
                                  borderRadius: '12px', fontSize: '11px', color: '#00ff88', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,255,136,0.18)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,255,136,0.08)'; }}
                                >
                                  {ra.name}
                                  <span style={{ marginLeft: '4px', opacity: 0.5, fontSize: '9px' }}>({ra.relationship})</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* MusicBrainz External Links */}
                        {artistMbData?.urls && Object.keys(artistMbData.urls).length > 0 && (
                          <div data-testid="mb-external-links" style={{ marginBottom: '20px' }}>
                            <h4 style={{ fontSize: '13px', color: '#00d4ff', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Links</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {Object.entries(artistMbData.urls).map(([type, url]) => (
                                <a key={type} href={url} target="_blank" rel="noopener noreferrer" style={{
                                  padding: '4px 12px', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)',
                                  borderRadius: '12px', fontSize: '11px', color: '#00d4ff', fontWeight: 500, textDecoration: 'none', transition: 'all 0.15s'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.18)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.08)'; }}
                                >
                                  {type.replace(/_/g, ' ')}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Music Videos */}
                        {artistVideos.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ fontSize: '13px', color: '#ff4444', marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Music Videos</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {artistVideos.map((vid, idx) => {
                                const videoId = extractYoutubeVideoId(vid.url);
                                return (
                                  <button
                                    key={idx}
                                    data-testid={`artist-video-${idx}`}
                                    onClick={() => {
                                      if (videoId) {
                                        setSelectedYouTubeTrack({ title: vid.track, artist: artistModalData?.name || '' });
                                        setYoutubeVideoData({ found: true, video_id: videoId, title: `${artistModalData?.name} - ${vid.track}` });
                                        setYoutubeError(null);
                                        setLoadingYoutubeVideo(false);
                                        setShowYouTubeModal(true);
                                      }
                                    }}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '10px',
                                      padding: '8px 12px', background: 'rgba(255,0,0,0.08)',
                                      border: '1px solid rgba(255,0,0,0.2)', borderRadius: '8px',
                                      color: '#fff', fontSize: '13px', cursor: videoId ? 'pointer' : 'default',
                                      textAlign: 'left', transition: 'all 0.15s ease', width: '100%',
                                      opacity: videoId ? 1 : 0.5
                                    }}
                                    onMouseEnter={(e) => videoId && (e.currentTarget.style.background = 'rgba(255,0,0,0.18)')}
                                    onMouseLeave={(e) => videoId && (e.currentTarget.style.background = 'rgba(255,0,0,0.08)')}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#ff4444"><path d="M8 5v14l11-7z"/></svg>
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vid.track}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Discography */}
                        {artistAlbums.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ fontSize: '13px', color: '#b06aff', marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Discography</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
                              {artistAlbums.map((album, idx) => (
                                <div key={idx} style={{
                                  background: 'rgba(138,43,226,0.08)', border: '1px solid rgba(138,43,226,0.15)',
                                  borderRadius: '10px', padding: '10px', textAlign: 'center'
                                }}>
                                  {album.artwork?.front ? (
                                    <img src={album.artwork.front} alt={album.title} style={{
                                      width: '100%', aspectRatio: '1', objectFit: 'cover',
                                      borderRadius: '6px', marginBottom: '6px'
                                    }} onError={(e) => { e.target.style.display = 'none'; }} />
                                  ) : (
                                    <div style={{
                                      width: '100%', aspectRatio: '1', background: 'rgba(138,43,226,0.15)',
                                      borderRadius: '6px', marginBottom: '6px', display: 'flex',
                                      alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#b06aff'
                                    }}>LP</div>
                                  )}
                                  <p style={{ fontSize: '11px', fontWeight: 600, color: '#fff', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{album.title}</p>
                                  {album.year && <p style={{ fontSize: '10px', color: '#888', margin: 0 }}>{album.year}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* MusicBrainz Releases Fallback (when no TheAudioDB albums) */}
                        {artistAlbums.length === 0 && artistMbReleases.length > 0 && (
                          <div data-testid="mb-releases-fallback" style={{ marginBottom: '20px' }}>
                            <h4 style={{ fontSize: '13px', color: '#b06aff', marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Discography</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {artistMbReleases.map((rel, idx) => (
                                <div key={idx} style={{
                                  display: 'flex', alignItems: 'center', gap: '10px',
                                  padding: '8px 12px', background: 'rgba(138,43,226,0.06)',
                                  border: '1px solid rgba(138,43,226,0.15)', borderRadius: '8px'
                                }}>
                                  <div style={{
                                    width: '36px', height: '36px', borderRadius: '6px', flexShrink: 0,
                                    background: rel.primary_type === 'Album' ? 'rgba(138,43,226,0.2)' : rel.primary_type === 'Single' ? 'rgba(0,212,255,0.2)' : 'rgba(255,165,0,0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '10px', fontWeight: 700,
                                    color: rel.primary_type === 'Album' ? '#b06aff' : rel.primary_type === 'Single' ? '#00d4ff' : '#ffa500'
                                  }}>
                                    {rel.primary_type === 'Album' ? 'LP' : rel.primary_type === 'Single' ? 'SG' : 'EP'}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ fontSize: '12px', fontWeight: 600, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rel.title}</p>
                                    <p style={{ fontSize: '10px', color: '#888', margin: 0 }}>
                                      {rel.primary_type}{rel.first_release_date ? ` · ${rel.first_release_date}` : ''}
                                      {rel.secondary_types?.length > 0 ? ` · ${rel.secondary_types.join(', ')}` : ''}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ═══ MUSICBRAINZ INSIGHTS ═══ */}
                        <div data-testid="mb-insights-section" style={{ marginBottom: '20px' }}>
                          <button
                            data-testid="mb-insights-btn"
                            onClick={async () => {
                              if (showInsights) {
                                setShowInsights(false);
                                return;
                              }
                              if (artistInsights) {
                                setShowInsights(true);
                                return;
                              }
                              setInsightsLoading(true);
                              try {
                                const name = artistModalData?.name || '';
                                const res = await fetch(`${BACKEND_URL}/api/musicbrainz/insights/${encodeURIComponent(name)}`);
                                const json = await res.json();
                                if (json.success && json.data) {
                                  setArtistInsights(json.data);
                                  setShowInsights(true);
                                }
                              } catch (e) {
                                console.error('Insights fetch error:', e);
                              } finally {
                                setInsightsLoading(false);
                              }
                            }}
                            style={{
                              width: '100%', padding: '12px 16px',
                              background: showInsights
                                ? 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(138,43,226,0.2))'
                                : 'linear-gradient(135deg, rgba(0,212,255,0.08), rgba(138,43,226,0.08))',
                              border: '1px solid rgba(0,212,255,0.3)',
                              borderRadius: '10px', cursor: 'pointer',
                              color: '#00d4ff', fontSize: '13px', fontWeight: 700,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                              transition: 'all 0.2s ease',
                              letterSpacing: '0.5px',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.6)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.3)'; }}
                          >
                            {insightsLoading ? (
                              <span style={{ animation: 'pulse 1.5s infinite', fontSize: '12px' }}>Loading Insights...</span>
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                                {showInsights ? 'Hide MusicBrainz Insights' : 'MusicBrainz Insights'}
                              </>
                            )}
                          </button>

                          {showInsights && artistInsights && (
                            <div data-testid="mb-insights-panel" style={{
                              marginTop: '12px', padding: '16px',
                              background: 'rgba(0,0,0,0.3)',
                              border: '1px solid rgba(0,212,255,0.15)',
                              borderRadius: '12px',
                            }}>
                              {/* Artist Stats Bar */}
                              <div data-testid="mb-insights-stats" style={{
                                display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', justifyContent: 'center'
                              }}>
                                {artistInsights.years_active && (
                                  <div style={{
                                    padding: '6px 14px', background: 'rgba(0,212,255,0.1)',
                                    border: '1px solid rgba(0,212,255,0.2)', borderRadius: '20px',
                                    fontSize: '11px', color: '#00d4ff', fontWeight: 600, textAlign: 'center'
                                  }}>
                                    {artistInsights.years_active}
                                  </div>
                                )}
                                {artistInsights.total_releases > 0 && (
                                  <div style={{
                                    padding: '6px 14px', background: 'rgba(138,43,226,0.1)',
                                    border: '1px solid rgba(138,43,226,0.2)', borderRadius: '20px',
                                    fontSize: '11px', color: '#b06aff', fontWeight: 600
                                  }}>
                                    {artistInsights.total_releases} Releases
                                  </div>
                                )}
                                {Object.entries(artistInsights.release_type_counts || {}).map(([type, count]) => (
                                  <div key={type} style={{
                                    padding: '6px 14px',
                                    background: type === 'Album' ? 'rgba(255,165,0,0.1)' : type === 'Single' ? 'rgba(0,255,136,0.1)' : 'rgba(255,0,255,0.1)',
                                    border: `1px solid ${type === 'Album' ? 'rgba(255,165,0,0.2)' : type === 'Single' ? 'rgba(0,255,136,0.2)' : 'rgba(255,0,255,0.2)'}`,
                                    borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                                    color: type === 'Album' ? '#ffa500' : type === 'Single' ? '#00ff88' : '#ff66ff',
                                  }}>
                                    {count} {type}{count !== 1 ? 's' : ''}
                                  </div>
                                ))}
                              </div>

                              {/* Discography Timeline */}
                              {artistInsights.timeline?.length > 0 && (
                                <div data-testid="mb-insights-timeline" style={{ marginBottom: '20px' }}>
                                  <h4 style={{ fontSize: '12px', color: '#00d4ff', marginBottom: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Discography Timeline</h4>
                                  <div style={{ position: 'relative', paddingLeft: '20px' }}>
                                    {/* Timeline line */}
                                    <div style={{
                                      position: 'absolute', left: '6px', top: '4px', bottom: '4px',
                                      width: '2px', background: 'linear-gradient(to bottom, rgba(0,212,255,0.4), rgba(138,43,226,0.4), rgba(255,0,255,0.4))',
                                    }} />
                                    {(() => {
                                      // Group by year
                                      const byYear = {};
                                      artistInsights.timeline.forEach(r => {
                                        const yr = r.year || 'Unknown';
                                        if (!byYear[yr]) byYear[yr] = [];
                                        byYear[yr].push(r);
                                      });
                                      const years = Object.keys(byYear).sort();
                                      return years.map((yr, yIdx) => (
                                        <div key={yr} style={{ marginBottom: '10px' }}>
                                          <div style={{
                                            position: 'relative', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'
                                          }}>
                                            <div style={{
                                              position: 'absolute', left: '-17px',
                                              width: '8px', height: '8px', borderRadius: '50%',
                                              background: '#00d4ff', border: '2px solid rgba(0,0,0,0.5)',
                                            }} />
                                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#00d4ff' }}>{yr}</span>
                                            <span style={{ fontSize: '10px', color: '#555' }}>({byYear[yr].length} release{byYear[yr].length > 1 ? 's' : ''})</span>
                                          </div>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', paddingLeft: '4px' }}>
                                            {byYear[yr].map((r, rIdx) => (
                                              <span key={rIdx} style={{
                                                padding: '2px 8px',
                                                background: r.type === 'Album' ? 'rgba(255,165,0,0.1)' : r.type === 'Single' ? 'rgba(0,255,136,0.06)' : 'rgba(255,0,255,0.08)',
                                                border: `1px solid ${r.type === 'Album' ? 'rgba(255,165,0,0.2)' : r.type === 'Single' ? 'rgba(0,255,136,0.15)' : 'rgba(255,0,255,0.15)'}`,
                                                borderRadius: '8px', fontSize: '10px',
                                                color: r.type === 'Album' ? '#ffa500' : r.type === 'Single' ? '#00ff88' : '#ff66ff',
                                                fontWeight: r.type === 'Album' ? 600 : 400,
                                                maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                              }}>
                                                {r.title}
                                                {r.secondary_types?.length > 0 && (
                                                  <span style={{ opacity: 0.5, marginLeft: '3px' }}>({r.secondary_types.join(', ')})</span>
                                                )}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                </div>
                              )}

                              {/* Label History */}
                              {artistInsights.labels?.length > 0 && (
                                <div data-testid="mb-insights-labels" style={{ marginBottom: '20px' }}>
                                  <h4 style={{ fontSize: '12px', color: '#b06aff', marginBottom: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Label History</h4>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {artistInsights.labels
                                      .filter(l => l.name && l.name !== '[no label]')
                                      .map((label, idx) => (
                                      <div key={idx} style={{
                                        padding: '6px 12px',
                                        background: 'rgba(138,43,226,0.08)',
                                        border: '1px solid rgba(138,43,226,0.2)',
                                        borderRadius: '8px', fontSize: '11px',
                                      }}>
                                        <span style={{ color: '#b06aff', fontWeight: 600 }}>{label.name}</span>
                                        {label.catalog_number && (
                                          <span style={{ color: '#666', marginLeft: '6px', fontSize: '9px' }}>Cat: {label.catalog_number}</span>
                                        )}
                                        {label.first_release && (
                                          <span style={{ color: '#555', marginLeft: '6px', fontSize: '9px' }}>{label.first_release.substring(0, 4)}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Remix Network */}
                              {(artistInsights.remix_network?.remixed_by?.length > 0 || artistInsights.remix_network?.remixed_for?.length > 0) && (
                                <div data-testid="mb-insights-remixes" style={{ marginBottom: '16px' }}>
                                  <h4 style={{ fontSize: '12px', color: '#ff66ff', marginBottom: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Remix Network</h4>
                                  {artistInsights.remix_network.remixed_by?.length > 0 && (
                                    <div style={{ marginBottom: '8px' }}>
                                      <span style={{ fontSize: '10px', color: '#888', fontWeight: 600 }}>Remixed by others:</span>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                        {artistInsights.remix_network.remixed_by.map((r, idx) => (
                                          <span key={idx} style={{
                                            padding: '3px 8px', background: 'rgba(255,0,255,0.06)',
                                            border: '1px solid rgba(255,0,255,0.15)', borderRadius: '8px',
                                            fontSize: '10px', color: '#ff66ff'
                                          }}>
                                            "{r.track}" by {r.artist}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {artistInsights.remix_network.remixed_for?.length > 0 && (
                                    <div>
                                      <span style={{ fontSize: '10px', color: '#888', fontWeight: 600 }}>Remixed for:</span>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                        {artistInsights.remix_network.remixed_for.map((r, idx) => (
                                          <span key={idx} style={{
                                            padding: '3px 8px', background: 'rgba(0,212,255,0.06)',
                                            border: '1px solid rgba(0,212,255,0.15)', borderRadius: '8px',
                                            fontSize: '10px', color: '#00d4ff'
                                          }}>
                                            "{r.original_track}" - {r.track}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Powered by */}
                              <div style={{ textAlign: 'center', paddingTop: '8px', borderTop: '1px solid rgba(0,212,255,0.08)' }}>
                                <span style={{ fontSize: '9px', color: '#444', fontStyle: 'italic' }}>MusicBrainz Intelligence</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Similar Artists */}
                        {similarArtists.length > 0 && (
                          <div data-testid="similar-artists-section" style={{ marginBottom: '16px' }}>
                            <h4 style={{ fontSize: '13px', color: '#00d4ff', marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Similar Artists</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
                              {similarArtists.map((sa, idx) => (
                                <button
                                  key={idx}
                                  data-testid={`similar-artist-${idx}`}
                                  onClick={() => openArtistDeepDive(sa.name)}
                                  style={{
                                    background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)',
                                    borderRadius: '10px', padding: '10px', textAlign: 'center',
                                    cursor: 'pointer', transition: 'all 0.15s ease', color: '#fff'
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.15)'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.15)'; }}
                                >
                                  {sa.artwork_thumb ? (
                                    <img src={sa.artwork_thumb} alt={sa.name} style={{
                                      width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover',
                                      margin: '0 auto 6px', display: 'block', border: '2px solid rgba(0,212,255,0.2)'
                                    }} onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                    }} />
                                  ) : null}
                                  <div style={{
                                    width: '56px', height: '56px', borderRadius: '50%',
                                    background: 'rgba(0,212,255,0.12)', margin: '0 auto 6px',
                                    display: sa.artwork_thumb ? 'none' : 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontSize: '18px', color: '#00d4ff', fontWeight: 700
                                  }}>{(sa.name || '?')[0]}</div>
                                  <p style={{ fontSize: '11px', fontWeight: 600, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sa.name}</p>
                                  <p style={{ fontSize: '9px', color: '#888', margin: 0 }}>{sa.genre || ''}</p>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Footer */}
                  <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(0,212,255,0.1)', textAlign: 'center' }}>
                    <span style={{ fontSize: '10px', color: '#555', fontStyle: 'italic' }}>
                      Powered by TheAudioDB{artistMbData ? ' + MusicBrainz' : ''}
                    </span>
                  </div>
                </div>
              </div>,
              document.body
            )}


            {/* My Jam Session Tab */}
            <TabsContent value="jam-session" className="tab-content">
              <JamSessionHub 
                key={currentJamSession?.session_id || 'no-session'}
                userToken={localStorage.getItem('beatseek_token')}
                userId={user?.id}
                username={user?.name || 'Guest'}
                externalSession={currentJamSession}
                externalShowModal={showJamSessionModal}
                onModalClose={() => {
                  setShowJamSessionModal(false);
                  setCurrentJamSession(null);
                }}
                onNavigateToDiscover={() => setActiveTab('discover')}
                setIsDiscoveringForJam={setIsDiscoveringForJam}
                setTargetSessionId={setTargetSessionId}
                onSelectLocalTrack={(sessionId) => {
                  console.log('🎵 Setting jam session context for local upload');
                  console.log('🎵 Session ID received:', sessionId);
                  setIsDiscoveringForJam(true);
                  setTargetSessionId(sessionId);
                  setShowLocalTrackUpload(true);
                }}
                onShareToDiscord={(jamData) => {
                  const discordToken = localStorage.getItem('discord_access_token');
                  if (!discordToken) {
                    alert('Please connect your Discord account to use this feature!\n\nGo to My Dashboard → Discord Integration → Connect with Discord');
                    return;
                  }
                  setDiscordShareTrack(jamData);
                  setDiscordShareType('jam');
                  setShowDiscordShareModal(true);
                }}
              />
            </TabsContent>

            <TabsContent value="social" className="tab-content">
              <div className="social-section">
                <h2>👥 Social Hub</h2>
                
                {/* User Profile Section */}
                <Card className="profile-card" data-tour="profile-card">
                  <CardHeader className="relative">
                    {/* Admin Moderation Button - Absolute Top-Right Corner */}
                    {/* Admin/Mod buttons and Default Tab Settings */}
                    <div className="dashboard-right-rail absolute top-2 right-2 sm:top-4 sm:right-4 flex flex-col gap-1.5 sm:gap-2 z-10 w-[120px] sm:w-auto">
                      {/* THE WALL Button */}
                      <button
                        data-testid="the-wall-btn"
                        className="the-wall-border the-wall-btn-mobile"
                        onClick={() => setShowTheWall(true)}
                        style={{ borderRadius: '6px', cursor: 'pointer', border: 'none' }}
                      >
                        <div className="the-wall-inner">
                          THE WALL
                        </div>
                      </button>

                      {/* Default Tab Settings Button */}
                      <div className="relative group mobile-action-wrap" data-tour="switch-homepage">
                        <button
                          onClick={() => {
                            const currentDefault = localStorage.getItem('beatseek_default_tab') || 'social';
                            const newDefault = currentDefault === 'social' ? 'listen' : 'social';
                            localStorage.setItem('beatseek_default_tab', newDefault);
                            alert(`Default tab set to: ${newDefault === 'social' ? 'Dashboard' : 'ID'}\n\nThis will take effect on your next login.`);
                            window.dispatchEvent(new Event('storage'));
                          }}
                          className="mobile-action-btn bg-gray-600/20 hover:bg-cyan-600/20 text-gray-400 hover:text-cyan-300 px-1 py-0 sm:px-2.5 sm:py-1 rounded text-[9px] sm:text-xs font-medium transition-all duration-200 flex items-center justify-center gap-0.5 sm:gap-1.5 whitespace-nowrap min-h-[16px] sm:min-h-0 border border-white/50"
                          title="Change default landing tab"
                        >
                          <svg className="w-2 h-2 sm:w-3 sm:h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Switch Homepage
                          <span className="text-cyan-400 opacity-70 hover:opacity-100 cursor-help ml-0.5 hidden sm:inline">ⓘ</span>
                        </button>
                        {/* Tooltip on hover */}
                        <div className="absolute right-0 top-full mt-1 w-48 p-2 bg-gray-900 border border-cyan-500/30 rounded-lg text-xs text-gray-300 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg">
                          <p className="mb-1"><span className="text-cyan-400 font-medium">Click to switch</span> your default landing tab between Dashboard and ID.</p>
                          <p className="text-gray-500 text-[10px]">Current: {localStorage.getItem('beatseek_default_tab') === 'listen' ? 'ID Tab' : 'Dashboard'}</p>
                        </div>
                      </div>

                      {/* Location Check-In Button */}
                      <button
                        onClick={() => {
                          setShowHomeBaseModal(true);
                          setHighlightUserId(user.user_id);
                        }}
                        className="mobile-action-btn bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-300 hover:text-emerald-200 px-1 py-0 sm:px-2.5 sm:py-1 rounded text-[9px] sm:text-xs font-medium transition-all duration-200 flex items-center justify-center gap-0.5 sm:gap-1.5 whitespace-nowrap border border-emerald-500/30 min-h-[16px] sm:min-h-0"
                        title="Check in and see who's nearby"
                        data-testid="location-checkin-btn"
                      >
                        <svg className="w-2 h-2 sm:w-3 sm:h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Location Check-In
                      </button>

                      {/* Moderate Button - Admin Only - Placed at bottom so non-admin users see same order */}
                      {user?.role === 'admin' && (
                        <button
                          onClick={openAdminModerationModal}
                          className="admin-moderate-clean mobile-action-btn bg-red-600/10 hover:bg-red-600/20 text-red-400 hover:text-red-300 px-1 py-0 sm:px-2.5 sm:py-1 rounded text-[9px] sm:text-xs font-medium transition-all duration-200 flex items-center justify-center gap-0.5 sm:gap-1 min-h-[16px] sm:min-h-0 border border-red-500/70 relative"
                          title="View Messages to Approve"
                          data-testid="moderate-btn"
                        >
                          <svg className="w-2 h-2 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          Moderate
                          {(newMembersUnreadCount > 0 || pendingCuratedIds > 0 || pendingModerations.length > 0 || pendingEmotes.length > 0 || pendingVideos.length > 0 || toolSuggestions.filter(s => s.status === 'pending').length > 0 || betaRequests.filter(r => r.status === 'pending').length > 0) && (
                            <span
                              style={{
                                position: 'absolute',
                                top: '-2px',
                                right: '-2px',
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: '#facc15',
                                boxShadow: '0 0 6px rgba(250, 204, 21, 0.9)',
                                animation: 'pulse 1.4s ease-in-out infinite',
                              }}
                              data-testid="moderate-unread-badge"
                              title="New activity inside Moderate"
                            />
                          )}
                        </button>
                      )}
                    </div>
                    <CardTitle className="profile-title">
                      <div className="profile-header">
                        <div 
                          className="profile-avatar clickable-avatar" 
                          data-tour="profile-avatar"
                          onClick={() => {
                            const userId = user?.id || user?.user_id;
                            if (userId) {
                              navigate(`/profile/${userId}`);
                            }
                          }}
                          title="View my profile"
                        >
                            {user?.profile_picture ? (
                              <img 
                                src={user.profile_picture.startsWith('/api/') ? 
                                  `${BACKEND_URL}${user.profile_picture}` : 
                                  `${BACKEND_URL}/api${user.profile_picture}`
                                } 
                                alt="Profile" 
                                className="avatar-image" 
                              />
                            ) : (
                              <>
                                {user?.name?.charAt(0).toUpperCase() || 'U'}
                                <div className="avatar-upload-hint">📷</div>
                              </>
                            )}
                            {/* Presence dot on avatar corner — green pulse+glow online, gray solid offline */}
                            <span
                              data-testid="dashboard-presence-dot"
                              className={isOnline ? 'avatar-presence-dot online' : 'avatar-presence-dot'}
                              title={isOnline ? 'Online' : 'Offline'}
                            />
                        </div>
                        <div className="profile-info">
                          <div className="profile-name-row">
                            <div className="profile-name-section" style={{textAlign: 'left', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px'}}>
                              {/* Username - own line */}
                              <h3 style={{ margin: 0, lineHeight: 1.2 }}>{user?.name || 'Anonymous User'}</h3>
                              
                              {/* Account type badges - second line */}
                              <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                                {user?.role === 'admin' && (
                                  <span className="admin-role-simple">admin</span>
                                )}
                              </div>
                              {/* Provider connections — third line (dim = unlinked, lit + glow = connected; click scrolls to its panel) */}
                              <ProviderConnectRow
                                connected={connectedServices}
                                onNavigate={handleConnectNudge}
                              />
                            </div>
                          </div>
                          
                          {/* Tier icon only for non-admin users */}
                          {user?.role !== 'admin' && (
                            <span className={`tier-icon ${user?.subscription || 'free'}`}>
                              {getTierIcon(user?.subscription || 'free')}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="profile-stats">
                      <div className="stats-header">
                        <h3 className="stats-title" style={{ textAlign: 'center' }}>MUSIC & COMMUNITY</h3>
                      </div>
                      
                      {/* Three Column Layout */}
                      <div className="profile-content-grid">
                        
                        {/* Left Column - Music Stats */}
                        <div className="stats-column" data-tour="music-activity" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                          <div style={{flex: '1'}}>
                            <h4 className="column-title" style={{ color: '#00ffff', textAlign: 'center' }}>Music Activity</h4>
                            <div className="stats-list">
                              <div className="stat-item-row">
                                <span className="stat-label" title="Tracks you identified with the BEATSEEK Track ID engine">Identified Tracks:</span>
                                <span className="stat-number" data-testid="identified-tracks-count">{Math.max(idStats?.identified_tracks ?? 0, history.length)}</span>
                              </div>
                              <div className="stat-item-row">
                                <span className="stat-label" title="Wall clips you ID'd for other BeatSeekers">Hunted Tracks:</span>
                                <span className="stat-number" data-testid="hunted-tracks-count">{idStats?.hunted_tracks ?? 0}</span>
                              </div>
                              {idStats?.hunter_rank ? (
                                <div data-testid="hunter-rank-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '2px 0 8px', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '0.7rem', color: '#9aa4b2', letterSpacing: '0.04em' }}>#{idStats.hunter_rank} this month</span>
                                  <IdHunterMedal userId={user?.id || user?.user_id} showCount={false} />
                                </div>
                              ) : null}
                              <div className="stat-item-row">
                                <span className="stat-label">Shared Tracks:</span>
                                <span className="stat-number">0</span>
                              </div>
                              <div className="stat-item-row">
                                <span className="stat-label">Saved Playlists:</span>
                                <span className="stat-number">{myPlaylists.length}</span>
                              </div>
                            </div>
                            <EngineCredit userId={user?.id || user?.user_id} />
                          </div>
                          
                          {/* Phase 5: My Tracks Button - Positioned at absolute bottom of panel */}
                          <div style={{marginTop: 'auto', paddingTop: '16px'}}>
                            {/* Refresh Music Stats - above separator */}
                            <div 
                              onClick={async () => {
                                if (isRefreshingMusic) return;
                                setIsRefreshingMusic(true);
                                console.log('🔄 Refreshing music stats...');
                                // Reload ID stats
                                try {
                                  await loadIdStats();
                                } catch (error) {
                                  console.error('Error refreshing music stats:', error);
                                } finally {
                                  setTimeout(() => setIsRefreshingMusic(false), 600);
                                }
                              }}
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                marginBottom: '8px',
                                cursor: isRefreshingMusic ? 'default' : 'pointer',
                                color: 'rgba(255, 255, 255, 0.4)',
                                transition: 'color 0.2s'
                              }}
                              onMouseEnter={(e) => !isRefreshingMusic && (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)')}
                              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}
                              title="Refresh Music Stats"
                            >
                              <svg 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                style={{
                                  animation: isRefreshingMusic ? 'spin 1s linear infinite' : 'none'
                                }}
                              >
                                <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
                              </svg>
                            </div>
                            <div style={{borderTop: '1px solid rgba(107, 114, 128, 0.3)', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px'}}>
                              <button
                                onClick={() => setShowMyTracksFromProfile(true)}
                                data-tour="dash-my-track-previews"
                                className="dashboard-panel-btn w-full bg-gradient-to-r from-cyan-600/20 to-teal-600/20 hover:from-cyan-600/30 hover:to-teal-600/30 border border-cyan-500/50 text-cyan-300 hover:text-white rounded transition-all duration-300 flex items-center justify-center"
                                style={{ padding: '3px 8px', fontSize: '13px', gap: '5px', height: '26px', lineHeight: '1' }}
                              >
                                <svg style={{ width: '12px', height: '12px', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                </svg>
                                My Track Previews
                              </button>
                              <button
                                onClick={() => {
                                  fetchMyPlaylists();
                                  setPlaylistModalData({ 
                                    createPlaylist,
                                    deletePlaylist,
                                    fetchMyPlaylists,
                                    addTrackToPlaylist,
                                    reorderPlaylistTracks,
                                    playPlaylist,
                                    playTrack,
                                    user,
                                    setActiveTab,
                                    setShowPlaylistsModal,
                                    setShowPlaylistDetailModal,
                                    setCurrentPlaylist
                                  });
                                  setShowPlaylistsModal(true);
                                }}
                                className="dashboard-panel-btn w-full bg-gradient-to-r from-cyan-600/20 to-teal-600/20 hover:from-cyan-600/30 hover:to-teal-600/30 border border-cyan-500/50 text-cyan-300 hover:text-white rounded transition-all duration-300 flex items-center justify-center"
                                style={{ padding: '3px 8px', fontSize: '13px', gap: '5px', height: '26px', lineHeight: '1' }}
                                data-testid="my-playlists-btn"
                              >
                                <svg style={{ width: '12px', height: '12px', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                </svg>
                                My Playlists
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Middle Column - Social Stats */}
                        <div className="stats-column" data-tour="community-stats">
                          <h4 className="column-title" style={{ color: '#00ffff', textAlign: 'center' }}>Community</h4>
                          <div className="stats-list">
                            <div className="stat-item-row">
                              <span className="stat-label">Friends:</span>
                              <span className="stat-number">{friends.length}</span>
                            </div>
                            <div className="stat-item-row">
                              <span className="stat-label">Followers:</span>
                              <span className="stat-number">{followersCount}</span>
                            </div>
                            <div className="stat-item-row">
                              <span className="stat-label">Following:</span>
                              <span className="stat-number">{followingCount}</span>
                            </div>
                          </div>
                          
                          {/* View Private Feed Button - Positioned at bottom of Community panel */}
                          <div style={{marginTop: 'auto', paddingTop: '16px'}}>
                            {/* Refresh Community Stats - above separator */}
                            <div 
                              onClick={async () => {
                                if (isRefreshingCommunity) return;
                                setIsRefreshingCommunity(true);
                                console.log('🔄 Refreshing community stats...');
                                await refreshSocialData();
                                setTimeout(() => setIsRefreshingCommunity(false), 600);
                              }}
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                marginBottom: '8px',
                                cursor: isRefreshingCommunity ? 'default' : 'pointer',
                                color: 'rgba(255, 255, 255, 0.4)',
                                transition: 'color 0.2s'
                              }}
                              onMouseEnter={(e) => !isRefreshingCommunity && (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)')}
                              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}
                              title="Refresh Community Stats"
                            >
                              <svg 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                style={{
                                  animation: isRefreshingCommunity ? 'spin 1s linear infinite' : 'none'
                                }}
                              >
                                <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
                              </svg>
                            </div>
                            <div style={{borderTop: '1px solid rgba(107, 114, 128, 0.3)', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px'}}>
                              <button
                                onClick={() => setShowFriendRequests(true)}
                                data-tour="dash-friend-requests"
                                className="dashboard-panel-btn w-full bg-gradient-to-r from-cyan-600/20 to-teal-600/20 hover:from-cyan-600/30 hover:to-teal-600/30 border border-cyan-500/50 text-cyan-300 hover:text-white rounded transition-all duration-300 flex items-center justify-center"
                                style={{ padding: '3px 8px', fontSize: '13px', gap: '5px', height: '26px', lineHeight: '1' }}
                                data-testid="friend-requests-btn"
                              >
                                <svg style={{ width: '12px', height: '12px', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                </svg>
                                Friend Requests {friendRequests.length > 0 && `(${friendRequests.length})`}
                              </button>
                              <button
                                onClick={openPrivateFeedModal}
                                data-tour="dash-private-feed"
                                className="dashboard-panel-btn w-full bg-gradient-to-r from-cyan-600/20 to-teal-600/20 hover:from-cyan-600/30 hover:to-teal-600/30 border border-cyan-500/50 text-cyan-300 hover:text-white rounded transition-all duration-300 flex items-center justify-center"
                                style={{ padding: '3px 8px', fontSize: '13px', gap: '5px', height: '26px', lineHeight: '1' }}
                              >
                                <svg style={{ width: '12px', height: '12px', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                Private Feed
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Right Column - Dynamic Social Media Links */}
                        <div className="social-links-column" data-tour="beatseeker-connect">
                          <div className="social-links-header">
                            <h4 className="column-title" style={{ color: '#00ffff', textAlign: 'center', flex: 1 }}>Social Links</h4>
                            <button 
                              className="add-social-btn"
                              data-tour="add-social-link"
                              onClick={() => setShowAddSocialLink(true)}
                              title="Add Social Media Link"
                            >
                              +
                            </button>
                          </div>
                          
                          <div className="social-links-list">
                            {socialLinks.length === 0 ? (
                              <div className="empty-social-state">
                                <p className="empty-text">No social links added yet</p>
                                <p className="empty-subtext">Click + to add your profiles</p>
                              </div>
                            ) : (
                              <DragDropContext onDragEnd={handleSocialLinkDragEnd}>
                                <Droppable droppableId="social-links" direction="vertical" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
                                  {(provided) => (
                                    <div
                                      {...provided.droppableProps}
                                      ref={provided.innerRef}
                                      className="social-links-droppable"
                                    >
                                      {socialLinks.map((link, index) => (
                                        <Draggable key={`${link.platform}-${index}`} draggableId={`${link.platform}-${index}`} index={index} isDragDisabled={false}>
                                          {(provided, snapshot) => {
                                            // Lock drag movement to vertical (Y-axis) only
                                            const dragStyle = provided.draggableProps.style || {};
                                            const lockedStyle = snapshot.isDragging && dragStyle.transform
                                              ? { ...dragStyle, transform: dragStyle.transform.replace(/translate\(([^,]+),/, 'translate(0px,') }
                                              : dragStyle;
                                            return (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              className={`social-link-item-compact ${snapshot.isDragging ? 'dragging' : ''}`}
                                              style={{ 
                                                ...lockedStyle,
                                                border: 'none', 
                                                outline: 'none',
                                                transition: snapshot.isDragging ? 'none' : 'transform 0.1s ease'
                                              }}
                                            >
                                              <div 
                                                {...provided.dragHandleProps}
                                                className="drag-handle-compact"
                                                title="Drag to reorder"
                                              >
                                                <GripVertical className="w-3 h-3 text-gray-400" />
                                              </div>
                                              <a 
                                                href={link.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="social-link-main-content"
                                                onClick={(e) => {
                                                  // Prevent navigation when dragging
                                                  if (snapshot.isDragging) {
                                                    e.preventDefault();
                                                  }
                                                }}
                                              >
                                                <div className={`social-icon-compact ${link.platform}`}>
                                                  {getSocialIcon(link.platform)}
                                                </div>
                                                <span className="social-platform-compact">{link.platformName}</span>
                                              </a>
                                              <button 
                                                className="edit-social-btn-compact"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setEditingSocialLinkIndex(index);
                                                  setShowAddSocialLink(true);
                                                }}
                                                title="Edit link"
                                              >
                                                <Pencil className="w-3 h-3" />
                                              </button>
                                              <button 
                                                className="remove-social-btn-compact"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  removeSocialLink(index);
                                                }}
                                                title="Remove link"
                                              >
                                                ×
                                              </button>
                                            </div>
                                            );
                                          }}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            )}
                          </div>
                        </div>
                        
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-4 justify-center dashboard-action-buttons" data-tour="dashboard-actions">
                      {/* (i) Icon key — mobile only, first icon with attention glow */}
                      <button
                        type="button"
                        onClick={() => setShowActionLegend(true)}
                        className="dab-btn dab-info-btn dab-info-pulse sm:hidden"
                        title="What do these icons mean?"
                        aria-label="Show icon key"
                        data-testid="action-legend-btn"
                      >
                        <Info size={16} />
                      </button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setShowMyEmotes(true);
                          fetchMyEmotes();
                          fetchEmoteLimits();
                        }}
                        className="dab-btn bg-purple-600/10 hover:bg-purple-600/20 text-purple-300 border-purple-500/30 text-[11px] font-medium px-2.5 py-1 h-7 border"
                        title="My Emotes — your saved emotes"
                      >
                        <span className="dab-icon">😊</span><span className="dab-label"> My Emotes</span>
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="dashboard-emote-generator-btn"
                        onClick={() => {
                          setEmoteLabPrefill({ prompt: '', style: 'neon' });
                          setShowEmoteLabLite(true);
                        }}
                        className="dab-btn bg-gradient-to-r from-cyan-600/15 to-fuchsia-600/15 hover:from-cyan-600/30 hover:to-fuchsia-600/30 text-cyan-200 border-cyan-500/40 text-[11px] font-semibold px-2.5 py-1 h-7 border inline-flex items-center gap-1"
                        title="Emote Generator — create custom AI emotes"
                      >
                        <Sparkles size={12} /> <span className="dab-label">Emote Generator</span>
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowWhisperConversations(true)}
                        className="dab-btn dab-whisper bg-blue-600/10 hover:bg-blue-600/20 text-blue-300 border-blue-500/30 text-[11px] font-medium px-2.5 py-1 h-7 border flex items-center gap-1"
                        title="Whispers — private messages"
                      >
                        <WhisperIcon size={16} className="text-blue-300" />
                        <span className="dab-label">Whispers</span>
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowArtistNotifications(true)}
                        className="dab-btn bg-amber-600/10 hover:bg-amber-600/20 text-amber-300 border-amber-500/30 text-[11px] font-medium px-2.5 py-1 h-7 border flex items-center gap-1"
                        title="Artist Notifications — updates from artists you follow"
                      >
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="currentColor"
                          stroke="currentColor" 
                          strokeWidth="1"
                        >
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        <span className="dab-label">Artist Notifications</span>
                      </Button>

                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setShowNotificationsPanel(true);
                          loadNotifications();
                        }}
                        className="dab-btn bg-cyan-600/10 hover:bg-cyan-600/20 text-cyan-300 border-cyan-500/30 text-[11px] font-medium px-2.5 py-1 h-7 border flex items-center gap-1 relative"
                        title="BEATSEEK Notifications — platform news & alerts"
                        data-testid="platform-notifications-btn"
                      >
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none"
                          stroke="currentColor" 
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        <span className="dab-label">BEATSEEK Notifications</span>
                        {notifications.filter(n => !n.read).length > 0 && (
                          <span className="absolute -top-1 -right-1 bg-cyan-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] text-center">
                            {notifications.filter(n => !n.read).length > 99 ? '99+' : notifications.filter(n => !n.read).length}
                          </span>
                        )}
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowExpiringNotifications(true)}
                        className="dab-btn bg-red-600/10 hover:bg-red-600/20 text-red-400 border-red-500/30 text-[11px] font-medium px-2.5 py-1 h-7 border flex items-center gap-1 relative"
                        data-testid="expiring-notifications-btn"
                      >
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none"
                          stroke="currentColor" 
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span className="dab-label">Expiring</span>
                        {expiringCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] text-center">
                            {expiringCount > 99 ? '99+' : expiringCount}
                          </span>
                        )}
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowSuggestionBox(true)}
                        className="dab-btn bg-yellow-600/10 hover:bg-yellow-600/20 text-yellow-300 border-yellow-500/30 text-[11px] font-medium px-2.5 py-1 h-7 border flex items-center gap-1"
                        title="Suggestion Box — share feature ideas"
                        data-testid="suggestion-box-btn"
                      >
                        <Lightbulb size={14} />
                        <span className="dab-label">Suggestion Box</span>
                      </Button>
                    </div>
                      
                </CardContent>
                </Card>
                
                <SuggestionBoxModal
                  isOpen={showSuggestionBox}
                  onClose={() => setShowSuggestionBox(false)}
                />

                {/* Dashboard action-icon legend (mobile key) */}
                {showActionLegend && (
                  <div className="action-legend-overlay" onClick={() => setShowActionLegend(false)} data-testid="action-legend-overlay">
                    <div className="action-legend-sheet" onClick={(e) => e.stopPropagation()}>
                      <div className="action-legend-header">
                        <h3>Quick Key</h3>
                        <button onClick={() => setShowActionLegend(false)} className="action-legend-close" aria-label="Close" data-testid="action-legend-close">✕</button>
                      </div>
                      <p className="action-legend-sub">Tap any icon on your dashboard — here's what each one does:</p>
                      <ul className="action-legend-list">
                        <li>
                          <span className="alg-ico" style={{ color: '#d8b4fe' }}>😊</span>
                          <div><span className="alg-name">My Emotes</span><span className="alg-desc">Your saved emotes</span></div>
                        </li>
                        <li>
                          <span className="alg-ico" style={{ color: '#67e8f9' }}><Sparkles size={18} /></span>
                          <div><span className="alg-name">Emote Generator</span><span className="alg-desc">Create custom AI emotes</span></div>
                        </li>
                        <li>
                          <span className="alg-ico" style={{ color: '#93c5fd' }}><WhisperIcon size={18} className="text-blue-300" /></span>
                          <div><span className="alg-name">Whispers</span><span className="alg-desc">Private messages</span></div>
                        </li>
                        <li>
                          <span className="alg-ico" style={{ color: '#fcd34d' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                          </span>
                          <div><span className="alg-name">Artist Notifications</span><span className="alg-desc">Updates from artists you follow</span></div>
                        </li>
                        <li>
                          <span className="alg-ico" style={{ color: '#67e8f9' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                          </span>
                          <div><span className="alg-name">BEATSEEK Notifications</span><span className="alg-desc">Platform news &amp; alerts</span></div>
                        </li>
                        <li>
                          <span className="alg-ico" style={{ color: '#f87171' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                          </span>
                          <div><span className="alg-name">Expiring</span><span className="alg-desc">Items expiring soon</span></div>
                        </li>
                        <li>
                          <span className="alg-ico" style={{ color: '#fde047' }}><Lightbulb size={18} /></span>
                          <div><span className="alg-name">Suggestion Box</span><span className="alg-desc">Share feature ideas</span></div>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                
                <PushToVoteModal
                  suggestion={pushVoteTarget}
                  onClose={() => setPushVoteTarget(null)}
                  onPushed={fetchToolSuggestions}
                />

                {/* Community Features */}
                <div className="community-features">
                  <Card className="community-card" data-tour="browse-djs">
                    <CardHeader>
                      <CardTitle className="friend-search-header" style={{ textAlign: 'center', justifyContent: 'center' }}>
                        Connect with Fellow Beatseekers
                        {friendRequests.length > 0 && (
                          <Badge variant="destructive" className="friend-requests-badge">
                            {friendRequests.length}
                          </Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* New Member Feed ticker — auto-populates on signup, 7-day window */}
                      <NewMemberTicker />

                      <div className="social-connect-cta">
                        <p className="connect-description">
                          Join our community of electronic music enthusiasts and share your discoveries!
                        </p>
                        
                        {/* Enhanced Friend Search */}
                        <div className="search-friends" style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}>
                          {/* Row 1: Search input and button */}
                          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <input 
                              type="text" 
                              placeholder="Search by name..." 
                              className="friend-search-input"
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(e.target.value);
                                if (e.target.value.length > 2) {
                                  searchUsers(e.target.value);
                                } else if (e.target.value.length === 0 && (searchUserType !== 'all' || searchGenre !== 'all')) {
                                  searchUsers('', { userType: searchUserType, genre: searchGenre });
                                } else {
                                  setSearchResults([]);
                                }
                              }}
                              style={{ flex: 1 }}
                            />
                            <button 
                              onClick={() => searchUsers(searchQuery)}
                              disabled={isSearching}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: '6px',
                                cursor: isSearching ? 'default' : 'pointer',
                                opacity: isSearching ? 0.5 : 0.7,
                                transition: 'opacity 0.2s'
                              }}
                              onMouseEnter={(e) => !isSearching && (e.currentTarget.style.opacity = '1')}
                              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                              title="Search"
                            >
                              🔍
                            </button>
                          </div>
                          
                          {/* Row 2: Advanced Search - all on one line below basic search */}
                          <div className="adv-search-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '500', whiteSpace: 'nowrap' }}>Adv Search:</span>
                            
                            <select
                              value={searchUserType}
                              onChange={(e) => {
                                setSearchUserType(e.target.value);
                                if (searchQuery.length > 2 || e.target.value !== 'all' || searchGenre !== 'all') {
                                  searchUsers(searchQuery, { userType: e.target.value, genre: searchGenre });
                                }
                              }}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '6px',
                                background: '#000',
                                border: '1px solid rgba(0, 255, 255, 0.3)',
                                color: '#fff',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              <option value="all">User Type</option>
                              <option value="dj">DJs</option>
                              <option value="producer">Producers</option>
                              <option value="streamer">Streamers</option>
                              <option value="mod">Mods</option>
                              <option value="listener">Listeners</option>
                            </select>
                            
                            <select
                              value={searchGenre}
                              onChange={(e) => {
                                setSearchGenre(e.target.value);
                                if (searchQuery.length > 2 || searchUserType !== 'all' || e.target.value !== 'all') {
                                  searchUsers(searchQuery, { userType: searchUserType, genre: e.target.value });
                                }
                              }}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '6px',
                                background: '#000',
                                border: '1px solid rgba(0, 255, 255, 0.3)',
                                color: '#fff',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              <option value="all">Genre Interest</option>
                              <option value="house">House</option>
                              <option value="techno">Techno</option>
                              <option value="trance">Trance</option>
                              <option value="drum and bass">Drum & Bass</option>
                              <option value="dubstep">Dubstep</option>
                              <option value="edm">EDM</option>
                              <option value="deep house">Deep House</option>
                              <option value="progressive">Progressive</option>
                              <option value="minimal">Minimal</option>
                              <option value="electro">Electro</option>
                            </select>
                            
                            <Button 
                              size="sm"
                              onClick={() => searchUsers('', { randomDiscover: true })}
                              disabled={isSearching}
                              className="discover-random-btn"
                            >
                              🎲 Discover Random
                            </Button>
                          </div>
                        </div>
                        
                        {/* Search Results (desktop inline — mobile uses full-screen modal) */}
                        {searchResults.length > 0 && window.innerWidth > 768 && (
                          <div className="search-results">
                            <h4>Search Results</h4>
                            {searchResults.map((searchUser) => (
                              <div key={searchUser.id} className="search-result-item">
                                <div className="search-user-info">
                                  {/* Avatar and Bell Container - vertically stacked, moved right by 2rem */}
                                  <div className="search-avatar-stack" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                    <div className="search-user-avatar" style={{ width: '52px', height: '52px', fontSize: '1.3rem' }}>
                                      {searchUser.profile_picture ? (
                                        <img 
                                          src={`${process.env.REACT_APP_BACKEND_URL}${searchUser.profile_picture.replace('/uploads/', '/api/uploads/')}`}
                                          alt={searchUser.name}
                                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                                        />
                                      ) : (
                                        searchUser.name?.charAt(0).toUpperCase() || 'U'
                                      )}
                                    </div>
                                    {/* Artist Notification Bell - centered below avatar for DJ/Producer/Streamer */}
                                    {['dj', 'producer', 'streamer', 'mod'].includes(searchUser.user_type?.toLowerCase()) && (
                                      <ArtistNotificationBell 
                                        artistName={searchUser.name}
                                        artistId={searchUser.id}
                                        source="beatseek"
                                        artworkUrl={searchUser.profile_picture ? `${process.env.REACT_APP_BACKEND_URL}${searchUser.profile_picture.replace('/uploads/', '/api/uploads/')}` : null}
                                        size="medium"
                                        showEmailAlert={true}
                                      />
                                    )}
                                  </div>
                                  <div className="search-user-details">
                                    <span className="search-user-name">{searchUser.name}</span>
                                    <span className="search-user-email">{searchUser.email}</span>
                                    {/* Show role text for artists - no background */}
                                    {['dj', 'producer', 'streamer', 'mod'].includes(searchUser.user_type?.toLowerCase()) && (
                                      <span style={{
                                        fontSize: '11px',
                                        color: '#fbbf24',
                                        marginLeft: '8px',
                                        textTransform: 'capitalize',
                                        fontWeight: '500'
                                      }}>
                                        {searchUser.user_type}
                                      </span>
                                    )}
                                    {(searchUser.mutual_friends_count > 0 || searchUser.shared_genres?.length > 0) && (
                                      <div className="search-user-hints">
                                        {searchUser.mutual_friends_count > 0 && (
                                          <span
                                            className="mutual-friends-hint mutual-friends-clickable"
                                            data-testid={`mutual-friends-hint-${searchUser.id}`}
                                            onClick={() => setMutualModalTarget({ id: searchUser.id, name: searchUser.name })}
                                            title="View mutual friends"
                                          >
                                            🤝 {searchUser.mutual_friends_count} mutual {searchUser.mutual_friends_count === 1 ? 'friend' : 'friends'}
                                            {searchUser.mutual_friends_preview?.length > 0 && ` · ${searchUser.mutual_friends_preview.join(', ')}`}
                                            <span className="view-mutuals-link"> · View</span>
                                          </span>
                                        )}
                                        {searchUser.shared_genres?.length > 0 && (
                                          <span className="shared-genres-hint" data-testid={`shared-genres-hint-${searchUser.id}`}>
                                            🎧 You both like {searchUser.shared_genres.join(', ')}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="user-action-buttons">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => navigate(`/profile/${searchUser.id}`)}
                                    className="view-profile-btn"
                                    data-testid={`view-profile-btn-${searchUser.id}`}
                                    style={{
                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                      color: 'white',
                                      border: 'none'
                                    }}
                                  >
                                    👤 View Profile
                                  </Button>
                                  <div className="user-action-row">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => {
                                        if (searchUser.is_following) {
                                          unfollowUser(searchUser.id);
                                        } else {
                                          followUser(searchUser.id);
                                        }
                                      }}
                                      className={`follow-btn ${searchUser.is_following ? 'is-following' : ''}`}
                                      data-testid={`follow-btn-${searchUser.id}`}
                                    >
                                      {searchUser.is_following ? '✓ Following' : 'Follow'}
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => sendFriendRequest(searchUser.id)}
                                      disabled={searchUser.friend_status === 'request_sent'}
                                      className="add-friend-btn"
                                      data-testid={`add-friend-btn-${searchUser.id}`}
                                    >
                                      {searchUser.friend_status === 'request_sent' ? 'Request Sent' : 'Add Friend'}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <MutualFriendsModal
                          isOpen={!!mutualModalTarget}
                          onClose={() => setMutualModalTarget(null)}
                          targetUserId={mutualModalTarget?.id}
                          targetUserName={mutualModalTarget?.name}
                          viewerId={user?.user_id}
                        />
                        
                        <MobileSearchResultsModal
                          isOpen={showMobileSearchModal}
                          onClose={closeMobileSearch}
                          results={searchResults}
                          isSearching={isSearching}
                          query={searchQuery}
                          setQuery={setSearchQuery}
                          userType={searchUserType}
                          setUserType={setSearchUserType}
                          genre={searchGenre}
                          setGenre={setSearchGenre}
                          searchUsers={searchUsers}
                          onViewProfile={handleMobileViewProfile}
                          onFollowToggle={(u) => u.is_following ? unfollowUser(u.id) : followUser(u.id)}
                          onFriendRequest={(u) => sendFriendRequest(u.id)}
                          onViewMutuals={(u) => setMutualModalTarget({ id: u.id, name: u.name })}
                          initialScrollTop={mobileSearchScrollTop}
                        />
                        
                        {/* Friend Requests Section */}
                        {friendRequests.length > 0 && (
                          <div className="friend-requests-section">
                            <div className="friend-requests-header">
                              <h4>Friend Requests ({friendRequests.length})</h4>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => setShowFriendRequests(!showFriendRequests)}
                              >
                                {showFriendRequests ? 'Hide' : 'Show'}
                              </Button>
                            </div>
                            
                            {showFriendRequests && (
                              <div className="friend-requests-list">
                                {friendRequests.map((request) => (
                                  <div key={request.id} className="friend-request-item">
                                    <div className="request-user-info">
                                      <div className="request-user-avatar">
                                        {request.avatar ? (
                                          <img src={request.avatar} alt={request.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                        ) : (
                                          request.name?.charAt(0).toUpperCase() || 'U'
                                        )}
                                      </div>
                                      <div className="request-user-details">
                                        <span className="request-user-name">{request.name}</span>
                                        <span className="request-user-email">{request.email}</span>
                                      </div>
                                    </div>
                                    <div className="friend-request-actions">
                                      <Button 
                                        size="sm" 
                                        onClick={() => acceptFriendRequest(request.id, request.user_id)}
                                        className="accept-btn"
                                      >
                                        Accept
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={() => declineFriendRequest(request.id)}
                                        className="decline-btn"
                                      >
                                        Decline
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        
                        <p className="community-note">
                          Find users by name or email to start building your music discovery network.
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="community-card" data-tour="community-feed">
                    <CardHeader style={{ paddingBottom: 0 }}>
                      <CardTitle className="community-feed-header" style={{ textAlign: 'center' }}>
                        Community Feed
                      </CardTitle>
                    </CardHeader>
                    <CardContent style={{ paddingTop: '0.5rem' }}>
                      <div className="community-feed-cta">
                        <p className="feed-description">
                          Explore, connect, and discover what&apos;s happening in the global BEATSEEK community!
                        </p>
                        
                        {/* Community Scrolling Banner */}
                        {bannerItems.length > 0 && (
                          <div className="community-banner-ticker" data-testid="community-banner-ticker">
                            <div className="banner-track">
                              {[...bannerItems, ...bannerItems].map((item, idx) => (
                                <div 
                                  key={idx}
                                  className={`banner-item ${item.priority === 'important' ? 'banner-important' : ''} banner-type-${item.type}`}
                                  data-testid={`banner-item-${item.type}-${idx}`}
                                >
                                  <span className="banner-icon">
                                    {item.icon === 'trophy' && '\u{1F3C6}'}
                                    {item.icon === 'users' && '\u{1F465}'}
                                    {item.icon === 'cake' && '\u{1F382}'}
                                    {item.icon === 'megaphone' && '\u{1F4E2}'}
                                  </span>
                                  <span className="banner-text">{item.text}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Community Features Dashboard */}
                        <div className="community-features-grid">
                          <Button 
                            className="feature-btn"
                            data-tour="cf-beatseek-global"
                            onClick={() => setShowGlobeModal(true)}
                          >
                            <div className="feature-icon"><span className="globe-icon">🌍</span></div>
                            <div className="feature-info">
                              <span className="feature-name">
                                BEATSEEK Global <span style={{ fontSize: '0.85em' }}>(check-in)</span>
                              </span>
                              <span className="feature-desc">See where Beatseekers are discovering music</span>
                            </div>
                          </Button>
                          
                          {/* Discoveries & Trending - Opens choice modal */}
                          <Button 
                            className="feature-btn"
                            data-tour="cf-discoveries"
                            onClick={() => setShowDiscoveriesChoice(true)}
                          >
                            <div className="feature-icon"><span className="music-note-icon">🎵</span></div>
                            <div className="feature-info">
                              <span className="feature-name">Discoveries & Trending</span>
                              <span className="feature-desc">Latest & popular tracks</span>
                            </div>
                          </Button>
                          
                          {/* NEW: The Promo Spot Card */}
                          <Button 
                            className="feature-btn promo-spot-btn"
                            data-tour="cf-promo-spot"
                            onClick={() => setShowPromoSpot(true)}
                          >
                            <div className="feature-icon"><span className="promo-icon">📢</span></div>
                            <div className="feature-info">
                              <span className="feature-name">The Promo Spot</span>
                              <span className="feature-desc">Promote your mixes, events & announcements</span>
                            </div>
                          </Button>
                          
                          <Button 
                            className="feature-btn"
                            data-tour="cf-community-chat"
                            onClick={() => setShowCommunityChat(true)}
                          >
                            <div className="feature-icon"><span className="chat-bubble-icon">💬</span></div>
                            <div className="feature-info">
                              <span className="feature-name">Community Chat</span>
                              <span className="feature-desc">Connect with the community</span>
                            </div>
                          </Button>
                          
                          <Button 
                            className="feature-btn"
                            data-tour="cf-wishlist"
                            onClick={() => setShowWishlistModal(true)}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%'
                            }}
                          >
                            <div className="feature-icon"><span className="producer-icon">⭐</span></div>
                            <div className="feature-info" style={{ flex: 1 }}>
                              <span className="feature-name">Wishlist</span>
                              <span className="feature-desc">Save gear &amp; items you want</span>
                            </div>
                          </Button>
                          
                          <Button 
                            className="feature-btn"
                            data-tour="cf-festival"
                            onClick={() => setShowFestivalEvents(true)}
                          >
                            <div className="feature-icon"><span className="festival-icon">🎪</span></div>
                            <div className="feature-info">
                              <span className="feature-name">Festival & Events</span>
                              <span className="feature-desc">Events, tickets & community photos</span>
                            </div>
                          </Button>
                        </div>
                        
                        <p className="community-note">
                          Explore the BEATSEEK community - discover music, connect with producers, and stay updated on events!
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Spotify Integration - Moved to bottom */}
                <Card id="connect-panel-spotify" className="spotify-integration-card" data-tour="spotify-integration">
                  <CardHeader>
                    <CardTitle className="spotify-section-title">
                      <img 
                        src="/images/social-logos/spotify.png" 
                        alt="Spotify" 
                        className="spotify-title-logo"
                      />
                      Spotify Integration
                      <span className="powered-by-badge spotify-badge">Powered by Spotify</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {spotifyConnected && spotifyAccessToken ? (
                      <div className="spotify-connected-layout">
                        {/* Left side - Connection info and actions */}
                        <div className="spotify-connection-side">
                          <div className="spotify-status-connected">
                            {spotifyAccessToken === 'mock_token_for_testing' 
                              ? '🧪 Mock Connected (Test Mode)'
                              : '✅ Connected to Spotify'
                            }
                          </div>
                          
                          <div className="spotify-actions-enhanced">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={async () => {
                                try {
                                  if (spotifyAccessToken === 'mock_token_for_testing') {
                                    alert('🧪 Mock connection verified!');
                                    return;
                                  }
                                  
                                  const response = await fetch(`${API}/spotify/user/info?access_token=${encodeURIComponent(spotifyAccessToken)}`, {
                                    method: 'GET',
                                    headers: { 'Accept': 'application/json' }
                                  });
                                  
                                  if (response.ok) {
                                    const data = await response.json();
                                    alert(`✅ Hello ${data.display_name || data.name || 'Spotify User'}`);
                                  } else {
                                    throw new Error('Connection test failed');
                                  }
                                } catch (error) {
                                  alert(`❌ Connection test failed: ${error.message}`);
                                }
                              }}
                              className="enhanced-action-btn test-btn"
                            >
                              ✓ Test
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={async () => {
                                try {
                                  if (spotifyAccessToken === 'mock_token_for_testing') {
                                    alert('🧪 Using mock playlists');
                                    return;
                                  }
                                  
                                  console.log('🎵 Manual playlist refresh triggered');
                                  setIsLoadingPlaylists(true);
                                  
                                  const response = await fetch(`${API}/spotify/playlists?access_token=${encodeURIComponent(spotifyAccessToken)}`);
                                  console.log('🎵 Manual playlist response:', response.status);
                                  
                                  if (response.ok) {
                                    const data = await response.json();
                                    console.log('🎵 Manual playlist data:', data);
                                    setSpotifyPlaylists(data.playlists || []);
                                    alert(`✅ Loaded ${data.playlists?.length || 0} playlists!`);
                                  } else {
                                    const errorText = await response.text();
                                    console.error('🎵 Manual playlist error:', errorText);
                                    throw new Error(`Failed to load playlists: ${response.status}`);
                                  }
                                } catch (error) {
                                  console.error('🎵 Manual playlist exception:', error);
                                  alert(`❌ Failed to load playlists: ${error.message}`);
                                } finally {
                                  setIsLoadingPlaylists(false);
                                }
                              }}
                              className="enhanced-action-btn refresh-btn"
                            >
                              🔄 Refresh Playlists
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                const isMode = spotifyAccessToken === 'mock_token_for_testing' ? 'mock connection' : 'Spotify account';
                                if (window.confirm(`Disconnect ${isMode}?`)) {
                                  localStorage.removeItem('spotify_access_token');
                                  localStorage.removeItem('spotify_refresh_token');
                                  setSpotifyAccessToken(null);
                                  setSpotifyConnected(false);
                                  setSpotifyPlaylists([]);
                                  alert(`✅ Disconnected.`);
                                }
                              }}
                              className="enhanced-action-btn disconnect-btn"
                            >
                              Disconnect
                            </Button>
                          </div>
                          
                          <p className="spotify-connection-subtitle">
                            {spotifyAccessToken === 'mock_token_for_testing' 
                              ? 'Mock mode active for testing'
                              : 'Ready to manage playlists'
                            }
                          </p>
                        </div>

                        {/* Right side - Enhanced Playlist Manager */}
                        <div className="spotify-playlist-side">
                          <div className="playlist-manager-header">
                            <Music className="playlist-manager-icon" />
                            <h4 className="playlist-manager-title">Playlist Manager</h4>
                          </div>
                          
                          {/* Enhanced Search & Create Section */}
                          <div className="playlist-controls-section">
                            {/* Playlist Search */}
                            <div className="playlist-search-container">
                              <input
                                type="text"
                                placeholder="🔍 Search your playlists..."
                                value={playlistSearchTerm || ''}
                                onChange={(e) => {
                                  setPlaylistSearchTerm(e.target.value);
                                }}
                                className="playlist-search-input"
                              />
                            </div>
                            
                            {/* Create New Playlist */}
                            <div className="compact-create-section">
                              {!showCreatePlaylist ? (
                                <Button 
                                  size="sm" 
                                  onClick={() => setShowCreatePlaylist(true)}
                                  className="enhanced-create-btn"
                                >
                                  <Plus className="btn-icon-small" />
                                  Create New Playlist
                                </Button>
                              ) : (
                                <div className="enhanced-create-form">
                                  <input
                                    type="text"
                                    placeholder="Enter playlist name..."
                                    value={newPlaylistName || ''}
                                    onChange={(e) => setNewPlaylistName(e.target.value)}
                                    className="enhanced-input"
                                  />
                                  <div className="enhanced-form-actions">
                                    <Button 
                                      size="sm" 
                                      onClick={() => {
                                        if (!newPlaylistName?.trim()) {
                                          alert('Please enter a playlist name');
                                          return;
                                        }
                                        
                                        if (spotifyAccessToken === 'mock_token_for_testing') {
                                          const newPlaylist = {
                                            id: Date.now().toString(),
                                            name: newPlaylistName,
                                            tracks_total: 0
                                          };
                                          setSpotifyPlaylists([...spotifyPlaylists, newPlaylist]);
                                          setShowCreatePlaylist(false);
                                          setNewPlaylistName('');
                                          alert('🎵 Playlist created successfully!');
                                        } else {
                                          alert('Playlist creation coming soon!');
                                        }
                                      }}
                                      className="create-confirm-btn"
                                    >
                                      ✓ Create
                                    </Button>
                                    <Button 
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setShowCreatePlaylist(false);
                                        setNewPlaylistName('');
                                      }}
                                      className="create-cancel-btn"
                                    >
                                      ✕ Cancel
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Enhanced Playlists List */}
                          <div className="enhanced-playlists-section">
                            <div className="playlists-header">
                              <span className="playlists-count">
                                {filteredPlaylists.length > 0 ? `${filteredPlaylists.length} playlist${filteredPlaylists.length !== 1 ? 's' : ''}` : 'No playlists'}
                              </span>
                              
                              {/* Smart Controls */}
                              <div className="playlist-controls">
                                <select 
                                  value={playlistSortBy} 
                                  onChange={(e) => setPlaylistSortBy(e.target.value)}
                                  className="playlist-sort-select"
                                >
                                  <option value="recent">Recent</option>
                                  <option value="alphabetical">A-Z</option>
                                  <option value="track_count">Most Tracks</option>
                                </select>
                              </div>
                            </div>

                            <div className={`enhanced-playlists-list ${isBrowseMode ? 'browse-mode' : 'compact-mode'}`}>
                              {isLoadingPlaylists ? (
                                <div className="enhanced-loading">
                                  <div className="loading-spinner"></div>
                                  Loading playlists...
                                </div>
                              ) : filteredPlaylists.length > 0 ? (
                                filteredPlaylists.slice(0, isBrowseMode ? filteredPlaylists.length : 4).map((playlist, index) => (
                                  <div 
                                    key={playlist.id} 
                                    className={`enhanced-playlist-item ${isBrowseMode ? 'browse-item' : ''}`}
                                    style={{animationDelay: `${index * 0.05}s`}}
                                  >
                                    <div className="playlist-item-icon">🎵</div>
                                    <div className="enhanced-playlist-info">
                                      <div className="enhanced-playlist-name">{playlist.name}</div>
                                      <div className="enhanced-playlist-meta">
                                        <span className="playlist-tracks">
                                          {playlist.tracks_total || 0} track{(playlist.tracks_total || 0) !== 1 ? 's' : ''}
                                        </span>
                                        {isBrowseMode && playlist.lastModified && (
                                          <span className="playlist-modified"> • {playlist.lastModified}</span>
                                        )}
                                      </div>
                                      {isBrowseMode && playlist.topTracks && (
                                        <div className="playlist-preview">
                                          {playlist.topTracks.slice(0, 2).join(', ')}
                                          {playlist.topTracks.length > 2 && '...'}
                                        </div>
                                      )}
                                    </div>
                                    <div className="playlist-item-actions">
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="enhanced-add-btn"
                                        onClick={() => alert(`🎵 Would add current track to "${playlist.name}"`)}
                                      >
                                        {isBrowseMode ? (
                                          <>
                                            <Plus className="btn-icon-tiny" />
                                            Add Track
                                          </>
                                        ) : (
                                          <>
                                            <Play className="btn-icon-tiny" />
                                            View
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="enhanced-empty">
                                  <div className="empty-icon">🎶</div>
                                  <div className="empty-text">No playlists found</div>
                                  <div className="empty-subtext">
                                    {playlistSearchTerm ? 'Try adjusting your search' : 'Create your first playlist to get started'}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Browse Toggle */}
                            <div className="browse-controls">
                              {!isBrowseMode && filteredPlaylists.length > 4 && (
                                <div className="enhanced-more-info">
                                  <span className="more-count">+{filteredPlaylists.length - 4} more playlists</span>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="browse-toggle-btn"
                                    onClick={() => setShowSpotifyFullView(true)}
                                  >
                                    <Search className="btn-icon-tiny" />
                                    Full View
                                  </Button>
                                </div>
                              )}
                              
                              {isBrowseMode && (
                                <div className="browse-mode-controls">
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="compact-toggle-btn"
                                    onClick={() => setIsBrowseMode(false)}
                                  >
                                    ↑ Compact View
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="spotify-not-connected">
                        <p>Connect your Spotify account to save tracks to playlists</p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Button 
                            className="connect-spotify-btn"
                            onClick={connectSpotify}
                          >
                            <Music className="btn-icon" />
                            Connect Spotify
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* SoundCloud Integration - Powered by SoundCloud */}
                <Card id="connect-panel-soundcloud" className="soundcloud-integration-card" data-tour="soundcloud-integration">
                  <CardHeader>
                    <CardTitle className="soundcloud-section-title">
                      <img 
                        src="/social-logos/soundcloud-logo.svg" 
                        alt="SoundCloud" 
                        className="soundcloud-title-logo"
                      />
                      SoundCloud Integration
                      <span className="powered-by-badge">Powered by SoundCloud</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {soundcloudConnected && soundcloudAccessToken ? (
                      <div className="soundcloud-connected-layout">
                        {/* Connection info */}
                        <div className="soundcloud-connection-side">
                          <div className="soundcloud-status-connected">
                            {soundcloudAccessToken === 'mock_soundcloud_token' 
                              ? '🧪 Mock Connected (Test Mode)'
                              : '✅ Connected to SoundCloud'
                            }
                          </div>
                          
                          {soundcloudUser && (
                            <div className="soundcloud-user-info">
                              <div className="soundcloud-avatar-wrapper">
                                {soundcloudUser.avatar_url && !soundcloudUser.avatar_url.includes('default_avatar') ? (
                                  <img 
                                    src={soundcloudUser.avatar_url.replace('-large', '-t200x200')} 
                                    alt={soundcloudUser.display_name}
                                    className="soundcloud-avatar"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className="soundcloud-avatar-fallback" style={{ display: soundcloudUser.avatar_url && !soundcloudUser.avatar_url.includes('default_avatar') ? 'none' : 'flex' }}>
                                  <Music size={20} />
                                </div>
                              </div>
                              <div className="soundcloud-user-details">
                                <span className="soundcloud-username">{soundcloudUser.display_name || soundcloudUser.username}</span>
                                <span className="soundcloud-stats">
                                  {soundcloudUser.followers_count?.toLocaleString() || 0} followers • {soundcloudUser.track_count || 0} tracks
                                </span>
                              </div>
                            </div>
                          )}
                          
                          <div className="soundcloud-actions-enhanced">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={async () => {
                                try {
                                  if (soundcloudAccessToken === 'mock_soundcloud_token') {
                                    alert('🧪 Mock connection verified!');
                                    return;
                                  }
                                  
                                  const response = await fetch(`${API}/soundcloud/user?access_token=${encodeURIComponent(soundcloudAccessToken)}`);
                                  
                                  if (response.ok) {
                                    const data = await response.json();
                                    setSoundcloudUser(data);
                                    alert(`✅ Hello ${data.display_name || data.username || 'SoundCloud User'}!`);
                                  } else {
                                    throw new Error('Connection test failed');
                                  }
                                } catch (error) {
                                  alert(`❌ Connection test failed: ${error.message}`);
                                }
                              }}
                              className="enhanced-action-btn test-btn soundcloud-btn"
                            >
                              ✓ Test
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={loadSoundCloudPlaylists}
                              disabled={isLoadingSoundcloud}
                              className="enhanced-action-btn refresh-btn soundcloud-btn"
                            >
                              {isLoadingSoundcloud ? '⏳' : '🔄'} Playlists
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={loadSoundCloudLikes}
                              disabled={isLoadingSoundcloud}
                              className="enhanced-action-btn soundcloud-btn"
                            >
                              {isLoadingSoundcloud ? '⏳' : '❤️'} Likes
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={disconnectSoundCloud}
                              className="enhanced-action-btn disconnect-btn soundcloud-btn"
                            >
                              Disconnect
                            </Button>
                          </div>
                          
                          <p className="soundcloud-connection-subtitle">
                            {soundcloudAccessToken === 'mock_soundcloud_token' 
                              ? 'Mock mode active for testing'
                              : 'Ready for DJ-focused track discovery with BPM filtering'
                            }
                          </p>
                        </div>

                        {/* Playlists/Likes display */}
                        <div className="soundcloud-content-side">
                          {soundcloudPlaylists.length > 0 && (
                            <div className="soundcloud-playlists-section">
                              <h4 className="soundcloud-section-header">
                                <Music size={16} /> Your Playlists ({soundcloudPlaylists.length})
                              </h4>
                              <div className="soundcloud-playlist-grid">
                                {soundcloudPlaylists.slice(0, 4).map(playlist => (
                                  <div key={playlist.id} className="soundcloud-playlist-item">
                                    <div className="soundcloud-playlist-art-wrapper">
                                      {playlist.artwork_url ? (
                                        <img 
                                          src={playlist.artwork_url.replace('-large', '-t200x200')} 
                                          alt={playlist.title} 
                                          className="soundcloud-playlist-art"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                      ) : null}
                                      <div className="soundcloud-art-fallback" style={{ display: playlist.artwork_url ? 'none' : 'flex' }}>
                                        <Music size={14} />
                                      </div>
                                    </div>
                                    <div className="soundcloud-playlist-info">
                                      <span className="soundcloud-playlist-title">{playlist.title}</span>
                                      <span className="soundcloud-playlist-tracks">{playlist.track_count} tracks</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {soundcloudLikes.length > 0 && (
                            <div className="soundcloud-likes-section">
                              <h4 className="soundcloud-section-header">
                                <Heart size={16} /> Your Likes ({soundcloudLikes.length})
                              </h4>
                              <div className="soundcloud-likes-list">
                                {soundcloudLikes.slice(0, 5).map(track => (
                                  <div key={track.id} className="soundcloud-like-item">
                                    <div className="soundcloud-track-art-wrapper">
                                      {track.artwork_url ? (
                                        <img 
                                          src={track.artwork_url.replace('-large', '-t200x200')} 
                                          alt={track.title} 
                                          className="soundcloud-track-art"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                      ) : null}
                                      <div className="soundcloud-art-fallback" style={{ display: track.artwork_url ? 'none' : 'flex' }}>
                                        <Music size={16} />
                                      </div>
                                    </div>
                                    <div className="soundcloud-track-info">
                                      <span className="soundcloud-track-title">{track.title}</span>
                                      <span className="soundcloud-track-artist">{track.artist}</span>
                                      {track.bpm && <span className="soundcloud-track-bpm">{track.bpm} BPM</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {soundcloudPlaylists.length === 0 && soundcloudLikes.length === 0 && (
                            <div className="soundcloud-empty-state">
                              <p>Click &quot;Playlists&quot; or &quot;Likes&quot; to load your SoundCloud library</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="soundcloud-not-connected">
                        <p>Connect SoundCloud for DJ-focused discovery with BPM filtering!</p>
                        <div className="soundcloud-features">
                          <span className="feature-tag">🎚️ BPM Search</span>
                          <span className="feature-tag">🎵 Genre Filter</span>
                          <span className="feature-tag">📻 Stream Tracks</span>
                          <span className="feature-tag">📝 Manage Playlists</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                          <Button 
                            className="connect-soundcloud-btn"
                            onClick={connectSoundCloud}
                          >
                            <img src="/social-logos/soundcloud-logo.svg" alt="" className="btn-icon-img" />
                            Connect SoundCloud
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Discord Integration Widget */}
                <div id="connect-panel-discord" data-tour="discord-integration">
                <DiscordWidget 
                  user={user} 
                  onShareStats={() => {
                    // Check if Discord is connected
                    const discordToken = localStorage.getItem('discord_access_token');
                    if (!discordToken) {
                      alert('Please connect your Discord account first!');
                      return;
                    }
                    // Set up stats data
                    setDiscordShareTrack({
                      tracksIdentified: user?.tracks_identified || Math.floor(Math.random() * 50) + 10,
                      hoursListening: Math.floor(Math.random() * 20) + 5,
                      topGenre: 'Tech House',
                      favoriteArtist: 'Various Artists'
                    });
                    setDiscordShareType('stats');
                    setShowDiscordShareModal(true);
                  }}
                />
                </div>

              </div>
            </TabsContent>



            {/* Crate Box Tab - Feature Cards */}
            <TabsContent value="crate-box" className="tab-content">
              <div style={{
                padding: '2rem',
                maxWidth: '1200px',
                margin: '0 auto'
              }}>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                  <h2 style={{
                    fontSize: '2.5rem',
                    background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '0.5rem'
                  }}>
                    📦 Crate Box
                  </h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem' }}>
                    Your DJ toolkit for discovering, organizing, and building sets
                  </p>
                </div>

                {/* Feature Cards Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '2rem',
                  maxWidth: '800px',
                  margin: '0 auto'
                }}>
                  {/* Diggin' in the Crates Card */}
                  <div
                    onClick={() => setShowDigginInTheCrates(true)}
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(218, 165, 32, 0.25))',
                      border: '2px solid rgba(255, 215, 0, 0.5)',
                      borderRadius: '20px',
                      padding: '2rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.borderColor = '#ffd700';
                      e.currentTarget.style.boxShadow = '0 20px 40px rgba(255, 215, 0, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.5)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    data-testid="diggin-crates-card"
                    data-tour="crate-diggin"
                  >
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                      <svg width="90" height="90" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        {/* Outer ring - richer black */}
                        <circle cx="50" cy="50" r="48" fill="#0a0a0a" stroke="#222" strokeWidth="2"/>
                        {/* Grooves - more visible */}
                        <circle cx="50" cy="50" r="42" fill="none" stroke="#3a3a3a" strokeWidth="0.8"/>
                        <circle cx="50" cy="50" r="38" fill="none" stroke="#333333" strokeWidth="0.8"/>
                        <circle cx="50" cy="50" r="34" fill="none" stroke="#3a3a3a" strokeWidth="0.8"/>
                        <circle cx="50" cy="50" r="30" fill="none" stroke="#333333" strokeWidth="0.8"/>
                        <circle cx="50" cy="50" r="26" fill="none" stroke="#3a3a3a" strokeWidth="0.8"/>
                        <circle cx="50" cy="50" r="22" fill="none" stroke="#333333" strokeWidth="0.8"/>
                        {/* Label */}
                        <circle cx="50" cy="50" r="16" fill="#e8e8e8"/>
                        <circle cx="50" cy="50" r="13" fill="#ffffff"/>
                        {/* Center hole */}
                        <circle cx="50" cy="50" r="3" fill="#0a0a0a"/>
                        {/* Vinyl shine effect */}
                        <ellipse cx="35" cy="35" rx="18" ry="10" fill="rgba(255,255,255,0.06)" transform="rotate(-45 35 35)"/>
                        {/* Tone arm - vertical, rotated 180 */}
                        <g>
                          {/* Arm base */}
                          <circle cx="85" cy="15" r="6" fill="#333" stroke="#444" strokeWidth="1"/>
                          {/* Arm */}
                          <rect x="83" y="15" width="4" height="50" rx="2" fill="#444" stroke="#555" strokeWidth="0.5"/>
                          {/* Head shell */}
                          <rect x="81" y="64" width="8" height="8" rx="1" fill="#555" stroke="#666" strokeWidth="0.5"/>
                          {/* Cartridge */}
                          <rect x="83" y="71" width="4" height="5" fill="#888"/>
                          {/* Stylus */}
                          <line x1="85" y1="76" x2="85" y2="80" stroke="#ccc" strokeWidth="1" strokeLinecap="round"/>
                        </g>
                      </svg>
                    </div>
                    <h3 style={{
                      fontSize: '1.5rem',
                      color: '#fff',
                      marginBottom: '0.75rem',
                      fontWeight: '700'
                    }}>
                      Diggin&apos; in the Crates
                    </h3>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.95rem',
                      lineHeight: '1.5'
                    }}>
                      Explore vinyl releases, discover hidden gems, and find rare tracks across Discogs, Spotify & YouTube
                    </p>
                    <div style={{
                      marginTop: '1.5rem',
                      padding: '0.5rem 1rem',
                      background: 'rgba(255, 215, 0, 0.25)',
                      borderRadius: '8px',
                      display: 'inline-block',
                      color: '#ffd700',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      Start Digging →
                    </div>
                  </div>

                  {/* My Crates Card - Now Active! */}
                  <div
                    onClick={() => user ? setShowMyCrates(true) : alert('Please log in to access My Crates')}
                    style={{
                      background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.15), rgba(0, 128, 255, 0.25))',
                      border: '2px solid rgba(0, 255, 255, 0.5)',
                      borderRadius: '20px',
                      padding: '2rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.borderColor = '#00ffff';
                      e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 255, 255, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.5)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    data-testid="my-crates-card"
                    data-tour="crate-my-crates"
                  >
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                      <svg width="90" height="90" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        {/* Crate box */}
                        <rect x="15" y="25" width="70" height="55" rx="4" fill="#1a1a2e" stroke="#00ffff" strokeWidth="2"/>
                        {/* Crate dividers */}
                        <line x1="15" y1="45" x2="85" y2="45" stroke="#00ffff" strokeWidth="1" opacity="0.5"/>
                        <line x1="15" y1="60" x2="85" y2="60" stroke="#00ffff" strokeWidth="1" opacity="0.5"/>
                        {/* Records peeking out */}
                        <ellipse cx="35" cy="35" rx="12" ry="2" fill="#333" stroke="#444" strokeWidth="1"/>
                        <ellipse cx="50" cy="32" rx="12" ry="2" fill="#222" stroke="#333" strokeWidth="1"/>
                        <ellipse cx="65" cy="34" rx="12" ry="2" fill="#2a2a2a" stroke="#3a3a3a" strokeWidth="1"/>
                        {/* Crate label */}
                        <rect x="35" y="65" width="30" height="8" rx="2" fill="rgba(0, 255, 255, 0.3)"/>
                        {/* Handle */}
                        <rect x="40" y="20" width="20" height="4" rx="2" fill="#333" stroke="#00ffff" strokeWidth="1"/>
                      </svg>
                    </div>
                    <h3 style={{
                      fontSize: '1.5rem',
                      color: '#fff',
                      marginBottom: '0.75rem',
                      fontWeight: '700'
                    }}>
                      My Crates
                    </h3>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.95rem',
                      lineHeight: '1.5'
                    }}>
                      Build and organize your DJ sets with BPM/Key filtering, harmonic mixing suggestions, and export to DJ software
                    </p>
                    <div style={{
                      marginTop: '1.5rem',
                      padding: '0.5rem 1rem',
                      background: 'rgba(0, 255, 255, 0.25)',
                      borderRadius: '8px',
                      display: 'inline-block',
                      color: '#00ffff',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      Open My Crates →
                    </div>
                  </div>
                </div>
              </div>

              {/* Diggin' in the Crates Modal */}
              {showDigginInTheCrates && (
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.95)',
                    zIndex: 99999,
                    overflow: 'auto'
                  }}
                >
                  <DigginInTheCrates 
                    user={user}
                    onAddToWishlist={addToWishlist}
                    onClose={() => setShowDigginInTheCrates(false)}
                  />
                </div>
              )}

              {/* My Crates Modal */}
              {showMyCrates && !selectedCrateForBuilder && (
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.95)',
                    zIndex: 99999,
                    overflow: 'auto'
                  }}
                >
                  <button
                    onClick={() => setShowMyCrates(false)}
                    style={{
                      position: 'fixed',
                      top: '20px',
                      right: '20px',
                      zIndex: 100000,
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}
                    data-testid="close-my-crates"
                  >
                    ×
                  </button>
                  <MyCrates
                    user={user}
                    onOpenSetBuilder={(crate) => setSelectedCrateForBuilder(crate)}
                    onClose={() => setShowMyCrates(false)}
                    onSearchInDiscovery={(searchQuery) => {
                      setShowMyCrates(false);
                      setActiveTab('discover');
                      setDiscoverySearchQuery(searchQuery);
                      setFromReleaseRadar(true);
                      // Trigger search after tab switch
                      setTimeout(() => {
                        loadDiscoverContent(selectedGenre === 'All Genres' ? 'electronic' : selectedGenre, searchQuery);
                      }, 150);
                    }}
                  />
                </div>
              )}

              {/* Set Builder Modal */}
              {selectedCrateForBuilder && (
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: '#0a0a12',
                    zIndex: 99999,
                    overflow: 'auto'
                  }}
                >
                  <SetBuilder
                    crate={selectedCrateForBuilder}
                    onClose={() => setSelectedCrateForBuilder(null)}
                    onUpdate={(updatedCrate) => {
                      setSelectedCrateForBuilder(updatedCrate);
                    }}
                    onAddFromDiscovery={() => {
                      // Set crate context and go to discovery
                      setIsDiscoveringForCrate(true);
                      setTargetCrateId(selectedCrateForBuilder.id);
                      setTargetCrateName(selectedCrateForBuilder.name);
                      setSelectedCrateForBuilder(null); // Close SetBuilder
                      setActiveTab('discover'); // Go to discover tab (correct tab name)
                    }}
                    onAddFromSpotify={() => {
                      // Set crate context
                      setIsDiscoveringForCrate(true);
                      setTargetCrateId(selectedCrateForBuilder.id);
                      setTargetCrateName(selectedCrateForBuilder.name);
                      // Close SetBuilder first, then open Spotify modal after a tick
                      setSelectedCrateForBuilder(null);
                      setTimeout(() => {
                        setShowSpotifyFullView(true);
                      }, 100);
                    }}
                    onAddFromSoundCloud={() => {
                      // Set crate context and open SoundCloud library
                      setIsDiscoveringForCrate(true);
                      setTargetCrateId(selectedCrateForBuilder.id);
                      setTargetCrateName(selectedCrateForBuilder.name);
                      setSelectedCrateForBuilder(null);
                      setTimeout(() => {
                        setShowSoundCloudLibrary(true);
                      }, 100);
                    }}
                    onAddFromFavorites={() => {
                      // Set crate context and open favorites picker
                      setIsDiscoveringForCrate(true);
                      setTargetCrateId(selectedCrateForBuilder.id);
                      setTargetCrateName(selectedCrateForBuilder.name);
                      setSelectedCrateForBuilder(null);
                      setTimeout(() => {
                        setShowFavoritesPicker(true);
                      }, 100);
                    }}
                    onAddFromPlaylists={() => {
                      // Set crate context and open playlist picker
                      setIsDiscoveringForCrate(true);
                      setTargetCrateId(selectedCrateForBuilder.id);
                      setTargetCrateName(selectedCrateForBuilder.name);
                      setSelectedCrateForBuilder(null);
                      setTimeout(() => {
                        setShowPlaylistPicker(true);
                      }, 100);
                    }}
                    onAddFromLocal={() => {
                      // Set crate context and open local upload
                      setIsDiscoveringForCrate(true);
                      setTargetCrateId(selectedCrateForBuilder.id);
                      setTargetCrateName(selectedCrateForBuilder.name);
                      setSelectedCrateForBuilder(null);
                      setTimeout(() => {
                        setShowLocalTrackUpload(true);
                      }, 100);
                    }}
                    spotifyConnected={spotifyConnected}
                    soundcloudConnected={soundcloudConnected}
                  />
                </div>
              )}
            </TabsContent>

            <TabsContent value="dj-tools" className="tab-content">
              <div className="dj-tools-section">
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minHeight: '400px',
                  textAlign: 'center',
                  padding: '2rem',
                  maxWidth: '1200px',
                  margin: '0 auto'
                }}>
                  <h2 style={{
                    fontSize: '2.5rem',
                    background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '0.5rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    justifyContent: 'center'
                  }}>
                    <Disc3 size={40} style={{ color: '#00ffff' }} />
                    The DJ Booth
                  </h2>
                  {user?.id && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }} data-testid="dj-booth-hunter-medal-slot">
                      <IdHunterMedal userId={user.id} size="lg" />
                    </div>
                  )}
                  <p style={{
                    fontSize: '1rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '1rem',
                    maxWidth: '600px'
                  }}>
                    Your ultimate DJ hub for education, tools, community, and opportunities.
                  </p>
                  
                  {/* View All Button - Magenta theme on hover */}
                  <button 
                    className="dj-booth-view-all-btn"
                    onClick={openFullDJBoothModal}
                  >
                    View All Features →
                  </button>

                  {/* Feature Cards Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '1.5rem',
                    width: '100%',
                    maxWidth: '1100px'
                  }}>
                    {/* Industry News Card */}
                    <div 
                      className="dj-booth-feature-card"
                      data-tour="djb-news"
                      onClick={() => openFeatureModal('news')}
                    >
                      <div className="feature-icon">
                        <Newspaper size={36} />
                      </div>
                      <h3>Industry News</h3>
                      <p>Stay ahead with the latest DJ industry news, artist updates, and scene trends.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedFeatures['news'] ? 'liked' : ''}`} 
                          onClick={(e) => handleFeatureCardLike('news', e)}
                        >
                          <ThumbsUp size={14} /> {featureCardStats['news']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleFeatureCardShare('news', e)}>
                          <Share2 size={14} /> {featureCardStats['news']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* Forum Card */}
                    <div 
                      className="dj-booth-feature-card"
                      data-tour="djb-forum"
                      onClick={() => openFeatureModal('forum')}
                    >
                      <div className="feature-icon">
                        <MessageCircle size={36} />
                      </div>
                      <h3>Community Forum</h3>
                      <p>Connect with DJs worldwide. Share tips, ask questions, and grow together.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedFeatures['forum'] ? 'liked' : ''}`} 
                          onClick={(e) => handleFeatureCardLike('forum', e)}
                        >
                          <ThumbsUp size={14} /> {featureCardStats['forum']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleFeatureCardShare('forum', e)}>
                          <Share2 size={14} /> {featureCardStats['forum']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* Gig Hub Card */}
                    <div 
                      className="dj-booth-feature-card"
                      data-tour="djb-gigs"
                      onClick={() => openFeatureModal('gigs')}
                    >
                      <div className="feature-icon">
                        <Mic2 size={36} />
                      </div>
                      <h3>Gig & Event Hub</h3>
                      <p>Find your next gig. Browse opportunities and connect with venues & promoters.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedFeatures['gigs'] ? 'liked' : ''}`} 
                          onClick={(e) => handleFeatureCardLike('gigs', e)}
                        >
                          <ThumbsUp size={14} /> {featureCardStats['gigs']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleFeatureCardShare('gigs', e)}>
                          <Share2 size={14} /> {featureCardStats['gigs']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* Marketplace & Directory Card */}
                    <div 
                      className="dj-booth-feature-card"
                      data-tour="djb-marketplace"
                      onClick={() => openFeatureModal('marketplace')}
                    >
                      <div className="feature-icon">
                        <ShoppingBag size={36} />
                      </div>
                      <h3>Marketplace & Directory</h3>
                      <p>Buy, sell, and discover gear. Explore venues, services, and industry resources.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedFeatures['marketplace'] ? 'liked' : ''}`} 
                          onClick={(e) => handleFeatureCardLike('marketplace', e)}
                        >
                          <ThumbsUp size={14} /> {featureCardStats['marketplace']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleFeatureCardShare('marketplace', e)}>
                          <Share2 size={14} /> {featureCardStats['marketplace']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* DJ Tools Card */}
                    <div 
                      className="dj-booth-feature-card"
                      data-tour="djb-tools"
                      onClick={() => openFeatureModal('tools')}
                    >
                      <div className="feature-icon">
                        <Headphones size={36} />
                      </div>
                      <h3>Pro Tips & DJ Tools</h3>
                      <p>Professional tools at your fingertips. BPM counter, key finder, and more.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedFeatures['tools'] ? 'liked' : ''}`} 
                          onClick={(e) => handleFeatureCardLike('tools', e)}
                        >
                          <ThumbsUp size={14} /> {featureCardStats['tools']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleFeatureCardShare('tools', e)}>
                          <Share2 size={14} /> {featureCardStats['tools']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* The Academy Card */}
                    <div 
                      className="dj-booth-feature-card"
                      data-tour="djb-academy"
                      onClick={() => openFeatureModal('academy')}
                    >
                      <div className="feature-icon">
                        <GraduationCap size={36} />
                      </div>
                      <h3>The Academy</h3>
                      <p>Level up your skills. Tutorials, courses, and knowledge from industry pros.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedFeatures['academy'] ? 'liked' : ''}`} 
                          onClick={(e) => handleFeatureCardLike('academy', e)}
                        >
                          <ThumbsUp size={14} /> {featureCardStats['academy']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleFeatureCardShare('academy', e)}>
                          <Share2 size={14} /> {featureCardStats['academy']?.shares || 0}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="producers-corner" className="tab-content">
              <div className="producers-corner-section">
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minHeight: '400px',
                  textAlign: 'center',
                  padding: '2rem'
                }}>
                  {/* Header */}
                  <h2 style={{
                    fontSize: '2.5rem',
                    background: 'linear-gradient(135deg, #9333ea, #fbbf24)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '0.5rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    justifyContent: 'center'
                  }}>
                    <Headphones size={40} style={{ color: '#fbbf24' }} />
                    Producers Corner
                  </h2>
                  <p style={{
                    fontSize: '1rem',
                    color: 'rgba(255, 255, 255, 0.6)',
                    marginBottom: '1.5rem',
                    maxWidth: '600px'
                  }}>
                    Tips, tricks, demos & connections for producers.
                  </p>
                  
                  {/* View All Button - Purple/Gold theme */}
                  <button 
                    className="producer-view-all-btn"
                    onClick={() => openFullProducersCorner('videos')}
                  >
                    View All Features →
                  </button>

                  {/* Feature Cards Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '1.5rem',
                    width: '100%',
                    maxWidth: '1100px'
                  }}>
                    {/* Video Board Card */}
                    <div 
                      className="producer-feature-card"
                      data-tour="pc-videos"
                      onClick={() => openProducerFeatureModal('videos')}
                    >
                      <div className="feature-icon">
                        <Youtube size={36} />
                      </div>
                      <h3>Video Board</h3>
                      <p>Tutorials, production tips, and music videos from the community.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedProducerFeatures['videos'] ? 'liked' : ''}`} 
                          onClick={(e) => handleProducerFeatureLike('videos', e)}
                        >
                          <ThumbsUp size={14} /> {producerFeatureStats['videos']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleProducerFeatureShare('videos', e)}>
                          <Share2 size={14} /> {producerFeatureStats['videos']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* Forum Card */}
                    <div 
                      className="producer-feature-card"
                      data-tour="pc-forum"
                      onClick={() => openProducerFeatureModal('forum')}
                    >
                      <div className="feature-icon">
                        <MessageCircle size={36} />
                      </div>
                      <h3>Producer Forum</h3>
                      <p>Connect with producers, share knowledge, and get feedback on your work.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedProducerFeatures['forum'] ? 'liked' : ''}`} 
                          onClick={(e) => handleProducerFeatureLike('forum', e)}
                        >
                          <ThumbsUp size={14} /> {producerFeatureStats['forum']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleProducerFeatureShare('forum', e)}>
                          <Share2 size={14} /> {producerFeatureStats['forum']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* Pro Tips Card */}
                    <div 
                      className="producer-feature-card"
                      data-tour="pc-tips"
                      onClick={() => openProducerFeatureModal('tips')}
                    >
                      <div className="feature-icon">
                        <Lightbulb size={36} />
                      </div>
                      <h3>Pro Tips & Producer Tools</h3>
                      <p>Expert production tips, tricks, techniques, and professional tools.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedProducerFeatures['tips'] ? 'liked' : ''}`} 
                          onClick={(e) => handleProducerFeatureLike('tips', e)}
                        >
                          <ThumbsUp size={14} /> {producerFeatureStats['tips']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleProducerFeatureShare('tips', e)}>
                          <Share2 size={14} /> {producerFeatureStats['tips']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* Demo Drops & Contests Card */}
                    <div 
                      className="producer-feature-card"
                      data-tour="pc-demos"
                      onClick={() => openProducerFeatureModal('demos')}
                    >
                      <div className="feature-icon">
                        <Music size={36} />
                      </div>
                      <h3>Demo Drops & Contests</h3>
                      <p>Submit your demos and enter contests to get discovered by labels.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedProducerFeatures['demos'] ? 'liked' : ''}`} 
                          onClick={(e) => handleProducerFeatureLike('demos', e)}
                        >
                          <ThumbsUp size={14} /> {producerFeatureStats['demos']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleProducerFeatureShare('demos', e)}>
                          <Share2 size={14} /> {producerFeatureStats['demos']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* Connect Card */}
                    <div 
                      className="producer-feature-card"
                      data-tour="pc-connect"
                      onClick={() => openProducerFeatureModal('labels')}
                    >
                      <div className="feature-icon">
                        <Building2 size={36} />
                      </div>
                      <h3>Connect</h3>
                      <p>Labels, collaborators & vocalists — find your next creative partner.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedProducerFeatures['labels'] ? 'liked' : ''}`} 
                          onClick={(e) => handleProducerFeatureLike('labels', e)}
                        >
                          <ThumbsUp size={14} /> {producerFeatureStats['labels']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleProducerFeatureShare('labels', e)}>
                          <Share2 size={14} /> {producerFeatureStats['labels']?.shares || 0}
                        </button>
                      </div>
                    </div>

                    {/* Producer News Card */}
                    <div 
                      className="producer-feature-card"
                      data-tour="pc-news"
                      onClick={() => openProducerFeatureModal('news')}
                    >
                      <div className="feature-icon">
                        <Newspaper size={36} />
                      </div>
                      <h3>Producer News</h3>
                      <p>Latest news, trends, and updates in the music production world.</p>
                      <div className="card-actions">
                        <button 
                          className={`card-action-btn like-btn ${userLikedProducerFeatures['news'] ? 'liked' : ''}`} 
                          onClick={(e) => handleProducerFeatureLike('news', e)}
                        >
                          <ThumbsUp size={14} /> {producerFeatureStats['news']?.likes || 0}
                        </button>
                        <button className="card-action-btn share-btn" onClick={(e) => handleProducerFeatureShare('news', e)}>
                          <Share2 size={14} /> {producerFeatureStats['news']?.shares || 0}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          {/* Mobile-only: Take the tour footer link (widget lives on Dashboard only) */}
          {activeTab !== 'social' && (
            <div className="mobile-tour-footer-link">
              <button
                data-testid="footer-take-tour-btn"
                onClick={() => window.dispatchEvent(new Event('beatseek-open-tour'))}
              >
                ✨ Take the tour
              </button>
            </div>
          )}
          
          {/* Mobile Bottom Navigation - Only visible on mobile/tablet */}
          <nav className="mobile-bottom-nav" data-testid="mobile-bottom-nav">
            <div className="mobile-nav-items">
              <button 
                className={`mobile-nav-item ${activeTab === 'listen' ? 'active' : ''}`}
                onClick={() => setActiveTab('listen')}
                data-testid="mobile-nav-listen"
              >
                <Mic className="nav-icon" />
                <span className="nav-label">ID</span>
              </button>
              
              <button 
                className={`mobile-nav-item ${activeTab === 'discover' ? 'active' : ''}`}
                onClick={() => setActiveTab('discover')}
                data-testid="mobile-nav-discover"
              >
                <Search className="nav-icon" />
                <span className="nav-label">Discover</span>
              </button>
              
              <button 
                className={`mobile-nav-item ${activeTab === 'jam-session' ? 'active' : ''}`}
                onClick={() => setActiveTab('jam-session')}
                data-testid="mobile-nav-lounge"
              >
                <Music className="nav-icon" />
                <span className="nav-label">Lounge</span>
              </button>
              
              <button 
                className={`mobile-nav-item ${activeTab === 'social' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('social');
                  if (user?.user_id) {
                    setTimeout(() => refreshSocialData(), 100);
                  }
                }}
                data-testid="mobile-nav-dashboard"
              >
                <Users className="nav-icon" />
                <span className="nav-label">Dashboard</span>
              </button>
              
              <button 
                className={`mobile-nav-item ${activeTab === 'crate-box' ? 'active' : ''}`}
                onClick={() => setActiveTab('crate-box')}
                data-testid="mobile-nav-crates"
              >
                <Disc3 className="nav-icon" />
                <span className="nav-label">Crates</span>
              </button>
              
              <button 
                className={`mobile-nav-item ${activeTab === 'dj-tools' ? 'active' : ''}`}
                onClick={() => setActiveTab('dj-tools')}
                data-testid="mobile-nav-djbooth"
              >
                <Headphones className="nav-icon" />
                <span className="nav-label">DJ Booth</span>
              </button>
              
              <button 
                className={`mobile-nav-item ${activeTab === 'producers-corner' ? 'active' : ''}`}
                onClick={() => setActiveTab('producers-corner')}
                data-testid="mobile-nav-producers"
              >
                <GraduationCap className="nav-icon" />
                <span className="nav-label">Producers</span>
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Check-in Globe Modal */}
      {showGlobeModal && (
        <div 
          onClick={() => {
            setShowGlobeModal(false);
            setShowPinActionMenu(false);
            setSelectedPinUser(null);
            setSelectedPinPosition(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="globe-modal-animated-border"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '95vw',
              height: '92vh',
              maxHeight: '92vh',
              maxWidth: '90%',
              zIndex: 99999,
              borderRadius: '16px',
            }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
              borderRadius: '14px',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
            {/* Compact Header - Title | Separator | Tabs on left | Instructions | Close */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '20px',
              padding: '8px 20px',
              borderBottom: '1px solid rgb(55, 65, 81)',
              background: 'rgba(17, 24, 39, 0.5)'
            }}>
              <h2 style={{ fontSize: '18px', margin: 0, color: '#06b6d4', whiteSpace: 'nowrap' }}>🌍 BEATSEEK Global</h2>
              
              {/* Vertical Separator */}
              <div style={{ 
                width: '1px', 
                height: '24px', 
                background: 'linear-gradient(to bottom, transparent, #06b6d4, transparent)' 
              }}></div>
              
              {/* Tabs on left side - Live Check-ins and The Pulse only */}
              <div className="flex gap-2">
                <button
                  onClick={() => setGlobeTab('checkins')}
                  className={`px-4 py-1 text-xs font-medium rounded transition-all ${
                    globeTab === 'checkins'
                      ? 'bg-cyan-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  📍 Live Check-ins ({activeCheckins.length})
                </button>
                <button
                  onClick={() => {
                    setGlobeTab('area-activity');
                    // Show rotation alert on mobile when entering The Pulse
                    if (window.innerWidth <= 768 && !globeRotationAlertShown) {
                      setTimeout(() => {
                        alert('📱 Rotate your phone for a better experience!');
                        setGlobeRotationAlertShown(true);
                      }, 300);
                    }
                  }}
                  className={`px-4 py-1 text-xs font-medium rounded transition-all ${
                    globeTab === 'area-activity'
                      ? 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  🗺️ The Pulse
                </button>
              </div>
              
              {/* Instructional Text - KEEP EXACTLY AS IS (PERFECT) */}
              <div className="globe-header-instructions" style={{ 
                marginLeft: 'auto',
                fontSize: '11px', 
                color: '#94a3b8',
                textAlign: 'right',
                lineHeight: '1.3',
                paddingRight: '15px'
              }}>
                <div style={{ fontWeight: '700', marginBottom: '2px', color: '#cbd5e1' }}>Manual Mode Features</div>
                <div>Click to pause • Drag to rotate • Pinch to zoom</div>
              </div>
              
              {/* Close button - INSIDE the modal panel, in the yellow circle */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setShowGlobeModal(false);
                  setShowPinActionMenu(false);
                  setSelectedPinUser(null);
                  setSelectedPinPosition(null);
                }}
                className="globe-modal-close-btn hover:bg-gray-700"
                style={{ 
                  padding: '4px 8px', 
                  minWidth: '32px',
                  marginLeft: '12px'
                }}
              >
                ✕
              </Button>
            </div>
            
            {/* Conditional Content: Globe tabs OR The Pulse */}
            {globeTab === 'area-activity' ? (
              <AreaActivityModal user={user} BACKEND_URL={BACKEND_URL} />
            ) : (
              <div className="full-globe-container" style={{ 
                height: 'calc(100% - 40px)',
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {loadingLocations && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-50 rounded-lg">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-3"></div>
                      <p className="text-gray-300">Loading locations...</p>
                    </div>
                  </div>
                )}
                
                {/* Stats in top left - Live check-ins count */}
                <div style={{ 
                  position: 'absolute', 
                  top: '35px', 
                  left: '35px', 
                  zIndex: 10 
                }}>
                  <div className="stat-item" style={{ margin: 0 }}>
                    <span className="stat-number">
                      {activeCheckins.length}
                    </span>
                    <span className="stat-label">
                      LIVE CHECK-INS
                    </span>
                  </div>
                </div>
                
                {/* Check-in button in bottom right - only shows on checkins tab */}
                <div className="globe-checkin-btn-container" style={{ 
                  position: 'absolute', 
                  bottom: '35px', 
                  right: '35px', 
                  zIndex: 9999
                }}>
                  <Button 
                    className="globe-checkin-btn bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium shadow-lg"
                    style={{ pointerEvents: 'auto', position: 'relative', zIndex: 9999 }}
                    onClick={() => {
                      handleCheckIn();
                    }}
                  >
                    📍 Check In
                  </Button>
                </div>
                
                {/* 3D Globe Container - NOW FULL SIZE */}
                <div data-tour="pulse-globe" style={{ 
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  position: 'relative',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  <GlobeVisualization3D
                    users={activeCheckins}
                    onPinClick={handlePinClick}
                    currentUserId={user?.id}
                    BACKEND_URL={BACKEND_URL}
                    spaceVideoUrl1="/space-layer1.mp4"
                    spaceVideoUrl2="/space-layer2.mp4"
                  />
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}
      
      {/* Search Bar - Positioned below modal when modal is open - Hide on The Pulse tab */}
      {showGlobeModal && globeTab !== 'area-activity' && (
        <div className="globe-search-bar" style={{ 
          position: 'fixed',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          maxWidth: '90%',
          zIndex: 99999
        }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search users by name..."
              value={globeSearchQuery}
              onChange={(e) => {
                setGlobeSearchQuery(e.target.value);
                handleGlobeSearch(e.target.value);
              }}
              style={{
                width: '100%',
                padding: '14px 50px 14px 18px',
                borderRadius: '10px',
                border: '1px solid rgba(6, 182, 212, 0.4)',
                background: 'rgba(17, 24, 39, 0.95)',
                backdropFilter: 'blur(10px)',
                color: '#fff',
                fontSize: '15px',
                outline: 'none',
                transition: 'all 0.3s',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
              }}
              onFocus={(e) => e.target.style.borderColor = 'rgba(6, 182, 212, 0.8)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(6, 182, 212, 0.4)'}
            />
            <span style={{
              position: 'absolute',
              right: '18px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '20px',
              opacity: 0.6
            }}>🔍</span>
            
            {/* Search Results Dropdown */}
            {globeSearchResults.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                marginBottom: '10px',
                background: 'rgba(17, 24, 39, 0.98)',
                backdropFilter: 'blur(15px)',
                borderRadius: '10px',
                border: '1px solid rgba(6, 182, 212, 0.4)',
                maxHeight: '350px',
                overflowY: 'auto',
                boxShadow: '0 -4px 25px rgba(0, 0, 0, 0.6)'
              }}>
                {globeSearchResults.map((result) => (
                  <div
                    key={result.user_id}
                    onClick={() => {
                      // Find user in current data and zoom to them
                      const allUsers = globeTab === 'checkins' ? activeCheckins : headquartersLocations;
                      const foundUser = allUsers.find(u => u.user_id === result.user_id || u.id === result.user_id);
                      if (foundUser) {
                        handlePinClick(foundUser, null);
                      }
                      setGlobeSearchQuery('');
                      setGlobeSearchResults([]);
                    }}
                    style={{
                      padding: '14px 18px',
                      borderBottom: '1px solid rgba(55, 65, 81, 0.5)',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(6, 182, 212, 0.15)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {result.profile_picture ? (
                      <img 
                        src={`${BACKEND_URL}${result.profile_picture}`} 
                        alt={result.name}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '15px',
                        fontWeight: 'bold'
                      }}>
                        {result.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '15px', 
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {result.name}
                        {result.online_status && (
                          <span style={{ 
                            width: '7px', 
                            height: '7px', 
                            borderRadius: '50%', 
                            background: '#10b981' 
                          }}></span>
                        )}
                      </div>
                      <div style={{ 
                        color: '#94a3b8', 
                        fontSize: '13px',
                        marginTop: '3px'
                      }}>
                        {result.has_live_checkin && '📍 '}
                        {result.has_headquarters && '🏢 '}
                        {result.location}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Home Base Modal (Location Check) - Shows only Home Base locations */}
      {showHomeBaseModal && (
        <div 
          className="globe-modal-overlay"
          onClick={() => {
            setShowHomeBaseModal(false);
            setShowPinActionMenu(false);
            setSelectedPinUser(null);
            setSelectedPinPosition(null);
          }}
        >
          <div 
            className="globe-modal-animated-border"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '95vw',
              maxWidth: '1200px',
              height: '92vh',
              maxHeight: '92vh',
              borderRadius: '16px',
              zIndex: 99999,
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
              backdropFilter: 'blur(20px)',
              borderRadius: '14px',
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}>
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '20px',
              padding: '8px 20px',
              borderBottom: '1px solid rgb(55, 65, 81)',
              background: 'rgba(17, 24, 39, 0.5)'
            }}>
              <h2 style={{ fontSize: '18px', margin: 0, color: '#a855f7', whiteSpace: 'nowrap' }}>🏠 Location Check-In</h2>
              
              {/* Vertical Separator */}
              <div style={{ 
                width: '1px', 
                height: '24px', 
                background: 'linear-gradient(to bottom, transparent, #a855f7, transparent)' 
              }}></div>
              
              {/* Tab indicator */}
              <div className="flex gap-2">
                <div className="px-4 py-1 text-xs font-medium rounded bg-purple-600 text-white">
                  🏠 Home Base ({headquartersLocations.length})
                </div>
              </div>
              
              {/* Instructional Text */}
              <div style={{ 
                marginLeft: 'auto',
                fontSize: '11px', 
                color: '#94a3b8',
                textAlign: 'right',
                lineHeight: '1.3',
                paddingRight: '15px'
              }}>
                <div style={{ fontWeight: '700', marginBottom: '2px', color: '#cbd5e1' }}>Manual Mode Features</div>
                <div>Click to pause • Drag to rotate • Pinch to zoom</div>
                <div style={{ color: '#22d3ee', marginTop: '2px' }}>👆 Tap a pin to view that user's profile</div>
              </div>
              
              {/* Refresh button */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  fetchHeadquarters();
                }}
                disabled={loadingLocations}
                className="hover:bg-purple-700/30 text-purple-300 hover:text-purple-200"
                style={{ 
                  padding: '4px 10px', 
                  minWidth: '32px',
                  border: '1px solid rgba(168, 85, 247, 0.4)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Refresh home bases"
                data-testid="homebase-refresh-btn"
              >
                <svg className={loadingLocations ? 'animate-spin' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"></path>
                </svg>
                <span className="hidden sm:inline">Refresh</span>
              </Button>

              {/* Close button */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setShowHomeBaseModal(false);
                  setShowPinActionMenu(false);
                  setSelectedPinUser(null);
                  setSelectedPinPosition(null);
                }}
                className="globe-modal-close-btn hover:bg-gray-700"
                style={{ 
                  padding: '4px 8px', 
                  minWidth: '32px',
                  marginLeft: '12px'
                }}
              >
                ✕
              </Button>
            </div>
            
            {/* Globe Content */}
            <div className="full-globe-container" style={{ 
              height: 'calc(100% - 40px)',
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {loadingLocations && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-50 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-3"></div>
                    <p className="text-gray-300">Loading home bases...</p>
                  </div>
                </div>
              )}
              
              {/* Stats overlay */}
              <div style={{ 
                position: 'absolute', 
                top: '25px', 
                left: '30px', 
                zIndex: 10,
                background: 'rgba(17, 24, 39, 0.75)',
                backdropFilter: 'blur(8px)',
                borderRadius: '10px',
                padding: '10px 16px',
                border: '1px solid rgba(168, 85, 247, 0.3)'
              }}>
                <div style={{ 
                  fontSize: '22px', 
                  fontWeight: 'bold', 
                  color: '#a855f7'
                }}>
                  {headquartersLocations.length}
                </div>
                <div style={{ 
                  fontSize: '10px', 
                  color: '#94a3b8', 
                  letterSpacing: '0.05em'
                }}>
                  HOME BASES
                </div>
              </div>
              
              {/* Set Home Base button */}
              <div
                className="homebase-set-btn-container"
                style={{ 
                  position: 'absolute', 
                  bottom: '35px', 
                  right: '35px', 
                  zIndex: 10
                }}
              >
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium shadow-lg"
                  onClick={() => {
                    handleSetHeadquarters();
                  }}
                >
                  🏠 Set Home Base
                </Button>
              </div>
              
              {/* 3D Globe Container */}
              <div style={{ 
                flex: 1,
                minHeight: 0,
                width: '100%',
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <GlobeVisualization3D
                  users={headquartersLocations}
                  onPinClick={handlePinClick}
                  currentUserId={user?.id}
                  BACKEND_URL={BACKEND_URL}
                  spaceVideoUrl1="/space-layer1.mp4"
                  spaceVideoUrl2="/space-layer2.mp4"
                />
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar for Home Base Modal */}
      {showHomeBaseModal && (
        <div className="homebase-search-bar" style={{ 
          position: 'fixed',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          maxWidth: '90%',
          zIndex: 99999
        }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search users by name..."
              value={globeSearchQuery}
              onChange={(e) => {
                setGlobeSearchQuery(e.target.value);
                handleGlobeSearch(e.target.value);
              }}
              style={{
                width: '100%',
                padding: '14px 50px 14px 18px',
                borderRadius: '10px',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                background: 'rgba(17, 24, 39, 0.95)',
                backdropFilter: 'blur(10px)',
                color: '#fff',
                fontSize: '15px',
                outline: 'none',
                transition: 'all 0.3s',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
              }}
              onFocus={(e) => e.target.style.borderColor = 'rgba(168, 85, 247, 0.8)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(168, 85, 247, 0.4)'}
            />
            <span style={{
              position: 'absolute',
              right: '18px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '20px',
              opacity: 0.6
            }}>🔍</span>
            
            {/* Search Results Dropdown */}
            {globeSearchResults.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                marginBottom: '10px',
                background: 'rgba(17, 24, 39, 0.98)',
                backdropFilter: 'blur(15px)',
                borderRadius: '10px',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                maxHeight: '350px',
                overflowY: 'auto',
                boxShadow: '0 -4px 25px rgba(0, 0, 0, 0.6)'
              }}>
                {globeSearchResults.map((result) => (
                  <div
                    key={result.user_id}
                    onClick={() => {
                      // Find user in headquarters data and zoom to them
                      const foundUser = headquartersLocations.find(u => u.user_id === result.user_id || u.id === result.user_id);
                      if (foundUser) {
                        handlePinClick(foundUser, null);
                      }
                      setGlobeSearchQuery('');
                      setGlobeSearchResults([]);
                    }}
                    style={{
                      padding: '14px 18px',
                      borderBottom: '1px solid rgba(55, 65, 81, 0.5)',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(168, 85, 247, 0.15)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {result.profile_picture ? (
                      <img 
                        src={`${BACKEND_URL}${result.profile_picture}`} 
                        alt={result.name}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '15px',
                        fontWeight: 'bold'
                      }}>
                        {result.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '15px', 
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {result.name}
                        {result.online_status && (
                          <span style={{ 
                            width: '7px', 
                            height: '7px', 
                            borderRadius: '50%', 
                            background: '#10b981' 
                          }}></span>
                        )}
                      </div>
                      <div style={{ 
                        color: '#94a3b8', 
                        fontSize: '13px',
                        marginTop: '3px'
                      }}>
                        {result.has_headquarters && '🏠 '}
                        {result.location}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profile Card Modal */}
      {showPinActionMenu && selectedPinUser && (
        <ProfileCard
          user={selectedPinUser}
          onClose={() => {
            setShowPinActionMenu(false);
            setSelectedPinUser(null);
            setSelectedPinPosition(null);
          }}
          onViewProfile={handleViewProfile}
          onMessage={handleMessage}
          isCurrentUser={selectedPinUser.user_id === user?.id || selectedPinUser.id === user?.id}
          BACKEND_URL={BACKEND_URL}
          pinPosition={selectedPinPosition}
        />
      )}

      {/* Recent Discoveries Modal */}
      {showRecentDiscoveries && (
        <div className="discoveries-modal-overlay" onClick={() => setShowRecentDiscoveries(false)}>
          <div className="discoveries-modal" onClick={(e) => e.stopPropagation()}>
            <div className="discoveries-modal-header">
              {/* Top Row: Back, Title, Close */}
              <div className="modal-header-top-row">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setShowRecentDiscoveries(false);
                    setShowDiscoveriesChoice(true);
                  }}
                  className="modal-back-btn"
                >
                  ←
                </Button>
                
                <h2>🎵 Recent Discoveries</h2>
                
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowRecentDiscoveries(false)}
                  className="close-modal-btn"
                >
                  ✕
                </Button>
              </div>
              
              {/* Tabs Row */}
              <div className="modal-tabs-row">
                <button
                  onClick={() => setDiscoveriesTab('music')}
                  className={`modal-tab ${discoveriesTab === 'music' ? 'active' : ''}`}
                  style={{
                    background: discoveriesTab === 'music' ? 'linear-gradient(135deg, #06b6d4, #8b5cf6)' : 'rgba(255,255,255,0.1)',
                    color: discoveriesTab === 'music' ? '#fff' : '#888'
                  }}
                >
                  🎵 Music
                </button>
                <button
                  onClick={() => setDiscoveriesTab('community')}
                  className={`modal-tab ${discoveriesTab === 'community' ? 'active' : ''}`}
                  style={{
                    background: discoveriesTab === 'community' ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : 'rgba(255,255,255,0.1)',
                    color: discoveriesTab === 'community' ? '#fff' : '#888'
                  }}
                >
                  ✨ Community
                </button>
              </div>
              
              {/* Stats Row - Only for Music tab */}
              {discoveriesTab === 'music' && (
                <div className="modal-stats-row">
                  <div className="stat-item">
                    <span className="stat-value" style={{ color: '#06b6d4' }}>{recentDiscoveries.length}</span>
                    <span className="stat-label">TRACKS</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value" style={{ color: '#f472b6' }}>{recentDiscoveries.reduce((sum, d) => sum + (d.likes || 0), 0)}</span>
                    <span className="stat-label">LIKES</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="discoveries-content">
              {/* Music Discoveries Tab */}
              {discoveriesTab === 'music' && (
                <>
                  {loadingDiscoveries ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div className="animate-spin" style={{ width: '40px', height: '40px', border: '3px solid rgba(6, 182, 212, 0.2)', borderTopColor: '#06b6d4', borderRadius: '50%', margin: '0 auto 16px' }}></div>
                        <p style={{ color: '#888' }}>Loading discoveries...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="discoveries-feed">
                      {recentDiscoveries.map((discovery) => (
                        <div key={discovery.id} className="discovery-item" style={{ padding: '16px', marginBottom: '12px' }}>
                          <div className="discovery-header" style={{ marginBottom: '12px' }}>
                            <div className="discovery-user">
                              <div className="discovery-avatar">
                                {discovery.user?.profile_picture ? (
                                  <img src={`${BACKEND_URL}${discovery.user.profile_picture}`} alt={discovery.user?.name} />
                                ) : (
                                  (discovery.user?.name || 'A').charAt(0).toUpperCase()
                                )}
                              </div>
                              <div className="discovery-user-info">
                                <span className="discovery-user-name">{discovery.user?.name || 'Anonymous DJ'}</span>
                                <span className="discovery-time">{discovery.identified_at} • {discovery.user?.location || 'Unknown'}</span>
                              </div>
                            </div>
                            {/* Clean badges - no button styling */}
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <span style={{ 
                                fontSize: '11px', 
                                padding: '3px 8px',
                                borderRadius: '4px',
                                background: discovery.confidence >= 67 ? 'rgba(34, 197, 94, 0.15)' : discovery.confidence >= 34 ? 'rgba(251, 191, 36, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                color: discovery.confidence >= 67 ? '#22c55e' : discovery.confidence >= 34 ? '#fbbf24' : '#ef4444'
                              }}>
                                🎯 {discovery.confidence}%
                              </span>
                              <span style={{ 
                                fontSize: '11px', 
                                padding: '3px 8px',
                                borderRadius: '4px',
                                background: 'rgba(236, 72, 153, 0.15)',
                                color: '#ec4899'
                              }}>
                                ❤️ {discovery.vibeScore || 'N/A'}%
                              </span>
                            </div>
                          </div>
                          
                          <div className="discovery-track" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                            <div className="track-artwork" style={{ width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
                              {discovery.track?.artwork ? (
                                <img src={discovery.track.artwork} alt="Album art" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1a1a2e, #16213e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' }}>🎵</div>
                              )}
                            </div>
                            <div className="track-info" style={{ flex: 1, minWidth: 0 }}>
                              <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{discovery.track?.title || 'Unknown Track'}</h3>
                              <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#06b6d4' }}>{discovery.track?.artist || 'Unknown Artist'}</p>
                              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>{discovery.track?.album} • {discovery.track?.duration}</p>
                              {/* Clean tags - simple text style */}
                              <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#888' }}>
                                <span>{discovery.track?.genre || 'Electronic'}</span>
                                <span>•</span>
                                <span>{discovery.track?.bpm || 120} BPM</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="discovery-actions" style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <Button size="sm" variant="outline" className="like-btn" style={{ fontSize: '12px', padding: '6px 12px' }}>
                              ❤️ {discovery.likes || 0}
                            </Button>
                            <Button size="sm" variant="outline" className="comment-btn" style={{ fontSize: '12px', padding: '6px 12px' }}>
                              💬 {discovery.comments || 0}
                            </Button>
                            {/* Community Share Button */}
                            <Button 
                              size="sm" 
                              variant="outline" 
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                              onClick={() => {
                                // TODO: Implement community share
                                alert('Share to community feed coming soon!');
                              }}
                            >
                              📤 Share
                            </Button>
                            {/* Discord Share Button */}
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => {
                                const discordToken = localStorage.getItem('discord_access_token');
                                if (!discordToken) {
                                  alert('Please connect your Discord account to use this feature!\n\nGo to My Dashboard → Discord Integration → Connect with Discord');
                                  return;
                                }
                                setDiscordShareTrack({
                                  title: discovery.track?.title,
                                  artist: discovery.track?.artist,
                                  artwork: discovery.track?.artwork,
                                  genre: discovery.track?.genre,
                                  bpm: discovery.track?.bpm,
                                  album: discovery.track?.album
                                });
                                setDiscordShareType('track');
                                setShowDiscordShareModal(true);
                              }}
                              style={{
                                fontSize: '12px',
                                padding: '6px 10px',
                                background: 'rgba(88, 101, 242, 0.2)',
                                borderColor: '#5865F2'
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 71 55" fill="#5865F2">
                                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                              </svg>
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="listen-btn" 
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                              onClick={() => {
                                if (discovery.track?.preview_url) {
                                  // Play preview
                                  const audio = new Audio(discovery.track.preview_url);
                                  audio.play();
                                } else {
                                  alert('No preview available for this track');
                                }
                              }}
                            >
                              🎧 Listen
                            </Button>
                          </div>
                          
                          <div className="purchase-actions" style={{ marginTop: '12px' }}>
                            <div className="purchase-actions-wrapper">
                              <div className="shopping-cart-icon">🛒</div>
                              <div className="purchase-buttons-group">
                                <Button 
                                  size="sm" 
                                  className="purchase-btn beatport-btn"
                                  onClick={() => {
                                    const searchQuery = `${discovery.track?.artist} ${discovery.track?.title}`;
                                    window.open(`https://www.beatport.com/search?q=${encodeURIComponent(searchQuery)}`, '_blank');
                                  }}
                                >
                                  <div className="btn-logo beatport-logo">
                                    <img src="/logos/beatport-icon-clean.png" alt="Beatport" />
                                  </div>
                                  Beatport
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="purchase-btn traxsource-btn"
                                  onClick={() => {
                                    const searchQuery = `${discovery.track?.artist} ${discovery.track?.title}`;
                                    window.open(`https://www.traxsource.com/search?term=${encodeURIComponent(searchQuery)}`, '_blank');
                                  }}
                                >
                                  <div className="btn-logo traxsource-logo">
                                    <img src="/logos/traxsource-logo.png" alt="Traxsource" />
                                  </div>
                                  Traxsource
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="purchase-btn spotify-btn"
                                  onClick={() => {
                                    const searchQuery = `artist:${discovery.track?.artist} track:${discovery.track?.title}`;
                                    window.open(`https://open.spotify.com/search/${encodeURIComponent(searchQuery)}`, '_blank');
                                  }}
                                >
                                  <div className="btn-logo spotify-logo">
                                    <img src="/logos/spotify-logo.png" alt="Spotify" />
                                  </div>
                                  Spotify
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {recentDiscoveries.length === 0 && !loadingDiscoveries && (
                        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎵</div>
                          <h3 style={{ color: '#fff', marginBottom: '8px' }}>No discoveries yet</h3>
                          <p style={{ color: '#888', fontSize: '14px' }}>Start identifying tracks to see them here!</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              
              {/* Community Discoveries Tab - Coming Soon */}
              {discoveriesTab === 'community' && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  padding: '80px 20px',
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    fontSize: '64px', 
                    marginBottom: '24px',
                    animation: 'pulse 2s infinite'
                  }}>✨</div>
                  <h3 style={{ 
                    color: '#fff', 
                    fontSize: '24px', 
                    marginBottom: '12px',
                    background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>Community Finds</h3>
                  <p style={{ 
                    color: '#888', 
                    fontSize: '15px',
                    maxWidth: '400px',
                    lineHeight: '1.6',
                    marginBottom: '24px'
                  }}>
                    Discover trending gear reviews, hot merch drops, and community favorites all in one place.
                  </p>
                  <div style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(239, 68, 68, 0.2))',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    borderRadius: '8px',
                    color: '#f59e0b',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    🚀 Coming Soon
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trending Tracks Modal */}
      {showTrendingTracks && (
        <div className="trending-modal-overlay" onClick={() => setShowTrendingTracks(false)}>
          <div className="trending-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trending-modal-header">
              {/* Top Row: Back, Title, Close */}
              <div className="modal-header-top-row">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setShowTrendingTracks(false);
                    setShowDiscoveriesChoice(true);
                  }}
                  className="modal-back-btn"
                >
                  ←
                </Button>
                
                <h2>🔥 Trending Tracks</h2>
                
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowTrendingTracks(false)}
                  className="close-modal-btn"
                >
                  ✕
                </Button>
              </div>
              
              {/* Tabs Row */}
              <div className="modal-tabs-row">
                <button
                  onClick={() => setTrendingTab('music')}
                  className={`modal-tab ${trendingTab === 'music' ? 'active' : ''}`}
                  style={{
                    background: trendingTab === 'music' ? 'linear-gradient(135deg, #f97316, #ef4444)' : 'rgba(255,255,255,0.1)',
                    color: trendingTab === 'music' ? '#fff' : '#888'
                  }}
                >
                  🎵 Music
                </button>
                <button
                  onClick={() => setTrendingTab('community')}
                  className={`modal-tab ${trendingTab === 'community' ? 'active' : ''}`}
                  style={{
                    background: trendingTab === 'community' ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : 'rgba(255,255,255,0.1)',
                    color: trendingTab === 'community' ? '#fff' : '#888'
                  }}
                >
                  ✨ Community
                </button>
              </div>
              
              {/* Stats Row - Only for Music tab */}
              {trendingTab === 'music' && (
                <div className="modal-stats-row">
                  <div className="stat-item">
                    <span className="stat-value" style={{ color: '#f97316' }}>{trendingTracks.length}</span>
                    <span className="stat-label">TRACKS</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value" style={{ color: '#ef4444' }}>{trendingTracks.reduce((sum, t) => sum + (t.trending_data?.total_identifications || 0), 0)}</span>
                    <span className="stat-label">TOTAL IDs</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="trending-content">
              {/* Music Trending Tab */}
              {trendingTab === 'music' && (
                <div className="trending-feed">
                  {trendingTracks.map((trendingTrack) => (
                    <div key={trendingTrack.id} className="trending-item" style={{ padding: '16px', marginBottom: '12px' }}>
                      <div className="trending-header" style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ 
                            fontSize: '20px', 
                            fontWeight: '800', 
                            background: 'linear-gradient(135deg, #f97316, #ef4444)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}>#{trendingTrack.rank}</span>
                          {trendingTrack.trending_data.global_trending && (
                            <span style={{ 
                              fontSize: '10px', 
                              padding: '2px 8px',
                              borderRadius: '4px',
                              background: 'rgba(59, 130, 246, 0.15)',
                              color: '#3b82f6'
                            }}>🌍 Global</span>
                          )}
                          {trendingTrack.trending_data.local_trending && (
                            <span style={{ 
                              fontSize: '10px', 
                              padding: '2px 8px',
                              borderRadius: '4px',
                              background: 'rgba(34, 197, 94, 0.15)',
                              color: '#22c55e'
                            }}>📍 Local</span>
                          )}
                        </div>
                        {/* Clean badges - no button styling */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ 
                            fontSize: '11px', 
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: trendingTrack.confidence >= 67 ? 'rgba(34, 197, 94, 0.15)' : trendingTrack.confidence >= 34 ? 'rgba(251, 191, 36, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                            color: trendingTrack.confidence >= 67 ? '#22c55e' : trendingTrack.confidence >= 34 ? '#fbbf24' : '#ef4444'
                          }}>
                            🎯 {trendingTrack.confidence}%
                          </span>
                          <span style={{ 
                            fontSize: '11px', 
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: 'rgba(236, 72, 153, 0.15)',
                            color: '#ec4899'
                          }}>
                            ❤️ {trendingTrack.confidence < 60 ? 'N/A' : `${trendingTrack.trending_data.avg_vibe_score}%`}
                          </span>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                        <div style={{ width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
                          {trendingTrack.track.artwork ? (
                            <img src={trendingTrack.track.artwork} alt="Album art" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1a1a2e, #16213e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' }}>🎵</div>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{trendingTrack.track.title}</h3>
                          <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#f97316' }}>{trendingTrack.track.artist}</p>
                          <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>{trendingTrack.track.album} • {trendingTrack.track.duration}</p>
                          {/* Clean tags - simple text style */}
                          <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#888' }}>
                            <span>{trendingTrack.track.genre}</span>
                            <span>•</span>
                            <span>{trendingTrack.track.bpm} BPM</span>
                            <span>•</span>
                            <span style={{ color: '#f97316' }}>{trendingTrack.trending_data.identifications_today} IDs today</span>
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button size="sm" variant="outline" className="like-btn" style={{ fontSize: '12px', padding: '6px 12px' }}>
                          ❤️ {trendingTrack.trending_data.likes || 0}
                        </Button>
                        {/* Community Share Button */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                          onClick={() => {
                            alert('Share to community feed coming soon!');
                          }}
                        >
                          📤 Share
                        </Button>
                        {/* Discord Share Button */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            const discordToken = localStorage.getItem('discord_access_token');
                            if (!discordToken) {
                              alert('Please connect your Discord account to use this feature!\n\nGo to My Dashboard → Discord Integration → Connect with Discord');
                              return;
                            }
                            setDiscordShareTrack({
                              title: trendingTrack.track?.title,
                              artist: trendingTrack.track?.artist,
                              artwork: trendingTrack.track?.artwork,
                              genre: trendingTrack.track?.genre,
                              bpm: trendingTrack.track?.bpm,
                              album: trendingTrack.track?.album
                            });
                            setDiscordShareType('track');
                            setShowDiscordShareModal(true);
                          }}
                          style={{
                            fontSize: '12px',
                            padding: '6px 10px',
                            background: 'rgba(88, 101, 242, 0.2)',
                            borderColor: '#5865F2'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 71 55" fill="#5865F2">
                            <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                          </svg>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="listen-btn" 
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                          onClick={() => {
                            if (trendingTrack.track?.preview_url) {
                              const audio = new Audio(trendingTrack.track.preview_url);
                              audio.play();
                            } else {
                              alert('No preview available for this track');
                            }
                          }}
                        >
                          🎧 Listen
                        </Button>
                      </div>
                      
                      <div className="purchase-actions" style={{ marginTop: '12px' }}>
                        <div className="purchase-actions-wrapper">
                          <div className="shopping-cart-icon">🛒</div>
                          <div className="purchase-buttons-group">
                            <Button 
                              size="sm" 
                              className="purchase-btn beatport-btn"
                              onClick={() => {
                                const searchQuery = `${trendingTrack.track.artist} ${trendingTrack.track.title}`;
                                window.open(`https://www.beatport.com/search?q=${encodeURIComponent(searchQuery)}`, '_blank');
                              }}
                            >
                              <div className="btn-logo beatport-logo">
                                <img src="/logos/beatport-icon-clean.png" alt="Beatport" />
                              </div>
                              Beatport
                            </Button>
                            <Button 
                              size="sm" 
                              className="purchase-btn traxsource-btn"
                              onClick={() => {
                                const searchQuery = `${trendingTrack.track.artist} ${trendingTrack.track.title}`;
                                window.open(`https://www.traxsource.com/search?term=${encodeURIComponent(searchQuery)}`, '_blank');
                              }}
                            >
                              <div className="btn-logo traxsource-logo">
                                <img src="/logos/traxsource-logo.png" alt="Traxsource" />
                              </div>
                              Traxsource
                            </Button>
                            <Button 
                              size="sm" 
                              className="purchase-btn spotify-btn"
                              onClick={() => {
                                const searchQuery = `artist:${trendingTrack.track.artist} track:${trendingTrack.track.title}`;
                                window.open(`https://open.spotify.com/search/${encodeURIComponent(searchQuery)}`, '_blank');
                              }}
                            >
                              <div className="btn-logo spotify-logo">
                                <img src="/logos/spotify-logo.png" alt="Spotify" />
                              </div>
                              Spotify
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {trendingTracks.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔥</div>
                      <h3 style={{ color: '#fff', marginBottom: '8px' }}>No trending tracks yet</h3>
                      <p style={{ color: '#888', fontSize: '14px' }}>Tracks will appear here as they gain popularity!</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Community Trending Tab - Coming Soon */}
              {trendingTab === 'community' && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  padding: '80px 20px',
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    fontSize: '64px', 
                    marginBottom: '24px',
                    animation: 'pulse 2s infinite'
                  }}>✨</div>
                  <h3 style={{ 
                    color: '#fff', 
                    fontSize: '24px', 
                    marginBottom: '12px',
                    background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>Community Trending</h3>
                  <p style={{ 
                    color: '#888', 
                    fontSize: '15px',
                    maxWidth: '400px',
                    lineHeight: '1.6',
                    marginBottom: '24px'
                  }}>
                    See what&apos;s hot in the community - trending gear reviews, popular setups, and viral DJ moments.
                  </p>
                  <div style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(239, 68, 68, 0.2))',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    borderRadius: '8px',
                    color: '#f59e0b',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    🚀 Coming Soon
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Spotify Integration Modal */}
      {showSpotifyModal && spotifyTrackToSave && (
        <SpotifyIntegration
          identifiedTrack={spotifyTrackToSave}
          onClose={() => {
            setShowSpotifyModal(false);
            setSpotifyTrackToSave(null);
          }}
        />
      )}

      {/* Discord Share Modal */}
      <DiscordShareModal
        isOpen={showDiscordShareModal}
        onClose={() => {
          setShowDiscordShareModal(false);
          setDiscordShareTrack(null);
          setDiscordShareType('track');
        }}
        track={discordShareTrack}
        user={user}
        shareType={discordShareType}
      />

      {/* Community Chat - NEW VERSION */}
      {showCommunityChat && (
        <NewCommunityChat 
          user={user}
          isOpen={showCommunityChat} 
          onClose={() => {
            setShowCommunityChat(false);
            setChatNavigationState(null);
          }}
          onWhisperClick={initiateWhisper}
          navigationState={chatNavigationState}
        />
      )}

      {/* Producers Corner Modal */}
      <ProducersCorner 
        isOpen={showProducersCorner}
        onClose={() => setShowProducersCorner(false)}
        user={user}
        onWhisperClick={initiateWhisper}
        initialCategory={producerCornerInitialCategory}
      />

      {/* Producer Corner Individual Feature Modal */}
      <ProducerCornerFeatureModal
        isOpen={showProducerFeatureModal}
        onClose={() => { setShowProducerFeatureModal(false); setActiveProducerFeatureId(null); }}
        featureId={activeProducerFeatureId}
        onOpenFullModal={(category) => {
          setShowProducerFeatureModal(false);
          setActiveProducerFeatureId(null);
          openFullProducersCorner(category);
        }}
      />

      {/* DJ Booth Modal */}
      <DJBoothModal 
        isOpen={showDJBoothModal}
        onClose={() => setShowDJBoothModal(false)}
        onShareToDiscord={(gearData) => {
          const discordToken = localStorage.getItem('discord_access_token');
          if (!discordToken) {
            alert('Please connect your Discord account to use this feature!\n\nGo to My Dashboard → Discord Integration → Connect with Discord');
            return;
          }
          setDiscordShareTrack(gearData);
          setDiscordShareType('gear');
          setShowDiscordShareModal(true);
        }}
        onViewInMyEvents={() => {
          // Close the DJBoothModal and open Festival Events to My Events tab
          setShowDJBoothModal(false);
          setFestivalEventsInitialTab('my-events');
          setShowFestivalEvents(true);
        }}
      />

      {/* Individual Feature Modal */}
      <DJBoothFeatureModal
        isOpen={showFeatureModal}
        onClose={() => { setShowFeatureModal(false); setActiveFeatureId(null); }}
        featureId={activeFeatureId}
        onViewInMyEvents={openFestivalEventsToMyEvents}
        onShareToDiscord={(gearData) => {
          const discordToken = localStorage.getItem('discord_access_token');
          if (!discordToken) {
            alert('Please connect your Discord account to use this feature!\n\nGo to My Dashboard → Discord Integration → Connect with Discord');
            return;
          }
          setDiscordShareTrack(gearData);
          setDiscordShareType('gear');
          setShowDiscordShareModal(true);
        }}
      />

      {/* Festival & Events Modal */}
      <FestivalEvents 
        isOpen={showFestivalEvents}
        onClose={() => {
          setShowFestivalEvents(false);
          setFestivalEventsInitialTab('upcoming'); // Reset to default tab
        }}
        initialTab={festivalEventsInitialTab}
        onDiscordShare={(eventData) => {
          setDiscordShareTrack({
            title: eventData.title,
            artist: eventData.artist,
            artwork: eventData.artwork,
            genre: eventData.genre,
            location: eventData.location,
            date: eventData.date,
            url: eventData.url
          });
          setDiscordShareType('event');
          setShowDiscordShareModal(true);
        }}
      />

      {/* Wishlist Modal */}
      <WishlistModal 
        isOpen={showWishlistModal}
        onClose={() => setShowWishlistModal(false)}
        onViewTrack={(trackData) => {
          // Navigate to Discovery Zone and search for the track
          const searchQuery = `${trackData.artist} ${trackData.title}`;
          setDiscoverySearchQuery(searchQuery);
          setActiveTab('discover');
          // Load the search results after a brief delay to ensure tab switch
          setTimeout(() => {
            loadDiscoverContent(selectedGenre, searchQuery);
          }, 100);
        }}
      />

      {/* Artist Notifications Modal */}
      <ArtistNotificationsModal
        isOpen={showArtistNotifications}
        onClose={() => setShowArtistNotifications(false)}
      />

      {/* Expiring Notifications Modal */}
      <ExpiringNotifications
        isOpen={showExpiringNotifications}
        onClose={() => {
          setShowExpiringNotifications(false);
          // Refresh count when closing
          loadExpiringCount();
        }}
      />

      {/* Platform Notifications Panel */}
      <NotificationsPanel
        isOpen={showNotificationsPanel}
        onClose={() => setShowNotificationsPanel(false)}
        notifications={notifications}
        onRefresh={loadNotifications}
        userId={user?.user_id}
      />

      {/* The Promo Spot Modal */}
      <PromoFeedModal
        isOpen={showPromoSpot}
        onClose={() => setShowPromoSpot(false)}
        user={user}
      />

      {/* Discoveries & Trending Choice Modal */}
      {showDiscoveriesChoice && (
        <div className="modal-overlay" onClick={() => setShowDiscoveriesChoice(false)}>
          <div className="choice-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Choose View</h3>
            <button
              className="choice-btn"
              onClick={() => {
                setShowDiscoveriesChoice(false);
                setShowRecentDiscoveries(true);
              }}
            >
              🎵 Recent Discoveries
              <span className="choice-desc">Latest tracks identified by friends</span>
            </button>
            <button
              className="choice-btn"
              onClick={() => {
                setShowDiscoveriesChoice(false);
                setShowTrendingTracks(true);
              }}
            >
              🔥 Trending Now
              <span className="choice-desc">Popular tracks in the community</span>
            </button>
          </div>
        </div>
      )}

      {/* Phase 5: My Tracks Modal from Profile */}
      <MyTracksModal 
        isOpen={showMyTracksFromProfile}
        onClose={() => setShowMyTracksFromProfile(false)}
      />

      {/* My Playlists Modal from Profile Music Activity */}
      {showPlaylistsModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '2rem'
          }}
          onClick={() => setShowPlaylistsModal(false)}
          data-testid="playlists-modal-backdrop"
        >
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.98), rgba(30, 20, 40, 0.98))',
              border: '2px solid rgba(0, 255, 255, 0.4)',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '1000px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="playlists-modal"
          >
            {/* Header */}
            <div data-testid="playlists-modal-header" style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <div data-testid="playlists-modal-title-block">
                <h2 data-testid="playlists-modal-title" style={{ 
                  margin: 0,
                  background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '1.8rem',
                  whiteSpace: 'nowrap'
                }}>
                  🎵 My Playlists
                </h2>
                <p data-testid="playlists-modal-subtitle" style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.5rem', marginBottom: 0, whiteSpace: 'nowrap' }}>
                  Create and manage your BEATSEEK playlists
                </p>
              </div>
              <div data-testid="playlists-modal-actions" style={{ display: 'flex', gap: '0.75rem' }}>
                <button 
                  className="the-wall-fill-btn"
                  onClick={() => {
                    setPlaylistModalData({ 
                      createPlaylist,
                      deletePlaylist,
                      fetchMyPlaylists,
                      addTrackToPlaylist,
                      reorderPlaylistTracks,
                      playPlaylist,
                      playTrack,
                      user,
                      setActiveTab,
                      setShowPlaylistsModal,
                      setShowPlaylistDetailModal,
                      setCurrentPlaylist
                    });
                    setShowCreatePlaylistModal(true);
                  }}
                  data-testid="create-playlist-btn"
                >
                  + Create Playlist
                </button>
                <button
                  onClick={() => setShowPlaylistsModal(false)}
                  style={{
                    padding: '0.5rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    aspectRatio: '1 / 1',
                    flexShrink: 0
                  }}
                  data-testid="close-playlists-modal"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div data-testid="playlists-modal-tabs" style={{ 
              display: 'flex', 
              gap: '1rem',
              borderBottom: '2px solid rgba(0, 255, 255, 0.2)',
              marginBottom: '2rem'
            }}>
              <button
                style={{
                  padding: '0.75rem 1.5rem',
                  background: playlistTab === 'my' ? 'rgba(0, 255, 255, 0.1)' : 'transparent',
                  border: 'none',
                  borderBottom: playlistTab === 'my' ? '3px solid #00ffff' : '3px solid transparent',
                  color: playlistTab === 'my' ? '#00ffff' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onClick={() => {
                  setPlaylistTab('my');
                  fetchMyPlaylists();
                }}
              >
                My Playlists ({myPlaylists.length})
              </button>
              <button
                style={{
                  padding: '0.75rem 1.5rem',
                  background: playlistTab === 'public' ? 'rgba(255, 0, 255, 0.1)' : 'transparent',
                  border: 'none',
                  borderBottom: playlistTab === 'public' ? '3px solid #ff00ff' : '3px solid transparent',
                  color: playlistTab === 'public' ? '#ff00ff' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onClick={() => {
                  setPlaylistTab('public');
                  fetchPublicPlaylists();
                }}
              >
                Public Playlists ({publicPlaylists.length})
              </button>
            </div>

            {/* Loading State */}
            {loadingPlaylists && (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
                Loading playlists...
              </div>
            )}

            {/* My Playlists View */}
            {!loadingPlaylists && playlistTab === 'my' && (
              <div>
                {myPlaylists.length === 0 ? (
                  <div style={{
                    padding: '3rem',
                    background: 'rgba(0, 255, 255, 0.05)',
                    border: '2px dashed rgba(0, 255, 255, 0.3)',
                    borderRadius: '12px',
                    textAlign: 'center',
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎵</div>
                    <h3>No playlists yet</h3>
                    <p>Create your first playlist using the button above!</p>
                  </div>
                ) : (
                  <div data-testid="playlists-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '1.5rem'
                  }}>
                    {myPlaylists.map(playlist => (
                      <div
                        key={playlist.id}
                        data-testid="playlist-card"
                        style={{
                          background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1), rgba(255, 0, 255, 0.1))',
                          border: '2px solid rgba(0, 255, 255, 0.3)',
                          borderRadius: '12px',
                          padding: '1.25rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                        onClick={() => {
                          setCurrentPlaylist(playlist);
                          setShowPlaylistDetailModal(true);
                          setShowPlaylistsModal(false);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.02)';
                          e.currentTarget.style.borderColor = '#00ffff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.3)';
                        }}
                      >
                        <div style={{ 
                          width: '100%',
                          aspectRatio: '1',
                          background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(255, 0, 255, 0.2))',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '2.5rem',
                          marginBottom: '0.75rem'
                        }}>
                          🎵
                        </div>
                        <h3 style={{ 
                          margin: '0 0 0.5rem 0',
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {playlist.name}
                        </h3>
                        <p style={{ 
                          margin: 0,
                          fontSize: '0.8rem',
                          color: 'rgba(255, 255, 255, 0.6)'
                        }}>
                          {playlist.tracks?.length || 0} tracks
                        </p>
                        <p style={{ 
                          margin: '0.25rem 0 0 0',
                          fontSize: '0.7rem',
                          color: playlist.is_public ? '#00ffff' : 'rgba(255, 255, 255, 0.4)'
                        }}>
                          {playlist.is_public ? '🌐 Public' : '🔒 Private'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Public Playlists View */}
            {!loadingPlaylists && playlistTab === 'public' && (
              <div>
                {publicPlaylists.length === 0 ? (
                  <div style={{
                    padding: '3rem',
                    background: 'rgba(255, 0, 255, 0.05)',
                    border: '2px dashed rgba(255, 0, 255, 0.3)',
                    borderRadius: '12px',
                    textAlign: 'center',
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌐</div>
                    <h3>No public playlists yet</h3>
                    <p>Be the first to share a playlist with the BEATSEEK community!</p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '1.5rem'
                  }}>
                    {publicPlaylists.map(playlist => (
                      <div
                        key={playlist.id}
                        style={{
                          background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.1), rgba(0, 255, 255, 0.1))',
                          border: '2px solid rgba(255, 0, 255, 0.3)',
                          borderRadius: '12px',
                          padding: '1.25rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                        onClick={() => {
                          setCurrentPlaylist(playlist);
                          setShowPlaylistDetailModal(true);
                          setShowPlaylistsModal(false);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.02)';
                          e.currentTarget.style.borderColor = '#ff00ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.3)';
                        }}
                      >
                        <div style={{ 
                          width: '100%',
                          aspectRatio: '1',
                          background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.2), rgba(0, 255, 255, 0.2))',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '2.5rem',
                          marginBottom: '0.75rem'
                        }}>
                          🌐
                        </div>
                        <h3 style={{ 
                          margin: '0 0 0.5rem 0',
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {playlist.name}
                        </h3>
                        <p style={{ 
                          margin: 0,
                          fontSize: '0.8rem',
                          color: 'rgba(255, 255, 255, 0.6)'
                        }}>
                          {playlist.track_count || 0} tracks • by {playlist.owner_username || 'Unknown'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Phase 4: Friend Requests Modal */}
      <FriendRequestsModal
        isOpen={showFriendRequests}
        onClose={() => setShowFriendRequests(false)}
      />

      {/* Phase 4: Whisper Conversations List */}
      <WhisperConversations
        isOpen={showWhisperConversations}
        onClose={() => setShowWhisperConversations(false)}
        onSelectConversation={(conv) => {
          setWhisperTarget(conv);
          setShowWhisperModal(true);
        }}
      />

      {/* Phase 4: Whisper Modal */}
      <WhisperModal
        isOpen={showWhisperModal}
        onClose={() => setShowWhisperModal(false)}
        otherUser={whisperTarget}
        currentUser={user}
        globalEmotes={globalEmotes}
        customEmotes={customEmotes}
      />

      {/* Edit Profile Modal */}
      <EditProfileModal
        user={user}
        isOpen={showEditProfile}
        onClose={() => setShowEditProfile(false)}
        onUpdate={handleProfileUpdate}
      />

      {/* Friends Modal */}
      <FriendsModal
        isOpen={showFriendsModal}
        onClose={() => setShowFriendsModal(false)}
        user={user}
        onWhisperClick={(target) => {
          setWhisperTarget(target);
          setShowWhisperModal(true);
        }}
      />

      {/* Private Feed Modal */}
      {showPrivateFeedModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-2xl border border-purple-500/20 w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">🔒 Private Feedback Feed</h3>
                  <p className="text-sm text-gray-400">All private feedback on your posts</p>
                </div>
              </div>
              <button
                onClick={() => setShowPrivateFeedModal(false)}
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto max-h-[70vh] p-6">
              {loadingPrivateFeed ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                  <span className="ml-3 text-gray-400">Loading private feedback...</span>
                </div>
              ) : privateFeedReplies.length > 0 ? (
                <div className="space-y-4">
                  {privateFeedReplies.map((reply) => (
                    <div key={reply.id} className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                            {reply.author_name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <h4 className="font-semibold text-white text-sm">{reply.author_name || 'Anonymous'}</h4>
                            <p className="text-xs text-gray-400">
                              {new Date(reply.created_at).toLocaleDateString()} at {new Date(reply.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-300 border border-purple-500/50">
                          🔒 Private
                        </span>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 mb-1">Feedback on:</p>
                        <h5 className="text-sm font-medium text-blue-300">{reply.post_title}</h5>
                      </div>
                      
                      <div className="text-gray-300 text-sm leading-relaxed mb-3">
                        {reply.content}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <button className="flex items-center gap-1 text-gray-400 hover:text-red-400 text-xs transition-colors duration-200">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            {reply.likes_count || 0}
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            setShowPrivateFeedModal(false);
                            // Navigate to the specific post (you could implement this later)
                          }}
                          className="text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200"
                        >
                          View Post →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <h4 className="text-lg font-semibold text-white mb-2">No Private Feedback Yet</h4>
                  <p className="text-gray-400">
                    Private feedback will appear here when others leave private replies on your posts.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-6 border-t border-gray-700/50">
              <div className="text-xs text-gray-400">
                Private feedback is only visible to you as the post author
              </div>
              <button
                onClick={() => setShowPrivateFeedModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Moderation Modal */}
      {showAdminModeration && user?.role === 'admin' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-900 via-red-900 to-gray-900 rounded-2xl border border-red-500/20 w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-red-700/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">🛡️ Admin Moderation Center</h3>
                  <p className="text-sm text-gray-400">Review and approve flagged content</p>
                </div>
              </div>
              <button
                onClick={() => setShowAdminModeration(false)}
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Tabs — horizontally scrollable strip with desktop arrows */}
            <div className="relative border-b border-red-700/50 bg-gray-900/50">
              <button
                onClick={() => document.getElementById('moderation-tabs-strip')?.scrollBy({ left: -280, behavior: 'smooth' })}
                className="hidden sm:flex absolute left-0 top-0 bottom-0 z-10 items-center px-1.5 bg-gradient-to-r from-gray-900 via-gray-900/90 to-transparent text-gray-400 hover:text-white"
                data-testid="moderation-tabs-scroll-left"
                aria-label="Scroll tabs left"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => document.getElementById('moderation-tabs-strip')?.scrollBy({ left: 280, behavior: 'smooth' })}
                className="hidden sm:flex absolute right-0 top-0 bottom-0 z-10 items-center px-1.5 bg-gradient-to-l from-gray-900 via-gray-900/90 to-transparent text-gray-400 hover:text-white"
                data-testid="moderation-tabs-scroll-right"
                aria-label="Scroll tabs right"
              >
                <ChevronRight size={20} />
              </button>
              <div
                id="moderation-tabs-strip"
                className="flex overflow-x-auto sm:px-7"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
              <button
                onClick={() => setModerationTab('messages')}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'messages'
                    ? 'text-white bg-red-600/20 border-b-2 border-red-500'
                    : 'text-gray-400 hover:text-white hover:bg-red-600/10'
                }`}
              >
                💬 Messages ({pendingModerations.length})
                {pendingModerations.length > 0 && (
                  <span className="absolute top-2.5 right-2 w-[6px] h-[6px] rounded-full bg-yellow-400 shadow-sm animate-pulse" style={{boxShadow: '0 0 6px rgba(250, 204, 21, 0.9)'}} />
                )}
              </button>
              <button
                onClick={() => {
                  setModerationTab('emotes');
                  fetchPendingEmotes();
                }}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'emotes'
                    ? 'text-white bg-red-600/20 border-b-2 border-red-500'
                    : 'text-gray-400 hover:text-white hover:bg-red-600/10'
                }`}
              >
                😊 Emotes ({pendingEmotes.length})
                {pendingEmotes.length > 0 && (
                  <span className="absolute top-2.5 right-2 w-[6px] h-[6px] rounded-full bg-yellow-400 shadow-sm animate-pulse" style={{boxShadow: '0 0 6px rgba(250, 204, 21, 0.9)'}} />
                )}
              </button>
              <button
                onClick={() => {
                  setModerationTab('videos');
                  fetchPendingVideos();
                }}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'videos'
                    ? 'text-white bg-red-600/20 border-b-2 border-red-500'
                    : 'text-gray-400 hover:text-white hover:bg-red-600/10'
                }`}
              >
                🎬 Videos ({pendingVideos.length})
                {pendingVideos.length > 0 && (
                  <span className="absolute top-2.5 right-2 w-[6px] h-[6px] rounded-full bg-yellow-400 shadow-sm animate-pulse" style={{boxShadow: '0 0 6px rgba(250, 204, 21, 0.9)'}} />
                )}
              </button>
              <button
                onClick={() => {
                  setModerationTab('beta');
                  fetchBetaData();
                }}
                className={`flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 relative ${
                  moderationTab === 'beta'
                    ? 'text-white bg-purple-600/20 border-b-2 border-purple-500'
                    : 'text-gray-400 hover:text-white hover:bg-purple-600/10'
                }`}
              >
                🔑 Beta Access
                {betaRequests.filter(r => r.status === 'pending').length > 0 && (
                  <span className="absolute top-2.5 right-2 w-[6px] h-[6px] rounded-full bg-yellow-400 shadow-sm animate-pulse" style={{boxShadow: '0 0 6px rgba(250, 204, 21, 0.9)'}} />
                )}
              </button>
              <button
                onClick={() => {
                  setModerationTab('news');
                  fetchAdminAnnouncements();
                }}
                className={`flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'news'
                    ? 'text-white bg-amber-600/20 border-b-2 border-amber-500'
                    : 'text-gray-400 hover:text-white hover:bg-amber-600/10'
                }`}
                data-testid="moderation-news-tab"
              >
                📢 BEATSEEK News
              </button>
              <button
                onClick={() => {
                  setModerationTab('new_members');
                  fetchNewMembersList();
                  markNewMembersViewed();
                }}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'new_members'
                    ? 'text-white bg-fuchsia-600/20 border-b-2 border-fuchsia-500'
                    : 'text-gray-400 hover:text-white hover:bg-fuchsia-600/10'
                }`}
                data-testid="moderation-new-members-tab"
              >
                🎉 New Members ({newMembersUnreadCount})
                {newMembersUnreadCount > 0 && (
                  <span
                    className="absolute top-2.5 right-2 w-[6px] h-[6px] rounded-full bg-yellow-400 shadow-sm animate-pulse"
                    style={{boxShadow: '0 0 6px rgba(250, 204, 21, 0.9)'}}
                    data-testid="new-members-tab-badge"
                  />
                )}
              </button>
              <button
                onClick={() => {
                  setModerationTab('suggestions');
                  fetchToolSuggestions();
                }}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'suggestions'
                    ? 'text-white bg-teal-600/20 border-b-2 border-teal-500'
                    : 'text-gray-400 hover:text-white hover:bg-teal-600/10'
                }`}
                data-testid="moderation-suggestions-tab"
              >
                💡 Suggestions ({toolSuggestions.length})
                {toolSuggestions.filter(s => s.status === 'pending').length > 0 && (
                  <span className="absolute top-2.5 right-2 w-[6px] h-[6px] rounded-full bg-yellow-400 shadow-sm animate-pulse" style={{boxShadow: '0 0 6px rgba(250, 204, 21, 0.9)'}} />
                )}
              </button>
              <button
                onClick={() => setModerationTab('id_corrections')}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'id_corrections'
                    ? 'text-white bg-pink-600/20 border-b-2 border-pink-500'
                    : 'text-gray-400 hover:text-white hover:bg-pink-600/10'
                }`}
                data-testid="moderation-id-corrections-tab"
              >
                🎯 ID Corrections
                {pendingCuratedIds > 0 && (
                  <span className="ml-2 rounded-full bg-amber-500/25 px-1.5 py-0.5 text-[10px] font-bold text-amber-200" data-testid="moderation-curated-pending-count">{pendingCuratedIds}</span>
                )}
                {pendingCuratedIds > 0 && (
                  <span className="absolute top-2.5 right-2 w-[6px] h-[6px] rounded-full bg-amber-400 animate-pulse" style={{ boxShadow: '0 0 6px rgba(251, 191, 36, 0.9)' }} />
                )}
              </button>
              <button
                onClick={() => setModerationTab('badges')}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'badges'
                    ? 'text-white bg-cyan-600/20 border-b-2 border-cyan-500'
                    : 'text-gray-400 hover:text-white hover:bg-cyan-600/10'
                }`}
                data-testid="moderation-badges-tab"
              >
                🏅 Badges
              </button>
              <button
                onClick={() => {
                  setModerationTab('vip');
                  fetchVipList();
                }}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'vip'
                    ? 'text-white bg-yellow-600/20 border-b-2 border-yellow-500'
                    : 'text-gray-400 hover:text-white hover:bg-yellow-600/10'
                }`}
                data-testid="moderation-vip-tab"
              >
                👑 VIP
              </button>
              <button
                onClick={() => setModerationTab('gate')}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'gate'
                    ? 'text-white bg-cyan-600/20 border-b-2 border-cyan-500'
                    : 'text-gray-400 hover:text-white hover:bg-cyan-600/10'
                }`}
                data-testid="moderation-gate-tab"
              >
                ⏳ Countdown
              </button>
              <button
                onClick={() => setModerationTab('id-engine')}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'id-engine'
                    ? 'text-white bg-cyan-600/20 border-b-2 border-cyan-500'
                    : 'text-gray-400 hover:text-white hover:bg-cyan-600/10'
                }`}
                data-testid="moderation-id-engine-tab"
              >
                🎯 ID Engine
              </button>
              <button
                onClick={() => setModerationTab('email-blast')}
                className={`relative flex-none whitespace-nowrap px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  moderationTab === 'email-blast'
                    ? 'text-white bg-cyan-600/20 border-b-2 border-cyan-500'
                    : 'text-gray-400 hover:text-white hover:bg-cyan-600/10'
                }`}
                data-testid="moderation-email-blast-tab"
              >
                📧 Email Blast
              </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6">
              {moderationTab === 'messages' && (
                // Messages Tab Content
                loadingModerations ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                    <span className="ml-3 text-gray-400">Loading pending moderations...</span>
                  </div>
                ) : pendingModerations.length > 0 ? (
                  <div className="space-y-4">
                    {pendingModerations.map((item) => {
                      // Get the content based on content type
                      const contentText = item.content_type === 'message' 
                        ? item.message 
                        : item.content_type === 'post' 
                        ? `${item.title}: ${item.content}`
                        : item.content;
                      
                      const authorName = item.user_name || item.author_name || 'Anonymous';
                      const timestamp = item.timestamp || item.created_at;
                      const severity = item.moderation_severity || 'medium';
                      const severityColors = {
                        low: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
                        medium: 'bg-orange-500/20 text-orange-300 border-orange-500/50',
                        high: 'bg-red-500/20 text-red-300 border-red-500/50'
                      };
                      
                      return (
                        <div key={item.id} className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                                {authorName.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <h4 className="font-semibold text-white text-sm">{authorName}</h4>
                                <p className="text-xs text-gray-400">
                                  {timestamp ? new Date(timestamp).toLocaleString() : 'Unknown date'}
                                </p>
                                <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs bg-gray-700/50 text-gray-300">
                                  {item.content_type === 'message' ? '💬 Chat Message' : item.content_type === 'post' ? '📝 Forum Post' : '💭 Forum Reply'}
                                </span>
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs ${severityColors[severity]}`}>
                              {severity === 'high' ? '🚨 High' : severity === 'medium' ? '⚠️ Medium' : '⚡ Low'} Risk
                            </span>
                          </div>
                          
                          <div className="bg-gray-800/50 p-3 rounded-lg mb-3">
                            <p className="text-gray-300 text-sm leading-relaxed">
                              {contentText || 'No content available'}
                            </p>
                          </div>
                          
                          <div className="bg-gray-800/30 p-2 rounded-lg mb-3">
                            <p className="text-xs text-gray-400">
                              <span className="font-semibold text-red-300">AI Reason:</span> {item.moderation_reason || 'N/A'}
                            </p>
                            {item.moderation_explanation && (
                              <p className="text-xs text-gray-400 mt-1">
                                <span className="font-semibold text-red-300">Details:</span> {item.moderation_explanation}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              {item.expires_at && (
                                <>
                                  <span>⏰ Expires: {new Date(item.expires_at).toLocaleDateString()}</span>
                                </>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button 
                                onClick={() => approveContent(item.content_type, item.id)}
                                className="bg-green-600/20 hover:bg-green-600/30 border border-green-500/50 text-green-300 hover:text-white px-3 py-1.5 rounded text-xs transition-colors font-medium"
                              >
                                ✅ Approve
                              </button>
                              <button 
                                onClick={() => denyContent(item.content_type, item.id)}
                                className="bg-red-600/20 hover:bg-red-600/30 border border-red-500/50 text-red-300 hover:text-white px-3 py-1.5 rounded text-xs transition-colors font-medium"
                              >
                                ❌ Deny
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 mx-auto text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="text-lg font-semibold text-white mb-2">No Pending Messages</h4>
                    <p className="text-gray-400">
                      Great! All messages have been reviewed. New flagged messages will appear here.
                    </p>
                  </div>
                )
              )}
              
              {moderationTab === 'emotes' && (
                // Emotes Tab Content
                loadingEmotes ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                    <span className="ml-3 text-gray-400">Loading pending emotes...</span>
                  </div>
                ) : pendingEmotes.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {pendingEmotes.map((item) => (
                      <div key={item.emote.id} className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                        <div className="flex flex-col items-center">
                          {/* Emote Preview */}
                          <div className="w-24 h-24 bg-gray-800/50 rounded-lg flex items-center justify-center mb-3">
                            <img 
                              src={`${BACKEND_URL}/api${item.emote.file_path_112}`} 
                              alt={item.emote.name}
                              className="w-20 h-20 object-contain"
                              onError={(e) => {
                                console.error('Failed to load emote image:', item.emote.file_path_112);
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                          
                          {/* Emote Info */}
                          <div className="text-center mb-3">
                            <p className="text-white font-semibold text-sm">:{item.emote.name}:</p>
                            <p className="text-gray-400 text-xs mt-1">{item.emote.type === 'static' ? '📷 PNG' : '🎞️ GIF'}</p>
                            <p className="text-gray-500 text-xs mt-1">by {item.uploader_name}</p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-2 w-full">
                            <button 
                              onClick={() => approveEmote(item.emote.id)}
                              className="flex-1 bg-green-600/20 hover:bg-green-600/30 border border-green-500/50 text-green-300 hover:text-white px-3 py-1.5 rounded text-xs transition-colors"
                            >
                              ✅ Approve
                            </button>
                            <button 
                              onClick={() => denyEmote(item.emote.id)}
                              className="flex-1 bg-red-600/20 hover:bg-red-600/30 border border-red-500/50 text-red-300 hover:text-white px-3 py-1.5 rounded text-xs transition-colors"
                            >
                              ❌ Deny
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">😊</div>
                    <h4 className="text-lg font-semibold text-white mb-2">No Pending Emotes</h4>
                    <p className="text-gray-400">
                      Great! All emotes have been reviewed. New uploaded emotes will appear here.
                    </p>
                  </div>
                )
              )}
              
              {moderationTab === 'videos' && (
                // Videos Tab Content
                loadingVideos ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                    <span className="ml-3 text-gray-400">Loading pending videos...</span>
                  </div>
                ) : pendingVideos.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {pendingVideos.map((video) => (
                      <div key={video.id} className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                        {/* Video Preview */}
                        <div className="relative w-full aspect-video bg-gray-800/50 rounded-lg mb-4 overflow-hidden">
                          <video 
                            src={`${BACKEND_URL}${video.video_url}`}
                            className="w-full h-full object-cover"
                            controls
                            preload="metadata"
                          />
                        </div>
                        
                        {/* Video Info */}
                        <div className="mb-4">
                          <h4 className="text-white font-semibold text-lg mb-2">{video.title}</h4>
                          {video.description && (
                            <p className="text-gray-300 text-sm mb-3 line-clamp-2">{video.description}</p>
                          )}
                          
                          {/* Uploader Info */}
                          <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                            <span className="font-semibold text-cyan-400">Uploader ID:</span>
                            <span className="font-mono text-xs">{video.user_id}</span>
                          </div>
                          
                          {/* Upload Date */}
                          <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                            <span>📅 Uploaded:</span>
                            <span>{new Date(video.created_at).toLocaleString()}</span>
                          </div>
                          
                          {/* Stats */}
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span>👁️ {video.view_count || 0} views</span>
                            <span>👍 {video.like_count || 0} likes</span>
                          </div>
                        </div>
                        
                        {/* AI Moderation Details */}
                        {video.moderation_details && (
                          <div className="bg-gray-800/30 p-3 rounded-lg mb-4">
                            <p className="text-xs text-gray-400 mb-1">
                              <span className="font-semibold text-red-300">AI Analysis:</span>
                            </p>
                            {video.moderation_details.explanation && (
                              <p className="text-xs text-gray-300 mb-2">{video.moderation_details.explanation}</p>
                            )}
                            {video.moderation_details.severity && (
                              <span className={`inline-block px-2 py-1 rounded text-xs ${
                                video.moderation_details.severity === 'high' 
                                  ? 'bg-red-500/20 text-red-300 border border-red-500/50'
                                  : video.moderation_details.severity === 'medium'
                                  ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50'
                                  : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50'
                              }`}>
                                Severity: {video.moderation_details.severity}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex gap-3">
                          <button 
                            onClick={() => approveVideo(video.id)}
                            className="flex-1 bg-green-600/20 hover:bg-green-600/30 border border-green-500/50 text-green-300 hover:text-white px-4 py-2 rounded-lg text-sm transition-colors font-medium"
                          >
                            ✅ Approve Video
                          </button>
                          <button 
                            onClick={() => rejectVideo(video.id)}
                            className="flex-1 bg-red-600/20 hover:bg-red-600/30 border border-red-500/50 text-red-300 hover:text-white px-4 py-2 rounded-lg text-sm transition-colors font-medium"
                          >
                            ❌ Reject Video
                          </button>
                        </div>
                        
                        {/* Example Info Banner */}
                        <div className="mt-3 bg-blue-900/20 border border-blue-700/50 rounded-lg p-2">
                          <p className="text-xs text-blue-300">
                            💡 <span className="font-semibold">Example:</span> Approve if content follows community guidelines. Reject if inappropriate, offensive, or violates policies.
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🎬</div>
                    <h4 className="text-lg font-semibold text-white mb-2">No Pending Videos</h4>
                    <p className="text-gray-400">
                      Great! All videos have been reviewed. New uploaded videos will appear here.
                    </p>
                  </div>
                )
              )}
              
              {/* Beta Access Tab */}
              {moderationTab === 'beta' && (
                <div className="space-y-6">
                  {loadingBeta ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                    </div>
                  ) : (
                    <>
                      {/* Preview Gate Button */}
                      <div className="flex justify-end">
                        <button
                          onClick={() => setForceShowGate(true)}
                          className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-lg transition-all text-sm font-medium flex items-center gap-2"
                        >
                          👁️ Preview Gate Page
                        </button>
                      </div>
                      
                      {/* Generate New Code Section */}
                      <div className="bg-purple-900/20 rounded-xl p-4 border border-purple-500/20">
                        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <span>🔑</span> Generate New Access Code
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                          <input
                            type="text"
                            placeholder="Custom code (optional)"
                            value={newCodeCustom}
                            onChange={(e) => setNewCodeCustom(e.target.value.toUpperCase())}
                            className="bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                          />
                          <input
                            type="number"
                            placeholder="Max uses (blank = unlimited)"
                            value={newCodeMaxUses}
                            onChange={(e) => setNewCodeMaxUses(e.target.value)}
                            className="bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                          />
                          <input
                            type="number"
                            step="0.5"
                            placeholder="Expires in days (e.g., 1 = 24hrs)"
                            value={newCodeExpiresDays}
                            onChange={(e) => setNewCodeExpiresDays(e.target.value)}
                            className="bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Note (e.g., Investor demo - John Smith)"
                            value={newCodeNote}
                            onChange={(e) => setNewCodeNote(e.target.value)}
                            className="bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                          />
                          <button
                            onClick={generateBetaCode}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                          >
                            Generate Code
                          </button>
                        </div>
                      </div>
                      
                      {/* Active Codes */}
                      <div>
                        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <span>📋</span> Active Codes ({betaCodes.filter(c => c.active).length})
                        </h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {betaCodes.filter(c => c.active).map((code) => (
                            <div key={code.id} className="flex items-center justify-between bg-gray-800/30 rounded-lg p-3 border border-gray-700/50">
                              <div className="flex items-center gap-4">
                                <span className="font-mono text-cyan-400 font-bold">{code.code}</span>
                                <span className="text-gray-400 text-sm">Uses: {code.uses}{code.max_uses ? `/${code.max_uses}` : ''}</span>
                                {code.note && <span className="text-gray-500 text-xs">{code.note}</span>}
                              </div>
                              <button
                                onClick={() => deleteBetaCode(code.id)}
                                className="text-red-400 hover:text-red-300 text-sm"
                              >
                                Deactivate
                              </button>
                            </div>
                          ))}
                          {betaCodes.filter(c => c.active).length === 0 && (
                            <p className="text-gray-500 text-center py-4">No active codes</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Pending Requests */}
                      <div>
                        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <span>📬</span> Access Requests ({betaRequests.filter(r => r.status === 'pending').length} pending)
                        </h4>
                        <div className="space-y-3 max-h-60 overflow-y-auto">
                          {betaRequests.map((request) => (
                            <div key={request.id} className={`rounded-lg p-4 border ${
                              request.status === 'pending' 
                                ? 'bg-yellow-900/20 border-yellow-500/30' 
                                : request.status === 'approved'
                                ? 'bg-green-900/20 border-green-500/30'
                                : 'bg-red-900/20 border-red-500/30'
                            }`}>
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-white font-medium">{request.name}</span>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">{request.user_type}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      request.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                      request.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                      'bg-red-500/20 text-red-400'
                                    }`}>
                                      {request.status}
                                    </span>
                                  </div>
                                  <p className="text-gray-400 text-sm">{request.email}</p>
                                  <p className="text-gray-500 text-xs mt-1 max-w-md">{request.reason}</p>
                                  {request.assigned_code && (
                                    <p className="text-cyan-400 text-xs mt-1">Code: {request.assigned_code}</p>
                                  )}
                                </div>
                                {request.status === 'pending' && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => approveBetaRequest(request.id)}
                                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => denyBetaRequest(request.id)}
                                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                    >
                                      Deny
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {betaRequests.length === 0 && (
                            <p className="text-gray-500 text-center py-4">No requests yet</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {moderationTab === 'news' && (
                <div className="space-y-6" data-testid="news-tab-content">
                  {/* Create New Announcement */}
                  <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <span>📢</span> {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
                    </h4>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Announcement Title"
                        value={editingAnnouncement ? editingAnnouncement.title : newAnnouncement.title}
                        onChange={(e) => editingAnnouncement 
                          ? setEditingAnnouncement({...editingAnnouncement, title: e.target.value})
                          : setNewAnnouncement({...newAnnouncement, title: e.target.value})
                        }
                        className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid="announcement-title-input"
                      />
                      <textarea
                        placeholder="Announcement message..."
                        rows={3}
                        value={editingAnnouncement ? editingAnnouncement.message : newAnnouncement.message}
                        onChange={(e) => editingAnnouncement
                          ? setEditingAnnouncement({...editingAnnouncement, message: e.target.value})
                          : setNewAnnouncement({...newAnnouncement, message: e.target.value})
                        }
                        className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm resize-none"
                        data-testid="announcement-message-input"
                      />
                      <div className="flex gap-3 flex-wrap items-end">
                        <div className="flex-1 min-w-[140px]">
                          <label className="text-xs text-gray-400 mb-1 block">Priority</label>
                          <select
                            value={editingAnnouncement ? editingAnnouncement.priority : newAnnouncement.priority}
                            onChange={(e) => editingAnnouncement
                              ? setEditingAnnouncement({...editingAnnouncement, priority: e.target.value})
                              : setNewAnnouncement({...newAnnouncement, priority: e.target.value})
                            }
                            className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                            data-testid="announcement-priority-select"
                          >
                            <option value="normal">Normal</option>
                            <option value="important">Important</option>
                          </select>
                        </div>
                        <div className="flex-1 min-w-[180px]">
                          <label className="text-xs text-gray-400 mb-1 block">Expiry Date</label>
                          <input
                            type="date"
                            value={editingAnnouncement 
                              ? (editingAnnouncement.expiry_date ? editingAnnouncement.expiry_date.substring(0, 10) : '') 
                              : newAnnouncement.expiry_date
                            }
                            onChange={(e) => editingAnnouncement
                              ? setEditingAnnouncement({...editingAnnouncement, expiry_date: e.target.value ? e.target.value + 'T23:59:59+00:00' : ''})
                              : setNewAnnouncement({...newAnnouncement, expiry_date: e.target.value})
                            }
                            className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                            data-testid="announcement-expiry-input"
                          />
                        </div>
                        <div className="flex gap-2">
                          {editingAnnouncement ? (
                            <>
                              <button
                                onClick={() => updateAnnouncement(editingAnnouncement.id, {
                                  title: editingAnnouncement.title,
                                  message: editingAnnouncement.message,
                                  priority: editingAnnouncement.priority,
                                  expiry_date: editingAnnouncement.expiry_date,
                                })}
                                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm"
                                data-testid="announcement-save-btn"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingAnnouncement(null)}
                                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={createAnnouncement}
                              disabled={!newAnnouncement.title || !newAnnouncement.message}
                              className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm"
                              data-testid="announcement-create-btn"
                            >
                              Publish
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Existing Announcements */}
                  <div>
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <span>📋</span> Published Announcements ({adminAnnouncements.length})
                    </h4>
                    {announcementsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                        <span className="ml-3 text-gray-400">Loading announcements...</span>
                      </div>
                    ) : adminAnnouncements.length > 0 ? (
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {adminAnnouncements.map((ann) => (
                          <div key={ann.id} className={`rounded-lg p-4 border ${
                            ann.priority === 'important' 
                              ? 'bg-amber-900/20 border-amber-500/50' 
                              : 'bg-gray-800/30 border-gray-600/50'
                          }`} data-testid={`announcement-item-${ann.id}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-white font-medium">{ann.title}</span>
                                  {ann.priority === 'important' && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Important</span>
                                  )}
                                </div>
                                <p className="text-gray-300 text-sm">{ann.message}</p>
                                <div className="flex gap-3 mt-2 text-xs text-gray-500">
                                  <span>By {ann.created_by_name}</span>
                                  <span>{ann.created_at ? new Date(ann.created_at).toLocaleDateString() : ''}</span>
                                  {ann.expiry_date && <span>Expires: {new Date(ann.expiry_date).toLocaleDateString()}</span>}
                                </div>
                              </div>
                              <div className="flex gap-2 ml-2">
                                <button
                                  onClick={() => setEditingAnnouncement({...ann})}
                                  className="bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/50 text-blue-300 px-2 py-1 rounded text-xs"
                                  data-testid={`announcement-edit-${ann.id}`}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => deleteAnnouncement(ann.id)}
                                  className="bg-red-600/20 hover:bg-red-600/30 border border-red-500/50 text-red-300 px-2 py-1 rounded text-xs"
                                  data-testid={`announcement-delete-${ann.id}`}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-400">No announcements yet. Create one above to display in the Community Banner!</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {moderationTab === 'new_members' && (
                <div className="space-y-4" data-testid="new-members-tab-content">
                  <div className="flex items-center justify-between mb-4 px-1">
                    <div>
                      <h4 className="text-white font-bold text-base">🎉 New Member Feed</h4>
                      <p className="text-xs text-gray-400">Auto-populates when a new BeatSeeker registers. 7-day rolling window on the dashboard ticker.</p>
                    </div>
                    <div className="text-xs text-fuchsia-300">{newMembersList.length} total</div>
                  </div>
                  {loadingNewMembers ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fuchsia-500"></div>
                      <span className="ml-3 text-gray-400">Loading new members...</span>
                    </div>
                  ) : newMembersList.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 text-sm">
                      <p>No new members yet. Once someone joins BEATSEEK, they&apos;ll show up here.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                      {newMembersList.map((m) => {
                        const isOg40 = !!m.is_og40;
                        const isExpired = m.expiry_date && new Date(m.expiry_date) < new Date();
                        return (
                          <div
                            key={m.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 ${
                              isOg40
                                ? 'bg-gradient-to-r from-yellow-500/10 to-fuchsia-500/10 border-yellow-500/40'
                                : 'bg-gray-800/40 border-gray-700/60'
                            } ${isExpired ? 'opacity-50' : ''}`}
                            data-testid={`new-member-row-${m.id}`}
                          >
                            <div className={`w-2 h-2 rounded-full ${isExpired ? 'bg-gray-500' : 'bg-green-400 animate-pulse'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-white font-medium truncate">{m.message}</div>
                              <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
                                <span>{new Date(m.created_at).toLocaleString()}</span>
                                {isOg40 && <span className="text-yellow-400 font-semibold">OG40</span>}
                                {isExpired && <span className="text-gray-500 italic">Expired</span>}
                              </div>
                            </div>
                            {m.user_id && (
                              <button
                                onClick={() => {
                                  setShowAdminModeration(false);
                                  navigate(`/profile/${m.user_id}`);
                                }}
                                className="text-cyan-400 hover:text-cyan-300 text-xs font-semibold px-2 py-1 rounded hover:bg-cyan-500/10 transition-all"
                                data-testid={`new-member-view-${m.id}`}
                              >
                                View
                              </button>
                            )}
                            <button
                              onClick={() => deleteNewMemberEntry(m.id)}
                              className="text-red-400 hover:text-red-300 text-xs font-semibold px-2 py-1 rounded hover:bg-red-500/10 transition-all"
                              data-testid={`new-member-delete-${m.id}`}
                              title="Delete this entry from the ticker"
                            >
                              🗑
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {moderationTab === 'id_corrections' && (
                <div className="space-y-6">
                  <CuratedIdQueue onReviewed={refreshPendingCuratedIds} />
                  <div className="border-t border-white/10 pt-5">
                    <div className="mb-3 text-xs font-bold uppercase tracking-wider text-pink-300">🎯 Reported wrong IDs</div>
                    <IdCorrectionsPanel />
                  </div>
                </div>
              )}

              {moderationTab === 'suggestions' && (
                <div className="space-y-4" data-testid="suggestions-tab-content">
                  {loadingSuggestions ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
                      <span className="ml-3 text-gray-400">Loading suggestions...</span>
                    </div>
                  ) : toolSuggestions.length > 0 ? (
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                      {toolSuggestions.map((s, idx) => (
                        <div
                          key={s.created_at || idx}
                          className={`rounded-lg p-4 border ${
                            s.status === 'approved'
                              ? 'bg-green-900/15 border-green-700/40'
                              : s.status === 'denied'
                              ? 'bg-gray-800/20 border-gray-700/30 opacity-60'
                              : 'bg-teal-900/15 border-teal-700/40'
                          }`}
                          data-testid={`suggestion-item-${idx}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-300 uppercase tracking-wide">
                                  {s.category || 'general'}
                                </span>
                                {s.source === 'community' && (
                                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300" data-testid={`community-idea-badge-${idx}`}>
                                    💡 Community Idea
                                  </span>
                                )}
                                {s.status && s.status !== 'pending' && (
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                    s.status === 'approved'
                                      ? 'bg-green-500/20 text-green-300'
                                      : 'bg-red-500/20 text-red-300'
                                  }`}>
                                    {s.status}
                                  </span>
                                )}
                              </div>
                              <p className="text-white text-sm mt-1">{s.suggestion}</p>
                              {s.url && (
                                <a
                                  href={s.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-teal-400 hover:text-teal-300 mt-1.5 underline underline-offset-2"
                                  data-testid={`suggestion-url-${idx}`}
                                >
                                  🔗 {s.url.length > 60 ? s.url.substring(0, 60) + '...' : s.url}
                                </a>
                              )}
                              <div className="flex gap-3 mt-2 text-xs text-gray-500">
                                <span>{s.username || s.user_email || 'Anonymous'}</span>
                                <span>{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1.5 shrink-0">
                              {s.status !== 'approved' && (
                                <button
                                  onClick={() => updateSuggestionStatus(s.created_at, 'approved')}
                                  className="bg-green-600/20 hover:bg-green-600/30 border border-green-500/50 text-green-300 px-2.5 py-1 rounded text-xs"
                                  data-testid={`suggestion-approve-${idx}`}
                                >
                                  Approve
                                </button>
                              )}
                              {s.status !== 'denied' && s.status !== 'approved' && (
                                <button
                                  onClick={() => updateSuggestionStatus(s.created_at, 'denied')}
                                  className="bg-orange-600/20 hover:bg-orange-600/30 border border-orange-500/50 text-orange-300 px-2.5 py-1 rounded text-xs"
                                  data-testid={`suggestion-deny-${idx}`}
                                >
                                  Deny
                                </button>
                              )}
                              {s.status !== 'denied' && s.status !== 'approved' && !pollTallies[s.created_at] && (
                                <button
                                  onClick={() => setPushVoteTarget(s)}
                                  className="bg-yellow-600/20 hover:bg-yellow-600/30 border border-yellow-500/50 text-yellow-300 px-2.5 py-1 rounded text-xs"
                                  data-testid={`suggestion-push-vote-${idx}`}
                                >
                                  📊 Push to Vote
                                </button>
                              )}
                              {pollTallies[s.created_at] && (
                                <span
                                  className="border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 px-2.5 py-1 rounded text-xs font-semibold"
                                  data-testid={`suggestion-poll-tally-${idx}`}
                                  title={pollTallies[s.created_at].status === 'live' ? 'Community vote in progress' : 'Final community result'}
                                >
                                  🔥 {pollTallies[s.created_at].build_votes} / 🤔 {pollTallies[s.created_at].pass_votes}
                                  {pollTallies[s.created_at].announced
                                    ? ' · final'
                                    : pollTallies[s.created_at].status !== 'live' && ' · ⌛ expired'}
                                </span>
                              )}
                              {pollTallies[s.created_at] && !pollTallies[s.created_at].announced && (
                                <button
                                  onClick={() => closeAndAnnouncePoll(pollTallies[s.created_at].poll_id)}
                                  className="bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/50 text-amber-300 px-2.5 py-1 rounded text-xs"
                                  data-testid={`suggestion-close-announce-${idx}`}
                                  title="Close this vote and announce the result on The Wall"
                                >
                                  🏁 Close & Announce
                                </button>
                              )}
                              {pollTallies[s.created_at]?.announced && (
                                <span
                                  className="border border-amber-400/50 bg-amber-400/10 text-amber-200 px-2.5 py-1 rounded text-xs font-bold"
                                  data-testid={`suggestion-community-voted-${idx}`}
                                  title={`Result announced on The Wall — community ${pollTallies[s.created_at].outcome === 'build' ? 'chose to build it' : pollTallies[s.created_at].outcome === 'pass' ? 'passed' : 'tied'}`}
                                >
                                  🏆 Community Voted
                                </span>
                              )}
                              {pollTallies[s.created_at]?.announced && pollTallies[s.created_at].outcome === 'build' && (
                                <select
                                  value={pollTallies[s.created_at].build_status || 'voted'}
                                  onChange={(e) => setBuildStatus(pollTallies[s.created_at].poll_id, e.target.value)}
                                  className="bg-emerald-900/30 border border-emerald-500/40 text-emerald-200 px-2 py-1 rounded text-xs cursor-pointer"
                                  data-testid={`suggestion-build-status-${idx}`}
                                  title="Move this idea along the public Build List pipeline"
                                >
                                  <option value="voted">🏆 Voted</option>
                                  <option value="in_progress">🔨 In Progress</option>
                                  <option value="shipped">🚀 Shipped</option>
                                </select>
                              )}
                              <button
                                onClick={() => deleteToolSuggestion(s.created_at)}
                                className="bg-red-600/20 hover:bg-red-600/30 border border-red-500/50 text-red-300 px-2.5 py-1 rounded text-xs"
                                data-testid={`suggestion-delete-${idx}`}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-3">💡</div>
                      <p className="text-gray-400">No tool suggestions yet.</p>
                      <p className="text-gray-500 text-sm mt-1">Users can submit ideas from DJ Booth, Producers Corner, and Streamer tool panels.</p>
                    </div>
                  )}
                </div>
              )}

              {moderationTab === 'badges' && (
                <div className="space-y-4" data-testid="badges-tab-content">
                  <div className="bg-cyan-900/15 border border-cyan-700/40 rounded-lg p-4">
                    <h4 className="text-white font-semibold text-sm mb-1">🏅 Grant / Revoke Badges</h4>
                    <p className="text-xs text-gray-400 mb-3">Pick a badge, search a user, then Grant or Revoke.</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={badgeSearchQuery}
                        onChange={(e) => setBadgeSearchQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') searchUsersForBadges(); }}
                        placeholder="Search users by name or email…"
                        className="flex-1 bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        data-testid="badges-search-input"
                      />
                      <select
                        value={selectedBadgeToGrant}
                        onChange={(e) => setSelectedBadgeToGrant(e.target.value)}
                        className="bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 sm:w-56"
                        data-testid="badges-select"
                      >
                        {GRANTABLE_BADGES_FE.map((b) => (
                          <option key={b} value={b}>{BADGE_LABELS_FE[b] || b}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => searchUsersForBadges()}
                        disabled={badgeSearchLoading || !badgeSearchQuery.trim()}
                        className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium"
                        data-testid="badges-search-btn"
                      >
                        {badgeSearchLoading ? '…' : 'Search'}
                      </button>
                    </div>
                  </div>

                  {badgeSearchResults.length > 0 ? (
                    <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                      {badgeSearchResults.map((u) => {
                        const userBadges = u.badges || [];
                        const hasSelected = userBadges.includes(selectedBadgeToGrant);
                        const grantKey = u.id + ':' + selectedBadgeToGrant + ':grant';
                        const revokeKey = u.id + ':' + selectedBadgeToGrant + ':revoke';
                        const isGranting = badgeUpdatingUserId === grantKey;
                        const isRevoking = badgeUpdatingUserId === revokeKey;
                        return (
                          <div key={u.id} className="bg-gray-800/40 border border-gray-700/40 rounded-lg p-3" data-testid={`badges-user-row-${u.id}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white text-sm font-semibold overflow-hidden shrink-0">
                                  {u.profile_picture ? (
                                    <img src={u.profile_picture} alt={u.name} className="w-full h-full object-cover" />
                                  ) : (
                                    (u.name || '?').charAt(0).toUpperCase()
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-white text-sm font-semibold truncate">{u.name}</span>
                                    <UserBadgeGroup badges={userBadges} size="sm" />
                                    <IdHunterMedal userId={u.id} showCount={false} />
                                  </div>
                                  <div className="text-xs text-gray-400 truncate">{u.email}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  onClick={() => grantBadgeToUser(u.id, selectedBadgeToGrant)}
                                  disabled={isGranting || hasSelected}
                                  className="bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/50 text-cyan-200 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded text-xs font-medium"
                                  data-testid={`badge-grant-${u.id}`}
                                  title={hasSelected ? 'User already has this badge' : `Grant ${BADGE_LABELS_FE[selectedBadgeToGrant] || selectedBadgeToGrant}`}
                                >
                                  {isGranting ? '…' : hasSelected ? '✓ Granted' : 'Grant'}
                                </button>
                                <button
                                  onClick={() => revokeBadgeFromUser(u.id, selectedBadgeToGrant)}
                                  disabled={isRevoking || !hasSelected}
                                  className="bg-red-500/15 hover:bg-red-500/25 border border-red-500/50 text-red-200 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded text-xs font-medium"
                                  data-testid={`badge-revoke-${u.id}`}
                                  title={!hasSelected ? 'User does not have this badge' : `Revoke ${BADGE_LABELS_FE[selectedBadgeToGrant] || selectedBadgeToGrant}`}
                                >
                                  {isRevoking ? '…' : 'Revoke'}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-3">🔎</div>
                      <p className="text-gray-400 text-sm">{badgeSearchQuery ? 'No users found.' : 'Search for a user above to manage their badges.'}</p>
                    </div>
                  )}
                </div>
              )}

              {moderationTab === 'gate' && (
                <GateCountdownSettings />
              )}
              {moderationTab === 'id-engine' && (
                <IdEngineDiagnostics />
              )}
              {moderationTab === 'email-blast' && (
                <EmailBlastTab />
              )}

              {moderationTab === 'vip' && (
                <div className="space-y-4" data-testid="vip-tab-content">
                  <div className="bg-yellow-900/15 border border-yellow-700/40 rounded-lg p-4">
                    <h4 className="text-white font-semibold text-sm mb-1">👑 VIP Lifetime Membership</h4>
                    <p className="text-xs text-gray-400 mb-3">
                      VIP members get full access to every feature, free for life — even after paid subscriptions launch.
                      Emails not registered yet auto-activate the moment they sign up.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="email"
                        value={vipEmailInput}
                        onChange={(e) => setVipEmailInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addVipEmail(); }}
                        placeholder="Enter email to add as VIP…"
                        className="flex-1 bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        data-testid="vip-email-input"
                      />
                      <button
                        onClick={addVipEmail}
                        disabled={vipUpdating || !vipEmailInput.trim()}
                        className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium"
                        data-testid="vip-add-btn"
                      >
                        {vipUpdating ? '…' : '+ Add VIP'}
                      </button>
                    </div>
                  </div>

                  {vipLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                      <span className="ml-3 text-gray-400">Loading VIP list...</span>
                    </div>
                  ) : vipList.length > 0 ? (
                    <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                      {vipList.map((v) => (
                        <div key={v.email} className="bg-gray-800/40 border border-gray-700/40 rounded-lg p-3" data-testid={`vip-row-${v.email}`}>
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center text-white text-sm font-semibold overflow-hidden shrink-0">
                                {v.profile_picture ? (
                                  <img src={v.profile_picture} alt={v.user_name || v.email} className="w-full h-full object-cover" />
                                ) : (
                                  '👑'
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-white text-sm font-semibold truncate">{v.user_name || v.email}</span>
                                  {v.registered ? (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 border border-green-500/40 text-green-300 font-medium">Active</span>
                                  ) : (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-500/15 border border-gray-500/40 text-gray-300 font-medium">Awaiting signup</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-400 truncate">{v.email}</div>
                              </div>
                            </div>
                            <button
                              onClick={() => removeVipEmail(v.email)}
                              disabled={vipUpdating}
                              className="bg-red-500/15 hover:bg-red-500/25 border border-red-500/50 text-red-200 disabled:opacity-40 px-3 py-1.5 rounded text-xs font-medium shrink-0"
                              data-testid={`vip-remove-${v.email}`}
                              title={`Remove ${v.email} from VIP`}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-3">👑</div>
                      <p className="text-gray-400 text-sm">No VIP members yet. Add an email above to get started.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-6 border-t border-red-700/50">
              <div className="text-xs text-gray-400">
                This admin panel is only visible to administrators
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (moderationTab === 'messages') {
                      fetchPendingModerations();
                    } else if (moderationTab === 'emotes') {
                      fetchPendingEmotes();
                    } else if (moderationTab === 'videos') {
                      fetchPendingVideos();
                    } else if (moderationTab === 'beta') {
                      fetchBetaData();
                    } else if (moderationTab === 'news') {
                      fetchAdminAnnouncements();
                    } else if (moderationTab === 'suggestions') {
                      fetchToolSuggestions();
                    } else if (moderationTab === 'badges') {
                      searchUsersForBadges();
                    } else if (moderationTab === 'vip') {
                      fetchVipList();
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-sm"
                >
                  🔄 Refresh
                </button>
                <button
                  onClick={() => setShowAdminModeration(false)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* My Emotes Modal */}
      {showMyEmotes && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 rounded-2xl border border-purple-500/20 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-purple-700/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <span className="text-3xl">😊</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">My Custom Emotes</h3>
                  <p className="text-sm text-gray-400">Upload and manage your personal emotes</p>
                </div>
              </div>
              <button
                onClick={() => setShowMyEmotes(false)}
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Emote Limits */}
            <div className="px-6 pt-4 pb-2 bg-purple-900/20 border-b border-purple-700/30">
              <div className="flex items-center justify-around text-sm">
                <div className="text-center">
                  <p className="text-gray-400">Static PNG</p>
                  <p className="text-white font-semibold">{emoteLimits.static.used} / {emoteLimits.static.limit}</p>
                  <p className="text-xs text-gray-500">{emoteLimits.static.available} available</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400">Animated GIF</p>
                  <p className="text-white font-semibold">{emoteLimits.animated.used} / {emoteLimits.animated.limit}</p>
                  <p className="text-xs text-gray-500">{emoteLimits.animated.available} available</p>
                </div>
              </div>
            </div>

            {/* Upload Form */}
            <div className="px-6 py-4 bg-purple-900/10 border-b border-purple-700/30">
              <h4 className="text-white font-semibold mb-3">Upload New Emote</h4>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const name = formData.get('emoteName');
                const type = formData.get('emoteType');
                const file = formData.get('emoteFile');
                
                if (!name || !file) {
                  alert('Please provide both name and file');
                  return;
                }
                
                const success = await uploadCustomEmote(name, type, file);
                if (success) {
                  e.target.reset();
                }
              }} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Emote Name (max 8 chars)</label>
                  <input
                    type="text"
                    name="emoteName"
                    maxLength="8"
                    pattern="[a-zA-Z0-9]+"
                    placeholder="smile"
                    required
                    className="w-full bg-gray-800/50 border border-purple-500/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Type</label>
                  <select
                    name="emoteType"
                    required
                    className="w-full bg-gray-800/50 border border-purple-500/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="static">📷 Static (PNG/JPG)</option>
                    <option value="animated">🎞️ Animated (GIF)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">File</label>
                  <input
                    type="file"
                    name="emoteFile"
                    accept="image/png,image/jpeg,image/jpg,image/gif"
                    required
                    className="w-full bg-gray-800/50 border border-purple-500/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:text-sm file:bg-purple-600 file:text-white hover:file:bg-purple-700"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={uploadingEmote}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white px-4 py-2 rounded transition-colors text-sm font-medium"
                  >
                    {uploadingEmote ? '⏳ Uploading...' : '⬆️ Upload'}
                  </button>
                </div>
              </form>
              <p className="text-xs text-gray-500 mt-2">
                ℹ️ Emotes require admin approval before use. Images will be resized to 28×28, 56×56, and 112×112 pixels.
              </p>
            </div>

            {/* My Emotes List */}
            <div className="flex-1 overflow-y-auto p-6">
              <h4 className="text-white font-semibold mb-3">Your Emotes</h4>
              {loadingEmotes ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                  <span className="ml-3 text-gray-400">Loading your emotes...</span>
                </div>
              ) : myEmotes.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {myEmotes.map((emote) => (
                    <div key={emote.id} className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-3 text-center">
                      <div className="w-16 h-16 bg-gray-800/50 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <img 
                          src={`${BACKEND_URL}/api${emote.file_path_56}`} 
                          alt={emote.name}
                          className="w-14 h-14 object-contain"
                        />
                      </div>
                      <p className="text-white text-sm font-semibold">:{emote.name}:</p>
                      <p className="text-gray-400 text-xs">{emote.type === 'static' ? '📷 PNG' : '🎞️ GIF'}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-300 border border-green-500/50">
                        ✅ Approved
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">😊</div>
                  <h4 className="text-lg font-semibold text-white mb-2">No Emotes Yet</h4>
                  <p className="text-gray-400">
                    Upload your first custom emote above! It will need admin approval before you can use it.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-6 border-t border-purple-700/50">
              <div className="text-xs text-gray-400">
                Approved emotes can be used anywhere in BEATSEEK
              </div>
              <button
                onClick={() => setShowMyEmotes(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Emote Lab — Lite (Basic tier) */}
      <EmoteLabLite
        isOpen={showEmoteLabLite}
        onClose={() => setShowEmoteLabLite(false)}
        currentUser={user}
        initialPrompt={emoteLabPrefill.prompt}
        initialStyle={emoteLabPrefill.style}
      />

      {/* Add / Edit Social Link Modal */}
      {showAddSocialLink && (
        <AddSocialLinkModal 
          onAdd={addSocialLink}
          onClose={() => { setShowAddSocialLink(false); setEditingSocialLinkIndex(null); }}
          initialPlatform={editingSocialLinkIndex !== null ? socialLinks[editingSocialLinkIndex]?.platform : ''}
          initialUsername={editingSocialLinkIndex !== null ? socialLinks[editingSocialLinkIndex]?.username : ''}
          isEditing={editingSocialLinkIndex !== null}
        />
      )}


      {/* Spotify Full View Modal */}
      <SpotifyFullViewModal
        isOpen={showSpotifyFullView}
        onClose={() => {
          setShowSpotifyFullView(false);
          // Clear crate context if in crate mode
          if (isDiscoveringForCrate) {
            setIsDiscoveringForCrate(false);
            setTargetCrateId(null);
            setTargetCrateName(null);
          }
        }}
        activePlaylistForAdding={activePlaylistForAdding}
        addTrackToPlaylist={addTrackToPlaylist}
        onTrackAdded={() => {
          // Close Spotify modal
          setShowSpotifyFullView(false);
          // Reopen playlist detail modal to show updated playlist
          setShowPlaylistDetailModal(true);
        }}
        // Crate context props
        isAddingToCrate={isDiscoveringForCrate}
        targetCrateId={targetCrateId}
        targetCrateName={targetCrateName}
        onTrackAddedToCrate={async (track) => {
          try {
            const token = localStorage.getItem('beatseek_token');
            const trackData = {
              track_id: track.id || track.uri || `spotify_${Date.now()}`,
              source: 'spotify',
              title: track.name || 'Unknown Track',
              artist: track.artist || 'Unknown Artist',
              album: track.album_name || '',
              duration_ms: track.duration_ms || 0,
              artwork_url: track.album_art || '',
              preview_url: track.preview_url || null,
              spotify_uri: track.uri || null,
              external_url: track.external_url || null
            };
            
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}/tracks`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(trackData)
              }
            );
            
            if (!response.ok) throw new Error('Failed to add track');
            
            alert(`✅ "${track.name}" added to "${targetCrateName}"!`);
          } catch (error) {
            console.error('Error adding track to crate:', error);
            alert(`Failed to add track: ${error.message}`);
          }
        }}
        onReturnToCrate={async () => {
          // Close Spotify modal and return to crate
          setShowSpotifyFullView(false);
          try {
            const token = localStorage.getItem('beatseek_token');
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const crateData = await response.json();
            setSelectedCrateForBuilder(crateData);
            setIsDiscoveringForCrate(false);
            setTargetCrateId(null);
            setTargetCrateName(null);
            setActiveTab('crate-box');
          } catch (error) {
            console.error('Error returning to crate:', error);
          }
        }}
      />

      {/* SoundCloud Library Modal */}
      <SoundCloudLibraryModal
        isOpen={showSoundCloudLibrary}
        onClose={() => {
          setShowSoundCloudLibrary(false);
          if (isDiscoveringForCrate) {
            setIsDiscoveringForCrate(false);
            setTargetCrateId(null);
            setTargetCrateName(null);
          }
        }}
        isAddingToCrate={isDiscoveringForCrate}
        targetCrateId={targetCrateId}
        targetCrateName={targetCrateName}
        onTrackAddedToCrate={async (track) => {
          try {
            const token = localStorage.getItem('beatseek_token');
            const trackData = {
              track_id: track.id || `soundcloud_${Date.now()}`,
              source: 'soundcloud',
              title: track.name || 'Unknown Track',
              artist: track.artist || 'Unknown Artist',
              duration_ms: track.duration_ms || 0,
              artwork_url: track.album_art || '',
              preview_url: track.preview_url || null,
              external_url: track.external_url || null
            };
            
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}/tracks`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(trackData)
              }
            );
            
            if (!response.ok) throw new Error('Failed to add track');
            
            alert(`✅ "${track.name}" added to "${targetCrateName}"!`);
          } catch (error) {
            console.error('Error adding track to crate:', error);
            alert(`Failed to add track: ${error.message}`);
          }
        }}
        onReturnToCrate={async () => {
          setShowSoundCloudLibrary(false);
          try {
            const token = localStorage.getItem('beatseek_token');
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const crateData = await response.json();
            setSelectedCrateForBuilder(crateData);
            setIsDiscoveringForCrate(false);
            setTargetCrateId(null);
            setTargetCrateName(null);
            setActiveTab('crate-box');
          } catch (error) {
            console.error('Error returning to crate:', error);
          }
        }}
      />

      {/* Favorites Picker Modal */}
      <FavoritesPickerModal
        isOpen={showFavoritesPicker}
        onClose={() => {
          setShowFavoritesPicker(false);
          if (isDiscoveringForCrate) {
            setIsDiscoveringForCrate(false);
            setTargetCrateId(null);
            setTargetCrateName(null);
          }
        }}
        isAddingToCrate={isDiscoveringForCrate}
        targetCrateId={targetCrateId}
        targetCrateName={targetCrateName}
        onTrackAddedToCrate={async (track) => {
          try {
            const token = localStorage.getItem('beatseek_token');
            const trackData = {
              track_id: track.track_id || track.id || `fav_${Date.now()}`,
              source: track.source || 'favorites',
              title: track.title || 'Unknown Track',
              artist: track.artist || 'Unknown Artist',
              album: track.album || '',
              duration_ms: track.duration_ms || 0,
              bpm: track.bpm || null,
              key: track.key || null,
              energy: track.energy || null,
              artwork_url: track.artwork_url || track.artwork || '',
              preview_url: track.preview_url || null,
              spotify_uri: track.spotify_uri || null,
              youtube_id: track.youtube_id || null,
              external_url: track.external_url || null
            };
            
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}/tracks`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(trackData)
              }
            );
            
            if (!response.ok) throw new Error('Failed to add track');
            
            alert(`✅ "${track.title}" added to "${targetCrateName}"!`);
          } catch (error) {
            console.error('Error adding track to crate:', error);
            alert(`Failed to add track: ${error.message}`);
          }
        }}
        onReturnToCrate={async () => {
          setShowFavoritesPicker(false);
          try {
            const token = localStorage.getItem('beatseek_token');
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const crateData = await response.json();
            setSelectedCrateForBuilder(crateData);
            setIsDiscoveringForCrate(false);
            setTargetCrateId(null);
            setTargetCrateName(null);
            setActiveTab('crate-box');
          } catch (error) {
            console.error('Error returning to crate:', error);
          }
        }}
      />

      {/* Playlist Picker Modal */}
      <PlaylistPickerModal
        isOpen={showPlaylistPicker}
        onClose={() => {
          setShowPlaylistPicker(false);
          if (isDiscoveringForCrate) {
            setIsDiscoveringForCrate(false);
            setTargetCrateId(null);
            setTargetCrateName(null);
          }
        }}
        isAddingToCrate={isDiscoveringForCrate}
        targetCrateId={targetCrateId}
        targetCrateName={targetCrateName}
        onTrackAddedToCrate={async (track) => {
          try {
            const token = localStorage.getItem('beatseek_token');
            const trackData = {
              track_id: track.track_id || track.id || `playlist_${Date.now()}`,
              source: track.source || 'playlist',
              title: track.title || 'Unknown Track',
              artist: track.artist || 'Unknown Artist',
              album: track.album || '',
              duration_ms: track.duration_ms || 0,
              bpm: track.bpm || null,
              key: track.key || null,
              energy: track.energy || null,
              artwork_url: track.artwork_url || track.artwork || '',
              preview_url: track.preview_url || null,
              spotify_uri: track.spotify_uri || null,
              youtube_id: track.youtube_id || null,
              external_url: track.external_url || null
            };
            
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}/tracks`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(trackData)
              }
            );
            
            if (!response.ok) throw new Error('Failed to add track');
            
            alert(`✅ "${track.title}" added to "${targetCrateName}"!`);
          } catch (error) {
            console.error('Error adding track to crate:', error);
            alert(`Failed to add track: ${error.message}`);
          }
        }}
        onReturnToCrate={async () => {
          setShowPlaylistPicker(false);
          try {
            const token = localStorage.getItem('beatseek_token');
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const crateData = await response.json();
            setSelectedCrateForBuilder(crateData);
            setIsDiscoveringForCrate(false);
            setTargetCrateId(null);
            setTargetCrateName(null);
            setActiveTab('crate-box');
          } catch (error) {
            console.error('Error returning to crate:', error);
          }
        }}
      />

      {/* Local Track Upload Modal */}
      {showLocalTrackUpload && (
        <LocalTrackUpload
          isJamSession={isDiscoveringForJam}
          jamSessionId={targetSessionId}
          onTrackUploaded={async (track, playlistFromUpload) => {
            alert('🎵 CALLBACK TRIGGERED! Track: ' + track.title);
            console.log('🎵 Track uploaded:', track);
            console.log('📋 Playlist from upload modal:', playlistFromUpload);
            console.log('📋 Active playlist for adding (state):', activePlaylistForAdding);
            
            // Use playlist from upload modal (saved ref) instead of state
            const targetPlaylist = playlistFromUpload || activePlaylistForAdding;
            
            // Refresh local tracks list
            const updatedTracks = await getLocalTracks();
            setLocalTracks(updatedTracks);
            console.log('📋 Local tracks updated:', updatedTracks.length);
            
            // If there's a target playlist, automatically add the track
            if (targetPlaylist) {
              alert('📝 Adding to playlist: ' + targetPlaylist.name);
              try {
                console.log('📝 Adding track to playlist:', targetPlaylist.name);
                console.log('📝 Track data:', {
                  track_id: track.track_id,
                  title: track.title,
                  artist: track.artist,
                  source: 'local',
                  artwork: track.artwork,
                  duration_ms: track.duration_ms,
                  local_id: track.id
                });
                
                const result = await addTrackToPlaylist(targetPlaylist.id, {
                  track_id: track.track_id,
                  title: track.title,
                  artist: track.artist,
                  source: 'local',
                  artwork: track.artwork || null,
                  preview_url: null,
                  spotify_uri: null,
                  duration_ms: track.duration_ms || 0,
                  local_id: track.id
                });
                
                console.log('✅ Track added to playlist result:', result);
                alert('✅ Backend returned success!');
                
                // Force refresh the playlist view
                if (currentPlaylist?.id === targetPlaylist.id) {
                  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                  const refreshed = await fetch(`${BACKEND_URL}/api/playlists/${targetPlaylist.id}`).then(r => r.json());
                  setCurrentPlaylist(refreshed);
                  console.log('✅ Playlist refreshed:', refreshed);
                  alert('✅ Playlist refreshed! Track count: ' + refreshed.tracks.length);
                }
                
                // Refresh all playlists
                await fetchMyPlaylists();
                
                // Reopen playlist detail modal to show the new track
                setShowPlaylistDetailModal(true);
                
              } catch (error) {
                console.error('❌ Error adding local track to playlist:', error);
                alert(`❌ ERROR: ${error.message}`);
              }
            } else {
              alert('⚠️ No active playlist found!');
            }
            
            // If in crate context, add track to crate
            if (isDiscoveringForCrate && targetCrateId) {
              try {
                const token = localStorage.getItem('beatseek_token');
                const trackData = {
                  track_id: track.track_id || `local_${Date.now()}`,
                  source: 'local_upload',
                  title: track.title || 'Unknown Track',
                  artist: track.artist || 'Unknown Artist',
                  album: track.album || '',
                  duration_ms: track.duration_ms || 0,
                  bpm: track.bpm || null,
                  key: track.key || null,
                  energy: track.energy || null,
                  artwork_url: track.artwork || '',
                  preview_url: track.file_url || null,
                  external_url: null
                };
                
                const response = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}/tracks`,
                  {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(trackData)
                  }
                );
                
                if (!response.ok) throw new Error('Failed to add track to crate');
                
                alert(`✅ "${track.title}" added to crate!`);
                setShowLocalTrackUpload(false);
                
                // Return to crate
                const crateResponse = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}`,
                  { headers: { 'Authorization': `Bearer ${token}` } }
                );
                const crateData = await crateResponse.json();
                setSelectedCrateForBuilder(crateData);
                setIsDiscoveringForCrate(false);
                setTargetCrateId(null);
                setTargetCrateName(null);
                setActiveTab('crate-box');
              } catch (error) {
                console.error('Error adding track to crate:', error);
                alert(`Failed to add track: ${error.message}`);
              }
            }
          }}
          onClose={async () => {
            setShowLocalTrackUpload(false);
            
            // If in crate context, return to crate
            if (isDiscoveringForCrate && targetCrateId) {
              try {
                const token = localStorage.getItem('beatseek_token');
                const response = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/crates/${targetCrateId}`,
                  { headers: { 'Authorization': `Bearer ${token}` } }
                );
                const crateData = await response.json();
                setSelectedCrateForBuilder(crateData);
                setIsDiscoveringForCrate(false);
                setTargetCrateId(null);
                setTargetCrateName(null);
                setActiveTab('crate-box');
              } catch (error) {
                console.error('Error returning to crate:', error);
              }
              return;
            }
            
            // If in jam session context, refresh and reopen jam session modal
            if (isDiscoveringForJam && targetSessionId) {
              try {
                const axios = (await import('axios')).default;
                const token = localStorage.getItem('beatseek_token');
                const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                
                // Fetch updated session
                const updatedSession = await axios.get(
                  `${API}/api/jam/${targetSessionId}`,
                  { headers: { 'Authorization': `Bearer ${token}` } }
                );
                
                setCurrentJamSession(updatedSession.data);
                setShowJamSessionModal(true);
                setIsDiscoveringForJam(false);
                setTargetSessionId(null);
                setActiveTab('jam-session');
              } catch (error) {
                console.error('Error refreshing jam session:', error);
              }
            } else if (currentPlaylist) {
              // Reopen playlist detail modal when closing upload
              setShowPlaylistDetailModal(true);
            }
          }}
          activePlaylist={activePlaylistForAdding}
        />
      )}

      {/* Jam Session Selector Modal */}
      {showJamSessionSelector && selectedLocalTrackForJam && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(0, 15, 30, 0.98), rgba(10, 0, 30, 0.98))',
            border: '2px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 212, 255, 0.3)'
          }}>
            <h2 style={{
              margin: '0 0 1rem 0',
              background: 'linear-gradient(90deg, #00d4ff, #ff00ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '1.5rem'
            }}>
              Add &quot;{selectedLocalTrackForJam.title}&quot; to Jam Session
            </h2>

            {availableJamSessions.length > 0 ? (
              <>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem' }}>
                  Select an existing jam session:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                  {availableJamSessions.map((session) => (
                    <button
                      key={session.session_id}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('beatseek_token');
                          const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                          
                          // Add track to jam session
                          await axios.post(
                            `${API}/api/jam/${session.session_id}/queue`,
                            {
                              track_id: selectedLocalTrackForJam.track_id,
                              title: selectedLocalTrackForJam.title,
                              artist: selectedLocalTrackForJam.artist,
                              preview_url: null,
                              spotify_uri: null,
                              duration_ms: selectedLocalTrackForJam.duration_ms || 0,
                              artwork: selectedLocalTrackForJam.artwork,
                              source: 'local',
                              local_id: selectedLocalTrackForJam.id
                            },
                            { headers: { 'Authorization': `Bearer ${token}` } }
                          );
                          
                          // Wait a moment for backend to update
                          await new Promise(resolve => setTimeout(resolve, 300));
                          
                          // Fetch updated session
                          const updatedSession = await axios.get(
                            `${API}/api/jam/${session.session_id}`,
                            { headers: { 'Authorization': `Bearer ${token}` } }
                          );
                          
                          // Close selector and navigate to jam session
                          setShowJamSessionSelector(false);
                          setSelectedLocalTrackForJam(null);
                          
                          // Set jam session and open modal
                          setCurrentJamSession(updatedSession.data);
                          setActiveTab('jam-session');
                          setShowJamSessionModal(true);
                          
                          alert(`✅ "${selectedLocalTrackForJam.title}" added to "${session.name}"!`);
                        } catch (error) {
                          console.error('Error adding track to jam session:', error);
                          alert('Failed to add track. Please try again.');
                        }
                      }}
                      style={{
                        padding: '1rem',
                        background: 'rgba(255, 0, 255, 0.1)',
                        border: '1px solid rgba(255, 0, 255, 0.3)',
                        borderRadius: '8px',
                        color: '#fff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 0, 255, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.5)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 0, 255, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.3)';
                      }}
                    >
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{session.name}</div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        {session.participants?.length || 0} participant{session.participants?.length !== 1 ? 's' : ''} • 
                        {session.queue?.length || 0} track{session.queue?.length !== 1 ? 's' : ''} in queue
                      </div>
                    </button>
                  ))}
                </div>
                <div style={{ 
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
                  paddingTop: '1rem',
                  marginBottom: '1rem'
                }}>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '0.5rem' }}>Or create a new jam session:</p>
                </div>
              </>
            ) : (
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem' }}>
                No active jam sessions found. Create a new one:
              </p>
            )}

            <button
              onClick={() => {
                setShowJamSessionSelector(false);
                setSelectedLocalTrackForJam(null);
                setActiveTab('jam-session');
                setShowCreateJamModal(true);
              }}
              style={{
                width: '100%',
                padding: '1rem',
                background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.2), rgba(255, 0, 255, 0.2))',
                border: '2px solid rgba(0, 212, 255, 0.5)',
                borderRadius: '8px',
                color: '#00d4ff',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '1rem'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, rgba(0, 212, 255, 0.3), rgba(255, 0, 255, 0.3))';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, rgba(0, 212, 255, 0.2), rgba(255, 0, 255, 0.2))';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              ➕ Create New Jam Session
            </button>

            <button
              onClick={() => {
                setShowJamSessionSelector(false);
                setSelectedLocalTrackForJam(null);
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Spotify Playlist Manager now embedded in Social tab card */}
    </div>
      )}
    </>
  );
};

// Add Social Link Modal Component - BEATSEEK Red + Blue Styling with White Platform Buttons
const AddSocialLinkModal = ({ onAdd, onClose, initialPlatform = '', initialUsername = '', isEditing = false }) => {
  const [selectedPlatform, setSelectedPlatform] = useState(initialPlatform);
  const [username, setUsername] = useState(initialUsername);

  // Enhanced platform data - Grouped by Music/Producer vs Personal/Social (alphabetical within groups)
  const platforms = [
    // Music & Producer Platforms (alphabetical)
    { 
      id: 'beatport', 
      name: 'Beatport', 
      logoUrl: '/images/social-logos/beatport.png',
      color: '#01FF95',
      placeholder: 'your-artist-page' 
    },
    { 
      id: 'mixcloud', 
      name: 'Mixcloud', 
      logoUrl: '/images/social-logos/mixcloud.svg',
      color: '#314359',
      placeholder: 'your-profile' 
    },
    { 
      id: 'soundcloud', 
      name: 'SoundCloud', 
      logoUrl: '/images/social-logos/soundcloud.svg',
      color: '#FF3300',
      placeholder: 'your-profile' 
    },
    { 
      id: 'spotify', 
      name: 'Spotify', 
      logoUrl: '/images/social-logos/spotify.png',
      color: '#1DB954',
      placeholder: 'your-profile' 
    },
    { 
      id: 'traxsource', 
      name: 'Traxsource', 
      logoUrl: '/images/social-logos/traxsource.png',
      color: '#FF6B00',
      placeholder: 'your-artist-page' 
    },
    { 
      id: 'twitch', 
      name: 'Twitch', 
      logoUrl: '/images/social-logos/twitch.svg',
      color: '#9146FF',
      placeholder: 'your_channel' 
    },
    { 
      id: 'lfgtv', 
      name: 'LFG.TV', 
      logoUrl: '/images/social-logos/lfgtv.png',
      color: '#FF6B35',
      placeholder: 'your_channel' 
    },
    // Personal & Social Platforms (alphabetical)
    { 
      id: 'facebook', 
      name: 'Facebook', 
      logoUrl: '/images/social-logos/facebook.svg',
      color: '#1877F2',
      placeholder: 'your.name' 
    },
    { 
      id: 'instagram', 
      name: 'Instagram', 
      logoUrl: '/images/social-logos/instagram.svg',
      color: '#E4405F',
      placeholder: 'your_username' 
    },
    { 
      id: 'tiktok', 
      name: 'TikTok', 
      logoUrl: '/images/social-logos/tiktok.svg',
      color: '#000000',
      placeholder: 'your_username' 
    },
    { 
      id: 'youtube', 
      name: 'YouTube', 
      logoUrl: '/images/social-logos/youtube.svg',
      color: '#FF0000',
      placeholder: 'channel-name' 
    },
    { 
      id: 'website', 
      name: 'Website', 
      logoUrl: null, // No icon - just text
      color: '#6B7280',
      placeholder: 'https://yoursite.com' 
    },
    { 
      id: 'discord', 
      name: 'Discord', 
      logoUrl: null,
      color: '#5865F2',
      placeholder: 'invite-code or server-link' 
    },
    { 
      id: 'other', 
      name: 'Other', 
      logoUrl: null,
      color: '#9CA3AF',
      placeholder: 'Full URL to your profile' 
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedPlatform && username.trim()) {
      onAdd(selectedPlatform, username.trim());
      setSelectedPlatform('');
      setUsername('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-lg flex items-center justify-center z-50">
      {/* BEATSEEK Branded Modal - Red Header + Blue Accents */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 border-2 border-blue-500/30 backdrop-filter backdrop-blur-xl">
        {/* BEATSEEK Header with Red Branding */}
        <div className="relative p-4 border-b border-red-500/20 bg-gradient-to-r from-red-600/10 to-slate-800/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* BEATSEEK Logo/Icon - Red */}
              <div className="w-7 h-7 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center shadow-lg">
                <Music className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">{isEditing ? 'Edit Your Link' : 'Choose Your Platform'}</h3>
                <p className="text-xs text-red-400 font-medium">BEATSEEK Community</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-all duration-200 p-1.5 rounded-lg hover:bg-blue-500/20"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4">
          {/* Platform Selection with White Styling */}
          <div className="mb-4">
            <div className="grid grid-cols-3 gap-2">
              {platforms.map((platform) => (
                <button
                  key={platform.id}
                  type="button"
                  onClick={() => setSelectedPlatform(platform.id)}
                  className={`group flex flex-col items-center gap-1.5 p-2.5 rounded-lg border transition-all duration-300 ${
                    selectedPlatform === platform.id
                      ? 'border-blue-500 bg-blue-500/10 text-white shadow-lg shadow-blue-500/20'
                      : 'border-white/20 bg-transparent text-white hover:border-white/40 hover:bg-white/5'
                  }`}
                >
                  {/* Real Social Media Logo or Icon */}
                  <div className={`w-5 h-5 rounded flex items-center justify-center transition-all duration-300 ${
                    selectedPlatform === platform.id ? 'bg-white/10' : 'bg-white/5 group-hover:bg-white/10'
                  }`}>
                    {platform.logoUrl ? (
                      <img 
                        src={platform.logoUrl} 
                        alt={`${platform.name} logo`}
                        className={`w-3.5 h-3.5 transition-all duration-300 ${
                          selectedPlatform === platform.id ? 'brightness-110' : 'brightness-75 group-hover:brightness-100'
                        }`}
                        style={{ 
                          filter: platform.logoUrl.endsWith('.png') 
                            ? (selectedPlatform === platform.id ? 'brightness(0) saturate(0) invert(1) contrast(2)' : 'brightness(0) saturate(0) invert(0.8) contrast(2)')
                            : (selectedPlatform === platform.id ? 'none' : 'brightness(0) saturate(0) invert(1) contrast(2)'),
                          objectFit: 'contain',
                          maxWidth: '14px',
                          width: '14px',
                          height: '14px'
                        }}
                      />
                    ) : (
                      <svg 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="white" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                      </svg>
                    )}
                  </div>
                  <span className="text-xs font-medium truncate">{platform.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Username Input with Blue Accents */}
          {selectedPlatform && (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-300 mb-2 flex items-center gap-2">
                <ExternalLink className="w-3.5 h-3.5 text-blue-400" />
                {platforms.find(p => p.id === selectedPlatform)?.name} Profile
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={platforms.find(p => p.id === selectedPlatform)?.placeholder}
                  className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  autoFocus
                />
              </div>
              <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 text-blue-400" />
                {selectedPlatform === 'website' || selectedPlatform === 'other'
                  ? 'Enter full URL including https://'
                  : 'Enter username without @ symbol'
                }
              </p>
            </div>
          )}

          {/* Action Buttons - Blue Primary */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-all duration-200 font-medium text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedPlatform || !username.trim()}
              className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm shadow-lg hover:shadow-blue-500/20"
            >
              Add Link
            </button>
          </div>
        </form>
        
        {/* BEATSEEK Footer Branding */}
        <div className="px-4 pb-3">
          <div className="text-center">
            <p className="text-xs text-slate-500">
              Powered by <span className="text-red-400 font-semibold">BEATSEEK</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple Login Form Component
const AuthForm = ({ onLogin, onRegister, authError, isSignupMode, setIsSignupMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [userType, setUserType] = useState('regular');
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [linkedinProfile, setLinkedinProfile] = useState('');
  const [demoSubmissionEmail, setDemoSubmissionEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const userTypes = [
    { value: 'regular', label: '👤 Regular User' },
    { value: 'dj', label: '🎧 DJ' },
    { value: 'producer', label: '🎹 Producer' },
    { value: 'streamer', label: '📡 Streamer' },
    { value: 'mod', label: '🛡️ Mod' },
    { value: 'label', label: '💿 Record Label' },
    { value: 'ar', label: '🎤 A&R Representative' },
    { value: 'venue_owner', label: '🏛️ Venue Owner' },
    { value: 'promoter', label: '📣 Promoter' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    let success = false;
    if (isSignupMode) {
      const companyInfo = {
        companyName,
        companyWebsite,
        linkedinProfile,
        demoSubmissionEmail
      };
      success = await onRegister(name, email, password, userType, companyInfo);
    } else {
      success = await onLogin(email, password);
    }
    
    setIsLoading(false);
    if (!success) {
      setPassword('');
    }
  };

  const showCompanyFields = userType === 'label' || userType === 'ar';

  return (
    <Card className="auth-card">
      <CardContent className="auth-content">
        <div className="auth-toggle">
          <Button 
            variant={!isSignupMode ? "default" : "ghost"} 
            size="sm"
            onClick={() => setIsSignupMode(false)}
          >
            Login
          </Button>
          <Button 
            variant={isSignupMode ? "default" : "ghost"} 
            size="sm"
            onClick={() => setIsSignupMode(true)}
          >
            Sign Up
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {isSignupMode && (
            <>
              <div className="form-field">
                <Label htmlFor="name">Name:</Label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-field">
                <Label htmlFor="userType">I am a:</Label>
                <select
                  id="userType"
                  value={userType}
                  onChange={(e) => setUserType(e.target.value)}
                  className="form-input"
                  style={{ 
                    padding: '0.5rem', 
                    background: 'rgba(30, 20, 50, 0.8)',
                    border: '1px solid rgba(147, 51, 234, 0.3)',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                >
                  {userTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {showCompanyFields && (
                <div style={{ 
                  background: 'rgba(147, 51, 234, 0.1)', 
                  border: '1px solid rgba(147, 51, 234, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '0.5rem'
                }}>
                  <p style={{ 
                    color: '#fbbf24', 
                    fontSize: '0.75rem', 
                    marginBottom: '0.75rem' 
                  }}>
                    ✨ {userType === 'label' ? 'Label' : 'A&R'} Verification Info (Optional)
                  </p>
                  
                  <div className="form-field" style={{ marginBottom: '0.5rem' }}>
                    <Label htmlFor="companyName" style={{ fontSize: '0.8rem' }}>
                      {userType === 'label' ? 'Label Name:' : 'Company/Label:'}
                    </Label>
                    <input
                      id="companyName"
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder={userType === 'label' ? 'Your label name' : 'Company you represent'}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-field" style={{ marginBottom: '0.5rem' }}>
                    <Label htmlFor="companyWebsite" style={{ fontSize: '0.8rem' }}>Official Website:</Label>
                    <input
                      id="companyWebsite"
                      type="url"
                      value={companyWebsite}
                      onChange={(e) => setCompanyWebsite(e.target.value)}
                      placeholder="https://..."
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-field">
                    <Label htmlFor="linkedinProfile" style={{ fontSize: '0.8rem' }}>LinkedIn Profile:</Label>
                    <input
                      id="linkedinProfile"
                      type="url"
                      value={linkedinProfile}
                      onChange={(e) => setLinkedinProfile(e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-field" style={{ marginTop: '0.5rem' }}>
                    <Label htmlFor="demoSubmissionEmail" style={{ fontSize: '0.8rem' }}>
                      Demo Submission Email:
                    </Label>
                    <input
                      id="demoSubmissionEmail"
                      type="email"
                      value={demoSubmissionEmail}
                      onChange={(e) => setDemoSubmissionEmail(e.target.value)}
                      placeholder="demos@yourlabel.com"
                      className="form-input"
                    />
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem', marginTop: '0.25rem' }}>
                      This email will be shown when you enable &quot;Accepting Demos&quot; in your profile
                    </p>
                  </div>
                  
                  <p style={{ 
                    color: 'rgba(255,255,255,0.5)', 
                    fontSize: '0.65rem', 
                    marginTop: '0.5rem' 
                  }}>
                    Providing verification info helps you post in Demo Requests. Admin verification pending.
                  </p>
                </div>
              )}
            </>
          )}
          
          <div className="form-field">
            <Label htmlFor="email">{isSignupMode ? "Email:" : "Username or Email:"}</Label>
            <input
              id="email"
              name={isSignupMode ? "email" : "identifier"}
              type={isSignupMode ? "email" : "text"}
              inputMode={isSignupMode ? "email" : "text"}
              autoComplete={isSignupMode ? "email" : "username"}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={isSignupMode ? "your@email.com" : "username or email"}
              required
              className="form-input"
            />
          </div>
          
          <div className="form-field">
            <Label htmlFor="password">Password:</Label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isSignupMode ? "Min 6 characters" : "password"}
                required
                minLength={isSignupMode ? 6 : undefined}
                className="form-input"
                style={{ paddingRight: '2.75rem', width: '100%', boxSizing: 'border-box' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
                data-testid="toggle-password-visibility"
                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', padding: 0 }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          
          <Button type="submit" disabled={isLoading} className="auth-button">
            {isLoading 
              ? (isSignupMode ? 'Creating Account...' : 'Logging in...') 
              : (isSignupMode ? 'Create Account' : 'Login')
            }
          </Button>
          
          {/* Social Login Divider */}
          <div className="social-login-divider">
            <span>or continue with</span>
          </div>
          
          {/* Social Login Buttons */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <div className="social-login-buttons">
            <button
              type="button"
              className="social-login-btn google-btn"
              onClick={() => {
                // Redirect to root - the hash with session_id will be preserved
                // App.js useEffect handles session_id exchange on mount
                const redirectUrl = window.location.origin + '/';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              title="Sign in with Google"
            >
              <svg viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </button>
            
            <button
              type="button"
              className="social-login-btn discord-btn"
              onClick={async () => {
                try {
                  const response = await fetch(`${BACKEND_URL}/api/auth/discord/url`);
                  const data = await response.json();
                  if (data.url) {
                    window.location.href = data.url;
                  }
                } catch (error) {
                  console.error('Discord auth error:', error);
                }
              }}
              title="Sign in with Discord"
            >
              <svg viewBox="0 0 24 24" fill="#5865F2">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </button>
            
            <button
              type="button"
              className="social-login-btn twitter-btn"
              onClick={async () => {
                try {
                  const response = await fetch(`${BACKEND_URL}/api/auth/twitter/url`);
                  const data = await response.json();
                  if (data.url) {
                    window.location.href = data.url;
                  }
                } catch (error) {
                  console.error('Twitter auth error:', error);
                }
              }}
              title="Sign in with X"
            >
              <svg viewBox="0 0 24 24">
                <path fill="#000000" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </button>
          </div>
          
          {authError && (
            <p className="error-message">{String(authError)}</p>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

// Spotify OAuth Callback Component
const SpotifyCallback = () => {
  useEffect(() => {
    const handleCallback = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      
      if (error) {
        console.error('Spotify OAuth error:', error);
        // Redirect back to main app with error
        window.location.href = '/?spotify_error=' + encodeURIComponent(error);
        return;
      }
      
      if (code) {
        console.log('Spotify OAuth code received:', code);
        // Send code to backend for token exchange
        fetch(`${BACKEND_URL}/api/spotify/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code })
        })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            // Store tokens and redirect back to main app
            localStorage.setItem('spotify_access_token', data.access_token);
            localStorage.setItem('spotify_refresh_token', data.refresh_token);
            window.location.href = '/?spotify_connected=true';
          } else {
            console.error('Token exchange failed:', data.error);
            window.location.href = '/?spotify_error=' + encodeURIComponent(data.error);
          }
        })
        .catch(error => {
          console.error('Callback request failed:', error);
          window.location.href = '/?spotify_error=' + encodeURIComponent(error.message);
        });
      }
    };
    
    handleCallback();
  }, []);
  
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      backgroundColor: '#0f0f23',
      color: 'white'
    }}>
      <div style={{ textAlign: 'center' }}>
        <h2>🎵 Connecting to Spotify...</h2>
        <p>Please wait while we complete the authentication process.</p>
      </div>
    </div>
  );
};

// Spotify Playlist Manager Component
const SpotifyPlaylistManager = ({ 
  isOpen, 
  onClose, 
  accessToken, 
  playlists, 
  setPlaylists, 
  isLoading, 
  setIsLoading 
}) => {
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Load user's playlists when modal opens
  useEffect(() => {
    if (isOpen && accessToken && accessToken !== 'mock_token_for_testing') {
      loadPlaylists();
    } else if (accessToken === 'mock_token_for_testing') {
      // Mock playlists for testing
      setPlaylists([
        { id: '1', name: 'BEATSEEK Discoveries', description: 'Tracks found via BEATSEEK', tracks_total: 12 },
        { id: '2', name: 'Electronic Favorites', description: 'My favorite electronic tracks', tracks_total: 45 },
        { id: '3', name: 'Deep House Mix', description: 'Curated deep house collection', tracks_total: 28 }
      ]);
    }
  }, [isOpen, accessToken]);

  const loadPlaylists = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API}/spotify/playlists?access_token=${encodeURIComponent(accessToken)}`);
      
      if (response.ok) {
        const data = await response.json();
        setPlaylists(data.playlists || []);
      } else {
        throw new Error('Failed to load playlists');
      }
    } catch (error) {
      console.error('Error loading playlists:', error);
      alert('Failed to load playlists. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) {
      alert('Please enter a playlist name');
      return;
    }

    try {
      const spotifyToken = localStorage.getItem('spotify_access_token');
      
      if (!spotifyToken) {
        alert('Please connect your Spotify account first');
        return;
      }

      // Call backend API to create Spotify playlist
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/spotify/playlist/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: spotifyToken,
          playlist_name: newPlaylistName,
          playlist_description: newPlaylistDescription || 'Created from BEATSEEK'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create playlist');
      }

      const data = await response.json();
      
      // Refresh playlists list
      await loadPlaylists();
      
      setShowCreatePlaylist(false);
      setNewPlaylistName('');
      setNewPlaylistDescription('');
      alert(`✅ Playlist "${data.playlist_name}" created successfully!`);
      
    } catch (error) {
      console.error('Error creating playlist:', error);
      alert('Failed to create playlist. Please try again.');
    }
  };

  const searchTracks = async () => {
    if (!searchTerm.trim()) return;

    try {
      setIsSearching(true);
      
      if (accessToken === 'mock_token_for_testing') {
        // Mock search results
        const mockResults = [
          { id: '1', name: 'One More Time', artist: 'Daft Punk', album: 'Discovery' },
          { id: '2', name: 'Strobe', artist: 'Deadmau5', album: 'For Lack of a Better Name' },
          { id: '3', name: 'Levels', artist: 'Avicii', album: 'Levels' }
        ];
        setSearchResults(mockResults);
        return;
      }

      // Real Spotify search would go here
      alert('Track search feature coming soon!');
    } catch (error) {
      console.error('Error searching tracks:', error);
    } finally {
      setIsSearching(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="spotify-playlist-modal" onClick={e => e.stopPropagation()}>
        <div className="spotify-modal-header">
          <h2>🎵 Spotify Playlist Manager</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="spotify-modal-content">
          {/* Create New Playlist Section */}
          <div className="create-playlist-section">
            <Button 
              onClick={() => setShowCreatePlaylist(!showCreatePlaylist)}
              className="create-playlist-btn"
            >
              + Create New Playlist
            </Button>

            {showCreatePlaylist && (
              <div className="create-playlist-form">
                <input
                  type="text"
                  placeholder="Playlist name"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  className="playlist-input"
                />
                <textarea
                  placeholder="Description (optional)"
                  value={newPlaylistDescription}
                  onChange={(e) => setNewPlaylistDescription(e.target.value)}
                  className="playlist-textarea"
                  rows="2"
                />
                <div className="create-actions">
                  <Button onClick={createPlaylist} size="sm">Create</Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowCreatePlaylist(false);
                      setNewPlaylistName('');
                      setNewPlaylistDescription('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Your Playlists Section */}
          <div className="playlists-section">
            <h3>Your Playlists</h3>
            {isLoading ? (
              <div className="loading-playlists">Loading playlists...</div>
            ) : (
              <div className="playlists-grid">
                {playlists.map(playlist => (
                  <div key={playlist.id} className="playlist-card">
                    <div className="playlist-info">
                      <h4>{playlist.name}</h4>
                      <p className="playlist-description">{playlist.description}</p>
                      <p className="playlist-tracks">{playlist.tracks_total} tracks</p>
                    </div>
                    <div className="playlist-actions">
                      <Button size="sm" variant="outline">
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Track Search Section */}
          <div className="track-search-section">
            <h3>Search & Add Tracks</h3>
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Search for tracks to add..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchTracks()}
                className="track-search-input"
              />
              <Button onClick={searchTracks} disabled={isSearching}>
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map(track => (
                  <div key={track.id} className="search-result-item">
                    <div className="track-info">
                      <span className="track-name">{track.name}</span>
                      <span className="track-artist"> - {track.artist}</span>
                    </div>
                    <Button size="sm">Add to Playlist</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Playlist Modals moved to App component level */}

    </div>
  );
};

function App() {
  // PLAYLIST MODAL STATE - MOVED TO APP LEVEL TO FIX REACT 19 ISSUE
  const [showCreatePlaylistModal, setShowCreatePlaylistModal] = useState(false);
  const [showPlaylistDetailModal, setShowPlaylistDetailModal] = useState(false);
  const [showAddTrackModal, setShowAddTrackModal] = useState(false);
  const [showManageSuggestionsModal, setShowManageSuggestionsModal] = useState(false);
  const [showSpotifyLibraryModal, setShowSpotifyLibraryModal] = useState(false);
  const [showPlaylistsModal, setShowPlaylistsModal] = useState(false);
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [playlistModalData, setPlaylistModalData] = useState(null);
  const [activePlaylistForAdding, setActivePlaylistForAdding] = useState(null); // Track which playlist we're adding to
  const [addTrackSearchResults, setAddTrackSearchResults] = useState([]); // Spotify search results for Add Track modal
  const [addTrackSearching, setAddTrackSearching] = useState(false);
  
  // Spotify connection state for Spotify Library Modal in App
  const [appSpotifyConnected, setAppSpotifyConnected] = useState(() => {
    return !!localStorage.getItem('spotify_access_token');
  });
  const [appSpotifyPlaylists, setAppSpotifyPlaylists] = useState([]);
  
  // Fetch Spotify playlists when modal opens
  useEffect(() => {
    const fetchSpotifyPlaylists = async () => {
      const token = localStorage.getItem('spotify_access_token');
      if (!token || !showSpotifyLibraryModal) return;
      
      try {
        const response = await fetch('https://api.spotify.com/v1/me/playlists?limit=50', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAppSpotifyPlaylists(data.items || []);
        }
      } catch (err) {
        console.error('Error fetching Spotify playlists:', err);
      }
    };
    
    fetchSpotifyPlaylists();
  }, [showSpotifyLibraryModal]);

  // PLAYLIST PLAYER STATE
  const [playerState, setPlayerState] = useState({
    currentTrack: null,
    playlist: null, // { name, tracks, currentIndex, totalTracks }
    isPlaying: false,
    playbackMode: 'normal', // 'normal', 'reverse', 'shuffle'
    queue: []
  });

  // PLAYLIST PLAYER FUNCTIONS
  
  // Add track to playlist - defined in App for access in Add Track modal
  const addTrackToPlaylist = async (playlistId, trackData) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      throw new Error('User not authenticated');
    }
    
    const response = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}/tracks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(trackData)
    });
    
    if (response.ok) {
      const result = await response.json();
      return result;
    } else {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to add track to playlist');
    }
  };

  const playPlaylist = (playlist, mode = 'normal') => {
    if (!playlist || !playlist.tracks || playlist.tracks.length === 0) {
      alert('No tracks in playlist to play');
      return;
    }

    let queue = [...playlist.tracks];
    
    if (mode === 'reverse') {
      queue = queue.reverse();
    } else if (mode === 'shuffle') {
      // Fisher-Yates shuffle
      for (let i = queue.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [queue[i], queue[j]] = [queue[j], queue[i]];
      }
    }

    setPlayerState({
      currentTrack: queue[0],
      playlist: {
        id: playlist.id,
        name: playlist.name,
        tracks: queue,
        currentIndex: 0,
        totalTracks: queue.length
      },
      isPlaying: true,
      playbackMode: mode,
      queue: queue
    });
  };

  const playTrack = (track, playlistContext = null) => {
    setPlayerState(prev => ({
      ...prev,
      currentTrack: track,
      isPlaying: true,
      playlist: playlistContext || prev.playlist
    }));
  };

  const handlePlayPause = () => {
    setPlayerState(prev => ({
      ...prev,
      isPlaying: !prev.isPlaying
    }));
  };

  const handleNext = () => {
    if (!playerState.playlist || !playerState.playlist.tracks) return;
    
    const nextIndex = playerState.playlist.currentIndex + 1;
    if (nextIndex < playerState.playlist.tracks.length) {
      setPlayerState(prev => ({
        ...prev,
        currentTrack: prev.playlist.tracks[nextIndex],
        playlist: {
          ...prev.playlist,
          currentIndex: nextIndex
        },
        isPlaying: true
      }));
    } else {
      // End of playlist
      setPlayerState(prev => ({
        ...prev,
        isPlaying: false
      }));
    }
  };

  const handlePrevious = () => {
    if (!playerState.playlist || !playerState.playlist.tracks) return;
    
    const prevIndex = playerState.playlist.currentIndex - 1;
    if (prevIndex >= 0) {
      setPlayerState(prev => ({
        ...prev,
        currentTrack: prev.playlist.tracks[prevIndex],
        playlist: {
          ...prev.playlist,
          currentIndex: prevIndex
        },
        isPlaying: true
      }));
    }
  };

  const handleSeek = (time) => {
    // Handled in PlaylistPlayer component
  };

  const handleExitPlaylist = () => {
    setPlayerState({
      currentTrack: null,
      playlist: null,
      isPlaying: false,
      playbackMode: 'normal',
      queue: []
    });
  };

  return (
    <SpotifyPlayerProvider>
      <JamWebSocketProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route 
                path="/" 
                element={
                  <ElectroShazam 
                    showCreatePlaylistModal={showCreatePlaylistModal}
                    setShowCreatePlaylistModal={setShowCreatePlaylistModal}
                    showPlaylistDetailModal={showPlaylistDetailModal}
                    setShowPlaylistDetailModal={setShowPlaylistDetailModal}
                    showAddTrackModal={showAddTrackModal}
                    setShowAddTrackModal={setShowAddTrackModal}
                    showManageSuggestionsModal={showManageSuggestionsModal}
                    setShowManageSuggestionsModal={setShowManageSuggestionsModal}
                    showSpotifyLibraryModal={showSpotifyLibraryModal}
                    setShowSpotifyLibraryModal={setShowSpotifyLibraryModal}
                    currentPlaylist={currentPlaylist}
                    setCurrentPlaylist={setCurrentPlaylist}
                    playlistModalData={playlistModalData}
                    setPlaylistModalData={setPlaylistModalData}
                    activePlaylistForAdding={activePlaylistForAdding}
                    setActivePlaylistForAdding={setActivePlaylistForAdding}
                    playPlaylist={playPlaylist}
                    playTrack={playTrack}
                    showPlaylistsModal={showPlaylistsModal}
                    setShowPlaylistsModal={setShowPlaylistsModal}
                    addTrackSearchResults={addTrackSearchResults}
                    setAddTrackSearchResults={setAddTrackSearchResults}
                    addTrackSearching={addTrackSearching}
                    setAddTrackSearching={setAddTrackSearching}
                  />
                } 
              />
              <Route path="/profile/:userId" element={<ProfileView />} />
              <Route path="/spotify/callback" element={<SpotifyCallback />} />
              <Route path="/early-access" element={<EarlyAccess />} />
              <Route path="/poster" element={<PosterViewer />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
            </Routes>
          </BrowserRouter>

          {/* CREATE PLAYLIST MODAL - RENDERED AT APP LEVEL */}
          {showCreatePlaylistModal && ReactDOM.createPortal(
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0, 0, 0, 0.9)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 999999
              }}
              onClick={() => setShowCreatePlaylistModal(false)}
            >
              <div 
                style={{
                  background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.98), rgba(30, 20, 40, 0.98))',
                  border: '2px solid rgba(0, 255, 255, 0.4)',
                  borderRadius: '20px',
                  padding: '2rem',
                  maxWidth: '500px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ marginBottom: '1.5rem' }}>
                  <h2 style={{ 
                    margin: 0,
                    background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: '1.8rem'
                  }}>
                    🎵 Create New Playlist
                  </h2>
                </div>

                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  if (playlistModalData && playlistModalData.createPlaylist) {
                    playlistModalData.createPlaylist(
                      formData.get('name'),
                      formData.get('description'),
                      formData.get('is_public') === 'on'
                    );
                  }
                }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      color: 'rgba(0, 255, 255, 0.9)',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}>
                      Playlist Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      maxLength={100}
                      placeholder="My Awesome Playlist"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(26, 26, 46, 0.8)',
                        border: '2px solid rgba(0, 255, 255, 0.3)',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      color: 'rgba(0, 255, 255, 0.9)',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}>
                      Description (Optional)
                    </label>
                    <textarea
                      name="description"
                      maxLength={500}
                      rows={3}
                      placeholder="Describe your playlist..."
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(26, 26, 46, 0.8)',
                        border: '2px solid rgba(0, 255, 255, 0.3)',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '1rem',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '2rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      color: 'rgba(255, 255, 255, 0.9)'
                    }}>
                      <input
                        type="checkbox"
                        name="is_public"
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          accentColor: '#00ffff'
                        }}
                      />
                      <span>
                        <strong>Make this playlist public</strong>
                        <br />
                        <small style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                          Public playlists allow others to suggest tracks for your approval
                        </small>
                      </span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      type="button"
                      onClick={() => setShowCreatePlaylistModal(false)}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Create Playlist
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )}

          {/* PLAYLIST DETAIL MODAL - AT APP LEVEL */}
          {showPlaylistDetailModal && currentPlaylist && ReactDOM.createPortal(
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.9)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 999999,
                padding: '2rem'
              }}
              onClick={() => {
                setShowPlaylistDetailModal(false);
                setCurrentPlaylist(null);
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.98), rgba(30, 20, 40, 0.98))',
                  border: '2px solid rgba(0, 255, 255, 0.4)',
                  borderRadius: '20px',
                  padding: '2rem',
                  maxWidth: '900px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h2 style={{
                        margin: '0 0 0.5rem 0',
                        background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: '1.8rem'
                      }}>
                        {currentPlaylist.name}
                      </h2>
                      <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                        {currentPlaylist.tracks?.length || 0} tracks • {Math.floor((currentPlaylist.total_duration_ms || 0) / 60000)} min
                      </div>
                    </div>
                    {/* Back and Delete buttons */}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => {
                          setShowPlaylistDetailModal(false);
                          setCurrentPlaylist(null);
                          setShowPlaylistsModal(true); // Go back to playlists list
                        }}
                        style={{
                          padding: '0.4rem 0.8rem',
                          background: 'rgba(0, 255, 255, 0.2)',
                          border: '2px solid rgba(0, 255, 255, 0.5)',
                          borderRadius: '6px',
                          color: '#00ffff',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        ← Back to Playlists
                      </button>
                      {playlistModalData?.user && currentPlaylist.owner_id === playlistModalData.user.id && (
                        <button
                          onClick={() => {
                            if (playlistModalData?.deletePlaylist) {
                              playlistModalData.deletePlaylist(currentPlaylist.id);
                            }
                          }}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: 'rgba(255, 0, 0, 0.2)',
                            border: '2px solid rgba(255, 0, 0, 0.5)',
                            borderRadius: '6px',
                            color: '#ff4444',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tracks */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    {/* Left: Add Track Link */}
                    {playlistModalData?.user && currentPlaylist.owner_id === playlistModalData.user.id && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span
                          onClick={() => currentPlaylist.tracks?.length < 10 && setShowAddTrackModal(true)}
                          style={{
                            color: currentPlaylist.tracks?.length >= 10 ? '#ff4444' : '#00ffff',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            cursor: currentPlaylist.tracks?.length >= 10 ? 'not-allowed' : 'pointer',
                            opacity: currentPlaylist.tracks?.length >= 10 ? 0.5 : 1,
                            textDecoration: currentPlaylist.tracks?.length >= 10 ? 'none' : 'none',
                            transition: 'color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (currentPlaylist.tracks?.length < 10) {
                              e.currentTarget.style.color = '#ff00ff';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (currentPlaylist.tracks?.length < 10) {
                              e.currentTarget.style.color = '#00ffff';
                            }
                          }}
                        >
                          {currentPlaylist.tracks?.length >= 10 ? '⚠️ limit reached (10)' : '+ add track to playlist'}
                        </span>
                      </div>
                    )}
                    
                    {/* Right: Playback Controls + Refresh */}
                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                      {/* Refresh Button */}
                      <button
                        onClick={async () => {
                          if (playlistModalData?.fetchMyPlaylists) {
                            await playlistModalData.fetchMyPlaylists();
                          }
                          const updatedPlaylist = await fetch(`${process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL}/api/playlists/${currentPlaylist.id}`).then(r => r.json());
                          setCurrentPlaylist(updatedPlaylist);
                        }}
                        style={{
                          padding: '0.3rem 0.6rem',
                          background: 'rgba(100, 100, 255, 0.2)',
                          border: '1px solid rgba(100, 100, 255, 0.5)',
                          borderRadius: '4px',
                          color: '#6666ff',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                        title="Refresh playlist"
                      >
                        🔄
                      </button>

                      {/* Playback Controls */}
                      {currentPlaylist.tracks && currentPlaylist.tracks.length > 0 && playlistModalData?.playPlaylist && (
                        <>
                          <button
                            onClick={() => {
                              playlistModalData.playPlaylist(currentPlaylist, 'normal');
                              setShowPlaylistDetailModal(false);
                              if (playlistModalData?.setActiveTab) {
                                playlistModalData.setActiveTab('social');
                              }
                            }}
                            style={{
                              padding: '0.3rem 0.6rem',
                              background: 'rgba(0, 255, 0, 0.2)',
                              border: '1px solid rgba(0, 255, 0, 0.5)',
                              borderRadius: '4px',
                              color: '#00ff00',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                            title="Play in order"
                          >
                            ▶️ Play
                          </button>
                          <button
                            onClick={() => {
                              playlistModalData.playPlaylist(currentPlaylist, 'reverse');
                              setShowPlaylistDetailModal(false);
                              if (playlistModalData?.setActiveTab) {
                                playlistModalData.setActiveTab('social');
                              }
                            }}
                            style={{
                              padding: '0.3rem 0.6rem',
                              background: 'rgba(255, 165, 0, 0.2)',
                              border: '1px solid rgba(255, 165, 0, 0.5)',
                              borderRadius: '4px',
                              color: '#FFA500',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                            title="Play in reverse"
                          >
                            🔄 Reverse
                          </button>
                          <button
                            onClick={() => {
                              playlistModalData.playPlaylist(currentPlaylist, 'shuffle');
                              setShowPlaylistDetailModal(false);
                              if (playlistModalData?.setActiveTab) {
                                playlistModalData.setActiveTab('social');
                              }
                            }}
                            style={{
                              padding: '0.3rem 0.6rem',
                              background: 'rgba(255, 0, 255, 0.2)',
                              border: '1px solid rgba(255, 0, 255, 0.5)',
                              borderRadius: '4px',
                              color: '#ff00ff',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                            title="Shuffle playlist"
                          >
                            🔀 Shuffle
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {(!currentPlaylist.tracks || currentPlaylist.tracks.length === 0) ? (
                    <div style={{
                      padding: '2rem',
                      background: 'rgba(0, 255, 255, 0.05)',
                      border: '2px dashed rgba(0, 255, 255, 0.3)',
                      borderRadius: '12px',
                      textAlign: 'center',
                      color: 'rgba(255, 255, 255, 0.7)'
                    }}>
                      <p>No tracks in this playlist yet</p>
                      <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                        Click &quot;+ Add Track&quot; above to get started
                      </p>
                    </div>
                  ) : (
                    <DragDropContext
                      onDragEnd={(result) => {
                        if (!result.destination) return;
                        if (result.source.index === result.destination.index) return;
                        
                        // Only allow owner to reorder
                        if (playlistModalData?.user && currentPlaylist.owner_id === playlistModalData.user.id) {
                          const tracks = Array.from(currentPlaylist.tracks);
                          const [movedTrack] = tracks.splice(result.source.index, 1);
                          tracks.splice(result.destination.index, 0, movedTrack);
                          
                          // Update local state immediately for smooth UX
                          setCurrentPlaylist({ ...currentPlaylist, tracks });
                          
                          // Send new order to backend
                          if (playlistModalData?.reorderPlaylistTracks) {
                            const trackOrder = tracks.map(t => t.track_id);
                            playlistModalData.reorderPlaylistTracks(currentPlaylist.id, trackOrder);
                          }
                        }
                      }}
                    >
                      <Droppable droppableId="playlist-tracks" direction="vertical" type="track">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.5rem',
                              background: snapshot.isDraggingOver ? 'rgba(0, 255, 255, 0.05)' : 'transparent',
                              borderRadius: '8px',
                              transition: 'background 0.2s ease',
                              minHeight: '50px'
                            }}
                          >
                            {currentPlaylist.tracks.map((track, index) => (
                              <Draggable
                                key={`${track.track_id}-${index}`}
                                draggableId={`${track.track_id}-${index}`}
                                index={index}
                                isDragDisabled={!(playlistModalData?.user && currentPlaylist.owner_id === playlistModalData.user.id)}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '1rem',
                                      padding: '0.75rem',
                                      background: snapshot.isDragging ? 'rgba(0, 255, 255, 0.2)' : 'rgba(0, 255, 255, 0.05)',
                                      border: snapshot.isDragging ? '2px solid rgba(0, 255, 255, 0.6)' : '1px solid rgba(0, 255, 255, 0.2)',
                                      borderRadius: '8px',
                                      ...provided.draggableProps.style,
                                      // Override left position during drag to prevent horizontal shift
                                      ...(snapshot.isDragging ? { left: 'auto', right: 'auto' } : {}),
                                      boxSizing: 'border-box',
                                      transition: snapshot.isDragging ? 'none' : 'background 0.2s ease, border 0.2s ease',
                                      userSelect: 'none',
                                      zIndex: snapshot.isDragging ? 1000 : 'auto'
                                    }}
                                  >
                                    {/* Drag Handle - Only show for owner */}
                                    {playlistModalData?.user && currentPlaylist.owner_id === playlistModalData.user.id && (
                                      <div
                                        {...provided.dragHandleProps}
                                        style={{
                                          cursor: 'grab',
                                          color: 'rgba(255, 255, 255, 0.4)',
                                          display: 'flex',
                                          alignItems: 'center'
                                        }}
                                        title="Drag to reorder"
                                      >
                                        <GripVertical size={20} />
                                      </div>
                                    )}
                                    
                                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', minWidth: '30px' }}>{index + 1}.</span>
                                    {track.artwork ? (
                                      <img 
                                        src={track.artwork} 
                                        alt={track.title} 
                                        style={{ width: '50px', height: '50px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div style={{ 
                                      width: '50px', 
                                      height: '50px', 
                                      borderRadius: '6px',
                                      background: 'rgba(0, 255, 255, 0.1)',
                                      border: '1px solid rgba(0, 255, 255, 0.2)',
                                      display: track.artwork ? 'none' : 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '1.3rem',
                                      flexShrink: 0
                                    }}>🎵</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ color: 'white', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {track.title}
                                      </div>
                                      <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                                        {track.artist}
                                      </div>
                                      <div style={{ marginTop: '2px' }}>
                                        <span style={{
                                          display: 'inline-block',
                                          padding: '1px 6px',
                                          borderRadius: '4px',
                                          fontSize: '0.65rem',
                                          fontWeight: '600',
                                          letterSpacing: '0.5px',
                                          background: track.source === 'spotify' 
                                            ? 'rgba(29, 185, 84, 0.2)' 
                                            : track.source === 'discovery' 
                                              ? 'rgba(0, 212, 255, 0.2)' 
                                              : track.source === 'local'
                                                ? 'rgba(255, 165, 0, 0.2)'
                                                : 'rgba(255, 0, 255, 0.2)',
                                          color: track.source === 'spotify' 
                                            ? '#1db954' 
                                            : track.source === 'discovery' 
                                              ? '#00d4ff' 
                                              : track.source === 'local'
                                                ? '#ffa500'
                                                : '#ff00ff',
                                          border: `1px solid ${
                                            track.source === 'spotify' 
                                              ? 'rgba(29, 185, 84, 0.4)' 
                                              : track.source === 'discovery' 
                                                ? 'rgba(0, 212, 255, 0.4)' 
                                                : track.source === 'local'
                                                  ? 'rgba(255, 165, 0, 0.4)'
                                                  : 'rgba(255, 0, 255, 0.4)'
                                          }`
                                        }}>
                                          {track.source === 'spotify' ? 'Spotify' 
                                            : track.source === 'discovery' ? 'Discovery' 
                                            : track.source === 'local' ? 'Local'
                                            : track.source || 'BEATSEEK'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Play Button */}
                                    <button
                                      onClick={() => {
                                        if (playlistModalData?.playTrack) {
                                          playlistModalData.playTrack(track, {
                                            id: currentPlaylist.id,
                                            name: currentPlaylist.name,
                                            tracks: currentPlaylist.tracks,
                                            currentIndex: index,
                                            totalTracks: currentPlaylist.tracks.length
                                          });
                                          setShowPlaylistDetailModal(false);
                                          if (playlistModalData?.setActiveTab) {
                                            playlistModalData.setActiveTab('social');
                                          }
                                        }
                                      }}
                                      style={{
                                        background: 'rgba(0, 255, 0, 0.2)',
                                        border: '1px solid rgba(0, 255, 0, 0.5)',
                                        borderRadius: '4px',
                                        color: '#00ff00',
                                        fontSize: '0.9rem',
                                        padding: '0.25rem 0.5rem',
                                        cursor: 'pointer'
                                      }}
                                      title={`Play ${track.title}`}
                                    >
                                      ▶️
                                    </button>

                                    {/* Remove Button (Owner Only) */}
                                    {playlistModalData?.user && currentPlaylist.owner_id === playlistModalData.user.id && (
                                      <button
                                        onClick={() => {
                                          if (playlistModalData?.removeTrackFromPlaylist) {
                                            playlistModalData.removeTrackFromPlaylist(currentPlaylist.id, track.track_id);
                                          }
                                        }}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: 'rgba(255, 0, 0, 0.7)',
                                          fontSize: '1.2rem',
                                          cursor: 'pointer',
                                          padding: '0.25rem'
                                        }}
                                        title="Remove track"
                                      >
                                        ✕
                                      </button>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* ADD TRACK MODAL - AT APP LEVEL */}
          {showAddTrackModal && currentPlaylist && ReactDOM.createPortal(
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.9)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999999
              }}
              onClick={() => {
                setShowAddTrackModal(false);
                setAddTrackSearchResults([]);
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.98), rgba(30, 20, 40, 0.98))',
                  border: '2px solid rgba(0, 255, 255, 0.4)',
                  borderRadius: '20px',
                  padding: '2rem',
                  maxWidth: '550px',
                  width: '90%',
                  maxHeight: '80vh',
                  overflowY: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header with Close Button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <h3 style={{
                    margin: 0,
                    background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Add Track to {currentPlaylist.name}
                  </h3>
                  <button
                    onClick={() => {
                      setShowAddTrackModal(false);
                      setAddTrackSearchResults([]);
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: 'white',
                      fontSize: '1.2rem',
                      fontWeight: '300'
                    }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
                <p style={{ 
                  margin: '0 0 1.5rem 0', 
                  fontSize: '0.85rem', 
                  color: currentPlaylist.tracks?.length >= 10 ? '#ff4444' : 'rgba(255, 255, 255, 0.6)' 
                }}>
                  {currentPlaylist.tracks?.length || 0} / 10 tracks
                  {currentPlaylist.tracks?.length >= 10 && ' (Limit reached)'}
                </p>

                {/* Quick Spotify Search */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    marginBottom: '0.75rem'
                  }}>
                    <img src="/logos/spotify-logo.png" alt="Spotify" style={{ width: '20px', height: '20px' }} />
                    <span style={{ color: '#1db954', fontWeight: '600', fontSize: '0.9rem' }}>Quick Spotify Search</span>
                  </div>
                  <form 
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const query = e.target.spotifySearch.value.trim();
                      if (!query) return;
                      
                      setAddTrackSearching(true);
                      try {
                        // Use the correct Spotify search endpoint (POST)
                        const response = await fetch(`${BACKEND_URL}/api/spotify/search`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ query, limit: 10 })
                        });
                        const data = await response.json();
                        
                        // Transform Spotify response to match our format
                        const results = (data.tracks || []).map(track => ({
                          title: track.name,
                          artist: track.artists?.join(', ') || 'Unknown Artist',
                          artwork: track.image_url,
                          preview_url: track.preview_url,
                          spotify_uri: track.uri,
                          source: 'spotify'
                        }));
                        
                        setAddTrackSearchResults(results);
                      } catch (err) {
                        console.error('Spotify search error:', err);
                        setAddTrackSearchResults([]);
                      }
                      setAddTrackSearching(false);
                    }}
                    style={{ display: 'flex', gap: '0.5rem' }}
                  >
                    <input
                      name="spotifySearch"
                      type="text"
                      placeholder="Search Spotify..."
                      defaultValue=""
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        background: 'rgba(30, 215, 96, 0.1)',
                        border: '1px solid rgba(30, 215, 96, 0.4)',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '0.9rem'
                      }}
                    />
                    <button
                      type="submit"
                      disabled={addTrackSearching}
                      style={{
                        padding: '0.75rem 1rem',
                        background: addTrackSearching ? '#666' : '#1db954',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontWeight: '600',
                        cursor: addTrackSearching ? 'wait' : 'pointer'
                      }}
                    >
                      {addTrackSearching ? '...' : 'Search'}
                    </button>
                  </form>
                  
                  {/* Search Results */}
                  {addTrackSearchResults.length > 0 && (
                    <div style={{ marginTop: '1rem', maxHeight: '200px', overflowY: 'auto' }}>
                      {addTrackSearchResults.map((track, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.5rem',
                            background: 'rgba(30, 215, 96, 0.05)',
                            borderRadius: '8px',
                            marginBottom: '0.5rem',
                            cursor: 'pointer'
                          }}
                          onClick={async () => {
                            if (currentPlaylist.tracks?.length >= 10) {
                              alert('Playlist is full (10 tracks max)');
                              return;
                            }
                            try {
                              console.log('Adding track to playlist:', currentPlaylist.id, track);
                              const result = await addTrackToPlaylist(currentPlaylist.id, {
                                track_id: track.spotify_uri || `spotify-${Date.now()}`, // Use spotify_uri as track_id
                                title: track.title,
                                artist: track.artist,
                                artwork: track.artwork || track.thumbnail,
                                preview_url: track.preview_url,
                                spotify_uri: track.spotify_uri,
                                source: 'spotify',
                                duration_ms: track.duration_ms || 0
                              });
                              console.log('Track added successfully:', result);
                              
                              // Refresh current playlist from server
                              const token = localStorage.getItem('beatseek_token');
                              const updatedResponse = await fetch(`${BACKEND_URL}/api/playlists/${currentPlaylist.id}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (updatedResponse.ok) {
                                const updatedPlaylist = await updatedResponse.json();
                                setCurrentPlaylist(updatedPlaylist);
                              }
                              
                              alert(`✅ Added "${track.title}" to playlist!`);
                            } catch (err) {
                              console.error('Error adding track:', err.message || err);
                              alert(`Failed to add track: ${err.message || 'Unknown error'}`);
                            }
                          }}
                        >
                          {track.artwork ? (
                            <img 
                              src={track.artwork} 
                              alt="" 
                              style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '4px',
                            background: 'rgba(30, 215, 96, 0.2)',
                            display: track.artwork ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.1rem',
                            flexShrink: 0
                          }}>🎵</div>
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div style={{ 
                              color: 'white', 
                              fontSize: '0.85rem', 
                              fontWeight: '500',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {track.title}
                            </div>
                            <div style={{ 
                              color: 'rgba(255,255,255,0.6)', 
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {track.artist}
                            </div>
                          </div>
                          <span style={{ color: '#1db954', fontSize: '1.2rem' }}>+</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ 
                  borderTop: '1px solid rgba(255,255,255,0.1)', 
                  paddingTop: '1rem',
                  marginTop: '0.5rem'
                }}>
                  <p style={{ 
                    fontSize: '0.8rem', 
                    color: 'rgba(255,255,255,0.5)', 
                    marginBottom: '0.75rem' 
                  }}>
                    Or browse from:
                  </p>
                </div>

                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {/* Spotify Library */}
                  <button
                    onClick={() => {
                      setActivePlaylistForAdding(currentPlaylist);
                      setShowSpotifyLibraryModal(true);
                    }}
                    style={{
                      padding: '1rem',
                      background: 'rgba(30, 215, 96, 0.1)',
                      border: '2px solid rgba(30, 215, 96, 0.4)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(30, 215, 96, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(30, 215, 96, 0.1)'}
                  >
                    <img
                      src="/logos/spotify-logo.png"
                      alt="Spotify"
                      style={{ width: '32px', height: '32px' }}
                    />
                    <div style={{ textAlign: 'left' }}>
                      <div>Import from Spotify</div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        Browse your Spotify library
                      </div>
                    </div>
                  </button>

                  {/* Favorites - Coming Soon */}
                  <button
                    onClick={() => {
                      alert('❤️ Favorites feature coming soon!');
                    }}
                    style={{
                      padding: '1.5rem',
                      background: 'rgba(255, 0, 100, 0.1)',
                      border: '2px solid rgba(255, 0, 100, 0.4)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 0, 100, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 0, 100, 0.1)'}
                  >
                    <span style={{ fontSize: '2rem' }}>❤️</span>
                    <div style={{ textAlign: 'left' }}>
                      <div>From Favorites</div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        Add your favorite tracks
                      </div>
                    </div>
                  </button>

                  {/* Saved Tracks - Coming Soon */}
                  <button
                    onClick={() => {
                      alert('💾 Saved Tracks feature coming soon!');
                    }}
                    style={{
                      padding: '1.5rem',
                      background: 'rgba(100, 100, 255, 0.1)',
                      border: '2px solid rgba(100, 100, 255, 0.4)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(100, 100, 255, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(100, 100, 255, 0.1)'}
                  >
                    <span style={{ fontSize: '2rem' }}>💾</span>
                    <div style={{ textAlign: 'left' }}>
                      <div>From Saved Tracks</div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        Add your saved tracks
                      </div>
                    </div>
                  </button>

                  {/* Discovery Zone */}
                  <button
                    onClick={() => {
                      setShowAddTrackModal(false);
                      setShowPlaylistDetailModal(false);
                      setActivePlaylistForAdding(currentPlaylist);
                      if (playlistModalData?.setActiveTab) {
                        playlistModalData.setActiveTab('discover');
                      }
                    }}
                    style={{
                      padding: '1.5rem',
                      background: 'rgba(0, 255, 255, 0.1)',
                      border: '2px solid rgba(0, 255, 255, 0.4)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)'}
                  >
                    <span style={{ fontSize: '2rem' }}>🔍</span>
                    <div style={{ textAlign: 'left' }}>
                      <div>Browse Discovery Zone</div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        Discover new electronic music
                      </div>
                    </div>
                  </button>

                  {/* Upload Local Tracks */}
                  <button
                    onClick={() => {
                      setShowAddTrackModal(false);
                      // Set active playlist before opening upload
                      setActivePlaylistForAdding(currentPlaylist);
                      if (playlistModalData?.setShowLocalTrackUpload) {
                        playlistModalData.setShowLocalTrackUpload(true);
                      }
                    }}
                    style={{
                      padding: '1rem',
                      background: 'rgba(255, 0, 255, 0.1)',
                      border: '2px solid rgba(255, 0, 255, 0.4)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 0, 255, 0.2)';
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 0, 255, 0.1)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <span style={{ fontSize: '2rem' }}>📁</span>
                    <div style={{ textAlign: 'left', flex: 1 }}>
                      <div style={{ fontSize: '0.95rem' }}>Upload My Tracks</div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        Add MP3/M4A files (Temporary)
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setShowAddTrackModal(false)}
                    style={{
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '8px',
                      color: 'white',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Spotify Library Modal - Browse and add from Spotify playlists */}
          {showSpotifyLibraryModal && ReactDOM.createPortal(
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.95)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999999
              }}
              onClick={() => setShowSpotifyLibraryModal(false)}
            >
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.98), rgba(20, 30, 20, 0.98))',
                  border: '2px solid rgba(30, 215, 96, 0.5)',
                  borderRadius: '20px',
                  padding: '2rem',
                  maxWidth: '600px',
                  width: '90%',
                  maxHeight: '80vh',
                  overflowY: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  <img src="/logos/spotify-logo.png" alt="Spotify" style={{ width: '32px', height: '32px' }} />
                  <h3 style={{ margin: 0, color: '#1db954', fontSize: '1.3rem' }}>
                    Your Spotify Library
                  </h3>
                </div>

                {appSpotifyConnected && appSpotifyPlaylists.length > 0 ? (
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {appSpotifyPlaylists.map((playlist, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          padding: '0.75rem',
                          background: 'rgba(30, 215, 96, 0.05)',
                          border: '1px solid rgba(30, 215, 96, 0.2)',
                          borderRadius: '10px',
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          // Fetch tracks from this Spotify playlist
                          try {
                            const token = localStorage.getItem('spotify_access_token');
                            if (!token) {
                              alert('Please connect Spotify first');
                              return;
                            }
                            
                            const response = await fetch(`https://api.spotify.com/v1/playlists/${playlist.id}/tracks?limit=20`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const data = await response.json();
                            
                            // Transform to our format and show for selection
                            const tracks = (data.items || []).map(item => ({
                              title: item.track?.name,
                              artist: item.track?.artists?.map(a => a.name).join(', '),
                              artwork: item.track?.album?.images?.[0]?.url,
                              preview_url: item.track?.preview_url,
                              spotify_uri: item.track?.uri,
                              source: 'spotify'
                            })).filter(t => t.title);
                            
                            setAddTrackSearchResults(tracks);
                            setShowSpotifyLibraryModal(false);
                          } catch (err) {
                            console.error('Error fetching playlist tracks:', err);
                            alert('Failed to load playlist tracks');
                          }
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(30, 215, 96, 0.15)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(30, 215, 96, 0.05)'}
                      >
                        {playlist.images?.[0]?.url ? (
                          <img 
                            src={playlist.images[0].url} 
                            alt="" 
                            style={{ width: '50px', height: '50px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const ph = e.target.nextElementSibling;
                              if (ph) ph.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div style={{ 
                          width: '50px', 
                          height: '50px', 
                          background: 'rgba(30, 215, 96, 0.3)', 
                          borderRadius: '6px',
                          display: playlist.images?.[0]?.url ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.5rem',
                          flexShrink: 0
                        }}>🎵</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'white', fontWeight: '500' }}>{playlist.name}</div>
                          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                            {playlist.tracks?.total || 0} tracks
                          </div>
                        </div>
                        <span style={{ color: '#1db954' }}>→</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.6)' }}>
                    {appSpotifyConnected ? (
                      <>
                        <p>No playlists found.</p>
                        <p style={{ fontSize: '0.85rem' }}>Create some playlists on Spotify first!</p>
                      </>
                    ) : (
                      <>
                        <p style={{ marginBottom: '1rem' }}>Connect Spotify to browse your playlists</p>
                        <button
                          onClick={async () => {
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/spotify/auth/url`);
                              const data = await response.json();
                              if (data.url) {
                                window.open(data.url, 'spotify_auth', 'width=500,height=700');
                              }
                            } catch (err) {
                              console.error('Spotify auth error:', err);
                            }
                          }}
                          style={{
                            padding: '0.75rem 1.5rem',
                            background: '#1db954',
                            border: 'none',
                            borderRadius: '20px',
                            color: 'white',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Connect Spotify
                        </button>
                      </>
                    )}
                  </div>
                )}

                <button
                  onClick={() => setShowSpotifyLibraryModal(false)}
                  style={{
                    marginTop: '1.5rem',
                    padding: '0.75rem',
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>,
            document.body
          )}

        </div>

        {/* PLAYLIST PLAYER - Bottom Bar */}
        {playerState.currentTrack && (
          <PlaylistPlayer
            currentTrack={playerState.currentTrack}
            playlist={playerState.playlist}
            isPlaying={playerState.isPlaying}
            onPlayPause={handlePlayPause}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSeek={handleSeek}
            onExit={handleExitPlaylist}
            onBackToPlaylist={async () => {
              const playlistId = playerState.playlist?.id;
              if (playlistId) {
                try {
                  const res = await fetch(`${BACKEND_URL}/api/playlists/${playlistId}`);
                  if (res.ok) {
                    const updatedPlaylist = await res.json();
                    setCurrentPlaylist(updatedPlaylist);
                    setShowPlaylistDetailModal(true);
                  }
                } catch (err) {
                  console.error('Failed to fetch playlist:', err);
                }
              }
            }}
            playbackMode={playerState.playbackMode}
          />
        )}

        <Toaster />
        <SonnerToaster position="bottom-center" theme="dark" richColors closeButton />
        <TourBot />
      </JamWebSocketProvider>
    </SpotifyPlayerProvider>
  );
}

export default App;